// What To Eat? - Application Logic
// Firebase + Firestore Configuration
// Firebase-only (no browserStorage fallback)
let FIRESTORE_ENABLED = true; // Set to false to disable Firebase sync
let scheduleMainSearchRender = null;
let scheduleClientSearchRender = null;
let searchMetricTracker = null;

// ============ TARGET MARKET COMPLIANCE RULES ============
// These rules are derived from the provider/client market location and are
// stored as Firestore order metadata instead of browser localStorage state.
const TARGET_MARKET_COMPLIANCE = {
    US: {
        label: 'United States',
        currency: 'USD',
        taxRate: 0.0825,
        refundWindowHours: 24,
        cancellationWindowHours: 2,
        payoutDelayDays: 7,
        payoutFeePercent: 0.05,
        settlementNotes: 'US orders are subject to state/local sales tax and a 24-hour refund eligibility window.'
    },
    CA: {
        label: 'Canada',
        currency: 'CAD',
        taxRate: 0.13,
        refundWindowHours: 24,
        cancellationWindowHours: 2,
        payoutDelayDays: 7,
        payoutFeePercent: 0.05,
        settlementNotes: 'Canadian orders follow GST/HST tax treatment and a short refund window for non-consumed meals.'
    },
    GB: {
        label: 'United Kingdom',
        currency: 'GBP',
        taxRate: 0.2,
        refundWindowHours: 48,
        cancellationWindowHours: 4,
        payoutDelayDays: 10,
        payoutFeePercent: 0.04,
        settlementNotes: 'UK orders use VAT treatment and allow a 48-hour refund review window for eligible transactions.'
    },
    EU: {
        label: 'European Union',
        currency: 'EUR',
        taxRate: 0.2,
        refundWindowHours: 48,
        cancellationWindowHours: 4,
        payoutDelayDays: 10,
        payoutFeePercent: 0.04,
        settlementNotes: 'EU orders follow VAT rules and require a clear cancellation and refund policy for each transaction.'
    },
    default: {
        label: 'Target Market',
        currency: 'USD',
        taxRate: 0,
        refundWindowHours: 24,
        cancellationWindowHours: 2,
        payoutDelayDays: 7,
        payoutFeePercent: 0.05,
        settlementNotes: 'Target-market compliance defaults are applied until a market-specific configuration is confirmed.'
    }
};

const TARGET_MARKET_RULES = {
    US: { currency: 'USD', taxRate: 0.08, name: 'United States', symbol: '$' },
    CA: { currency: 'CAD', taxRate: 0.13, name: 'Canada', symbol: 'CA$' },
    MX: { currency: 'MXN', taxRate: 0.16, name: 'Mexico', symbol: 'MX$' },
    GB: { currency: 'GBP', taxRate: 0.20, name: 'United Kingdom', symbol: '£' },
    DE: { currency: 'EUR', taxRate: 0.19, name: 'Germany', symbol: '€' },
    FR: { currency: 'EUR', taxRate: 0.20, name: 'France', symbol: '€' },
    ES: { currency: 'EUR', taxRate: 0.21, name: 'Spain', symbol: '€' },
    AU: { currency: 'AUD', taxRate: 0.10, name: 'Australia', symbol: 'A$' },
    JP: { currency: 'JPY', taxRate: 0.10, name: 'Japan', symbol: '¥' },
    AE: { currency: 'AED', taxRate: 0.05, name: 'United Arab Emirates', symbol: 'AED' },
    ZA: { currency: 'ZAR', taxRate: 0.15, name: 'South Africa', symbol: 'R' },
    DEFAULT: { currency: 'USD', taxRate: 0.00, name: 'International', symbol: '$' }
};

// Global launch: every country is enabled by default. Countries without an
// explicit Stripe Connect payout integration fall back to cash-only ordering
// (allowProviderPayouts: false) until payouts are configured for that market.
// Add a country-specific entry below once payouts/tax settlement is verified
// for that market (see COUNTRY_LAUNCH_RESTRICTIONS.VC as an example).
const COUNTRY_LAUNCH_RESTRICTIONS = {
    DEFAULT: { enabled: true, reason: null, orderPaymentMethod: 'cash', allowProviderPayouts: false },
    VC: {
        enabled: true,
        currency: 'XCD',
        timezone: 'America/St_Vincent',
        orderPaymentMethod: 'cash',
        allowProviderPayouts: false
    }
};
function getSupportedLaunchCountriesList() {
    // Every normalized country/market code is considered "supported" by
    // default (global launch), filtered by the Super Admin-configurable
    // country access settings (Settings tab: allowlist/blocklist), if set.
    const allCodes = Object.keys(TARGET_MARKET_RULES).filter((code) => code !== 'DEFAULT');
    const accessConfig = (typeof window !== 'undefined' && window.__countryAccessSettingsCache) || null;
    if (!accessConfig || !accessConfig.mode || accessConfig.mode === 'all') {
        return allCodes;
    }
    const list = Array.isArray(accessConfig.countries) ? accessConfig.countries.map((c) => String(c || '').trim().toUpperCase()) : [];
    if (accessConfig.mode === 'allowlist') {
        return allCodes.filter((code) => list.includes(code));
    }
    if (accessConfig.mode === 'blocklist') {
        return allCodes.filter((code) => !list.includes(code));
    }
    return allCodes;
}

// ============ CRITICAL: Application State Variables (declare at module top before functions) ============
// These must be declared early to avoid TDZ (Temporal Dead Zone) errors in functions that may be called during initialization
let currentClient = null;
let currentProvider = null;
let currentAdmin = null;

function normalizeCountryCode(countryCode = 'US') {
    const normalized = String(countryCode || 'US').trim().toUpperCase();
    const countryAliases = {
        'SAINT VINCENT AND THE GRENADINES': 'VC',
        'ST. VINCENT AND THE GRENADINES': 'VC',
        'ST VINCENT AND THE GRENADINES': 'VC'
    };
    return countryAliases[normalized] || normalized;
}

function getMarketRule(countryCode) {
    if (!countryCode) return TARGET_MARKET_RULES.DEFAULT;
    const code = normalizeCountryCode(countryCode);
    return TARGET_MARKET_RULES[code] || TARGET_MARKET_RULES.DEFAULT;
}

// ISO-3166 alpha-2 country code -> ITU-T E.164 calling code (no "+").
// Covers every country/territory with its own registration form entry so
// normalizePhoneNumber() can correctly auto-prefix a bare national number
// for any country, not just a small hardcoded subset.
const COUNTRY_CALLING_CODES = {
    AF: '93', AL: '355', DZ: '213', AD: '376', AO: '244', AG: '1', AR: '54', AM: '374',
    AU: '61', AT: '43', AZ: '994', BS: '1', BH: '973', BD: '880', BB: '1', BY: '375',
    BE: '32', BZ: '501', BJ: '229', BT: '975', BO: '591', BA: '387', BW: '267', BR: '55',
    BN: '673', BG: '359', BF: '226', BI: '257', KH: '855', CM: '237', CA: '1', CV: '238',
    CF: '236', TD: '235', CL: '56', CN: '86', CO: '57', KM: '269', CG: '242', CD: '243',
    CR: '506', CI: '225', HR: '385', CU: '53', CY: '357', CZ: '420', DK: '45', DJ: '253',
    DM: '1', DO: '1', EC: '593', EG: '20', SV: '503', GQ: '240', ER: '291', EE: '372',
    SZ: '268', ET: '251', FJ: '679', FI: '358', FR: '33', GA: '241', GM: '220', GE: '995',
    DE: '49', GH: '233', GR: '30', GD: '1', GT: '502', GN: '224', GW: '245', GY: '592',
    HT: '509', HN: '504', HK: '852', HU: '36', IS: '354', IN: '91', ID: '62', IR: '98',
    IQ: '964', IE: '353', IL: '972', IT: '39', JM: '1', JP: '81', JO: '962', KZ: '7',
    KE: '254', KI: '686', KP: '850', KR: '82', KW: '965', KG: '996', LA: '856', LV: '371',
    LB: '961', LS: '266', LR: '231', LY: '218', LI: '423', LT: '370', LU: '352', MO: '853',
    MG: '261', MW: '265', MY: '60', MV: '960', ML: '223', MT: '356', MH: '692', MR: '222',
    MU: '230', MX: '52', FM: '691', MD: '373', MC: '377', MN: '976', ME: '382', MA: '212',
    MZ: '258', MM: '95', NA: '264', NR: '674', NP: '977', NL: '31', NZ: '64', NI: '505',
    NE: '227', NG: '234', MK: '389', NO: '47', OM: '968', PK: '92', PW: '680', PA: '507',
    PG: '675', PY: '595', PE: '51', PH: '63', PL: '48', PT: '351', QA: '974', RO: '40',
    RU: '7', RW: '250', KN: '1', LC: '1', VC: '1', WS: '685', SM: '378', ST: '239',
    SA: '966', SN: '221', RS: '381', SC: '248', SL: '232', SG: '65', SK: '421', SI: '386',
    SB: '677', SO: '252', ZA: '27', SS: '211', ES: '34', LK: '94', SD: '249', SR: '597',
    SE: '46', CH: '41', SY: '963', TW: '886', TJ: '992', TZ: '255', TH: '66', TL: '670',
    TG: '228', TO: '676', TT: '1', TN: '216', TR: '90', TM: '993', TV: '688', UG: '256',
    UA: '380', AE: '971', GB: '44', US: '1', UY: '598', UZ: '998', VU: '678', VA: '39',
    VE: '58', VN: '84', YE: '967', ZM: '260', ZW: '263', PS: '970', GL: '299', PR: '1',
    XK: '383'
};

function normalizePhoneNumber(phone = '', countryCode = 'US') {
    if (!phone || typeof phone !== 'string') return null;
    const compact = phone.trim();
    if (!compact) return null;
    const plusDigits = compact.startsWith('+') ? compact.replace(/[^\d]/g, '') : '';
    if (plusDigits) {
        const candidatePlus = `+${plusDigits}`;
        return isValidE164Phone(candidatePlus) ? candidatePlus : null;
    }

    let digits = compact.replace(/\D/g, '');
    if (!digits) return null;
    if (digits.startsWith('00') && digits.length > 2) {
        digits = digits.slice(2);
    }
    const normalizedCountry = normalizeCountryCode(countryCode);
    const countryPrefix = COUNTRY_CALLING_CODES[normalizedCountry] || '';
    if (!countryPrefix) {
        const fallback = `+${digits}`;
        return isValidE164Phone(fallback) ? fallback : null;
    }
    if (digits.startsWith('0') && countryPrefix !== '1') {
        digits = digits.replace(/^0+/, '');
    }
    let fullDigits = digits;
    if (countryPrefix === '1' && digits.length === 10) {
        fullDigits = `${countryPrefix}${digits}`;
    } else if (!digits.startsWith(countryPrefix)) {
        fullDigits = `${countryPrefix}${digits}`;
    }
    const normalized = `+${fullDigits}`;
    return isValidE164Phone(normalized) ? normalized : null;
}

function isValidE164Phone(phone = '') {
    return /^\+[1-9]\d{7,14}$/.test(String(phone || '').trim());
}

function isValidEmailAddress(email = '') {
    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
}

async function sendVerificationEmailForUser(user) {
    if (!user || typeof user.getIdToken !== 'function') {
        throw new Error('Authenticated user is unavailable for email verification.');
    }

    const projectId = window.firebaseConfig?.projectId || 'what-to-eat-e5ce9';
    const token = await user.getIdToken(true);
    const appCheckToken = await getAppCheckToken();
    const response = await fetch(`https://us-central1-${projectId}.cloudfunctions.net/sendVerificationEmail`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Firebase-AppCheck': appCheckToken,
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
    });
    const text = await response.text();
    let result = null;
    try {
        result = text ? JSON.parse(text) : null;
    } catch (error) {
        result = null;
    }
    if (!response.ok || !result || result.ok !== true) {
        throw new Error((result && result.error) || `Verification email request failed (${response.status}).`);
    }
    return result;
}

function normalizeProviderTimezone(timezone = 'UTC') {
    if (!timezone || typeof timezone !== 'string') return 'UTC';
    const tz = timezone.trim();
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
        try {
            const supported = Intl.supportedValuesOf('timeZone');
            if (supported.includes(tz)) return tz;
        } catch (error) {}
    }
    const fallbackMap = {
        EST: 'America/New_York',
        CST: 'America/Chicago',
        MST: 'America/Denver',
        PST: 'America/Los_Angeles',
        UTC: 'UTC',
        GMT: 'Europe/London'
    };
    return fallbackMap[tz.toUpperCase()] || 'UTC';
}

function parseTimeRange(range = '') {
    if (!range || typeof range !== 'string') return null;
    const parts = range.split('-');
    if (parts.length !== 2) return null;
    const parseTime = (rawTime) => {
        const normalized = rawTime.trim();
        if (!normalized) return null;
        const match = normalized.match(/^(\d{1,2})(?::?(\d{2}))?\s*(AM|PM)?$/i);
        if (!match) return null;
        let hour = Number(match[1]);
        const minute = Number(match[2] || 0);
        const meridiem = (match[3] || '').toUpperCase();
        if (meridiem === 'PM' && hour < 12) hour += 12;
        if (meridiem === 'AM' && hour === 12) hour = 0;
        return hour * 60 + minute;
    };
    const startMinutes = parseTime(parts[0]);
    const endMinutes = parseTime(parts[1]);
    if (startMinutes === null || endMinutes === null) return null;
    return { startMinutes, endMinutes };
}

function isProviderOpenNow(provider = {}, now = new Date(), timezoneOverride = null) {
    if (!provider) return false;
    if (provider.isOpenNow === true) return true;
    const timezone = normalizeProviderTimezone(timezoneOverride || provider.timezone || 'UTC');
    const localDate = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).formatToParts(now);
    const localMap = {};
    for (const part of localDate) {
        if (part.type !== 'literal') localMap[part.type] = part.value;
    }
    const dayKey = localMap.weekday || 'Mon';
    const currentMinutes = Number(localMap.hour || 0) * 60 + Number(localMap.minute || 0);
    const schedule = provider.hours || provider.businessHours || provider.openingHours || provider.availability || {};
    const candidateEntries = [];
    const weekdayMap = { Mon: 'mon', Tue: 'tue', Wed: 'wed', Thu: 'thu', Fri: 'fri', Sat: 'sat', Sun: 'sun' };
    if (schedule && typeof schedule === 'object') {
        const matchingKey = weekdayMap[dayKey] || dayKey.toLowerCase();
        if (schedule[matchingKey]) candidateEntries.push(schedule[matchingKey]);
        if (Array.isArray(schedule[matchingKey])) candidateEntries.push(...schedule[matchingKey]);
        if (Array.isArray(schedule)) candidateEntries.push(...schedule);
    }
    if (!candidateEntries.length) return false;
    return candidateEntries.some((entry) => {
        if (typeof entry === 'string') {
            const parsed = parseTimeRange(entry);
            return parsed && currentMinutes >= parsed.startMinutes && currentMinutes <= parsed.endMinutes;
        }
        if (entry && typeof entry === 'object') {
            const start = parseTimeRange(entry.start || entry.open || '');
            const end = parseTimeRange(entry.end || entry.close || '');
            if (start && end) return currentMinutes >= start.startMinutes && currentMinutes <= end.endMinutes;
        }
        return false;
    });
}

function isSupportedLaunchCountry(countryCode = 'US') {
    // Launch is global: every normalized country code is supported. Retained
    // as a function (not a hardcoded allow-list) so a future opt-out can be
    // modeled via COUNTRY_LAUNCH_RESTRICTIONS without touching call sites.
    const policy = getLaunchCountryPolicy(countryCode);
    return policy.enabled !== false;
}

function isLocalLaunchPolicyOverrideEnabled() {
    let hostname = '';
    try {
        hostname = String(window.location && window.location.hostname ? window.location.hostname : '').toLowerCase();
    } catch (e) {
        hostname = '';
    }
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const enforceStrictList = Boolean(
        window.launchGateConfig &&
        window.launchGateConfig.enforceSupportedCountriesOnLocalhost === true
    );
    return isLocalhost && !enforceStrictList;
}

function getLaunchCountryPolicy(countryCode = 'US') {
    const normalizedCountry = normalizeCountryCode(countryCode);
    const basePolicy = COUNTRY_LAUNCH_RESTRICTIONS[normalizedCountry] || COUNTRY_LAUNCH_RESTRICTIONS.DEFAULT;
    if (isLocalLaunchPolicyOverrideEnabled()) {
        return {
            ...basePolicy,
            enabled: true,
            allowProviderPayouts: true,
            reason: null
        };
    }

    // Super Admin-configurable country access override (Settings tab).
    // Cached from Firestore siteSettings/main by renderAdminSubscriptions-extract.js.
    // mode: 'all' (default, global launch) | 'allowlist' (only listed countries
    // enabled) | 'blocklist' (all countries enabled except listed ones).
    const accessConfig = (window.__countryAccessSettingsCache) || null;
    if (accessConfig && accessConfig.mode && accessConfig.mode !== 'all') {
        const list = Array.isArray(accessConfig.countries) ? accessConfig.countries.map((c) => normalizeCountryCode(c)) : [];
        if (accessConfig.mode === 'allowlist') {
            if (!list.includes(normalizedCountry)) {
                return {
                    ...basePolicy,
                    enabled: false,
                    reason: accessConfig.reason || 'This market is not yet enabled for launch.'
                };
            }
        } else if (accessConfig.mode === 'blocklist') {
            if (list.includes(normalizedCountry)) {
                return {
                    ...basePolicy,
                    enabled: false,
                    reason: accessConfig.reason || 'This market is currently restricted for launch.'
                };
            }
        }
    }

    return basePolicy;
}

function getSupportedLaunchCountries() {
    return getSupportedLaunchCountriesList();
}

function getCurrentLanguageCode() {
    if (window.i18nService && typeof window.i18nService.getLanguage === 'function') {
        return window.i18nService.getLanguage();
    }
    return 'en';
}

function getCurrentLocale() {
    const localeByLanguage = {
        en: 'en-US',
        es: 'es-ES',
        fr: 'fr-FR',
        de: 'de-DE',
        pt: 'pt-PT'
    };
    const language = getCurrentLanguageCode();
    return localeByLanguage[language] || 'en-US';
}

function t(key, fallback = '') {
    if (window.i18nService && typeof window.i18nService.t === 'function') {
        return window.i18nService.t(key, fallback);
    }
    return fallback || key;
}

function getCountryDisplayName(countryCode = 'US') {
    const normalizedCountry = normalizeCountryCode(countryCode);
    const market = getMarketRule(normalizedCountry);
    return market.name || normalizedCountry;
}

function normalizeInternationalAddress(address = '', countryCode = 'US') {
    const raw = String(address || '').trim();
    if (!raw) {
        return {
            fullAddress: '',
            country: normalizeCountryCode(countryCode),
            line1: '',
            city: '',
            region: '',
            postalCode: ''
        };
    }
    const parts = raw.split(',').map(part => part.trim()).filter(Boolean);
    return {
        fullAddress: raw,
        country: normalizeCountryCode(countryCode),
        line1: parts[0] || '',
        city: parts[1] || '',
        region: parts[2] || '',
        postalCode: parts[3] || ''
    };
}

function normalizeCoordinate(raw) {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

function normalizeProviderCoordinates(latitude, longitude) {
    const lat = normalizeCoordinate(latitude);
    const lng = normalizeCoordinate(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { latitude: lat, longitude: lng };
}

function detectProviderTimezone() {
    if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
        return 'UTC';
    }
    const resolved = Intl.DateTimeFormat().resolvedOptions();
    const timezone = resolved && typeof resolved.timeZone === 'string' ? resolved.timeZone : 'UTC';
    return normalizeProviderTimezone(timezone || 'UTC');
}

function populateProviderTimezoneSelect(timezoneEl, selectedTimezone = '') {
    if (!timezoneEl || timezoneEl.tagName !== 'SELECT') return;

    const selected = normalizeProviderTimezone(selectedTimezone || detectProviderTimezone());
    const timezones = typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function'
        ? Intl.supportedValuesOf('timeZone')
        : ['UTC'];
    timezoneEl.replaceChildren();

    for (const timezone of timezones) {
        const option = document.createElement('option');
        option.value = timezone;
        option.textContent = formatProviderTimezoneOption(timezone);
        timezoneEl.appendChild(option);
    }

    timezoneEl.value = timezones.includes(selected) ? selected : 'UTC';
}

function formatProviderTimezoneOption(timezone) {
    const readableLocation = timezone.replace(/_/g, ' ').replace(/\//g, ' / ');
    let offset = 'UTC';
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'shortOffset'
        }).formatToParts(new Date());
        const offsetPart = parts.find((part) => part.type === 'timeZoneName');
        if (offsetPart && offsetPart.value) {
            offset = offsetPart.value.replace(/^GMT/, 'UTC');
        }
    } catch (error) {}
    return `${readableLocation} (${timezone}) - ${offset}`;
}

function initProviderLocationAssist({
    timezoneInputId,
    latitudeInputId,
    longitudeInputId,
    locationButtonId
}) {
    const timezoneEl = document.getElementById(timezoneInputId);
    if (timezoneEl) {
        populateProviderTimezoneSelect(timezoneEl, timezoneEl.value);
    }

    const locationButton = document.getElementById(locationButtonId);
    if (!locationButton || locationButton.dataset.locationAssistBound === 'true') {
        return;
    }
    locationButton.dataset.locationAssistBound = 'true';

    locationButton.addEventListener('click', () => {
        const timezoneField = document.getElementById(timezoneInputId);
        const latitudeField = document.getElementById(latitudeInputId);
        const longitudeField = document.getElementById(longitudeInputId);

        if (timezoneField && !timezoneField.value) {
            timezoneField.value = detectProviderTimezone();
        }

        if (!navigator.geolocation || typeof navigator.geolocation.getCurrentPosition !== 'function') {
            showNotification('Location autofill is not supported in this browser. You can continue without coordinates.', 'warning');
            return;
        }

        const buttonLabel = locationButton.textContent;
        locationButton.disabled = true;
        locationButton.textContent = 'Detecting location...';

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = Number(position.coords.latitude);
                const lng = Number(position.coords.longitude);
                if (latitudeField && Number.isFinite(lat)) latitudeField.value = lat.toFixed(6);
                if (longitudeField && Number.isFinite(lng)) longitudeField.value = lng.toFixed(6);
                if (timezoneField) timezoneField.value = detectProviderTimezone();
                showNotification('Location captured. You can edit it before submitting.', 'success');
                locationButton.disabled = false;
                locationButton.textContent = buttonLabel;
            },
            (error) => {
                let message = 'Unable to detect location. You can still continue without coordinates.';
                if (error && error.code === 1) {
                    message = 'Location permission was denied. You can still continue without coordinates.';
                } else if (error && error.code === 2) {
                    message = 'Location is unavailable right now. You can still continue without coordinates.';
                } else if (error && error.code === 3) {
                    message = 'Location request timed out. Please try again or continue without coordinates.';
                }
                showNotification(message, 'warning');
                locationButton.disabled = false;
                locationButton.textContent = buttonLabel;
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );
    });
}

function getProviderLaunchContext(countryCode = 'US') {
    const normalizedCountry = normalizeCountryCode(countryCode);
    const policy = getLaunchCountryPolicy(normalizedCountry);
    const rule = getMarketRule(normalizedCountry);
    return {
        country: normalizedCountry,
        policy,
        currency: policy.currency || rule.currency,
        timezone: normalizeProviderTimezone(policy.timezone || 'UTC')
    };
}

function formatCurrency(amount, countryCode = 'US', customCurrency = null) {
    const rule = getMarketRule(countryCode);
    const currencyCode = customCurrency || rule.currency;
    const locale = getCurrentLocale();
    const numericAmount = Number(amount) || 0;
    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode
        }).format(numericAmount);
    } catch (error) {
        return `${rule.symbol || '$'}${numericAmount.toFixed(2)} ${currencyCode}`;
    }
}

function calculateDynamicRegionalTax({
    subtotal = 0,
    countryCode = 'US',
    state = null,
    clientLocation = null,
    providerLocation = null,
    deliveryType = 'delivery',
    isPreparedFood = true
} = {}) {
    const country = String(countryCode || 'US').toUpperCase();
    const rule = getMarketRule(country);
    const amount = Number(subtotal) || 0;
    if (country === 'US') {
        const targetState = (state || clientLocation?.state || providerLocation?.state || '').toUpperCase();
        const usStateTax = {
            CA: { stateRate: 0.0725, localRate: 0.0160, preparedFoodTaxable: true, name: 'California' },
            NY: { stateRate: 0.0400, localRate: 0.0452, preparedFoodTaxable: true, name: 'New York' },
            OR: { stateRate: 0.0000, localRate: 0.0000, preparedFoodTaxable: false, name: 'Oregon' }
        };
        const stateRule = usStateTax[targetState];
        if (stateRule) {
            let effectiveRate = stateRule.stateRate + stateRule.localRate;
            if (!isPreparedFood && !stateRule.preparedFoodTaxable) effectiveRate = 0.00;
            const tax = Number((amount * effectiveRate).toFixed(2));
            return { subtotal: amount, taxRate: Number(effectiveRate.toFixed(4)), tax, total: Number((amount + tax).toFixed(2)), currency: rule.currency, formattedTotal: formatCurrency(amount + tax, country), jurisdiction: `${stateRule.name} (${targetState})`, calculationMethod: 'dynamic_regional_rules' };
        }
    }
    if (country === 'CA') {
        const targetProvince = (state || clientLocation?.state || providerLocation?.state || '').toUpperCase();
        const provinceRates = { ON: { hst: 0.13, gst: 0.05, name: 'Ontario' } };
        const provinceRule = provinceRates[targetProvince];
        if (provinceRule) {
            const effectiveRate = targetProvince === 'ON' && amount < 4.00 && isPreparedFood ? 0.05 : provinceRule.hst;
            const tax = Number((amount * effectiveRate).toFixed(2));
            return { subtotal: amount, taxRate: Number(effectiveRate.toFixed(4)), tax, total: Number((amount + tax).toFixed(2)), currency: rule.currency, formattedTotal: formatCurrency(amount + tax, country), jurisdiction: `${provinceRule.name} (${targetProvince})`, calculationMethod: 'dynamic_regional_rules' };
        }
    }
    if (country === 'GB') {
        const effectiveRate = isPreparedFood && deliveryType !== 'pickup' ? 0.20 : 0.00;
        const tax = Number((amount * effectiveRate).toFixed(2));
        return { subtotal: amount, taxRate: effectiveRate, tax, total: Number((amount + tax).toFixed(2)), currency: rule.currency, formattedTotal: formatCurrency(amount + tax, country), jurisdiction: 'United Kingdom (HMRC VAT)', calculationMethod: 'dynamic_regional_rules' };
    }
    const taxRate = rule.taxRate;
    const tax = Number((amount * taxRate).toFixed(2));
    return { subtotal: amount, taxRate, tax, total: Number((amount + tax).toFixed(2)), currency: rule.currency, formattedTotal: formatCurrency(amount + tax, country), jurisdiction: rule.name, calculationMethod: 'standard_country_default' };
}

function calculateOrderTax(subtotal, countryCode, customTaxRate = null, options = {}) {
    if (customTaxRate !== null && typeof customTaxRate === 'number') {
        const rule = getMarketRule(countryCode);
        const tax = Number((subtotal * customTaxRate).toFixed(2));
        return { subtotal: Number(subtotal), taxRate: customTaxRate, tax, total: Number((subtotal + tax).toFixed(2)), currency: rule.currency, formattedTotal: formatCurrency(subtotal + tax, countryCode), calculationMethod: 'custom_override' };
    }
    let opts = options;
    if (customTaxRate !== null && typeof customTaxRate === 'object') {
        opts = customTaxRate;
    }
    return calculateDynamicRegionalTax({
        subtotal,
        countryCode,
        state: opts.state,
        clientLocation: opts.clientLocation,
        providerLocation: opts.providerLocation,
        deliveryType: opts.deliveryType || 'delivery',
        isPreparedFood: opts.isPreparedFood !== undefined ? opts.isPreparedFood : true
    });
}

function buildMarketCompliancePolicy(orderPayload = {}) {
    const source = orderPayload && typeof orderPayload === 'object' ? orderPayload : {};
    const complianceRoot = source.compliance && typeof source.compliance === 'object' ? source.compliance : {};
    const merged = { ...complianceRoot, ...source };
    const priceNumber = Number(merged.price ?? complianceRoot.price ?? 0) || 0;
    const country = String(merged.country || merged.clientCountry || merged.providerCountry || complianceRoot.country || 'US').trim();
    const market = getTargetMarketCompliance(country || 'US');
    const taxRate = Number(merged.taxRate ?? complianceRoot.taxRate ?? market.taxRate ?? 0);
    const payoutFeePercent = Number(merged.payoutFeePercent ?? complianceRoot.payoutFeePercent ?? market.payoutFeePercent ?? 0.05);
    const rawTaxAmount = Number(merged.taxAmount ?? complianceRoot.taxAmount ?? (priceNumber * taxRate));
    const rawPayoutFeeAmount = Number(merged.payoutFeeAmount ?? complianceRoot.payoutFeeAmount ?? (priceNumber * payoutFeePercent));
    const taxAmount = Number((Number.isFinite(rawTaxAmount) ? rawTaxAmount : 0).toFixed(2));
    const payoutFeeAmount = Number((Number.isFinite(rawPayoutFeeAmount) ? rawPayoutFeeAmount : 0).toFixed(2));
    const refundWindowHours = Number(merged.refundWindowHours ?? complianceRoot.refundWindowHours ?? market.refundWindowHours ?? 24);
    const cancellationWindowHours = Number(merged.cancellationWindowHours ?? complianceRoot.cancellationWindowHours ?? market.cancellationWindowHours ?? 2);
    const payoutDelayDays = Number(merged.payoutDelayDays ?? complianceRoot.payoutDelayDays ?? market.payoutDelayDays ?? 7);
    const settlementNotes = merged.settlementNotes || complianceRoot.settlementNotes || market.settlementNotes || TARGET_MARKET_COMPLIANCE.default.settlementNotes;

    return {
        marketCode: normalizeMarketCode(country || 'US'),
        country: country || 'US',
        currency: merged.currency || complianceRoot.currency || market.currency || 'USD',
        taxRate: Number(Math.max(0, taxRate).toFixed(4)),
        taxAmount: Number(taxAmount.toFixed(2)),
        refundWindowHours: Number(refundWindowHours),
        cancellationWindowHours: Number(cancellationWindowHours),
        payoutDelayDays: Number(payoutDelayDays),
        payoutFeePercent: Number(Math.max(0, payoutFeePercent).toFixed(4)),
        payoutFeeAmount: Number(payoutFeeAmount.toFixed(2)),
        refundPolicy: merged.refundPolicy || complianceRoot.refundPolicy || `Refunds are available within ${refundWindowHours} hours for eligible orders before completion.`,
        cancellationPolicy: merged.cancellationPolicy || complianceRoot.cancellationPolicy || `Orders may be cancelled within ${cancellationWindowHours} hours of placement unless the provider has already started preparation.`,
        payoutPolicy: merged.payoutPolicy || complianceRoot.payoutPolicy || `Provider payouts are processed after ${payoutDelayDays} days and a ${Number((payoutFeePercent * 100)).toFixed(2)}% platform charge may apply.`,
        settlementNotes
    };
}

function validateMarketCompliancePayload(orderPayload = {}) {
    const policy = buildMarketCompliancePolicy(orderPayload);
    const errors = [];
    if (!Number.isFinite(policy.taxRate) || policy.taxRate < 0 || policy.taxRate > 0.35) errors.push('taxRate must be between 0 and 0.35');
    if (!Number.isFinite(policy.taxAmount) || policy.taxAmount < 0) errors.push('taxAmount must be a non-negative number');
    if (!Number.isFinite(policy.refundWindowHours) || policy.refundWindowHours < 0 || policy.refundWindowHours > 720) errors.push('refundWindowHours must be between 0 and 720 hours');
    if (!Number.isFinite(policy.cancellationWindowHours) || policy.cancellationWindowHours < 0 || policy.cancellationWindowHours > 720) errors.push('cancellationWindowHours must be between 0 and 720 hours');
    if (!Number.isFinite(policy.payoutDelayDays) || policy.payoutDelayDays < 0 || policy.payoutDelayDays > 90) errors.push('payoutDelayDays must be between 0 and 90 days');
    if (!Number.isFinite(policy.payoutFeePercent) || policy.payoutFeePercent < 0 || policy.payoutFeePercent > 1) errors.push('payoutFeePercent must be between 0 and 1');
    return { valid: errors.length === 0, errors, policy };
}

function calculateOrderCompliance(orderPayload = {}) {
    return buildMarketCompliancePolicy(orderPayload);
}

const TRANSLATIONS = {
    en: { appName: 'What To Eat?', tagline: 'Find available meals from restaurants & caterers - 24/7', navClient: 'For Clients', navProvider: 'For Restaurants/Caterers', navAdmin: 'Admin Panel', guestNotice: 'Guest (Not logged in)', searchPlaceholder: 'Search meals, cuisines, or providers...', allTypes: 'All Types', breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', dessert: 'Dessert', drinks: 'Drinks', snack: 'Snack', filter247: '24/7 Available', filterSpecial: 'Daily Specials', orderNow: 'Place Order', price: 'Price', tax: 'Estimated Tax', total: 'Total Amount', selectLanguage: 'Language', subscriptionPlan: 'Subscription Plan', providerPortal: 'Provider Management Portal', checkoutSuccess: 'Payment Successful!', checkoutCancel: 'Payment Cancelled.', marketNotSupported: 'This market is not supported yet.', supportedCountries: 'Supported countries', timezoneLabel: 'Local time zone', openNow: 'Open now', closedNow: 'Closed now', phoneValidation: 'Phone number must use international format.', countryRestriction: 'This country is currently restricted for launch.' },
    es: { appName: '¿Qué Comer?', tagline: 'Encuentra comidas disponibles de restaurantes y banquetes - 24/7', navClient: 'Para Clientes', navProvider: 'Para Restaurantes', navAdmin: 'Panel Admin', guestNotice: 'Invitado (Sin iniciar sesión)', searchPlaceholder: 'Buscar comidas, cocinas o proveedores...', allTypes: 'Todos los Tipos', breakfast: 'Desayuno', lunch: 'Almuerzo', dinner: 'Cena', dessert: 'Postre', drinks: 'Bebidas', snack: 'Apetitivo', filter247: 'Disponible 24/7', filterSpecial: 'Especiales del Día', orderNow: 'Realizar Pedido', price: 'Precio', tax: 'Impuesto Estimado', total: 'Monto Total', selectLanguage: 'Idioma', subscriptionPlan: 'Plan de Suscripción', providerPortal: 'Portal de Gestión de Proveedores', checkoutSuccess: '¡Pago Exitoso!', checkoutCancel: 'Pago Cancelado.', marketNotSupported: 'Este mercado aún no es compatible.', supportedCountries: 'Países admitidos', timezoneLabel: 'Zona horaria local', openNow: 'Abierto ahora', closedNow: 'Cerrado ahora', phoneValidation: 'El número de teléfono debe usar formato internacional.', countryRestriction: 'Este país está restringido para el lanzamiento actual.' },
    fr: { appName: 'Que Manger?', tagline: 'Trouvez des repas disponibles dans les restaurants et traiteurs - 24/7', navClient: 'Pour les Clients', navProvider: 'Pour les Restaurants', navAdmin: 'Panneau d\'Admin', guestNotice: 'Invité (Non connecté)', searchPlaceholder: 'Rechercher des repas, cuisines ou prestataires...', allTypes: 'Tous les types', breakfast: 'Petit Déjeuner', lunch: 'Déjeuner', dinner: 'Dîner', dessert: 'Dessert', drinks: 'Boissons', snack: 'Encas', filter247: 'Disponible 24/7', filterSpecial: 'Spécialités du Jour', orderNow: 'Passer la commande', price: 'Prix', tax: 'Taxe Estimée', total: 'Montant Total', selectLanguage: 'Langue', subscriptionPlan: 'Plan d\'Abonnement', providerPortal: 'Portail de Gestion Prestataire', checkoutSuccess: 'Paiement Réussi!', checkoutCancel: 'Paiement Annulé.', marketNotSupported: 'Ce marché n\'est pas encore pris en charge.', supportedCountries: 'Pays pris en charge', timezoneLabel: 'Fuseau horaire local', openNow: 'Ouvert maintenant', closedNow: 'Fermé maintenant', phoneValidation: 'Le numéro de téléphone doit utiliser le format international.', countryRestriction: 'Ce pays est actuellement restreint pour le lancement.' },
    de: { appName: 'Was Essen?', tagline: 'Finden Sie verfügbare Mahlzeiten von Restaurants & Caterern - 24/7', navClient: 'Für Kunden', navProvider: 'Für Restaurants', navAdmin: 'Admin-Bereich', guestNotice: 'Gast (Nicht angemeldet)', searchPlaceholder: 'Mahlzeiten, Küchen oder Anbieter suchen...', allTypes: 'Alle Typen', breakfast: 'Frühstück', lunch: 'Mittagessen', dinner: 'Abendessen', dessert: 'Dessert', drinks: 'Getränke', snack: 'Snack', filter247: '24/7 Verfügbar', filterSpecial: 'Tagesangebote', orderNow: 'Bestellung Aufgeben', price: 'Preis', tax: 'Geschätzte Steuer', total: 'Gesamtbetrag', selectLanguage: 'Sprache', subscriptionPlan: 'Abonnement-Plan', providerPortal: 'Anbieter-Verwaltungsportal', checkoutSuccess: 'Zahlung Erfolgreich!', checkoutCancel: 'Zahlung Abgebrochen.', marketNotSupported: 'Dieser Markt wird noch nicht unterstützt.', supportedCountries: 'Unterstützte Länder', timezoneLabel: 'Lokale Zeitzone', openNow: 'Jetzt geöffnet', closedNow: 'Jetzt geschlossen', phoneValidation: 'Die Telefonnummer muss im internationalen Format vorliegen.', countryRestriction: 'Dieses Land ist für den aktuellen Launch derzeit eingeschränkt.' },
    pt: { appName: 'O Que Comer?', tagline: 'Encontre refeições disponíveis de restaurantes e buffet - 24/7', navClient: 'Para Clientes', navProvider: 'Para Restaurantes', navAdmin: 'Painel Admin', guestNotice: 'Convidado (Não conectado)', searchPlaceholder: 'Buscar refeições, culinária ou fornecedores...', allTypes: 'Todos os Tipos', breakfast: 'Café da Manhã', lunch: 'Almoço', dinner: 'Jantar', dessert: 'Sobremesa', drinks: 'Bebidas', snack: 'Lanche', filter247: 'Disponível 24/7', filterSpecial: 'Especiais do Dia', orderNow: 'Fazer Pedido', price: 'Preço', tax: 'Imposto Estimado', total: 'Valor Total', selectLanguage: 'Idioma', subscriptionPlan: 'Plano de Assinatura', providerPortal: 'Portal do Fornecedor', checkoutSuccess: 'Pagamento Bem-sucedido!', checkoutCancel: 'Pagamento Cancelado.', marketNotSupported: 'Este mercado ainda não é suportado.', supportedCountries: 'Países suportados', timezoneLabel: 'Fuso horário local', openNow: 'Aberto agora', closedNow: 'Fechado agora', phoneValidation: 'O número de telefone deve estar em formato internacional.', countryRestriction: 'Este país está restrito ao lançamento atual.' }
};

const TRANSLATION_ENHANCEMENTS = {
    en: {
        searchMeals: 'Search Meals',
        findAvailableMeals: 'Find Available Meals',
        allCountries: 'All Countries',
        allProviders: 'All Restaurants/Caterers',
        allAvailability: 'All Availability',
        availableToday: 'Available today',
        availableTomorrow: 'Available tomorrow',
        availabilityNeedsUpdate: 'Availability needs updating',
        menuItemAvailability: 'Menu item availability',
        preorderForTomorrow: 'Preorder for tomorrow',
        fulfillmentDate: 'Scheduled for',
        afterHoursFilter: 'After Hours',
        afterHoursOnly: 'After Hours Only',
        afterHoursOk: 'After Hours OK',
        searchButton: 'Search',
        searchInputPlaceholder: 'Search for food or restaurant/caterer name...',
        clientSearchTitle: 'Search for Meals',
        clientSearchSubtitle: 'Find available meals from restaurants and caterers worldwide!',
        ingredientsAllergens: 'Ingredients/Allergens',
        drinksLabel: 'Drinks',
        availablePortions: 'Available Portions',
        servingsLeft: 'servings left',
        reviews: 'reviews',
        verifiedProvider: 'Verified Provider',
        prepSla: 'Prep SLA',
        cancellationReliability: 'Cancellation reliability',
        deliveryRadius: 'Delivery radius',
        deliveryAndPickup: 'Delivery & Pickup',
        deliveryOnly: 'Delivery',
        pickupOnly: 'Pickup Only',
        registerToViewDetails: 'Register to view price, servings, open/closed status, ingredients, drinks, delivery/pickup options & order!',
        registerToViewMenuItem: 'Register to view this menu item',
        registerSubtext: 'Create a free account to see prices, descriptions, ingredients, photos, delivery options & order!'
    },
    es: {
        searchMeals: 'Buscar comidas',
        findAvailableMeals: 'Encuentra comidas disponibles',
        allCountries: 'Todos los países',
        allProviders: 'Todos los restaurantes/caterers',
        allAvailability: 'Toda disponibilidad',
        availableToday: 'Disponible hoy',
        availableTomorrow: 'Disponible mañana',
        availabilityNeedsUpdate: 'Disponibilidad por actualizar',
        menuItemAvailability: 'Disponibilidad del elemento del menú',
        preorderForTomorrow: 'Reservar para mañana',
        fulfillmentDate: 'Programado para',
        afterHoursFilter: 'Fuera de horario',
        afterHoursOnly: 'Solo fuera de horario',
        afterHoursOk: 'Fuera de horario disponible',
        searchButton: 'Buscar',
        searchInputPlaceholder: 'Busca comida o nombre de restaurante/caterer...',
        clientSearchTitle: 'Buscar comidas',
        clientSearchSubtitle: '¡Encuentra comidas disponibles de restaurantes y caterers en todo el mundo!',
        ingredientsAllergens: 'Ingredientes/Alérgenos',
        drinksLabel: 'Bebidas',
        availablePortions: 'Porciones disponibles',
        servingsLeft: 'porciones disponibles',
        reviews: 'reseñas',
        verifiedProvider: 'Proveedor verificado',
        prepSla: 'SLA de preparación',
        cancellationReliability: 'Confiabilidad de cancelación',
        deliveryRadius: 'Radio de entrega',
        deliveryAndPickup: 'Entrega y recogida',
        deliveryOnly: 'Entrega',
        pickupOnly: 'Solo recogida',
        registerToViewDetails: '¡Regístrate para ver precio, porciones, estado abierto/cerrado, ingredientes, bebidas, opciones de entrega/recogida y pedir!',
        registerToViewMenuItem: 'Regístrate para ver este elemento del menú',
        registerSubtext: 'Crea una cuenta gratis para ver precios, descripciones, ingredientes, fotos, opciones de entrega y pedir.'
    },
    fr: {
        searchMeals: 'Rechercher des repas',
        findAvailableMeals: 'Trouver des repas disponibles',
        allCountries: 'Tous les pays',
        allProviders: 'Tous les restaurants/traiteurs',
        allAvailability: 'Toutes disponibilités',
        availableToday: "Disponible aujourd'hui",
        availableTomorrow: 'Disponible demain',
        availabilityNeedsUpdate: 'Disponibilité à mettre à jour',
        menuItemAvailability: 'Disponibilité de l’article du menu',
        preorderForTomorrow: 'Précommander pour demain',
        fulfillmentDate: 'Prévu pour',
        afterHoursFilter: 'Hors horaires',
        afterHoursOnly: 'Seulement hors horaires',
        afterHoursOk: 'Hors horaires accepté',
        searchButton: 'Rechercher',
        searchInputPlaceholder: 'Rechercher un plat ou un restaurant/traiteur...',
        clientSearchTitle: 'Rechercher des repas',
        clientSearchSubtitle: 'Trouvez des repas disponibles de restaurants et traiteurs du monde entier !',
        ingredientsAllergens: 'Ingrédients/Allergènes',
        drinksLabel: 'Boissons',
        availablePortions: 'Portions disponibles',
        servingsLeft: 'portions restantes',
        reviews: 'avis',
        verifiedProvider: 'Prestataire vérifié',
        prepSla: 'SLA de préparation',
        cancellationReliability: 'Fiabilité d’annulation',
        deliveryRadius: 'Rayon de livraison',
        deliveryAndPickup: 'Livraison et retrait',
        deliveryOnly: 'Livraison',
        pickupOnly: 'Retrait uniquement',
        registerToViewDetails: 'Inscrivez-vous pour voir le prix, les portions, le statut ouvert/fermé, les ingrédients, les boissons, les options de livraison/retrait et commander !',
        registerToViewMenuItem: 'Inscrivez-vous pour voir cet article du menu',
        registerSubtext: 'Créez un compte gratuit pour voir les prix, descriptions, ingrédients, photos, options de livraison et commander.'
    },
    de: {
        searchMeals: 'Mahlzeiten suchen',
        findAvailableMeals: 'Verfugbare Mahlzeiten finden',
        allCountries: 'Alle Länder',
        allProviders: 'Alle Restaurants/Caterer',
        allAvailability: 'Alle Verfügbarkeiten',
        availableToday: 'Heute verfügbar',
        availableTomorrow: 'Morgen verfügbar',
        availabilityNeedsUpdate: 'Verfügbarkeit aktualisieren',
        menuItemAvailability: 'Verfügbarkeit des Menüpunkts',
        preorderForTomorrow: 'Für morgen vorbestellen',
        fulfillmentDate: 'Geplant für',
        afterHoursFilter: 'Nach den Öffnungszeiten',
        afterHoursOnly: 'Nur nach den Öffnungszeiten',
        afterHoursOk: 'Nach den Öffnungszeiten verfügbar',
        searchButton: 'Suchen',
        searchInputPlaceholder: 'Nach Essen oder Restaurant/Caterer suchen...',
        clientSearchTitle: 'Nach Mahlzeiten suchen',
        clientSearchSubtitle: 'Finden Sie verfügbare Mahlzeiten von Restaurants und Caterern weltweit!',
        ingredientsAllergens: 'Zutaten/Allergene',
        drinksLabel: 'Getränke',
        availablePortions: 'Verfügbare Portionen',
        servingsLeft: 'Portionen verfügbar',
        reviews: 'Bewertungen',
        verifiedProvider: 'Verifizierter Anbieter',
        prepSla: 'Zubereitungs-SLA',
        cancellationReliability: 'Stornierungszuverlässigkeit',
        deliveryRadius: 'Liefer-Radius',
        deliveryAndPickup: 'Lieferung & Abholung',
        deliveryOnly: 'Lieferung',
        pickupOnly: 'Nur Abholung',
        registerToViewDetails: 'Registrieren Sie sich, um Preis, Portionen, Offen/Geschlossen-Status, Zutaten, Getränke, Liefer-/Abholoptionen und Bestellung zu sehen!',
        registerToViewMenuItem: 'Registrieren Sie sich, um diesen Menüpunkt zu sehen',
        registerSubtext: 'Erstellen Sie ein kostenloses Konto, um Preise, Beschreibungen, Zutaten, Fotos, Lieferoptionen und Bestellungen zu sehen.'
    },
    pt: {
        searchMeals: 'Buscar refeições',
        findAvailableMeals: 'Encontre refeições disponíveis',
        allCountries: 'Todos os países',
        allProviders: 'Todos os restaurantes/buffets',
        allAvailability: 'Toda disponibilidade',
        availableToday: 'Disponível hoje',
        availableTomorrow: 'Disponível amanhã',
        availabilityNeedsUpdate: 'Disponibilidade precisa ser atualizada',
        menuItemAvailability: 'Disponibilidade do item do menu',
        preorderForTomorrow: 'Reservar para amanhã',
        fulfillmentDate: 'Agendado para',
        afterHoursFilter: 'Fora do horário',
        afterHoursOnly: 'Somente fora do horário',
        afterHoursOk: 'Fora do horário disponível',
        searchButton: 'Buscar',
        searchInputPlaceholder: 'Busque por comida ou nome do restaurante/buffet...',
        clientSearchTitle: 'Buscar refeições',
        clientSearchSubtitle: 'Encontre refeições disponíveis de restaurantes e buffets no mundo todo!',
        ingredientsAllergens: 'Ingredientes/Alergênicos',
        drinksLabel: 'Bebidas',
        availablePortions: 'Porções disponíveis',
        servingsLeft: 'porções disponíveis',
        reviews: 'avaliações',
        verifiedProvider: 'Fornecedor verificado',
        prepSla: 'SLA de preparo',
        cancellationReliability: 'Confiabilidade de cancelamento',
        deliveryRadius: 'Raio de entrega',
        deliveryAndPickup: 'Entrega e retirada',
        deliveryOnly: 'Entrega',
        pickupOnly: 'Somente retirada',
        registerToViewDetails: 'Cadastre-se para ver preço, porções, status aberto/fechado, ingredientes, bebidas, opções de entrega/retirada e pedir!',
        registerToViewMenuItem: 'Cadastre-se para ver este item do cardápio',
        registerSubtext: 'Crie uma conta gratuita para ver preços, descrições, ingredientes, fotos, opções de entrega e pedir.'
    }
};

Object.keys(TRANSLATION_ENHANCEMENTS).forEach((langCode) => {
    if (!TRANSLATIONS[langCode]) return;
    Object.assign(TRANSLATIONS[langCode], TRANSLATION_ENHANCEMENTS[langCode]);
});

class I18nService {
    constructor(defaultLang = 'en') {
        this.currentLanguage = defaultLang;
    }
    setLanguage(langCode) {
        if (TRANSLATIONS[langCode]) {
            this.currentLanguage = langCode;
            if (typeof document !== 'undefined') {
                const elements = document.querySelectorAll('[data-i18n]');
                elements.forEach((el) => {
                    const key = el.getAttribute('data-i18n');
                    const translation = this.t(key);
                    if (translation) {
                        if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
                            if (el.hasAttribute('placeholder')) el.placeholder = translation;
                        } else {
                            el.textContent = translation;
                        }
                    }
                });
            }
            if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
                window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: langCode } }));
            }
        }
    }
    getLanguage() { return this.currentLanguage; }
    t(key, fallback = '') {
        const dict = TRANSLATIONS[this.currentLanguage] || TRANSLATIONS.en;
        return dict[key] || TRANSLATIONS.en[key] || fallback || key;
    }
}

const i18nService = new I18nService();

function normalizeMarketCode(country) {
    const code = String(country || '').trim().toUpperCase();
    if (!code) return 'default';
    if (code === 'US' || code === 'USA') return 'US';
    if (code === 'CA' || code === 'CAN') return 'CA';
    if (code === 'GB' || code === 'UK') return 'GB';
    if (['DE','FR','IT','ES','NL','BE','AT','IE','PT','LU','PL','SE','FI','DK','CZ','SK','SI','RO','HR','GR','EE','LV','LT','CY','MT'].includes(code)) return 'EU';
    return code;
}

function getTargetMarketCompliance(country) {
    const marketCode = normalizeMarketCode(country);
    return TARGET_MARKET_COMPLIANCE[marketCode] || TARGET_MARKET_COMPLIANCE.default;
}

window.marketComplianceService = {
    TARGET_MARKET_COMPLIANCE,
    TARGET_MARKET_RULES,
    getSupportedLaunchCountries,
    COUNTRY_LAUNCH_RESTRICTIONS,
    normalizeMarketCode,
    normalizeCountryCode,
    getMarketRule,
    getTargetMarketCompliance,
    isSupportedLaunchCountry,
    getLaunchCountryPolicy,
    normalizePhoneNumber,
    normalizeProviderTimezone,
    isProviderOpenNow,
    buildMarketCompliancePolicy,
    validateMarketCompliancePayload,
    calculateDynamicRegionalTax,
    calculateOrderTax,
    calculateOrderCompliance,
    formatCurrency
};

window.i18nService = i18nService;
window.TRANSLATIONS = TRANSLATIONS;

// ============ FIREBASE AUTH CONFIGURATION ============
// Check if Firebase is available and properly configured
let FIREBASE_AUTH_ENABLED = false;
let firebaseAuthInstance = null;
let firebaseDbInstance = null;

// Check for Firebase SDK and initialize auth - FIXED VERSION
function checkFirebaseAuthConfig() {
    // FIRST: Check if global variables were already set by firebase-config.js
    // This ensures we use the properly initialized instances
    if (window.firebaseAuthInstance && window.firebaseDbInstance) {
        firebaseAuthInstance = window.firebaseAuthInstance;
        firebaseDbInstance = window.firebaseDbInstance;
        FIREBASE_AUTH_ENABLED = true;
        console.log('[Firebase Auth] Using global Firebase instances from firebase-config.js');

        // Set up auth state listener
        firebaseAuthInstance.onAuthStateChanged(handleFirebaseAuthStateChange);
        return true;
    }

    // SECOND: Check for Firebase SDK and configuration (legacy path)
    if (typeof firebase !== 'undefined' && typeof firebaseConfig !== 'undefined') {
        try {
            // Get existing apps or initialize new
            if (firebase.apps.length === 0) {
                firebase.initializeApp(firebaseConfig);
            }

            // Get Firebase services - fix reference issues
            firebaseAuthInstance = firebase.auth();
            firebaseDbInstance = firebase.firestore();

            // Also set the global variables for other scripts to use
            window.firebaseAuthInstance = firebaseAuthInstance;
            window.firebaseDbInstance = firebaseDbInstance;

            // Verify instances are valid
            if (firebaseAuthInstance && firebaseDbInstance) {
                FIREBASE_AUTH_ENABLED = true;
                console.log('[Firebase Auth] Enabled and ready (legacy init)');

                // Set up auth state listener to sync with app sessions
                firebaseAuthInstance.onAuthStateChanged(handleFirebaseAuthStateChange);

                return true;
            } else {
                console.warn('[Firebase Auth] Auth or Firestore instance null');
                FIREBASE_AUTH_ENABLED = false;
                return false;
            }
        } catch (e) {
            console.error('[Firebase Auth] Init error:', e);
            FIREBASE_AUTH_ENABLED = false;
            return false;
        }
    } else {
        console.log('[Firebase Auth] SDK not found; Firebase-only auth is unavailable');
        if (typeof firebase === 'undefined') {
            console.error('[Firebase Auth] firebase global is undefined - SDK not loaded');
        }
        if (typeof firebaseConfig === 'undefined') {
            console.error('[Firebase Auth] firebaseConfig is undefined');
        }
        FIREBASE_AUTH_ENABLED = false;
        return false;
    }
}

// Also expose getter functions in case they're needed
window.getFirebaseAuthInstance = function() {
    return firebaseAuthInstance || window.firebaseAuthInstance || null;
};
window.getFirebaseDbInstance = function() {
    return firebaseDbInstance || window.firebaseDbInstance || null;
};

// Handle Firebase auth state changes - sync with app session
// Firebase is the source of truth: on page reload, onAuthStateChanged fires
// with the persisted Firebase user, so we restore the client session from Firestore.
// ============ SHARED ADMIN CHECK (claims + Firestore admins/{uid} doc) ============
// Admin status is granted by EITHER:
//   1. The backend-issued `admin: true` (or `role: admin`) custom claim, OR
//   2. The presence of an `admins/{uid}` document in Firestore (single source of truth)
// This mirrors requireAdmin() in the Cloud Functions. It is used to:
//   - Authorize super admin login
//   - BLOCK admins from being granted client/provider sessions
async function isAdminUser(user) {
  if (!user || !user.uid) return false;

  try {
    if (typeof user.getIdTokenResult === 'function') {
      const idTokenResult = await user.getIdTokenResult(true);
      const claims = (idTokenResult && idTokenResult.claims) || {};
      if (claims.admin === true || claims.role === 'admin') return true;
    }
  } catch (e) {
    console.warn('[isAdminUser] Could not read token claims:', e.message);
  }

  return false;
}
window.isAdminUser = isAdminUser;

function handleFirebaseAuthStateChange(user) {
    if (user) {
        console.log('[Firebase Auth] User authenticated:', user.email, user.uid);
        window.__lastKnownAuthUid = String(user.uid || '');
        window.__lastKnownAuthEmail = String(user.email || '').toLowerCase();
        if (window.__forceGuestModeUntilNextLogin === true) {
            // User explicitly logged out from this tab; do not auto-restore a client
            // session until they intentionally log in again.
            return;
        }

        if (user.emailVerified === false) {
            currentClient = null;
            if (typeof updateHeaderUserBadge === 'function') {
                updateHeaderUserBadge();
            }
            if (typeof renderSearchResults === 'function') {
                renderSearchResults();
            }
            return;
        }

        // ============ SECURITY FIX: NEVER AUTO-RESTORE A CLIENT SESSION FOR AN ADMIN ============
        // The super admin (or any user holding the `admin: true` / `role: admin`
        // backend-issued custom claim) MUST NOT be silently restored as a client
        // session on page reload. Otherwise an admin's persisted Firebase session
        // would grant client menu access (isUserLoggedIn passes) and create a
        // client dashboard session. We inspect the ID token claims and bail out
        // (leaving currentClient null) if the authenticated user is an admin.
        isAdminUser(user)
            .then(function(isAdmin) {
                if (isAdmin) {
                    console.warn('[Firebase Auth] Blocked admin session from being restored as a client:', user.email);
                    // Do NOT restore a client session. Admin dashboards are only
                    // unlocked through performAdminLogin().
                    currentClient = null;
                    if (typeof updateHeaderUserBadge === 'function') {
                        updateHeaderUserBadge();
                    }
                    if (typeof renderSearchResults === 'function') {
                        renderSearchResults();
                    }
                    return;
                }
                restoreClientSessionFromFirestore(user);
            })
            .catch(function() {
                // If we cannot determine admin status, fail closed: do NOT auto-restore
                // a client session for an unverified user.
                currentClient = null;
            });
    } else {
        console.log('[Firebase Auth] User signed out from Firebase');
        window.__lastKnownAuthUid = '';
        window.__lastKnownAuthEmail = '';
        // Clear the in-memory client session
        currentClient = null;
        if (typeof updateHeaderUserBadge === 'function') {
            updateHeaderUserBadge();
        }
        if (typeof renderSearchResults === 'function') {
            renderSearchResults();
        }
    }
}

// Restore a verified NON-admin Firebase user's client session from Firestore.
function restoreClientSessionFromFirestore(user) {
        if (!user || user.emailVerified === false) {
            currentClient = null;
            return;
        }
        // Restore the client session from Firestore (Firebase-only, no browserStorage)
        const db = firebaseDbInstance || window.firebaseDbInstance;
        if (db) {
            db.collection('clients').doc(user.uid).get()
                .then(function(doc) {
                    if (doc.exists) {
                        const clientData = normalizeClientSessionRecord(doc.data(), user, user.email || '');
                        currentClient = clientData;
                        const existingIdx = clients.findIndex(c => String(c.id) === String(clientData.id));
                        if (existingIdx !== -1) {
                            clients[existingIdx] = clientData;
                        } else {
                            clients.push(clientData);
                        }
                        console.log('[Firebase Auth] Client session restored from Firestore:', clientData.name);
                        // Update UI to reflect the restored session
                        if (typeof showClientDashboard === 'function') {
                            showClientDashboard();
                        }
                        if (typeof updateHeaderUserBadge === 'function') {
                            updateHeaderUserBadge();
                        }
                        if (typeof renderSearchResults === 'function') {
                            renderSearchResults();
                        }
                    } else {
                        console.log('[Firebase Auth] No client doc found for uid:', user.uid);
                    }
                })
.catch(function(err) {
                    console.warn('[Firebase Auth] Could not restore client session:', err.message);
                });
        }
}

// Initialize Firebase Auth on load
// Initialize Firebase Auth on load - delayed to allow Firebase SDK to load first
// Retry up to 5 times with 500ms delay if SDK not ready
function initFirebaseWithRetry() {
    initFirebaseWithRetry.retryCount = (initFirebaseWithRetry.retryCount || 0);

    // FIRST: Check if the modular bootstrap already exposed global instances.
    // The modular bootstrap (firebase-sdk-bootstrap.js) sets these globals and
    // wraps auth in a v8-compatible wrapper so legacy method-style calls work.
    if (window.firebaseAuthInstance && window.firebaseDbInstance) {
        checkFirebaseAuthConfig();
        return;
    }

    if (typeof firebase !== 'undefined' && typeof firebaseConfig !== 'undefined') {
        checkFirebaseAuthConfig();
    } else {
        initFirebaseWithRetry.retryCount++;
        if (initFirebaseWithRetry.retryCount <= 5) {
            console.log('[Firebase Auth] Waiting for SDK, retry ' + initFirebaseWithRetry.retryCount + '/5...');
            setTimeout(initFirebaseWithRetry, 500);
        } else {
            console.log('[Firebase Auth] SDK not found after 5 retries; auth remains disabled');
            FIREBASE_AUTH_ENABLED = false;
            initFirebaseWithRetry.retryCount = 0;
        }
    }
}
setTimeout(initFirebaseWithRetry, 500);

// ============ HYBRID AUTH FUNCTIONS ============
// Firebase-only authentication. Fail closed when auth is unavailable.
function hybridClientLogin(email, password) {
    return new Promise(function(resolve, reject) {
        if (!FIREBASE_AUTH_ENABLED || !firebaseAuthInstance) {
            reject(new Error('Firebase authentication is unavailable. Please ensure Firebase auth is configured and enabled.'));
            return;
        }

        firebaseAuthInstance.signInWithEmailAndPassword(email, password)
            .then(function(userCredential) {
                console.log('[Auth] Firebase login success:', userCredential.user.uid);
                resolve(userCredential.user);
            })
            .catch(function(error) {
                console.log('[Auth] Firebase login failed:', error.message);
                reject(error);
            });
    });
}

// Firebase-only provider authentication. Fail closed when auth is unavailable.
function hybridProviderLogin(email, password) {
    return new Promise(function(resolve, reject) {
        if (!FIREBASE_AUTH_ENABLED || !firebaseAuthInstance) {
            reject(new Error('Firebase authentication is unavailable. Please ensure Firebase auth is configured and enabled.'));
            return;
        }

        firebaseAuthInstance.signInWithEmailAndPassword(email, password)
            .then(function(userCredential) {
                console.log('[Auth] Firebase provider login success:', userCredential.user.uid);
                resolve(userCredential.user);
            })
            .catch(function(error) {
                console.log('[Auth] Firebase provider login failed:', error.message);
                reject(error);
            });
    });
}

// Firebase sign out (for hybrid logout)
function hybridSignOut() {
    return new Promise(function(resolve, reject) {
        if (!FIREBASE_AUTH_ENABLED || !firebaseAuthInstance) {
            resolve();
            return;
        }

        firebaseAuthInstance.signOut()
            .then(function() {
                console.log('[Auth] Signed out from Firebase');
                resolve();
            })
            .catch(function(error) {
                console.log('[Auth] Sign out error:', error.message);
                resolve(); // Don't reject, just resolve
            });
    });
}

// Firestore service layer - Uses existing initFirebase from firebase-config.js
// CRITICAL: Only ONE Firebase initialization - use firebase-config.js which handles duplicate initialization
const firestoreService = {
    db: null,
    auth: null,
    initialized: false,

    // Initialize Firebase services - ONLY use initFirebase from firebase-config.js
    // This prevents duplicate initialization issues by using the centralized init
    init: function() {
        if (!FIRESTORE_ENABLED) {
            console.log('[Firestore] Disabled - using browserStorage only');
            return Promise.resolve();
        }

        // CRITICAL FIX (cross-browser sync):
        // - firebase-config.js's initFirebase() is ASYNC (returns a Promise), so
        //   we MUST await it and read .db / .auth from the resolved value.
        // - Prefer the raw modular instances exposed by firebase-sdk-bootstrap.js
        //   (window.firebaseDbRaw / window.firebaseAuthRaw). These are compatible
        //   with the modular SDK helpers (collection, doc, setDoc, onSnapshot)
        //   used by firebase-integration.js and firestore-realtime-sync.js.
        const self = this;
        return (async () => {
            try {
                // Path 1: prefer the shared modular bootstrap (already initialized)
                if (typeof window.ensureFirebaseReady === 'function') {
                    const ready = await window.ensureFirebaseReady();
                    if (ready && ready.db) {
                        // CROSS-BROWSER SYNC FIX: use the v8-compatible wrapper
                        // (window.firebaseDbInstance) for firestoreService so its
                        // .collection()/.doc().set() calls work. The raw modular
                        // instance (window.firebaseDbRaw) does NOT expose .collection().
                        self.db = window.firebaseDbInstance || ready.db;
                        self.auth = window.firebaseAuthInstance || ready.auth;
                        self.initialized = true;
                        console.log('[Firestore] Initialized via shared bootstrap (modular)');
                        return;
                    }
                }

                // Path 2: fallback to initFirebase() (legacy async wrapper)
                if (typeof initFirebase === 'function') {
                    const firebaseObjs = await initFirebase();
                    if (firebaseObjs && firebaseObjs.db) {
                        // Same fix as Path 1: use the v8-compatible wrapper.
                        self.db = window.firebaseDbInstance || firebaseObjs.db;
                        self.auth = window.firebaseAuthInstance || firebaseObjs.auth;
                        self.initialized = true;
                        console.log('[Firestore] Initialized via initFirebase()');
                        return;
                    }
                }

                // If initFirebase doesn't exist, Firebase SDK wasn't loaded properly
                console.log('[Firestore] Firebase SDK not found or initFirebase missing');
            } catch (e) {
                console.error('[Firestore] Init error:', e);
            }
        })();
    },

    // Save to collection in Firestore
    save: function(collection, id, data) {
        if (!this.initialized || !this.db) {
            // Firebase-only (no browserStorage fallback)
            return Promise.resolve();
        }

        return this.db.collection(collection).doc(String(id)).set(data)
            .then(() => {
                console.log('[Firestore] Saved to', collection, ':', id);
                return data;
            })
            .catch(e => {
                console.error('[Firestore] Save error:', e);
                return data;
            });
    },

    // Delete from collection
    delete: function(collection, id) {
        if (!this.initialized || !this.db) {
            const items = JSON.parse(null || '[]');
            const filtered = items.filter(i => i.id !== id);
            return Promise.resolve();
        }

        return this.db.collection(collection).doc(String(id)).delete()
            .then(() => console.log('[Firestore] Deleted from', collection, ':', id))
            .catch(e => console.error('[Firestore] Delete error:', e));
    },

    // Get all documents from collection
    getAll: function(collection) {
        if (!this.initialized || !this.db) {
            return Promise.resolve(JSON.parse(null || '[]'));
        }

        return this.db.collection(collection).get()
            .then(snapshot => {
                const data = [];
                snapshot.forEach(doc => data.push(doc.data()));
                return data;
            })
            .catch(e => {
                console.error('[Firestore] Get error:', e);
                return JSON.parse(null || '[]');
            });
    },

    // Real-time listener
    listen: function(collection, callback) {
        if (!this.initialized || !this.db) {
            const data = JSON.parse(null || '[]');
            callback(data);
            return () => {};
        }

        return this.db.collection(collection).onSnapshot(snapshot => {
            const data = [];
            snapshot.forEach(doc => data.push(doc.data()));
            callback(data);
        }, e => console.error('[Firestore] Listen error:', e));
    }
};

// Initialize data - Firestore is the SINGLE SOURCE OF TRUTH.
// Arrays start empty and are populated by firestore-realtime-sync.js onSnapshot listeners.
let menuItems = [];
let mealRequests = [];
let clientMessages = [];
let clients = [];
let providers = [];
let reviews = [];
let orders = [];

// Pagination display state: how many items to show in each list view.
// Load More buttons increment these; Firestore sync brings in new pages via __firestoreLoadMore.
const DISPLAY_PAGE_SIZE = 20;
const paginationDisplay = {
    clientOrders: DISPLAY_PAGE_SIZE,
    providerOrders: DISPLAY_PAGE_SIZE,
    adminOrders: DISPLAY_PAGE_SIZE,
    adminClients: DISPLAY_PAGE_SIZE,
    adminProviders: DISPLAY_PAGE_SIZE,
};

// Reset display page size when a new Firestore sync replaces data (fresh login / role change)
function resetPaginationDisplay() {
    Object.keys(paginationDisplay).forEach(k => { paginationDisplay[k] = DISPLAY_PAGE_SIZE; });
}

// Load More handlers — increase display count then re-render.
// If all locally-loaded docs are visible, also fetch next page from Firestore.
async function loadMoreClientOrders() {
    paginationDisplay.clientOrders += DISPLAY_PAGE_SIZE;
    const state = window.__firestorePaginationState && window.__firestorePaginationState['orders'];
    const clientOrders = clientMessages.filter(msg =>
        msg.clientEmail && currentClient && msg.clientEmail.toLowerCase() === currentClient.email.toLowerCase()
    );
    if (state && state.hasMore && clientOrders.length <= paginationDisplay.clientOrders) {
        await window.__firestoreLoadMore('orders');
    }
    if (typeof renderClientOrders === 'function') renderClientOrders();
}

async function loadMoreProviderOrders() {
    paginationDisplay.providerOrders += DISPLAY_PAGE_SIZE;
    const state = window.__firestorePaginationState && window.__firestorePaginationState['orders'];
    const providerId = currentProvider && (currentProvider.id || currentProvider.providerId);
    const provName = currentProvider && currentProvider.name ? currentProvider.name.trim().toLowerCase() : '';
    const pOrders = clientMessages.filter(msg =>
        (msg.providerId && providerId && String(msg.providerId) === String(providerId)) ||
        (msg.providerName && provName && msg.providerName.toLowerCase() === provName)
    );
    if (state && state.hasMore && pOrders.length <= paginationDisplay.providerOrders) {
        await window.__firestoreLoadMore('orders');
    }
    if (typeof renderProviderOrders === 'function') renderProviderOrders();
}

async function loadMoreAdminClients() {
    paginationDisplay.adminClients += DISPLAY_PAGE_SIZE;
    const state = window.__firestorePaginationState && window.__firestorePaginationState['clients'];
    const src = Array.isArray(window.clients) ? window.clients : clients;
    if (state && state.hasMore && src.length <= paginationDisplay.adminClients) {
        await window.__firestoreLoadMore('clients');
    }
    if (typeof renderAdminClients === 'function') renderAdminClients();
}

async function loadMoreAdminProviders() {
    paginationDisplay.adminProviders += DISPLAY_PAGE_SIZE;
    const state = window.__firestorePaginationState && window.__firestorePaginationState['providers'];
    const src = Array.isArray(window.providers) ? window.providers : providers;
    if (state && state.hasMore && src.length <= paginationDisplay.adminProviders) {
        await window.__firestoreLoadMore('providers');
    }
    if (typeof renderAdminProviders === 'function') renderAdminProviders();
}

async function loadMoreAdminOrders() {
    paginationDisplay.adminOrders += DISPLAY_PAGE_SIZE;
    const state = window.__firestorePaginationState && window.__firestorePaginationState['orders'];
    const src = Array.isArray(window.orders) ? window.orders : orders;
    if (state && state.hasMore && src.length <= paginationDisplay.adminOrders) {
        await window.__firestoreLoadMore('orders');
    }
    if (typeof renderAdminOrders === 'function') renderAdminOrders();
}

function syncWindowCollectionState() {
    window.menuItems = menuItems;
    window.mealRequests = mealRequests;
    window.clientMessages = clientMessages;
    window.clients = clients;
    window.providers = providers;
    window.reviews = reviews;
    window.orders = orders;
    window.__wtE_orders = orders;
    window.__wtE_clientMessages = clientMessages;
    window.__wtE_allMenuItems = menuItems;
}

syncWindowCollectionState();
window.bridgeFirestoreMenuItemsToLocal = function(menuDocs) {
    if (!Array.isArray(menuDocs)) return;
    const indexesById = new Map();
    menuItems.forEach((item, index) => {
        if (item && item.id != null) indexesById.set(String(item.id), index);
    });
    menuDocs.forEach(item => {
        if (!item) return;
        const itemId = item.id == null ? '' : String(item.id);
        const existingIndex = itemId ? indexesById.get(itemId) : undefined;
        if (existingIndex === undefined) {
            if (itemId) indexesById.set(itemId, menuItems.length);
            menuItems.push(item);
        } else {
            menuItems[existingIndex] = { ...menuItems[existingIndex], ...item };
        }
    });
    window.menuItems = menuItems;
    window.__wtE_allMenuItems = menuItems;
};
// Provider Subscription Plans Configuration
// All plans renew MONTHLY (30-day recurring billing cycle).
const SUBSCRIPTION_PLANS = {
    free: { name: 'Free', durationDays: 30, interval: 'month', renewsMonthly: true, features: ['basic_menu', 'limited_orders'] },
    basic: { name: 'Basic', durationDays: 30, interval: 'month', renewsMonthly: true, features: ['full_menu', 'unlimited_orders', 'analytics'] },
    premium: { name: 'Premium', durationDays: 30, interval: 'month', renewsMonthly: true, features: ['full_menu', 'unlimited_orders', 'analytics', 'priority_support', 'custom_branding'] }
};
// Expose to window so bundled scripts (e.g. renderAdminSubscriptions-extract.js) can access it
window.SUBSCRIPTION_PLANS = SUBSCRIPTION_PLANS;

// Client Subscription Plans Configuration
const CLIENT_SUBSCRIPTION_PLANS = {
    free: { name: 'Free', durationDays: 30, features: ['basic_access', 'limited_orders'] },
    standard: { name: 'Standard', durationDays: 30, features: ['unlimited_orders', 'favorites'] },
    premium: { name: 'Premium', durationDays: 30, features: ['unlimited_orders', 'priority_support', 'exclusive_deals'] }
};
// Default subscription status for new providers
function getDefaultSubscription() {
    return {
        status: 'pending', // pending, approved, rejected, suspended
        plan: 'free',
        startDate: null,
        endDate: null,
        approvedBy: null,
        approvedAt: null,
        rejectionReason: '',
        suspendedReason: '',
        notes: ''
    };
}

// ==================== SUBSCRIPTION ACCESS CONTROL ====================

/**
 * Check if provider's subscription is active and not expired.
 * Returns { allowed: boolean, reason: string, canAddMenuItems: boolean }
 */
function checkProviderSubscriptionAccess(provider) {
    if (!provider) {
        return { allowed: false, reason: 'No provider account found', canAddMenuItems: false };
    }

    const status = String(provider.subscriptionStatus || 'pending').toLowerCase();
    const plan = String(provider.subscriptionPlan || 'free').toLowerCase();
    const now = new Date();
    const endDate = provider.subscriptionEnd ? new Date(provider.subscriptionEnd) : null;

    // Check if subscription is expired
    if (endDate && endDate < now) {
        return {
            allowed: false,
            reason: 'Your subscription has expired. Please renew to continue.',
            canAddMenuItems: false,
            expired: true
        };
    }

    // Check subscription status
    if (status === 'suspended') {
        return {
            allowed: false,
            reason: 'Your subscription is suspended. Please contact support.',
            canAddMenuItems: false
        };
    }

    if (status === 'rejected') {
        return {
            allowed: false,
            reason: 'Your subscription was rejected. Please contact support.',
            canAddMenuItems: false
        };
    }

    if (status === 'pending') {
        return {
            allowed: true,
            reason: 'Your subscription is pending admin approval.',
            canAddMenuItems: plan === 'free', // Free plan can add items while pending
            pending: true
        };
    }

    if (status === 'approved' || status === 'active') {
        return {
            allowed: true,
            reason: 'Subscription is active.',
            canAddMenuItems: true
        };
    }

    return {
        allowed: false,
        reason: 'Unknown subscription status. Please contact support.',
        canAddMenuItems: false
    };
}

/**
 * Check if provider can register with free plan.
 * Free plan can only be used ONCE per email address.
 */
function canUseFreePlan(email, providersList) {
    if (!email) return false;
    const normalizedEmail = String(email).toLowerCase().trim();

    const hasUsedFree = (providersList || []).some(p => {
        if (!p || !p.email) return false;
        const pEmail = String(p.email || '').toLowerCase().trim();
        if (pEmail !== normalizedEmail) return false;

        const planValue = String(p.subscriptionPlan || p.plan || p.planId || '').toLowerCase();
        return planValue === 'free';
    });

    return !hasUsedFree;
}

/**
 * Validate provider plan selection during registration.
 * Returns { valid: boolean, error: string }
 */
function validatePlanSelection(planId, email, providersList) {
    if (!planId) {
        return { valid: false, error: 'Please select a subscription plan.' };
    }

    const plan = String(planId).toLowerCase();
    
    // Free plan restriction
    if (plan === 'free') {
        if (!canUseFreePlan(email, providersList)) {
            return {
                valid: false,
                error: 'Free plan can only be used once. Please select a paid plan (Basic or Premium).'
            };
        }
    }

    return { valid: true };
}

// Make functions globally accessible
window.checkProviderSubscriptionAccess = checkProviderSubscriptionAccess;
window.canUseFreePlan = canUseFreePlan;
window.validatePlanSelection = validatePlanSelection;

// ==================== STRIPE CLIENT-SIDE INTEGRATION ====================
// Clients join the app FREE - only providers subscribe to paid plans.
// NOTE: stripe.publishableKey is empty until the user provides it. Until then,
// Stripe is disabled and providers can still register on the Free plan.

// ── SVG launch payment policy ──────────────────────────────────────────────
// Client food orders are cash-only. Stripe is reserved for provider
// subscriptions once the platform has an approved Stripe account.
window.orderPaymentConfig = Object.freeze({
    market: 'VC',
    clientOrderPaymentMethod: 'cash',
    providerSubscriptionBilling: 'stripe_when_configured',
    providerPayoutsEnabled: false
});

// Stripe Connect is deliberately disabled until Stripe supports the platform
// and provider entities for payouts. Server-side endpoints enforce this too.
window.stripeConnectConfig = window.stripeConnectConfig || {
    enabled: false,
    functionsRegion: 'us-central1',
    functionsBaseUrl: ''
};

// Get the current Firebase ID token for authenticated cloud function calls
function getFirebaseIdToken() {
    return new Promise(function(resolve, reject) {
        const auth = window.firebaseAuthInstance || firebaseAuthInstance;
        if (!auth || !auth.currentUser) {
            reject(new Error('Not authenticated'));
            return;
        }
        // v8-compatible wrapper exposes getIdToken
        if (typeof auth.currentUser.getIdToken === 'function') {
            auth.currentUser.getIdToken()
                .then(function(token) { resolve(token); })
                .catch(function(err) { reject(err); });
        } else {
            reject(new Error('Auth user missing getIdToken'));
        }
    });
}

async function getAppCheckToken() {
    try {
        if (typeof window.getFirebaseAppCheckToken !== 'function') {
            throw new Error('Firebase App Check token provider is unavailable.');
        }
        const result = await window.getFirebaseAppCheckToken();
        if (!result || !result.token) {
            throw new Error('Firebase App Check did not return a token.');
        }
        return result.token;
    } catch (e) {
        console.error('[AppCheck] Could not fetch required App Check token:', e && e.message ? e.message : e);
        throw new Error('This request could not be verified by Firebase App Check. Refresh and try again.');
    }
}

// Call a Stripe cloud function (https.onRequest) with an auth token
async function callStripeFunction(functionName, data) {
    const token = await getFirebaseIdToken();
    const appCheckToken = await getAppCheckToken();
    const projectId = (window.firebaseConfig && window.firebaseConfig.projectId) || 'what-to-eat-e5ce9';
    const finalUrl = `https://us-central1-${projectId}.cloudfunctions.net/${functionName}`;

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
    };
    if (appCheckToken) {
        headers['X-Firebase-AppCheck'] = appCheckToken;
    }

    const res = await fetch(finalUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(data || {})
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(json.error || ('Request failed: ' + res.status));
    }
    return json;
}

async function callProtectedFunction(functionName, data) {
    const token = await getFirebaseIdToken();
    const appCheckToken = await getAppCheckToken();
    const projectId = (window.firebaseConfig && window.firebaseConfig.projectId) || 'what-to-eat-e5ce9';
    const finalUrl = `https://us-central1-${projectId}.cloudfunctions.net/${functionName}`;

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
    };
    if (appCheckToken) {
        headers['X-Firebase-AppCheck'] = appCheckToken;
    }

    const res = await fetch(finalUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(data || {})
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(json.error || ('Protected function request failed: ' + res.status));
    }
    return json;
}
window.callProtectedFunction = callProtectedFunction;

async function registerPendingProviderViaBackend(provider) {
    const payload = {
        name: provider.name,
        phone: provider.phone,
        country: provider.country,
        type: provider.type,
        address: provider.address || '',
        timezone: normalizeProviderTimezone(provider.timezone || detectProviderTimezone()),
        currency: provider.currency || getProviderLaunchContext(provider.country).currency,
        acceptsCustomRequests: provider.acceptsCustomRequests === true
    };
    try {
        return await callProtectedFunction('registerProvider', payload);
    } catch (e) {
        const message = (e && e.message ? e.message : String(e)).toLowerCase();
        if (message.includes('already pending admin approval') || message.includes('already exists')) {
            return { ok: true, alreadyRegistered: true };
        }
        throw e;
    }
}

async function resumePendingProviderRegistration(email, password, provider, form) {
    const userCredential = await firebaseAuthInstance.signInWithEmailAndPassword(email, password);
    provider.id = userCredential.user.uid;
    provider.uid = userCredential.user.uid;
    await registerPendingProviderViaBackend(provider);
    await sendVerificationEmailForUser(userCredential.user);
    if (typeof hybridSignOut === 'function') {
        await hybridSignOut();
    }
    completeProviderRegistration(provider, form);
    showNotification('Verification email sent. Verify your email, then log in to finish provider onboarding.', 'success');
}

async function callPublicFunction(functionName, data) {
    const projectId = (window.firebaseConfig && window.firebaseConfig.projectId) || 'what-to-eat-e5ce9';
    const finalUrl = `https://us-central1-${projectId}.cloudfunctions.net/${functionName}`;
    const appCheckToken = await getAppCheckToken();
    const res = await fetch(finalUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Firebase-AppCheck': appCheckToken
        },
        body: JSON.stringify(data || {})
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(json.error || (`Public function request failed: ${res.status}`));
    }
    return json;
}

async function evaluateProviderAvailabilityServer(item = {}) {
    const itemCountry = normalizeCountryCode(item.country || item.providerCountry || 'US');
    const policy = getLaunchCountryPolicy(itemCountry);
    const timezone = normalizeProviderTimezone(item.providerTimezone || item.timezone || policy.timezone || 'UTC');
    let schedule = item.hours || item.businessHours || item.openingHours || item.availability || null;
    if (!schedule && item.openTime && item.closeTime) {
        const dailyRange = `${item.openTime}-${item.closeTime}`;
        schedule = { mon: dailyRange, tue: dailyRange, wed: dailyRange, thu: dailyRange, fri: dailyRange, sat: dailyRange, sun: dailyRange };
    }
    return callPublicFunction('evaluateProviderAvailability', {
        timezone,
        is247: item.is247 === true,
        hours: schedule || {},
        country: itemCountry
    });
}

async function ensureStripeJsLoaded() {
    if (window.Stripe) return;
    await new Promise((resolve, reject) => {
        const existing = document.getElementById('stripe-js');
        if (existing) {
            existing.addEventListener('load', () => resolve(), { once: true });
            existing.addEventListener('error', () => reject(new Error('Failed to load Stripe.js')), { once: true });
            return;
        }
        const script = document.createElement('script');
        script.id = 'stripe-js';
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Stripe.js'));
        document.head.appendChild(script);
    });
}

// Start Stripe Checkout for a provider's paid plan
async function startProviderStripeCheckout(planId) {
    if (!currentProvider) {
        showNotification('Please login to manage your subscription', 'error');
        return;
    }

    const providerUid = currentProvider.uid || currentProvider.id;
    if (!providerUid) {
        showNotification('Provider account not linked to Firebase. Please login again.', 'error');
        return;
    }

    showNotification('Redirecting to secure payment...', 'success');

    try {
        const result = await callStripeFunction('createProviderCheckoutSession', {
            providerUid: providerUid,
            planId: planId
        });

        if (result.alreadyActive) {
            showNotification(result.message || 'You already have an active subscription for this plan.', 'success');
            return;
        }

        if (result.url) {
            // Redirect to Stripe Checkout
            window.location.href = result.url;
        } else if (result.sessionId && window.Stripe) {
            const stripePublishableKey = (window.stripeConfig && window.stripeConfig.publishableKey) || '';
            if (stripePublishableKey) {
                await ensureStripeJsLoaded();
                const stripe = window.Stripe(stripePublishableKey);
                const { error } = await stripe.redirectToCheckout({ sessionId: result.sessionId });
                if (error) {
                    showNotification('Payment redirect failed: ' + error.message, 'error');
                }
            } else {
                showNotification('Stripe is not configured yet.', 'error');
            }
        } else if (result.sessionId) {
            const stripePublishableKey = (window.stripeConfig && window.stripeConfig.publishableKey) || '';
            if (!stripePublishableKey) {
                showNotification('Stripe is not configured yet.', 'error');
                return;
            }
            await ensureStripeJsLoaded();
            const stripe = window.Stripe(stripePublishableKey);
            const { error } = await stripe.redirectToCheckout({ sessionId: result.sessionId });
            if (error) {
                showNotification('Payment redirect failed: ' + error.message, 'error');
            }
        } else {
            showNotification('Could not start payment. Please try again.', 'error');
        }
    } catch (e) {
        console.error('[Stripe] startProviderStripeCheckout error:', e);
        showNotification('Payment error: ' + e.message, 'error');
    }
}

// Create a Stripe Billing Portal session (manage subscription)
async function openStripeBillingPortal() {
    if (!currentProvider) {
        showNotification('Please login to manage your subscription', 'error');
        return;
    }

    const providerUid = currentProvider.uid || currentProvider.id;
    if (!providerUid) {
        showNotification('Provider account not linked to Firebase. Please login again.', 'error');
        return;
    }

    try {
        const result = await callStripeFunction('createStripePortalSession', {
            providerUid: providerUid
        });

        if (result.url) {
            window.location.href = result.url;
        } else {
            showNotification('Could not open billing portal.', 'error');
        }
    } catch (e) {
        console.error('[Stripe] openStripeBillingPortal error:', e);
        showNotification('Billing portal error: ' + e.message, 'error');
    }
}

async function startProviderStripeConnectOnboarding() {
    if (!currentProvider) {
        showNotification('Please login to set up payouts.', 'error');
        return;
    }
    const providerUid = currentProvider.uid || currentProvider.id;
    if (!providerUid) {
        showNotification('Provider account not linked to Firebase. Please login again.', 'error');
        return;
    }

    try {
        const result = await callStripeFunction('createStripeConnectOnboardingLink', {
            providerId: providerUid
        });
        if (result && result.url) {
            window.location.href = result.url;
            return;
        }
        showNotification('Could not start Stripe payout onboarding. Please try again.', 'error');
    } catch (e) {
        console.error('[Stripe] startProviderStripeConnectOnboarding error:', e);
        showNotification('Stripe payout onboarding error: ' + e.message, 'error');
    }
}

// Cancel provider subscription (at period end)
async function cancelProviderStripeSubscription() {
    if (!currentProvider) {
        showNotification('Please login to manage your subscription', 'error');
        return;
    }

    if (!confirm('Cancel your subscription? You will keep access until the end of the current billing period.')) {
        return;
    }

    const providerUid = currentProvider.uid || currentProvider.id;
    if (!providerUid) {
        showNotification('Provider account not linked to Firebase. Please login again.', 'error');
        return;
    }

    try {
        const result = await callStripeFunction('cancelProviderSubscription', {
            providerUid: providerUid,
            immediate: false
        });
        showNotification(result.message || 'Subscription cancelled.', 'success');
        renderProviderSubscriptionStatus();
    } catch (e) {
        console.error('[Stripe] cancelProviderStripeSubscription error:', e);
        showNotification('Error cancelling subscription: ' + e.message, 'error');
    }
}

// Render the provider's subscription status panel (called when Subscription tab is clicked)
async function renderProviderSubscriptionStatus() {
    const container = document.getElementById('providerSubscriptionStatus');
    if (!container) return;

    if (!currentProvider) {
        container.innerHTML = '<div class="empty-state"><p>Please login to view your subscription.</p></div>';
        return;
    }

    // Show loading state
    container.innerHTML = '<p>Loading subscription status...</p>';

    const providerUid = currentProvider.uid || currentProvider.id;

    // Try to fetch live status from the cloud function
    try {
        const status = await callStripeFunction('getProviderSubscriptionStatus', {
            providerUid: providerUid
        });
        try {
            const connectStatusResult = await callStripeFunction('getProviderConnectOnboardingStatus', {
                providerUid: providerUid
            });
            if (connectStatusResult && connectStatusResult.connect) {
                status.stripeConnect = connectStatusResult.connect;
            }
        } catch (connectErr) {
            console.warn('[Stripe] Could not fetch live Connect onboarding status:', connectErr.message);
        }
        renderSubscriptionStatusUI(container, status);
    } catch (e) {
        console.warn('[Stripe] Could not fetch live subscription status, using local data:', e.message);
        // Fallback to local provider data
        const localStatus = {
            subscriptionStatus: currentProvider.subscriptionStatus || 'pending',
            subscriptionPlan: currentProvider.subscriptionPlan || 'free',
            subscriptionEnd: currentProvider.subscriptionEnd || null,
            stripePaymentStatus: currentProvider.stripePaymentStatus || null,
            isStripePaid: currentProvider.isStripePaid === true,
            stripeConnect: {
                accountId: currentProvider.stripeConnectAccountId || null,
                detailsSubmitted: currentProvider.stripeConnectDetailsSubmitted === true,
                chargesEnabled: currentProvider.stripeConnectChargesEnabled === true,
                payoutsEnabled: currentProvider.stripeConnectPayoutsEnabled === true,
                onboardingComplete: currentProvider.stripeConnectOnboardingComplete === true,
                readyForPayouts: currentProvider.stripeConnectOnboardingComplete === true &&
                    currentProvider.stripeConnectChargesEnabled === true &&
                    currentProvider.stripeConnectPayoutsEnabled === true,
                requirementsDue: currentProvider.stripeConnectRequirementsDue || [],
                disabledReason: currentProvider.stripeConnectDisabledReason || ''
            },
            planDetails: getLocalPlanDetails(currentProvider.subscriptionPlan || 'free')
        };
        renderSubscriptionStatusUI(container, localStatus);
    }
}

// Get local plan details (fallback when Stripe isn't configured)
function getLocalPlanDetails(planId) {
    const defaultRates = getDefaultSubscriptionRates();
    const plans = {
        free: { name: 'Free Plan', price: 0, interval: 'one-time' },
        basic: { name: 'Basic Plan', price: Number(defaultRates.basic || 100.50), interval: 'month' },
        premium: { name: 'Premium Plan', price: Number(defaultRates.premium || 200.00), interval: 'month' }
    };
    return plans[planId] || plans.free;
}

// Returns true when Stripe Connect (order-level card payments) is fully configured.
function stripeConnectEnabled() {
    return false;
}

// Resolves the base URL for Cloud Functions (handles local dev and multi-region).
function _getConnectFunctionUrl(functionName) {
    const cfg = window.stripeConnectConfig || {};
    if (cfg.functionsBaseUrl) return `${cfg.functionsBaseUrl}/${functionName}`;
    const region = cfg.functionsRegion || 'us-central1';
    const projectId = (window.firebaseConfig && window.firebaseConfig.projectId) || 'what-to-eat-e5ce9';
    return `https://${region}-${projectId}.cloudfunctions.net/${functionName}`;
}

// Build and inject the subscription status UI
function renderSubscriptionStatusUI(container, status) {
    const subStatus = status.subscriptionStatus || 'pending';
    const plan = status.subscriptionPlan || 'free';
    const planDetails = status.planDetails || getLocalPlanDetails(plan);
    const stripeEnabled = !!(window.stripeConfig && window.stripeConfig.enabled && window.stripeConfig.publishableKey);

    // Status badge
    let statusBadge = '';
    let statusText = '';
    if (subStatus === 'approved') {
        const endDate = status.subscriptionEnd ? new Date(status.subscriptionEnd) : null;
        const now = new Date();
        if (endDate && endDate < now) {
            statusText = '⚠️ Expired';
            statusBadge = '<span class="sub-status-badge expired">⚠️ Expired</span>';
        } else {
            statusText = '✅ Active';
            statusBadge = '<span class="sub-status-badge approved">✅ Active</span>';
        }
    } else if (subStatus === 'pending') {
        statusText = '⏳ Pending Approval';
        statusBadge = '<span class="sub-status-badge pending">⏳ Pending Approval</span>';
    } else if (subStatus === 'rejected') {
        statusText = '❌ Rejected';
        statusBadge = '<span class="sub-status-badge rejected">❌ Rejected</span>';
    } else if (subStatus === 'suspended') {
        statusText = '⛔ Suspended';
        statusBadge = '<span class="sub-status-badge suspended">⛔ Suspended</span>';
    } else {
        statusText = subStatus;
        statusBadge = `<span class="sub-status-badge">${escapeHtml(subStatus)}</span>`;
    }

    // Plan pricing display
    const priceDisplay = planDetails && planDetails.price > 0
        ? `$${planDetails.price.toFixed(2)}/${planDetails.interval || 'month'}`
        : '$0 (Free)';

    // Build plan features
    const planFeatures = getPlanFeatureList(plan);

    // Build action buttons based on status
    let actionButtons = '';
    const connect = status.stripeConnect || null;
    const connectReady = !!(connect && connect.readyForPayouts);
    const connectHasAccount = !!(connect && connect.accountId);

    // If pending and a paid plan is selected, show Pay Now button
    if (subStatus === 'pending' && plan !== 'free' && stripeEnabled) {
        actionButtons += `
            <button class="approve-btn" onclick="startProviderStripeCheckout('${plan}')">💳 Pay Now (${priceDisplay})</button>
        `;
    }

    // If approved and has a Stripe subscription, show manage/cancel buttons
    if (subStatus === 'approved' && status.stripeSubscriptionId && stripeEnabled) {
        actionButtons += `
            <button class="edit-btn" onclick="openStripeBillingPortal()">Manage Billing</button>
            <button class="reject-btn" onclick="cancelProviderStripeSubscription()">Cancel Subscription</button>
        `;
    }

    if ((subStatus === 'approved' || subStatus === 'active')) {
        if (connectReady) {
            actionButtons += `
                <button class="edit-btn" onclick="startProviderStripeConnectOnboarding()">Open Stripe Payout Dashboard</button>
            `;
        } else {
            actionButtons += `
                <button class="approve-btn" onclick="startProviderStripeConnectOnboarding()">
                    ${connectHasAccount ? 'Continue Stripe Payout Setup' : 'Set Up Stripe Payouts'}
                </button>
            `;
        }
    }

    // If approved but no Stripe subscription (admin-approved free plan), show upgrade option
    if (subStatus === 'approved' && plan === 'free' && stripeEnabled) {
        actionButtons += `
            <button class="approve-btn" onclick="startProviderStripeCheckout('basic')">Upgrade to Basic</button>
            <button class="approve-btn" onclick="startProviderStripeCheckout('premium')">Upgrade to Premium</button>
        `;
    }

    // If Stripe not configured, show a notice for paid plans
    let stripeNotice = '';
    if (plan !== 'free' && !stripeEnabled) {
        stripeNotice = `
            <div class="stripe-notice" style="background:#fff3cd;border:1px solid #ffc107;border-radius:8px;padding:12px;margin-top:12px;font-size:13px;color:#856404;">
                <strong>⚠️ Payment not yet configured.</strong> Online payment for the ${planDetails.name} will be available soon. Your account is pending approval.
            </div>
        `;
    }

    // Rejection/suspension reason
    let reasonHtml = '';
    if (subStatus === 'rejected' && status.subscriptionRejectionReason) {
        reasonHtml = `<div class="sub-details rejection">Reason: ${escapeHtml(status.subscriptionRejectionReason)}</div>`;
    }
    if (subStatus === 'suspended' && status.subscriptionSuspendedReason) {
        reasonHtml = `<div class="sub-details suspension">Reason: ${escapeHtml(status.subscriptionSuspendedReason)}</div>`;
    }

    // Subscription dates
    let datesHtml = '';
    if (status.subscriptionStart) {
        datesHtml += `<div><strong>Start:</strong> ${new Date(status.subscriptionStart).toLocaleString()}</div>`;
    }
    if (status.subscriptionEnd) {
        datesHtml += `<div><strong>End:</strong> ${new Date(status.subscriptionEnd).toLocaleString()}</div>`;
    }

    let connectHtml = '';
    if (subStatus === 'approved' || subStatus === 'active') {
        if (connectReady) {
            connectHtml = '<div class="sub-details" style="margin-top:10px;color:#166534;">✅ Stripe payouts are active. Card payments can flow to your account.</div>';
        } else {
            const missingItems = connect && Array.isArray(connect.requirementsDue) && connect.requirementsDue.length
                ? `<ul style="margin:6px 0 0 20px;">${connect.requirementsDue.map(req => `<li>${escapeHtml(req)}</li>`).join('')}</ul>`
                : '';
            const disabledReason = connect && connect.disabledReason
                ? `<div style="margin-top:6px;color:#92400e;">Reason: ${escapeHtml(connect.disabledReason)}</div>`
                : '';
            connectHtml = `
                <div class="stripe-notice" style="background:#fff3cd;border:1px solid #ffc107;border-radius:8px;padding:12px;margin-top:12px;font-size:13px;color:#856404;">
                    <strong>⚠️ Payout onboarding incomplete.</strong> Complete Stripe Connect onboarding to enable online card payouts.
                    ${disabledReason}
                    ${missingItems}
                </div>
            `;
        }
    }

    container.innerHTML = `
        <div class="subscription-plan-card">
            <div class="plan-header">
                <span class="plan-name">${escapeHtml(planDetails.name || plan.toUpperCase())}</span>
                <span class="plan-price">${priceDisplay}</span>
                ${statusBadge}
            </div>
            <div class="plan-details">
                ${datesHtml}
            </div>
            <div class="plan-features">
                <strong>Features:</strong>
                <ul style="margin:8px 0;padding-left:20px;">
                    ${planFeatures.map(f => `<li>${escapeHtml(f)}</li>`).join('')}
                </ul>
            </div>
            ${reasonHtml}
            <div class="plan-actions" style="margin-top:12px;">
                ${actionButtons}
            </div>
            ${connectHtml}
            ${stripeNotice}
        </div>
    `;
}

// Get human-readable feature list for a plan
function getPlanFeatureList(planId) {
    const featuresMap = {
        free: ['Basic menu (up to 10 items)', 'Limited orders', 'Standard support'],
        basic: ['Full menu (up to 50 items)', 'Unlimited orders', 'Sales analytics', 'Priority support', '$280/month'],
        premium: ['Unlimited menu items', 'Unlimited orders', 'Sales analytics', 'Priority support', 'Custom branding', 'Exclusive visibility', '$550/month']
    };
    return featuresMap[planId] || featuresMap.free;
}

// Initialize Stripe client - handle URL params and wire up payment buttons
function initStripePayment() {
    // Handle Stripe return URL params: ?stripe=success / cancelled / manage
    const urlParams = new URLSearchParams(window.location.search);
    const stripeResult = urlParams.get('stripe');
    const providerUid = urlParams.get('providerUid');
    const plan = urlParams.get('plan');

    if (stripeResult === 'success' && providerUid) {
        showNotification(`Payment successful! Your ${plan || ''} subscription is now active.`, 'success');
        // Clean up URL
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
    } else if (stripeResult === 'cancelled') {
        showNotification('Payment was cancelled. You can try again when ready.', 'warning');
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
    } else if (stripeResult === 'manage') {
        showNotification('Welcome back to your subscription management.', 'success');
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
    } else if (urlParams.get('stripeConnect') === 'return') {
        showNotification('Stripe payout onboarding updated. Refreshing account status...', 'success');
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
    } else if (urlParams.get('stripeConnect') === 'refresh') {
        showNotification('Stripe onboarding was not completed. Please continue setup to enable payouts.', 'warning');
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
    }

    // If provider is logged in and the subscription panel exists, render it
    if (currentProvider && document.getElementById('providerSubscriptionStatus')) {
        renderProviderSubscriptionStatus();
    }
}

// Make all Stripe functions globally accessible for onclick handlers
window.startProviderStripeCheckout = startProviderStripeCheckout;
window.startProviderStripeConnectOnboarding = startProviderStripeConnectOnboarding;
window.openStripeBillingPortal = openStripeBillingPortal;
window.cancelProviderStripeSubscription = cancelProviderStripeSubscription;
window.renderProviderSubscriptionStatus = renderProviderSubscriptionStatus;
window.initStripePayment = initStripePayment;
// Order Status System
// Status stages: pending -> confirmed -> preparing -> ready -> completed/delivered/cancelled
const ORDER_STATUSES = {
    PENDING: 'pending',
    CONFIRMED: 'confirmed',
    PREPARING: 'preparing',
    READY: 'ready',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled'
};
// Check if order can be cancelled (allowed only while order is still “placed/confirmed”)
function canCancelOrder(orderStatus) {
    return orderStatus === 'pending' || orderStatus === 'confirmed';
}

function getCancellationPolicyNote(orderStatus) {
    if (canCancelOrder(orderStatus)) {
        return '✅ You can cancel this order right now (while it is Pending/Confirmed).';
    }

    // preparing/ready/completed/cancelled/other statuses
    return '❌ Cancellation is not available after the order is beyond Confirmed.';
}


// Get status display info (text, color, next action)
function getStatusInfo(status) {
    const statusData = {
        pending: {
            text: 'Pending',
            color: '#f39c12',
            bg: '#fef9e7',
            next: 'confirmed',
            nextText: 'Confirm Order'
        },
        confirmed: {
            text: 'Confirmed',
            color: '#3498db',
            bg: '#ebf5fb',
            next: 'preparing',
            nextText: 'Start Preparing'
        },
        preparing: {
            text: 'Preparing',
            color: '#9b59b6',
            bg: '#f4ecf7',
            next: 'ready',
            nextText: 'Mark Ready'
        },
        ready: {
            text: 'Ready',
            color: '#27ae60',
            bg: '#eafaf1',
            next: 'completed',
            nextText: 'Complete Order'
        },
        completed: {
            text: 'Completed',
            color: '#2ecc71',
            bg: '#eafaf1',
            next: null,
            nextText: 'Completed'
        },
        cancelled: {
            text: 'Cancelled',
            color: '#e74c3c',
            bg: '#fdedec',
            next: null,
            nextText: 'Cancelled'
        }
    };
    return statusData[status] || statusData.pending;
}
// Get next status in the sequence
function getNextStatus(currentStatus) {
    const statusFlow = ['pending', 'confirmed', 'preparing', 'ready', 'completed'];
    const currentIndex = statusFlow.indexOf(currentStatus);
    if (currentIndex >= 0 && currentIndex < statusFlow.length - 1) {
        return statusFlow[currentIndex + 1];
    }
    return null;
}
// Current session (logged in user)
// Check if there's a cached session but validate it against stored clients.

// ============ SECURE ADMIN AUTHENTICATION ============
// Admin authorization is 100% backend-driven via Firebase Auth custom claims
// (`admin: true` or `role: admin`). The legacy `admin-credentials` Firestore
// collection is DENIED to all clients in firestore.rules (server-only via
// Admin SDK) and is no longer read by the client. See performAdminLogin().


// Session cleanup helper (defensive)
function clearProviderAndClientSessions() {
    if (typeof window.clearProviderInsights === 'function') window.clearProviderInsights();
    // In-memory
    currentProvider = null;
    currentClient = null;

    // Reset pagination so a fresh login always starts at page 1
    resetPaginationDisplay();

    // browserStorage
}

function hideProviderAndClientDashboards() {
    const providerDashboard = document.getElementById('providerDashboard');
    if (providerDashboard) providerDashboard.classList.add('hidden');

    const clientDashboard = document.getElementById('clientDashboard');
    if (clientDashboard) clientDashboard.classList.add('hidden');

    // Tear down social feature listeners on logout
    if (typeof window.teardownSocialFeatures === 'function') {
        window.teardownSocialFeatures();
    }
}

// Tab switcher for inline onclick="switchTab('...')" in index.html
function switchTab(tabName) {
    const tabBtns = document.querySelectorAll('.tab-btn');
    const tabContents = document.querySelectorAll('.tab-content');

    tabBtns.forEach(btn => {
        const matches = btn.dataset && btn.dataset.tab === tabName;
        btn.classList.toggle('active', matches);
    });

    tabContents.forEach(content => {
        const expectedId = `${tabName}-tab`;
        const isTarget = content.id === expectedId;
        content.classList.toggle('active', isTarget);
        // also ensure visibility for layouts that rely on display:none
        content.style.display = isTarget ? '' : 'none';
    });

    // Render reCAPTCHA widgets when switching to client/provider registration tabs
    if (tabName === 'clients' || tabName === 'providers') {
    }
}

window.switchTab = switchTab;

// Navigate to the Clients tab and show the registration form.
// Called from the "Register Now" button shown to unauthenticated users in search results.
function goToClientRegister() {
    switchTab('clients');
    // Ensure the Register panel is visible (not the Login panel)
    const registerForm = document.getElementById('clientRegisterForm');
    const loginForm = document.getElementById('clientLoginForm');
    if (registerForm) registerForm.classList.remove('hidden');
    if (loginForm) loginForm.classList.add('hidden');
    // Sync the toggle buttons
    document.querySelectorAll('#clientLoginSection .auth-toggle-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.auth === 'register');
    });
    // Scroll the auth panel into view
    if (registerForm) registerForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
window.goToClientRegister = goToClientRegister;


// Default site settings

const DEFAULT_SITE_SETTINGS = {
    footerText: '© 2026 What To Eat? - Connecting hungry people with available meals'
};

async function getEffectiveFirestoreDb() {
    if (typeof window.ensureFirebaseReady === 'function') {
        try {
            await window.ensureFirebaseReady();
        } catch (e) {
            console.warn('[Firestore] ensureFirebaseReady failed before reading/writing settings:', e);
        }
    }

    const db = firebaseDbInstance || window.firebaseDbInstance || (window.firestoreService && window.firestoreService.db) || null;
    if (!db && window.firestoreService && typeof window.firestoreService.init === 'function') {
        try {
            await window.firestoreService.init();
        } catch (e) {
            console.warn('[Firestore] firestoreService.init failed:', e);
        }
    }

    return firebaseDbInstance || window.firebaseDbInstance || (window.firestoreService && window.firestoreService.db) || null;
}

// Session helper: ensure only ONE role is active at a time.
function clearNonAdminSessions() {
    if (typeof window.clearProviderInsights === 'function') window.clearProviderInsights();
    if (currentProvider) {
        currentProvider = null;
    }
    if (currentClient) {
        currentClient = null;
    }
}

// Load site settings from Firestore cache or default values
function loadSiteSettings() {
    if (window.__siteSettingsCache) {
        return { ...window.__siteSettingsCache };
    }
    return { ...DEFAULT_SITE_SETTINGS };
}

// Save site settings to Firestore if available
async function saveSiteSettings(settings) {
    const rates = loadSubscriptionRates();
    const db = await getEffectiveFirestoreDb();
    if (typeof window.__saveSettingsToFirestore === 'function') {
        return window.__saveSettingsToFirestore(settings, rates);
    }
    if (!db) {
        console.warn('[Settings] Firestore not available, cannot save site settings');
        return false;
    }
    try {
        await db.collection('siteSettings').doc('main').set({
            footerText: settings.footerText,
            basicRate: rates.basic,
            premiumRate: rates.premium,
            updatedAt: new Date().toISOString()
        }, { merge: true });
        return true;
    } catch (e) {
        console.error('[Settings] Could not save site settings to Firestore:', e);
        return false;
    }
}

// Render footer with stored or default text
function renderFooter() {
    const settings = loadSiteSettings();
    const footerParagraphs = document.querySelectorAll('footer p');
    if (footerParagraphs.length > 0) {
        footerParagraphs[0].textContent = settings.footerText;
    }
}

// Initialize footer on page load
function initFooter() {
    renderFooter();
}

// On page load, try to restore valid session
const storedClientSession = null;

// SECURITY: Never auto-show the super admin dashboard.
// `currentAdmin` must only be set through successful admin login.
currentAdmin = null;

// Validate the stored session exists in the clients array
// CRITICAL FIX: Ensure strict validation - must have valid id and exist in clients array
if (storedClientSession && storedClientSession.id && storedClientSession.email) {
    const clientExists = clients.find(c => c.id === storedClientSession.id && c.email === storedClientSession.email);
    if (clientExists) {
        currentClient = storedClientSession;
        console.log('Restored valid client session:', currentClient.name);
    } else {
        // Session invalid - clear it
        console.log('Invalid client session found, clearing...');
    }
}

// Security check - the user must be logged in as a CLIENT to view menus.
// NOTE: A bare Firebase Auth session (e.g. a signed-in provider or admin) does NOT
// grant client menu access. The session must be populated from the authenticated
// user's Firestore client document.
function getCurrentAuthUser() {
    const candidates = [
        window.firebaseAuthRaw,
        window.firebaseAuthInstance,
        firebaseAuthInstance,
        firebaseAuthInstance?._raw,
        window.firebaseAuthInstance?._raw
    ];

    for (const candidate of candidates) {
        if (candidate && candidate.currentUser) {
            return candidate.currentUser;
        }
    }

    return null;
}

async function refreshProviderApprovalClaim() {
    const user = getCurrentAuthUser();
    if (!user || typeof user.getIdToken !== 'function') {
        return false;
    }

    try {
        const tokenResult = await user.getIdTokenResult(true);
        const claims = (tokenResult && tokenResult.claims) || {};
        if (claims.role === 'provider' && claims.approved === true) {
            return true;
        }
    } catch (e) {
        console.warn('[AuthGuard] Failed to read current provider token claims, retrying via backend refresh:', e && e.message ? e.message : e);
    }

    try {
        const projectId = (window.firebaseConfig && window.firebaseConfig.projectId) || 'what-to-eat-e5ce9';
        const idToken = await user.getIdToken();
        const response = await fetch(`https://us-central1-${projectId}.cloudfunctions.net/getProviderLoginStatus`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${idToken}`
            },
            body: JSON.stringify({ refresh: true })
        });

        const payload = response && typeof response.json === 'function' ? await response.json().catch(() => ({})) : {};
        if (response && !response.ok && !(payload && payload.ok === true)) {
            console.warn('[AuthGuard] Provider approval refresh failed:', payload && payload.error ? payload.error : response.statusText || response.status);
            return false;
        }

        const refreshedResult = await user.getIdTokenResult(true);
        const refreshedClaims = (refreshedResult && refreshedResult.claims) || {};
        const providerApproved = refreshedClaims.role === 'provider' && refreshedClaims.approved === true;
        if (providerApproved) {
            return true;
        }

        const providerIsApprovedInDoc = Boolean(payload && payload.approved === true);
        return providerIsApprovedInDoc;
    } catch (e) {
        console.warn('[AuthGuard] Failed to refresh provider approval claim:', e && e.message ? e.message : e);
        return false;
    }
}

async function getServerAuthorizedRoleForUser(firebaseUser) {
    if (!firebaseUser || !firebaseUser.uid) return null;

    try {
        const idTokenResult = await firebaseUser.getIdTokenResult(true);
        const claims = (idTokenResult && idTokenResult.claims) || {};
        const tokenRole = String(claims.role || '').toLowerCase();

        if (claims.admin === true || tokenRole === 'admin') return 'admin';
        if (tokenRole === 'provider') {
            if (claims.approved === true) return 'provider';
            console.warn('[AuthGuard] Provider token is missing approved claim; forcing a backend claim refresh.');
            const refreshed = await refreshProviderApprovalClaim();
            return refreshed ? 'provider' : null;
        }
        if (tokenRole === 'client') return tokenRole;
        return null;
    } catch (e) {
        console.warn('[AuthGuard] Unable to read authoritative role claim:', e.message || e);
        return null;
    }
}

function isUserLoggedIn() {
    if (window.__forceGuestModeUntilNextLogin === true) return false;
    if (!currentClient) return false;
    if (currentClient.id === undefined || !currentClient.email) return false;

    const authUser = getCurrentAuthUser();
    if (!authUser) {
        const lastKnownUid = String(window.__lastKnownAuthUid || '');
        const lastKnownEmail = String(window.__lastKnownAuthEmail || '').toLowerCase();
        const clientUid = String(currentClient.uid || currentClient.id || '');
        const clientEmail = String(currentClient.email || '').toLowerCase();
        return Boolean(lastKnownUid && clientUid && lastKnownUid === clientUid && lastKnownEmail && lastKnownEmail === clientEmail);
    }

    const authUid = authUser.uid || null;
    const clientUid = currentClient.uid || currentClient.id || null;
    const authEmail = (authUser.email || '').toLowerCase();
    const clientEmail = (currentClient.email || '').toLowerCase();

    if (authUid && clientUid && authUid === clientUid) {
        return authEmail === clientEmail;
    }

    // Fallback: when the Firestore doc is present but UID is missing, confirm via email.
    return authEmail && authEmail === clientEmail;
}

function normalizeClientSessionRecord(client, fallbackUser, fallbackEmail) {
    const raw = client && typeof client === 'object' ? client : {};
    const uid = String(
        raw.uid ||
        raw.id ||
        (fallbackUser && fallbackUser.uid) ||
        ''
    );
    const email = String(
        raw.email ||
        (fallbackUser && fallbackUser.email) ||
        fallbackEmail ||
        ''
    ).toLowerCase();

    return {
        ...raw,
        uid: uid,
        id: uid,
        email: email
    };
}

// Admin authorization gate (STRICT)
function isAdminLoggedIn() {
    return Boolean(currentAdmin && currentAdmin.username);
}

function getCurrentHostname() {
    try {
        return String(window.location && window.location.hostname ? window.location.hostname : 'localhost').toLowerCase();
    } catch (e) {
        return 'localhost';
    }
}

function getPhase1ControlledBetaGate() {
    const hostname = getCurrentHostname();
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const recaptchaKey = String((window.recaptchaConfig && window.recaptchaConfig.siteKey) || '').trim();
    const appCheckKey = String((window.__APP_CHECK_SITE_KEY__ || (window.firebaseAppCheckConfig && window.firebaseAppCheckConfig.siteKey) || '') || '').trim();

    if (isLocalhost) {
        return { blocked: false, reason: null };
    }

    if (!recaptchaKey || !appCheckKey) {
        return {
            blocked: true,
            reason: 'Controlled beta access is blocked until a valid reCAPTCHA site key and App Check key are configured for this domain.'
        };
    }

    return { blocked: false, reason: null };
}

function enforcePhase1ControlledBeta(mode = 'provider') {
    const gate = getPhase1ControlledBetaGate();
    if (gate.blocked) {
        console.warn('[Phase1] Controlled beta gate blocked', mode, gate.reason);
        showNotification(gate.reason, 'error');
        return false;
    }
    return true;
}

// Backward-compatible alias (some code may call older name)
function isUserRegisteredClient() {
    return isUserLoggedIn();
}



function normalizeProviderState(provider = {}) {
    provider = provider || {};
    provider.role = 'provider';
    const subscriptionStatus = String(provider.subscriptionStatus || provider.accountStatus || provider.verificationStatus || 'pending').toLowerCase();
    const verificationStatus = String(provider.verificationStatus || '').toLowerCase();
    const accountStatus = String(provider.accountStatus || '').toLowerCase();
    const payoutStatus = String(provider.payoutStatus || '').toLowerCase();

    const isApprovedOrActive = subscriptionStatus === 'approved' || subscriptionStatus === 'active' || accountStatus === 'active';

    const next = {
        subscriptionStatus: isApprovedOrActive ? (subscriptionStatus === 'pending' || !subscriptionStatus ? 'approved' : subscriptionStatus) : subscriptionStatus,
        verificationStatus: isApprovedOrActive ? (verificationStatus === 'rejected' ? 'rejected' : 'verified') : (verificationStatus || 'pending'),
        accountStatus: isApprovedOrActive ? (accountStatus === 'suspended' || accountStatus === 'rejected' ? accountStatus : 'active') : (accountStatus || 'pending'),
        payoutStatus: isApprovedOrActive ? (payoutStatus === 'blocked' ? 'blocked' : (payoutStatus || 'approved')) : (payoutStatus || 'pending')
    };

    if (next.subscriptionStatus === 'rejected' || next.accountStatus === 'rejected' || next.verificationStatus === 'rejected') {
        next.subscriptionStatus = 'rejected';
        next.accountStatus = 'rejected';
        next.verificationStatus = 'rejected';
    }
    if (next.subscriptionStatus === 'suspended' || next.accountStatus === 'suspended') {
        next.subscriptionStatus = 'suspended';
        next.accountStatus = 'suspended';
    }
    if (isApprovedOrActive && next.subscriptionStatus !== 'rejected' && next.subscriptionStatus !== 'suspended' && next.accountStatus !== 'rejected' && next.accountStatus !== 'suspended') {
        next.accountStatus = 'active';
        next.verificationStatus = 'verified';
        next.payoutStatus = next.payoutStatus === 'blocked' ? 'blocked' : 'approved';
        if (next.subscriptionStatus === 'pending') {
            next.subscriptionStatus = 'approved';
        }
    }
    if (next.subscriptionStatus === 'pending' && !isApprovedOrActive) {
        next.accountStatus = next.accountStatus || 'pending';
        next.verificationStatus = next.verificationStatus || 'pending';
        next.payoutStatus = next.payoutStatus || 'pending';
    }

    return next;
}

function applyProviderState(provider) {
    const state = normalizeProviderState(provider);
    provider.subscriptionStatus = state.subscriptionStatus;
    provider.verificationStatus = state.verificationStatus;
    provider.accountStatus = state.accountStatus;
    provider.payoutStatus = state.payoutStatus;
    return provider;
}

let clientSearchGeo = null;

function toRadians(value) {
    return (Number(value) || 0) * (Math.PI / 180);
}

function calculateDistanceKm(from, to) {
    if (!from || !to) return null;
    const lat1 = Number(from.latitude);
    const lon1 = Number(from.longitude);
    const lat2 = Number(to.latitude);
    const lon2 = Number(to.longitude);
    if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;
    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return 6371 * c;
}

function resolveItemCoordinates(item = {}) {
    if (!item || typeof item !== 'object') return null;
    if (item.providerCoordinates && typeof item.providerCoordinates === 'object') {
        return normalizeProviderCoordinates(item.providerCoordinates.latitude, item.providerCoordinates.longitude);
    }
    return normalizeProviderCoordinates(item.providerLatitude, item.providerLongitude);
}

function normalizeProviderIdentifier(value) {
    return String(value || '').trim().toLowerCase();
}

function getProviderProfileForItem(item = {}) {
    if (!Array.isArray(providers) || providers.length === 0) return null;
    const itemProviderId = normalizeProviderIdentifier(item.providerId || item.id);
    const itemProviderName = normalizeProviderIdentifier(item.providerName);
    const itemProviderEmail = normalizeProviderIdentifier(item.providerEmail);
    return providers.find((provider) => {
        const providerId = normalizeProviderIdentifier(provider.id || provider.providerId || provider.uid);
        const providerName = normalizeProviderIdentifier(provider.name || provider.providerName);
        const providerEmail = normalizeProviderIdentifier(provider.email || provider.providerEmail);
        if (itemProviderId && providerId && providerId === itemProviderId) return true;
        if (itemProviderName && providerName && providerName === itemProviderName) return true;
        if (itemProviderEmail && providerEmail && providerEmail === itemProviderEmail) return true;
        return false;
    }) || null;
}

function getProviderOrderStats(providerProfile, item = {}) {
    if (!Array.isArray(clientMessages) || clientMessages.length === 0) {
        return { totalOrders: 0, canceledOrders: 0, reliabilityPercent: 98 };
    }
    const providerId = normalizeProviderIdentifier(
        (providerProfile && (providerProfile.id || providerProfile.providerId || providerProfile.uid)) ||
        item.providerId
    );
    const providerName = normalizeProviderIdentifier(
        (providerProfile && (providerProfile.name || providerProfile.providerName)) ||
        item.providerName
    );
    const relevantOrders = clientMessages.filter((order) => {
        const orderProviderId = normalizeProviderIdentifier(order.providerId);
        const orderProviderName = normalizeProviderIdentifier(order.providerName);
        if (providerId && orderProviderId && orderProviderId === providerId) return true;
        if (providerName && orderProviderName && orderProviderName === providerName) return true;
        return false;
    });
    if (relevantOrders.length === 0) {
        return { totalOrders: 0, canceledOrders: 0, reliabilityPercent: 98 };
    }
    const canceledOrders = relevantOrders.filter((order) => {
        const status = String(order.status || '').toLowerCase();
        return status === 'cancelled' || status === 'canceled';
    }).length;
    const reliabilityPercent = Math.max(0, Math.min(100, Math.round(((relevantOrders.length - canceledOrders) / relevantOrders.length) * 100)));
    return {
        totalOrders: relevantOrders.length,
        canceledOrders,
        reliabilityPercent
    };
}

function getEstimatedPrepMinutes(item = {}, providerProfile = null) {
    const prepCandidates = [
        item.prepMinutes,
        item.preparationMinutes,
        item.estimatedPrepMinutes,
        item.timeToReadyMinutes,
        item.readyInMinutes,
        providerProfile && providerProfile.prepMinutes,
        providerProfile && providerProfile.preparationMinutes,
        providerProfile && providerProfile.estimatedPrepMinutes,
        providerProfile && providerProfile.timeToReadyMinutes
    ];
    for (let i = 0; i < prepCandidates.length; i += 1) {
        const value = Number(prepCandidates[i]);
        if (Number.isFinite(value) && value > 0) return Math.round(value);
    }
    return 30;
}

function getDeliveryRadiusKm(item = {}, providerProfile = null) {
    const radiusCandidates = [
        item.deliveryRadiusKm,
        item.deliveryRadius,
        item.deliveryRangeKm,
        item.deliveryRange,
        providerProfile && providerProfile.deliveryRadiusKm,
        providerProfile && providerProfile.deliveryRadius,
        providerProfile && providerProfile.deliveryRangeKm,
        providerProfile && providerProfile.deliveryRange
    ];
    for (let i = 0; i < radiusCandidates.length; i += 1) {
        const value = Number(radiusCandidates[i]);
        if (Number.isFinite(value) && value > 0) return Number(value.toFixed(1));
    }
    return null;
}

function getProviderTrustSignals(item = {}, ratingData = null) {
    const providerProfile = getProviderProfileForItem(item);
    const normalizedState = providerProfile ? normalizeProviderState(providerProfile) : null;
    const verified = Boolean(
        normalizedState && normalizedState.verificationStatus === 'verified' &&
        (normalizedState.accountStatus === 'active' || normalizedState.subscriptionStatus === 'approved')
    );
    const prepMinutes = getEstimatedPrepMinutes(item, providerProfile);
    const orderStats = getProviderOrderStats(providerProfile, item);
    const deliveryRadiusKm = getDeliveryRadiusKm(item, providerProfile);
    const ratingAverage = ratingData ? Number(ratingData.average) : 0;
    return {
        verified,
        prepMinutes,
        cancellationReliability: orderStats.reliabilityPercent,
        deliveryRadiusKm,
        ratingAverage: Number.isFinite(ratingAverage) ? ratingAverage : 0
    };
}

function buildProviderTrustSignalsHtml(trustSignals = {}) {
    const signalParts = [];
    if (trustSignals.verified) {
        signalParts.push('<span class="detail-tag status-open">✔ ' + escapeHtml(t('verifiedProvider', 'Verified Provider')) + '</span>');
    }
    signalParts.push('<span class="detail-tag">⏱ ' + escapeHtml(t('prepSla', 'Prep SLA')) + ': ' + Number(trustSignals.prepMinutes || 30) + ' min</span>');
    signalParts.push('<span class="detail-tag">✅ ' + escapeHtml(t('cancellationReliability', 'Cancellation reliability')) + ': ' + Number(trustSignals.cancellationReliability || 0) + '%</span>');
    if (Number.isFinite(trustSignals.deliveryRadiusKm)) {
        signalParts.push('<span class="detail-tag location">🚚 ' + escapeHtml(t('deliveryRadius', 'Delivery radius')) + ': ' + Number(trustSignals.deliveryRadiusKm).toFixed(1) + ' km</span>');
    }
    return '<div class="meal-details trust-signals">' + signalParts.join('') + '</div>';
}

function getSearchQueryTokens(searchInput = '') {
    return String(searchInput || '')
        .toLowerCase()
        .split(/\s+/)
        .map(token => token.trim())
        .filter(Boolean);
}

function computeCuisineMatchScore(item = {}, queryTokens = []) {
    if (!Array.isArray(queryTokens) || queryTokens.length === 0) return 0.6;
    const searchableFields = [
        item.foodName,
        item.foodDesc,
        item.mealType,
        item.providerName
    ].map(value => String(value || '').toLowerCase());
    const matchedTokens = queryTokens.filter((token) => searchableFields.some(field => field.includes(token)));
    return Math.min(1, matchedTokens.length / queryTokens.length);
}

function computeSearchRelevanceScore(item = {}, context = {}) {
    const queryTokens = context.queryTokens || [];
    const ratingData = context.ratingData || getProviderRating(item.providerName, item.providerId);
    const trustSignals = context.trustSignals || getProviderTrustSignals(item, ratingData);
    const distanceKm = calculateDistanceKm(clientSearchGeo, resolveItemCoordinates(item));

    const distanceScore = Number.isFinite(distanceKm)
        ? Math.max(0, 1 - Math.min(distanceKm / 25, 1))
        : 0.45;
    const cuisineScore = computeCuisineMatchScore(item, queryTokens);
    const prepScore = Math.max(0, 1 - Math.min((Number(trustSignals.prepMinutes || 30) - 10) / 50, 1));
    const ratingScore = Math.max(0, Math.min(1, Number(trustSignals.ratingAverage || 0) / 5));
    const specialBoost = isDailySpecial(item) ? 0.15 : 0;
    const openBoost = isCurrentlyOpen(item) ? 0.05 : 0;
    const countryBoost = context.prioritizeUserCountry && context.userCountry && item.country === context.userCountry ? 0.06 : 0;

    return (distanceScore * 0.35) +
        (cuisineScore * 0.25) +
        (prepScore * 0.2) +
        (ratingScore * 0.2) +
        specialBoost +
        openBoost +
        countryBoost;
}

function rankSearchResults(items = [], options = {}) {
    const queryTokens = getSearchQueryTokens(options.searchInput);
    return items
        .map((item, index) => {
            const ratingData = getProviderRating(item.providerName, item.providerId);
            const trustSignals = getProviderTrustSignals(item, ratingData);
            const score = computeSearchRelevanceScore(item, {
                queryTokens,
                userCountry: options.userCountry,
                prioritizeUserCountry: options.prioritizeUserCountry,
                ratingData,
                trustSignals
            });
            return { item, index, score };
        })
        .sort((a, b) => b.score - a.score || a.index - b.index)
        .map(entry => entry.item);
}

function initLanguageSelector() {
    const selector = document.getElementById('languageSelector');
    if (!selector) return;
    const savedLanguage = localStorage.getItem('wte-language');
    const initialLanguage = TRANSLATIONS[savedLanguage] ? savedLanguage : 'en';
    i18nService.setLanguage(initialLanguage);
    selector.value = initialLanguage;
    selector.addEventListener('change', () => {
        const nextLanguage = selector.value;
        if (!TRANSLATIONS[nextLanguage]) return;
        i18nService.setLanguage(nextLanguage);
        localStorage.setItem('wte-language', nextLanguage);
        renderSearchResults();
        renderClientSearchResults();
    });
}

function initClientSearchGeolocation() {
    // Location is requested only from the provider's explicit location button.
    // Do not prompt every visitor on application load.
}

// Review state
let selectedReviewRating = 0;
let reviewedProviders = [];

// Client Order Review state
let selectedOrderReviewRating = 0;
let selectedReviewOrderId = null;
// DOM Elements
function initAppCore() {
    if (typeof window.initMonitoring === 'function') {
        window.initMonitoring();
    }
    if (typeof window.initPwaAndNotifications === 'function') {
        window.initPwaAndNotifications();
    }

    // Tab navigation
    initTabs();
    initLanguageSelector();
    initClientSearchGeolocation();

    // Client auth toggle
    initClientAuthToggle();

    // Client forms
    initClientRegisterForm();
    initClientLoginForm();
    initClientLogout();

    // Provider auth toggle
    initProviderAuthToggle();

    // Provider forms
    initProviderRegisterForm();
    initProviderLoginForm();
    initProviderLogout();

    // Menu form
    initMenuForm();

    // Photo upload handler
    initPhotoUpload();

// Search
    initSearch();

    // Client search
    initClientSearch();

// Request form (main page - for providers)
    initRequestForm();

    // Client request form (in client dashboard)
    initClientRequestForm();

// Contact modal
    initContactModal();

// Reviews feature
    initReviewFeature();

// Profile editing
    initClientProfileEdit();
    initProviderProfileEdit();

    // Forgot password
    initForgotPassword();

// Check for logged in sessions
    checkSessions();

// Initialize client dashboard tabs
    initClientDashboardTabs();

    // Initialize provider dashboard panel tabs and collapsible sections
    // Use setTimeout to ensure DOM is fully ready
    setTimeout(() => {
        initProviderPanelTabs();
        initCollapsibleSections();
    }, 50);

// Initial render - ensure search results are rendered even if no search input
        renderMenuList();
        renderClientRequestsList();
        renderSearchResults();
        renderProviderReviews();

        // Update the provider registration plan dropdown with current rates
        if (typeof updateProviderPlanDropdown === 'function') {
            updateProviderPlanDropdown();
        }
        if (typeof initSubscriptionRatesInputs === 'function') {
            initSubscriptionRatesInputs();
        }
        if (typeof initSubscriptionPlanForm === 'function') {
            initSubscriptionPlanForm();
        }
        if (typeof __loadSubscriptionPlansFromFirestore === 'function') {
            __loadSubscriptionPlansFromFirestore();
        }

        // IMPORTANT: If a client is logged in and their dashboard search panel exists,
        // render the client dashboard search results once on load.
        // This prevents an empty panel when switching tabs.
        if (isUserLoggedIn()) {
            // renderClientSearchResults() uses client-dashboard-prefixed IDs,
            // so it will no-op if those elements don't exist on the current page.
            try {
                renderClientSearchResults();
            } catch (e) {
                console.log('[Init] renderClientSearchResults skipped/failed:', e);
            }
        }

// Debug log to check login status
        console.log('=== INITIAL PAGE LOAD ===');
        console.log('Is logged in:', currentClient !== null);
        console.log('Client session:', currentClient);

    // Show login status in header for clarity
        updateHeaderUserBadge();

// Ensure header badge reflects any restored provider/admin session
        setTimeout(() => updateHeaderUserBadge(), 0);


// Initialize custom meal requests visibility (provider opt-in feature)
        initCustomMealRequestsFeature();

        // Initialize footer with stored settings or default
        initFooter();

        // Initialize Admin Panel
        initAdminPanel();

        // Initialize Settings panel buttons (Save and Reset footer text)
        initSettingsButtons();

        // Initialize Stripe payment flow (handles ?stripe=success/cancelled/manage URL params)
        // and renders the provider subscription panel if a provider is logged in.
        if (typeof initStripePayment === 'function') {
            initStripePayment();
        }

        // ============ CRITICAL FIX: Initialize Firestore service layer ============
        // This must run at boot so firestoreService.initialized becomes true and
        // all firestoreService.save() calls actually write to Firestore.
        // It also enables the modular onSnapshot listeners (firestore-realtime-sync.js)
        // that populate the in-memory arrays with cloud data for cross-browser sync.
        firestoreService.init().then(() => {
            console.log('[Firestore] Service layer initialized:', firestoreService.initialized);
            // Trigger a re-render once synced data arrives so the UI reflects cloud data
            if (typeof window.triggerAppReRender === 'function') {
                setTimeout(() => {
                    try {
                        window.triggerAppReRender();
                    } catch (renderErr) {
                        console.warn('[Firestore] Re-render after init failed:', renderErr);
                    }
                }, 500);
            }
        });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAppCore);
} else {
    initAppCore();
}

// Expose the Firestore service layer globally so firestore-realtime-sync.js,
// firebase-integration.js, and the admin panel can reuse the same instance.
window.firestoreService = firestoreService;

// Global re-render helper. Called after Firestore data is synced into the
// in-memory arrays so every view (menu, search, orders, reviews, admin)
// reflects the latest cloud data without a manual page refresh.
window.triggerAppReRender = function() {
    try {
        // Provider / public views
        if (typeof renderMenuList === 'function') renderMenuList();
        if (typeof renderSearchResults === 'function') renderSearchResults();
        if (typeof renderClientSearchResults === 'function') renderClientSearchResults();
        if (typeof renderProviderReviews === 'function') renderProviderReviews();
        if (typeof renderClientRequestsList === 'function') renderClientRequestsList();
        if (typeof renderRequestsList === 'function') renderRequestsList();
        if (typeof renderProviderOrders === 'function') renderProviderOrders();
        if (typeof renderOrdersHistory === 'function') renderOrdersHistory();
        if (typeof renderSalesReport === 'function') renderSalesReport();
        if (typeof renderClientOrders === 'function') renderClientOrders();

        // Admin views
        if (typeof isAdminLoggedIn === 'function' && isAdminLoggedIn()) {
            if (typeof renderAdminStats === 'function') renderAdminStats();
            if (typeof renderAdminClients === 'function') renderAdminClients();
            if (typeof renderAdminProviders === 'function') renderAdminProviders();
            if (typeof renderAdminOrders === 'function') renderAdminOrders();
            if (typeof renderAdminReviews === 'function') renderAdminReviews();
            if (typeof renderAdminSubscriptions === 'function') renderAdminSubscriptions();
            if (typeof renderAdminSettings === 'function') renderAdminSettings();
        }
    } catch (e) {
        console.warn('[Firestore] triggerAppReRender error:', e);
    }
};

// Update header user badge
function updateHeaderUserBadge() {
    const userInfo = document.getElementById('userInfo');
    if (!userInfo) return;

    // STRICT: badge must reflect only the active in-memory role.
    // Never show provider/client if admin is active.
    if (currentAdmin && currentAdmin.username) {
        userInfo.innerHTML = `<span class="user-badge">🛡️ ${currentAdmin.username}</span>`;
        return;
    }

    // If admin is active but somehow currentAdmin is cleared late, fail closed (no stale provider/client).
    // (This is defensive; primary guard is the check above.)

    if (currentProvider && currentProvider.name) {
        userInfo.innerHTML = `<span class="user-badge">🍽️ ${currentProvider.name}</span>`;
        return;
    }

    if (isUserLoggedIn() && currentClient && currentClient.name) {
        userInfo.innerHTML = `<span class="user-badge">👤 ${currentClient.name}</span>`;
        return;
    }

    userInfo.innerHTML = `<span class="user-badge" style="background:rgba(255,255,255,0.3)">👤 Guest (Not logged in)</span>`;
}

// Session Management
function checkSessions() {
    if (currentClient) {
        const authUser = getCurrentAuthUser();
        if (authUser && authUser.emailVerified === false) {
            currentClient = null;
            hideClientDashboard();
            showNotification('Please verify your email before accessing the client dashboard.', 'error');
            return;
        }
        showClientDashboard();
    }
    if (currentProvider) {
        const authUser = getCurrentAuthUser();
        if (authUser && authUser.emailVerified === false) {
            currentProvider = null;
            hideProviderDashboard();
            showNotification('Please verify your email before accessing the provider dashboard.', 'error');
            return;
        }
        showProviderDashboard();
    }
}
// Client Auth Toggle
function initClientAuthToggle() {
    const toggleBtns = document.querySelectorAll('#clientLoginSection .auth-toggle-btn');
    const registerForm = document.getElementById('clientRegisterForm');
    const loginForm = document.getElementById('clientLoginForm');

    toggleBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            const authType = btn.dataset.auth;

            // Update toggle buttons
            toggleBtns.forEach(b => b.classList.remove('active'));
            btn.classList.add('active');

            // Show/hide forms
            if (authType === 'register') {
                registerForm.classList.remove('hidden');
                loginForm.classList.add('hidden');
            } else {
                registerForm.classList.add('hidden');
                loginForm.classList.remove('hidden');
            }
        });
    });
}
// ==================== SAFE DUPLICATE-EMAIL CHECK BEFORE REGISTRATION ====================
// IMPORTANT: Pre-registration duplicate checks MUST NOT query Firestore before the user
// is authenticated. firestore.rules requires isAuthenticated() for reads on 'clients'
// and 'providers', so a guest (unauthenticated) Firestore query throws
// "Missing or insufficient permissions" and aborts the entire registration flow.
// This helper is authenticated-aware and never lets a permission error block signup.
//
// Strategy:
//   1. Check the in-memory Firestore-backed arrays first (single source of truth in app state).
//   2. Only query Firestore if a Firebase Auth user is currently signed in.
//   3. Wrap the Firestore query in try/catch so any read failure falls back to the
//      authoritative Firebase Auth duplicate check.
// Returns a Promise<boolean> - true if the email is already registered.
async function checkEmailExistsBeforeRegister(collectionName, email) {
    const normalizedEmail = String(email || '').toLowerCase().trim();
    if (!normalizedEmail) return false;

    try {
        const list = collectionName === 'providers' ? providers : clients;
        if (Array.isArray(list)) {
            const found = list.find(item => item.email && String(item.email).toLowerCase() === normalizedEmail);
            if (found) return true;
        }
    } catch (e) {
        console.warn('[Register] In-memory duplicate check failed for', collectionName, ':', e.message);
    }

    const isAuthenticated = !!(window.firebaseAuthInstance && window.firebaseAuthInstance.currentUser);
    if (!isAuthenticated || !window.firebaseDbInstance) {
        console.log('[Register] Skipping Firestore duplicate check for', collectionName,
            '(not authenticated or Firestore unavailable)');
        return false;
    }

    try {
        const snapshot = await window.firebaseDbInstance
            .collection(collectionName)
            .where('email', '==', normalizedEmail)
            .get();
        if (!snapshot.empty) {
            console.log('[Register] Email already exists in Firestore', collectionName, normalizedEmail);
            return true;
        }
    } catch (e) {
        console.warn('[Register] Firestore duplicate check skipped for', collectionName, ':', e.code || e.message);
    }

    return false;
}

function getPasswordStrengthError(password) {
    const value = String(password || '');
    if (value.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(value)) return 'Password must include at least one uppercase letter';
    if (!/[a-z]/.test(value)) return 'Password must include at least one lowercase letter';
    if (!/[0-9]/.test(value)) return 'Password must include at least one number';
    if (!/[^A-Za-z0-9]/.test(value)) return 'Password must include at least one special character';
    return '';
}

// Client Registration - WITH FIREBASE AUTH INTEGRATION
function initClientRegisterForm() {
    const form = document.getElementById('clientRegForm');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        if (!enforcePhase1ControlledBeta('client-registration')) {
            return;
        }

        const name = document.getElementById('clientRegName').value.trim();
        const phone = document.getElementById('clientRegPhone').value.trim();
        const email = document.getElementById('clientRegEmail').value.trim();
        const password = document.getElementById('clientRegPassword').value;
        const countryEl = document.getElementById('clientCountry');
        const countryRaw = countryEl ? countryEl.value : '';
        const country = countryRaw ? normalizeCountryCode(countryRaw) : '';

        if (!name || !phone || !email || !password) {
            showNotification('Please fill in all required fields', 'error');
            return;
        }
        if (!isValidEmailAddress(email)) {
            showNotification('Please enter a valid email address for client registration.', 'error');
            return;
        }

        if (!country) {
            showNotification('Please select your country so we can show you food in your location', 'error');
            countryEl.focus();
            return;
        }

        const passwordError = getPasswordStrengthError(password);
        if (passwordError) {
            showNotification(passwordError, 'error');
            return;
        }

        const normalizedPhone = normalizePhoneNumber(phone, country);
        if (!normalizedPhone || !isValidE164Phone(normalizedPhone)) {
            showNotification(t('phoneValidation', 'Phone number must use international format.'), 'error');
            return;
        }

// Check if client already exists before creating.
        // Uses an authenticated-aware helper: browserStorage is always checked,
        // Firestore is only queried when a user IS authenticated, and any
        // permission error is caught so it NEVER aborts registration.
        // createUserWithEmailAndPassword is the authoritative duplicate check.
        const clientEmailExists = await checkEmailExistsBeforeRegister('clients', email);
        if (clientEmailExists) {
            showNotification('Email already registered. Please login.', 'error');
            // Switch to login
            document.querySelector('#clientLoginSection .auth-toggle-btn[data-auth="login"]').click();
            return;
        }

        // Register new client (trim password to remove whitespace)
        // Clients no longer select a subscription plan during registration.
        // Keep a safe default so existing UI/business logic can rely on these fields.
        const client = {
            id: window.generateUUID(),
            name,
            phone: normalizedPhone,
            email: email.toLowerCase(),
            // IMPORTANT: Production stores no passwords in browserStorage/offline mode.
            password: '',
            country,

            // Client subscription fields (default)
            subscriptionPlan: 'free',
            subscriptionStatus: 'active',
            subscriptionStart: new Date().toISOString(),
            createdAt: new Date().toISOString()
        };

        // Try Firebase Auth first if available
        if (FIREBASE_AUTH_ENABLED && firebaseAuthInstance && firebaseDbInstance) {
            console.log('[ClientRegister] Creating Firebase Auth user...');

            firebaseAuthInstance.createUserWithEmailAndPassword(email, password)
                .then(function(userCredential) {
                    // Firebase user created successfully
                    console.log('[ClientRegister] Firebase user created:', userCredential.user.uid);

                    // CRITICAL FIX: Use the Firebase UID as the canonical client ID.
                    // This keeps browserStorage session, Firestore doc ID, and in-memory
                    // session all in sync, so isUserLoggedIn() and session restore work.
                    client.id = userCredential.user.uid;
                    client.uid = userCredential.user.uid;

                    // Enforce verified-email onboarding:
                    // send verification email first, then sign out.
                    return Promise.resolve()
                        .then(function() {
                            return sendVerificationEmailForUser(userCredential.user);
                        })
                        .then(function() {
                            if (typeof hybridSignOut === 'function') {
                                return hybridSignOut().catch(function() {});
                            }
                        })
                        .then(function() {
                            return userCredential;
                        });
                })
                .then(function(userCredential) {
                    // Continue with registration completion UX after Firebase success
                    completeClientRegistration(client, form);
                    if (userCredential && userCredential.user) {
                        showNotification('Verification email sent. Verify your email, then log in to access your client dashboard.', 'success');
                    }
                })
                .catch(function(error) {
                    console.log('[ClientRegister] Firebase registration failed:', error.code, error.message);

                    // If email is already in use, show a clear message
                    if (error.code === 'auth/email-already-in-use') {
                        showNotification('Email already registered. Please login.', 'error');
                        document.querySelector('#clientLoginSection .auth-toggle-btn[data-auth="login"]').click();
                        return;
                    }
                    showNotification('Registration failed. Please try again in a moment.', 'error');
                });
        } else {
            console.error('[ClientRegister] Firebase is unavailable. Blocking registration to avoid partial account creation.');
            showNotification('Registration is temporarily unavailable. Please try again shortly.', 'error');
        }
    });
}

// Complete client registration UI flow.
// Client access is granted only after verified-email login.
function completeClientRegistration(client, form) {
    // SECURITY: Strip password before any storage - NEVER store plaintext passwords client-side
    delete client.password;

    // Keep canonical role for downstream helpers.
    client.role = 'client';

    // Do not create a local session or dashboard access before email verification.
    currentClient = null;

    form.reset();
    const loginToggle = document.querySelector('#clientLoginSection .auth-toggle-btn[data-auth="login"]');
    if (loginToggle) loginToggle.click();
    const loginEmail = document.getElementById('clientLogEmail');
    const loginPassword = document.getElementById('clientLogPassword');
    if (loginEmail) loginEmail.value = client.email || '';
    if (loginPassword) loginPassword.focus();
}
// Client Login - FIXED VERSION (With direct onclick in HTML for reliability)
function initClientLoginForm() {
    // Get form reference
    const form = document.getElementById('clientLogForm');
    if (!form) {
        console.error('[ClientLogin] Form not found');
        return;
    }

    // Handle form submit event - this catches Enter key presses
    form.addEventListener('submit', function(e) {
        e.preventDefault();
        e.stopPropagation();

        // Call the login function directly
        performClientLogin();

        return false;
    });

    console.log('[ClientLogin] Form submit handler attached');
}

// Direct client login function (called by both button click and form submit)
// Firebase-only (no browserStorage fallback)
async function performClientLogin() {
    console.log('[ClientLogin] performClientLogin called');

    const emailInput = document.getElementById('clientLogEmail');
    const passwordInput = document.getElementById('clientLogPassword');

    if (!emailInput || !passwordInput) {
        showNotification('Login form fields not found', 'error');
        return;
    }

    const email = emailInput.value.trim().toLowerCase();
    const password = passwordInput.value;

    console.log('Login attempt - Email:', email);

    if (!email || !password) {
        showNotification('Please enter email and password', 'error');
        return;
    }
    if (!isValidEmailAddress(email)) {
        showNotification('Please enter a valid client email address (example: name@domain.com).', 'error');
        return;
    }

    const authUser = getCurrentAuthUser();
    const authEmail = String((authUser && authUser.email) || '').toLowerCase();
    if (authUser && authEmail && authEmail === email) {
        if (authUser.emailVerified === false) {
            sendVerificationEmailForUser(authUser)
                .then(function() {
                    showNotification('Please verify your email before logging in. We sent a new verification email to your inbox.', 'error');
                })
                .catch(function(error) {
                    console.error('[ClientLogin] Verification email resend failed:', error);
                    showNotification(`Your email is not verified, and a new verification email could not be sent: ${error.message}`, 'error');
                });
            return;
        }
        window.__forceGuestModeUntilNextLogin = false;
        window.__lastKnownAuthUid = String(authUser.uid || '');
        window.__lastKnownAuthEmail = authEmail;
        if (currentClient && String(currentClient.email || '').toLowerCase() === email) {
            showClientDashboard();
            showNotification('Welcome back!', 'success');
            return;
        }
        restoreClientSessionFromFirestore(authUser);
        showNotification('Welcome back!', 'success');
        return;
    }

    try {
        const keepSignedIn = Boolean(document.getElementById('clientKeepSignedIn')?.checked);
        await window.setFirebaseAuthPersistence?.(keepSignedIn);
    } catch (error) {
        console.error('[ClientLogin] Could not set sign-in persistence:', error);
        showNotification('Unable to apply your sign-in preference. Please try again.', 'error');
        return;
    }

    if ((!FIREBASE_AUTH_ENABLED || !firebaseAuthInstance) && window.authService && typeof window.authService.login === 'function') {
        console.log('[ClientLogin] Trying unified auth service as primary...');
        let authSucceeded = false;
        window.__forceGuestModeUntilNextLogin = false;

        window.authService.login(email, password)
            .then(function(authResult) {
                const user = authResult && authResult.user;
                const detectedRole = authResult && authResult.role;
                if (!user) {
                    throw new Error('Login succeeded but no authenticated user was returned.');
                }
                authSucceeded = true;
                window.__lastKnownAuthUid = String(user.uid || '');
                window.__lastKnownAuthEmail = String(user.email || email || '').toLowerCase();

                if (user.emailVerified === false) {
                    return sendVerificationEmailForUser(user)
                        .then(function() {
                            showNotification('Please verify your email before logging in. We sent a new verification email to your inbox.', 'error');
                        })
                        .catch(function(error) {
                            console.error('[ClientLogin] Verification email resend failed:', error);
                            showNotification(`Your email is not verified, and a new verification email could not be sent: ${error.message}`, 'error');
                        })
                        .then(function() {
                            return window.authService.signOut().catch(function() {});
                        })
                        .then(function() {
                            return null;
                        });
                }

                if (detectedRole === 'admin') {
                    return window.authService.signOut()
                        .catch(function() {})
                        .then(function() {
                            showNotification('Super Admin must use the Super Admin login form, not the client login.', 'error');
                            return null;
                        });
                }

                if (detectedRole === 'provider') {
                    return window.authService.signOut()
                        .catch(function() {})
                        .then(function() {
                            showNotification('Provider accounts must use the restaurant/caterer login form.', 'error');
                            return null;
                        });
                }

                return user;
            })
            .then(function(roleGuardUser) {
                if (!roleGuardUser) {
                    return null;
                }
                const userCredential = { user: roleGuardUser };
                if (firebaseDbInstance) {
                    return firebaseDbInstance.collection('clients').doc(userCredential.user.uid).get()
                        .then(function(doc) {
                            if (doc.exists) {
                                const clientDoc = normalizeClientSessionRecord(doc.data(), userCredential.user, email);
                                console.log('[ClientLogin] Client loaded from Firestore:', clientDoc.name);
                                return clientDoc;
                            }

                            console.log('[ClientLogin] No client profile found, creating one...');
                            const nowIso = new Date().toISOString();
                            const newClient = normalizeClientSessionRecord({
                                name: userCredential.user.displayName || email.split('@')[0],
                                createdAt: nowIso,
                                role: 'client',
                                subscriptionPlan: 'free',
                                subscriptionStatus: 'active',
                                subscriptionStart: nowIso,
                                admin: false,
                                isAdmin: false
                            }, userCredential.user, email);

                            return firebaseDbInstance.collection('clients').doc(userCredential.user.uid).set(newClient)
                                .then(function() {
                                    console.log('[ClientLogin] New client saved to Firestore');
                                    return newClient;
                                });
                        });
                }

                return normalizeClientSessionRecord({
                    name: userCredential.user.displayName || email.split('@')[0],
                    email: email
                }, userCredential.user, email);
            })
            .then(function(client) {
                if (!client) {
                    return;
                }
                currentClient = client;
                const existingIdx = clients.findIndex(c => c.id === client.id);
                if (existingIdx !== -1) {
                    clients[existingIdx] = client;
                } else {
                    clients.push(client);
                }
                const form = document.getElementById('clientLogForm');
                if (form) form.reset();

                showNotification('Welcome back!', 'success');
                showClientDashboard();
            })
            .catch(function(error) {
                console.log('[ClientLogin] Unified auth login failed:', error.message);
                const maybeAuthUser = getCurrentAuthUser();
                const maybeAuthEmail = String((maybeAuthUser && maybeAuthUser.email) || '').toLowerCase();
                if (maybeAuthUser && maybeAuthEmail && maybeAuthEmail === email) {
                    window.__forceGuestModeUntilNextLogin = false;
                    window.__lastKnownAuthUid = String(maybeAuthUser.uid || '');
                    window.__lastKnownAuthEmail = maybeAuthEmail;
                    restoreClientSessionFromFirestore(maybeAuthUser);
                    showNotification('Welcome back!', 'success');
                    return;
                }
                if (authSucceeded) {
                    showNotification('Login succeeded but your client profile could not be loaded. Please try again.', 'error');
                } else {
                    const loginReason = (error && (error.message || error.code)) ? String(error.message || error.code) : 'Firebase authentication failed.';
                    showNotification(`Client login failed: ${loginReason}`, 'error');
                }
            });
        return;
    }

// Try Firebase Auth FIRST (primary authentication)
    if (FIREBASE_AUTH_ENABLED && firebaseAuthInstance) {
        console.log('[ClientLogin] Trying Firebase Auth as primary...');
        let authSucceeded = false;
        window.__forceGuestModeUntilNextLogin = false;

        firebaseAuthInstance.signInWithEmailAndPassword(email, password)
            .then(function(userCredential) {
                const user = userCredential.user;
                console.log('[ClientLogin] Firebase login success:', user.uid);
                authSucceeded = true;
                window.__lastKnownAuthUid = String(user.uid || '');
                window.__lastKnownAuthEmail = String(user.email || email || '').toLowerCase();

                if (user.emailVerified === false) {
                    return sendVerificationEmailForUser(user)
                        .then(function() {
                            showNotification('Please verify your email before logging in. We sent a new verification email to your inbox.', 'error');
                        })
                        .catch(function(error) {
                            console.error('[ClientLogin] Verification email resend failed:', error);
                            showNotification(`Your email is not verified, and a new verification email could not be sent: ${error.message}`, 'error');
                        })
                        .then(function() {
                            if (typeof hybridSignOut === 'function') {
                                return hybridSignOut().catch(function() {});
                            }
                        })
                        .then(function() {
                            return null;
                        });
                }

                return getServerAuthorizedRoleForUser(user)
                    .then(function(serverRole) {
                        if (serverRole === 'admin') {
                            console.warn('[ClientLogin] Blocked admin user from client login:', email);
                            if (typeof hybridSignOut === 'function') {
                                hybridSignOut();
                            }
                            showNotification('Super Admin must use the Super Admin login form, not the client login.', 'error');
                            return null;
                        }

                        if (serverRole === 'provider') {
                            console.warn('[ClientLogin] Rejected provider auth role for client login:', serverRole, email);
                            if (typeof hybridSignOut === 'function') {
                                hybridSignOut();
                            }
                            showNotification('Provider accounts must use the restaurant/caterer login form.', 'error');
                            return null;
                        }

                        return user;
                    });
            })
            .then(function(adminGuardUser) {
                if (!adminGuardUser) {
                    // Admin was blocked - do not continue into client session creation.
                    return null;
                }
                // Load the client profile by UID (rules-safe) and create one if missing.
                const userCredential = { user: adminGuardUser };
                if (firebaseDbInstance) {
                    return firebaseDbInstance.collection('clients').doc(userCredential.user.uid).get()
                        .then(function(doc) {
                            if (doc.exists) {
                                const clientDoc = normalizeClientSessionRecord(doc.data(), userCredential.user, email);
                                console.log('[ClientLogin] Client loaded from Firestore:', clientDoc.name);
                                return clientDoc;
                            }

                            console.log('[ClientLogin] No client profile found, creating one...');
                            const nowIso = new Date().toISOString();
                            const newClient = normalizeClientSessionRecord({
                                name: userCredential.user.displayName || email.split('@')[0],
                                createdAt: nowIso,
                                role: 'client',
                                subscriptionPlan: 'free',
                                subscriptionStatus: 'active',
                                subscriptionStart: nowIso,
                                admin: false,
                                isAdmin: false
                            }, userCredential.user, email);

                            return firebaseDbInstance.collection('clients').doc(userCredential.user.uid).set(newClient)
                                .then(function() {
                                    console.log('[ClientLogin] New client saved to Firestore');
                                    return newClient;
                                });
                        });
                } else {
                    // No Firestore, shouldn't happen but handle gracefully
                    return normalizeClientSessionRecord({
                        name: userCredential.user.displayName || email.split('@')[0],
                        email: email
                    }, userCredential.user, email);
                }
            })
.then(function(client) {
                // SECURITY GUARD: If an admin was blocked earlier, `client` will be
                // null and we must NOT create a client session.
                if (!client) {
                    return;
                }
                // Set session (without password for security)
                currentClient = client;
                // CRITICAL FIX: Add the client to the in-memory clients array so
                // isUserLoggedIn() passes immediately (it validates the (id+email)
                // pair exists in the clients array).
                const existingIdx = clients.findIndex(c => c.id === client.id);
                if (existingIdx !== -1) {
                    clients[existingIdx] = client;
                } else {
                    clients.push(client);
                }
                // Reset form
                const form = document.getElementById('clientLogForm');
                if (form) form.reset();

                showNotification('Welcome back!', 'success');
                showClientDashboard();
            })
            .catch(function(error) {
                console.log('[ClientLogin] Firebase login failed:', error.message);
                const maybeAuthUser = getCurrentAuthUser();
                const maybeAuthEmail = String((maybeAuthUser && maybeAuthUser.email) || '').toLowerCase();
                if (maybeAuthUser && maybeAuthEmail && maybeAuthEmail === email) {
                    window.__forceGuestModeUntilNextLogin = false;
                    window.__lastKnownAuthUid = String(maybeAuthUser.uid || '');
                    window.__lastKnownAuthEmail = maybeAuthEmail;
                    restoreClientSessionFromFirestore(maybeAuthUser);
                    showNotification('Welcome back!', 'success');
                    return;
                }
                if (authSucceeded) {
                    showNotification('Login succeeded but your client profile could not be loaded. Please try again.', 'error');
                } else {
                    const loginReason = (error && (error.message || error.code)) ? String(error.message || error.code) : 'Firebase authentication failed.';
                    showNotification(`Client login failed: ${loginReason}`, 'error');
                }
            });
}
}

// Make function global
window.performClientLogin = performClientLogin;

// ==================== PROVIDER LOGIN FUNCTION (Direct onclick handler) ====================
// Firebase-only (no browserStorage fallback)
let providerLoginInProgress = false;

function providerLoginErrorMessage(error) {
    if (error && error.code === 'auth/invalid-email') {
        return 'Invalid email format. Please use the same email address used during provider registration.';
    }
    if (error && ['auth/invalid-recaptcha-token', 'auth/missing-recaptcha-token', 'auth/invalid-app-credential'].includes(error.code)) {
        return 'Google reCAPTCHA verification failed. Allow reCAPTCHA requests to google.com and gstatic.com, check browser extensions or network filtering, then reload and try again.';
    }
    return (error && (error.message || error.code)) ? String(error.message || error.code) : 'Firebase authentication failed.';
}

async function performProviderLogin() {
    console.log('[ProviderLogin] performProviderLogin called');

    const emailInput = document.getElementById('providerLogEmail');
    const passwordInput = document.getElementById('providerLogPassword');

    if (!emailInput || !passwordInput) {
        showNotification('Login form fields not found', 'error');
        return;
    }

    const email = emailInput.value.trim().toLowerCase();
    const password = passwordInput.value;

    console.log('[ProviderLogin] Login attempt - Email:', email);

    if (!email || !password) {
        showNotification('Please enter email and password', 'error');
        return;
    }

    if (providerLoginInProgress) return;
    providerLoginInProgress = true;
    const loginButton = document.querySelector('#providerLogForm .login-btn');
    const originalButtonText = loginButton ? loginButton.textContent : '';
    if (loginButton) {
        loginButton.disabled = true;
        loginButton.textContent = 'Signing in...';
    }
    const finishProviderLogin = () => {
        providerLoginInProgress = false;
        if (loginButton) {
            loginButton.disabled = false;
            loginButton.textContent = originalButtonText;
        }
    };

    try {
        const keepSignedIn = Boolean(document.getElementById('providerKeepSignedIn')?.checked);
        await window.setFirebaseAuthPersistence?.(keepSignedIn);
    } catch (error) {
        finishProviderLogin();
        console.error('[ProviderLogin] Could not set sign-in persistence:', error);
        showNotification('Unable to apply your sign-in preference. Please try again.', 'error');
        return;
    }

    if (window.authService && typeof window.authService.login === 'function') {
        console.log('[ProviderLogin] Trying unified auth service as primary...');

        window.authService.login(email, password)
            .then(async function(authResult) {
                const user = authResult && authResult.user;
                const detectedRole = authResult && authResult.role;
                if (!user) {
                    throw new Error('Login succeeded but no authenticated user was returned.');
                }

                if (detectedRole === 'admin') {
                    return window.authService.signOut()
                        .catch(function() {})
                        .then(function() {
                            showNotification('Super Admin must use the Super Admin login form, not the provider login.', 'error');
                            return null;
                        });
                }

                if (detectedRole !== 'provider') {
                    return window.authService.signOut()
                        .catch(function() {})
                        .then(function() {
                            showNotification('This account is not authorized for provider access. Please use the correct login form.', 'error');
                            return null;
                        });
                }

                const providerApproved = await refreshProviderApprovalClaim();
                if (!providerApproved) {
                    await window.authService.signOut().catch(function() {});
                    showNotification('Provider login failed: Your provider account is pending Super Admin approval. You will be able to log in after approval.', 'error');
                    return null;
                }

                return user;
            })
            .then(async function(roleGuardUser) {
                if (!roleGuardUser) {
                    return;
                }
                const userCredential = { user: roleGuardUser };
                let provider = providers.find(p => p.email.toLowerCase() === email);

                if (!provider) {
                    provider = {
                        id: userCredential.user.uid,
                        name: userCredential.user.displayName || email.split('@')[0],
                        email: email,
                        password: '',
                        createdAt: new Date().toISOString()
                    };
                    providers.push(provider);
                }

                provider.id = userCredential.user.uid;
                provider.uid = userCredential.user.uid;
                provider.email = email;

                if (firebaseDbInstance && typeof firebaseDbInstance.collection === 'function') {
                    const providerRef = firebaseDbInstance.collection('providers').doc(userCredential.user.uid);
                    const providerSnap = await providerRef.get();
                    if (!providerSnap.exists) {
                        const providerDoc = {
                            uid: userCredential.user.uid,
                            id: userCredential.user.uid,
                            name: provider.name || userCredential.user.displayName || email.split('@')[0],
                            phone: provider.phone || '',
                            email: email,
                            type: provider.type || 'Restaurant',
                            address: provider.address || '',
                            country: provider.country || '',
                            role: 'provider',
                            acceptsCustomRequests: provider.acceptsCustomRequests === true,
                            subscriptionStatus: provider.subscriptionStatus || 'pending',
                            subscriptionPlan: provider.subscriptionPlan || 'free',
                            subscriptionStart: provider.subscriptionStart || new Date().toISOString(),
                            subscriptionEnd: provider.subscriptionEnd || null,
                            subscriptionApprovedBy: provider.subscriptionApprovedBy || null,
                            subscriptionApprovedAt: provider.subscriptionApprovedAt || null,
                            subscriptionRejectionReason: provider.subscriptionRejectionReason || '',
                            subscriptionSuspendedReason: provider.subscriptionSuspendedReason || '',
                            subscriptionNotes: provider.subscriptionNotes || '',
                            verificationStatus: provider.verificationStatus || 'pending',
                            accountStatus: provider.accountStatus || 'pending',
                            payoutStatus: provider.payoutStatus || 'pending',
                            createdAt: provider.createdAt || new Date().toISOString()
                        };
                        await providerRef.set(providerDoc, { merge: true });
                        provider = providerDoc;
                    } else {
                        provider = { ...provider, ...(providerSnap.data() || {}) };
                    }
                }

                applyProviderState(provider);
                const providerIdx = providers.findIndex(p => String(p.id || p.uid) === String(provider.id || provider.uid));
                if (providerIdx !== -1) {
                    providers[providerIdx] = provider;
                } else {
                    providers.push(provider);
                }
                const state = normalizeProviderState(provider);
                const subStatus = state.subscriptionStatus || 'pending';
                const accountStatus = state.accountStatus || 'pending';
                const verificationStatus = state.verificationStatus || 'pending';
                const payoutStatus = state.payoutStatus || 'pending';

                if (verificationStatus === 'rejected' || accountStatus === 'rejected') {
                    const reason = provider.subscriptionRejectionReason || 'No reason provided';
                    showNotification(`Your registration was rejected. Reason: ${reason}. Please contact support.`, 'error');
                    return;
                }

                if (accountStatus === 'suspended' || subStatus === 'suspended') {
                    const reason = provider.subscriptionSuspendedReason || 'No reason provided';
                    showNotification(`Your account has been suspended. Reason: ${reason}. Please contact support.`, 'error');
                    return;
                }

                const isApproved = subStatus === 'approved' || subStatus === 'active' || accountStatus === 'active';
                if (!isApproved) {
                    showNotification('Your account is pending approval. Please wait for Super Admin to approve your registration.', 'error');
                    return;
                }

                if (payoutStatus === 'blocked') {
                    showNotification('Your payout account is blocked. Please contact Super Admin.', 'error');
                    return;
                }

                if (accountStatus !== 'active' && subStatus !== 'approved' && subStatus !== 'active') {
                    showNotification('Your account is not active. Please contact Super Admin.', 'error');
                    return;
                }

                if (provider.subscriptionEnd && subStatus === 'approved') {
                    const endDate = new Date(provider.subscriptionEnd);
                    const now = new Date();
                    if (endDate < now) {
                        provider.subscriptionStatus = 'suspended';
                        provider.accountStatus = 'suspended';
                        provider.subscriptionSuspendedReason = 'Subscription expired';
                        showNotification('Your subscription has expired. Please renew to continue using the service.', 'error');
                        return;
                    }

                    const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                    if (daysRemaining <= 7) {
                        showNotification(`Warning: Your subscription expires in ${daysRemaining} days. Please renew to avoid interruption.`, 'warning');
                    }
                }

                const providerApproved = await refreshProviderApprovalClaim();
                if (!providerApproved) {
                    if (typeof hybridSignOut === 'function') {
                        await hybridSignOut().catch(function() {});
                    }
                    showNotification('Provider login failed: Your provider account is pending Super Admin approval. You will be able to log in after approval.', 'error');
                    return;
                }

                currentProvider = provider;
                const form = document.getElementById('providerLogForm');
                if (form) form.reset();

                showNotification('Welcome back!', 'success');
                showProviderDashboard();
            })
            .catch(function(error) {
                console.log('[ProviderLogin] Unified auth login failed:', error.message);
                const loginReason = providerLoginErrorMessage(error);
                showNotification(`Provider login failed: ${loginReason}`, 'error');
            })
            .finally(finishProviderLogin);
        return;
    }

    // Try Firebase Auth FIRST (primary authentication)
    if (FIREBASE_AUTH_ENABLED && firebaseAuthInstance) {
        console.log('[ProviderLogin] Trying Firebase Auth as primary...');

firebaseAuthInstance.signInWithEmailAndPassword(email, password)
            .then(function(userCredential) {
                const user = userCredential.user;
                console.log('[ProviderLogin] Firebase login success:', user.uid);

                if (user && user.emailVerified === false) {
                    return Promise.resolve()
                        .then(function() {
                            return sendVerificationEmailForUser(user);
                        })
                        .catch(function(err) {
                            console.warn('[ProviderLogin] Failed to send verification email reminder:', err && err.message ? err.message : err);
                        })
                        .then(function() {
                            if (typeof hybridSignOut === 'function') {
                                return hybridSignOut().catch(function() {});
                            }
                        })
                        .then(function() {
                            showNotification('Please verify your email before provider login. We sent you a new verification email.', 'error');
                            return null;
                        });
                }

                return getServerAuthorizedRoleForUser(user)
                    .then(async function(serverRole) {
                        if (serverRole === 'admin') {
                            console.warn('[ProviderLogin] Blocked admin user from provider login:', email);
                            if (typeof hybridSignOut === 'function') {
                                hybridSignOut();
                            }
                            showNotification('Super Admin must use the Super Admin login form, not the provider login.', 'error');
                            return null;
                        }

                        if (serverRole !== 'provider') {
                            console.warn('[ProviderLogin] Rejected non-provider auth role for provider login:', serverRole, email);
                            if (typeof hybridSignOut === 'function') {
                                hybridSignOut();
                            }
                            showNotification('This account is not authorized for provider access. Please use the correct login form.', 'error');
                            return null;
                        }

                        return user;
                    });
            })
            .then(async function(adminGuardUser) {
                if (!adminGuardUser) {
                    // Admin was blocked - do not continue into provider session creation.
                    return;
                }
                const userCredential = { user: adminGuardUser };
                // Sync user data from Firebase provider collection
                let provider = providers.find(p => p.email.toLowerCase() === email);

                if (!provider) {
                    // Create provider entry in browserStorage from Firebase user
                    provider = {
                        id: userCredential.user.uid,
                        name: userCredential.user.displayName || email.split('@')[0],
                        email: email,
                        password: '', // Don't store password in browserStorage anymore
                        createdAt: new Date().toISOString()
                    };
                    providers.push(provider);
                }

                // Ensure canonical provider identity fields always match Firebase Auth.
                provider.id = userCredential.user.uid;
                provider.uid = userCredential.user.uid;
                provider.email = email;

                // Enforce verified-email provider profile creation:
                // if the provider document does not exist yet (common after email
                // verification is introduced), create it now from the authenticated
                // verified session so firestore.rules allow create.
                if (firebaseDbInstance && typeof firebaseDbInstance.collection === 'function') {
                    const providerRef = firebaseDbInstance.collection('providers').doc(userCredential.user.uid);
                    const providerSnap = await providerRef.get();
                    if (!providerSnap.exists) {
                        const providerDoc = {
                            uid: userCredential.user.uid,
                            id: userCredential.user.uid,
                            name: provider.name || userCredential.user.displayName || email.split('@')[0],
                            phone: provider.phone || '',
                            email: email,
                            type: provider.type || 'Restaurant',
                            address: provider.address || '',
                            country: provider.country || '',
                            role: 'provider',
                            acceptsCustomRequests: provider.acceptsCustomRequests === true,
                            subscriptionStatus: provider.subscriptionStatus || 'pending',
                            subscriptionPlan: provider.subscriptionPlan || 'free',
                            subscriptionStart: provider.subscriptionStart || new Date().toISOString(),
                            subscriptionEnd: provider.subscriptionEnd || null,
                            subscriptionApprovedBy: provider.subscriptionApprovedBy || null,
                            subscriptionApprovedAt: provider.subscriptionApprovedAt || null,
                            subscriptionRejectionReason: provider.subscriptionRejectionReason || '',
                            subscriptionSuspendedReason: provider.subscriptionSuspendedReason || '',
                            subscriptionNotes: provider.subscriptionNotes || '',
                            verificationStatus: provider.verificationStatus || 'pending',
                            accountStatus: provider.accountStatus || 'pending',
                            payoutStatus: provider.payoutStatus || 'pending',
                            createdAt: provider.createdAt || new Date().toISOString()
                        };
                        await providerRef.set(providerDoc, { merge: true });
                        provider = providerDoc;
                    } else {
                        provider = { ...provider, ...(providerSnap.data() || {}) };
                    }
                }

                // === PROVIDER STATE CHECK ===
                applyProviderState(provider);
                const providerIdx = providers.findIndex(p => String(p.id || p.uid) === String(provider.id || provider.uid));
                if (providerIdx !== -1) {
                    providers[providerIdx] = provider;
                } else {
                    providers.push(provider);
                }
                const state = normalizeProviderState(provider);
                const subStatus = state.subscriptionStatus || 'pending';
                const accountStatus = state.accountStatus || 'pending';
                const verificationStatus = state.verificationStatus || 'pending';
                const payoutStatus = state.payoutStatus || 'pending';

                if (verificationStatus === 'rejected' || accountStatus === 'rejected') {
                    const reason = provider.subscriptionRejectionReason || 'No reason provided';
                    showNotification(`Your registration was rejected. Reason: ${reason}. Please contact support.`, 'error');
                    return;
                }

                if (accountStatus === 'suspended' || subStatus === 'suspended') {
                    const reason = provider.subscriptionSuspendedReason || 'No reason provided';
                    showNotification(`Your account has been suspended. Reason: ${reason}. Please contact support.`, 'error');
                    return;
                }

                const isApproved = subStatus === 'approved' || subStatus === 'active' || accountStatus === 'active';
                if (!isApproved) {
                    showNotification('Your account is pending approval. Please wait for Super Admin to approve your registration.', 'error');
                    return;
                }

                if (payoutStatus === 'blocked') {
                    showNotification('Your payout account is blocked. Please contact Super Admin.', 'error');
                    return;
                }

                if (accountStatus !== 'active' && subStatus !== 'approved' && subStatus !== 'active') {
                    showNotification('Your account is not active. Please contact Super Admin.', 'error');
                    return;
                }

                // Check subscription expiration
                if (provider.subscriptionEnd && subStatus === 'approved') {
                    const endDate = new Date(provider.subscriptionEnd);
                    const now = new Date();
                    if (endDate < now) {
                        provider.subscriptionStatus = 'suspended';
                        provider.accountStatus = 'suspended';
                        provider.subscriptionSuspendedReason = 'Subscription expired';
                        showNotification('Your subscription has expired. Please renew to continue using the service.', 'error');
                        return;
                    }

                    const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                    if (daysRemaining <= 7) {
                        showNotification(`Warning: Your subscription expires in ${daysRemaining} days. Please renew to avoid interruption.`, 'warning');
                    }
                }
                // === END PROVIDER STATE CHECK ===

                const providerApproved = await refreshProviderApprovalClaim();
                if (!providerApproved) {
                    if (typeof hybridSignOut === 'function') {
                        await hybridSignOut().catch(function() {});
                    }
                    showNotification('Provider login failed: Your provider account is pending Super Admin approval. You will be able to log in after approval.', 'error');
                    return;
                }

                // Set session (without password for security)
                currentProvider = provider;
                // Reset form
                const form = document.getElementById('providerLogForm');
                if (form) form.reset();

                showNotification('Welcome back!', 'success');
                showProviderDashboard();
            })
            .catch(function(error) {
                console.log('[ProviderLogin] Firebase login failed:', error.message);
                const loginReason = providerLoginErrorMessage(error);
                showNotification(`Provider login failed: ${loginReason}`, 'error');
            })
            .finally(finishProviderLogin);
    } else {
        finishProviderLogin();
        showNotification('Firebase Auth is unavailable. Please reload and try again.', 'error');
    }
}

// Make provider login function global
window.performProviderLogin = performProviderLogin;

// ==================== ADMIN LOGIN FUNCTION (Firebase Auth + Custom Claims) ====================
// SECURITY IMPROVED:
// Admin credentials are NO LONGER read from the Firestore 'admin-credentials' collection.
// That collection is now DENIED to all clients in firestore.rules (server-only via Admin SDK).
// Instead, admin login uses Firebase Auth sign-in + verification of the `admin: true`
// custom claim on the user's ID token. The claim is set by the backend
// (Firebase Auth / Admin SDK / Cloud Functions) only - never by a client-side
// email whitelist. No client-side email whitelist is used.
//
// Authoritative admin authorization is the `admin: true` (or `role: admin`) custom
// claim issued by the backend (Firebase Auth / Admin SDK / Cloud Functions). No
// client-side email whitelist is used.

function performAdminLogin() {
    console.log('[AdminLogin] performAdminLogin called (Firebase Auth + claims)');

    const usernameEl = document.getElementById('adminUsername');
    const passwordEl = document.getElementById('adminPassword');

    if (!usernameEl || !passwordEl) {
        showNotification('Admin login form fields not found', 'error');
        return;
    }

    // Trim both username and password to handle whitespace issues
    const username = (usernameEl.value || '').trim();
    const password = (passwordEl.value || '').trim();

    console.log('[AdminLogin] Login attempt - username:', username);

    if (!username || !password) {
        showNotification('Please enter username and password', 'error');
        return;
    }

    // Guard against double-execution when both onclick AND form submit fire.
    // Uses a short time-based debounce so rapid duplicate calls are ignored
    // without needing to reset a flag across async callbacks.
    const _now = Date.now();
    if (window.__lastAdminLoginAttempt && (_now - window.__lastAdminLoginAttempt) < 300) {
        console.log('[AdminLogin] Skipping duplicate admin login call');
        return;
    }
    window.__lastAdminLoginAttempt = _now;

    if (window.authService && typeof window.authService.login === 'function') {
        const loginBtn = document.querySelector('#adminLogForm button[type="submit"]');
        if (loginBtn) {
            loginBtn.disabled = true;
            loginBtn.textContent = 'Signing in...';
        }

        window.authService.login(username, password)
            .then(async function(authResult) {
                if (!authResult || authResult.role !== 'admin' || !authResult.user) {
                    await window.authService.signOut().catch(function() {});
                    showNotification('Access denied: you do not have Super Admin privileges.', 'error');
                    return;
                }

                const user = authResult.user;
                const email = (user.email || '').toLowerCase();
                const hasAdminClaim = true;

                console.log('[AdminLogin] Admin authorization granted for:', email);

                clearNonAdminSessions();
                clearProviderAndClientSessions();
                hideProviderAndClientDashboards();

                currentAdmin = {
                    username: email,
                    uid: user.uid,
                    loginTime: new Date().toISOString(),
                    authenticatedVia: 'auth-service',
                    hasAdminClaim: hasAdminClaim
                };
                window.currentAdmin = currentAdmin;

                if (window.firebaseDbInstance) {
                    firebaseDbInstance = window.firebaseDbInstance;
                }

                try {
                    if (typeof window.bootstrapAdminData === 'function') {
                        await window.bootstrapAdminData();
                    }
                    if (typeof renderAdminStats === 'function') renderAdminStats();
                    if (typeof renderAdminClients === 'function') renderAdminClients();
                    if (typeof renderAdminProviders === 'function') renderAdminProviders();
                    if (typeof renderAdminOrders === 'function') renderAdminOrders();
                    if (typeof renderAdminSubscriptions === 'function') renderAdminSubscriptions();
                } catch (adminBootstrapErr) {
                    console.warn('[AdminLogin] Immediate admin bootstrap failed:', adminBootstrapErr && adminBootstrapErr.message ? adminBootstrapErr.message : adminBootstrapErr);
                }

                const adminLoginSection = document.getElementById('adminLoginSection');
                const adminDashboard = document.getElementById('adminDashboard');

                if (adminLoginSection) adminLoginSection.classList.add('hidden');
                if (adminDashboard) adminDashboard.classList.remove('hidden');

                const form = document.getElementById('adminLogForm');
                if (form) form.reset();

                showNotification('Welcome Super Admin!', 'success');
                updateHeaderUserBadge();
                renderAdminStats();

                try {
                    if (typeof window.__loadSiteSettingsFromFirestore === 'function') {
                        await window.__loadSiteSettingsFromFirestore();
                    }
                    if (typeof window.__loadSubscriptionRatesFromFirestore === 'function') {
                        await window.__loadSubscriptionRatesFromFirestore();
                    }
                    if (typeof window.__loadSubscriptionPlansFromFirestore === 'function') {
                        await window.__loadSubscriptionPlansFromFirestore();
                    }
                    if (typeof renderFooter === 'function') renderFooter();
                    if (typeof window.__setupSiteSettingsListener === 'function') {
                        window.__setupSiteSettingsListener();
                    }
                } catch (settingsErr) {
                    console.warn('[AdminLogin] Error reloading settings from Firestore:', settingsErr.message);
                }

                try {
                    if (typeof window.setupAdminRealTimeListeners === 'function') {
                        console.log('[AdminLogin] Setting up admin real-time listeners...');
                        window.setupAdminRealTimeListeners();
                    } else {
                        console.warn('[AdminLogin] setupAdminRealTimeListeners not available (firebase-integration.js not loaded yet)');
                        setTimeout(() => {
                            if (typeof window.setupAdminRealTimeListeners === 'function') {
                                console.log('[AdminLogin] Setting up admin real-time listeners (delayed)');
                                window.setupAdminRealTimeListeners();
                            } else {
                                console.warn('[AdminLogin] setupAdminRealTimeListeners still not available after delay');
                            }
                        }, 2000);
                    }
                } catch (listenerErr) {
                    console.warn('[AdminLogin] Error setting up admin real-time listeners:', listenerErr);
                }
            })
            .catch(function(error) {
                console.error('[AdminLogin] Unified auth sign-in failed:', error.code, error.message);
                showNotification(`Admin login failed: ${error.message || 'Unknown authentication error'}`, 'error');
            })
            .finally(function() {
                if (loginBtn) {
                    loginBtn.disabled = false;
                    loginBtn.textContent = 'Login';
                }
            });
        return;
    }

    // Fail closed if Firebase Auth instance isn't ready.
    // First try to sync from the modular bootstrap global instance.
    if (!firebaseAuthInstance) {
        if (window.firebaseAuthInstance) {
            firebaseAuthInstance = window.firebaseAuthInstance;
            FIREBASE_AUTH_ENABLED = true;
            console.log('[AdminLogin] Synced firebaseAuthInstance from window.firebaseAuthInstance');
        } else if (window.firebaseDbInstance) {
            // Legacy fallback: some scripts expose auth via firebaseDbInstance.auth
            if (window.firebaseDbInstance.auth) {
                firebaseAuthInstance = window.firebaseDbInstance.auth;
                FIREBASE_AUTH_ENABLED = true;
                console.log('[AdminLogin] Synced firebaseAuthInstance from firebaseDbInstance.auth');
            }
        }
        if (!firebaseAuthInstance) {
            showNotification('Firebase Auth not initialized yet. Please try again in a moment.', 'error');
            console.log('[AdminLogin] firebaseAuthInstance missing');
            return;
        }
    }

    // Disable double-submission while auth is in progress
    const loginBtn = document.querySelector('#adminLogForm button[type="submit"]');
    if (loginBtn) {
        loginBtn.disabled = true;
        loginBtn.textContent = 'Signing in...';
    }

    // Sign in with Firebase Auth
    firebaseAuthInstance.signInWithEmailAndPassword(username, password)
        .then(async function(userCredential) {
            const user = userCredential.user;
            console.log('[AdminLogin] Firebase Auth sign-in success:', user.email, user.uid);

            // Force custom claim refresh immediately after admin login to prevent 403 propagation delay
            try {
                if (user && typeof user.getIdToken === 'function') {
                    await user.getIdToken(true);
                }
            } catch (tokenErr) {
                console.warn('[AdminLogin] Token refresh error:', tokenErr.message);
            }

            const email = (user.email || '').toLowerCase();

// AUTHORIZATION CHECK:
            // Admin status is granted by the backend-issued `admin` custom claim
            // (or `role: admin`) OR the presence of an `admins/{uid}` Firestore
            // document (single source of truth). No client-side email whitelist is used.
            // isAdminUser() forces a fresh token refresh internally, so the
            // `admin: true` claim is guaranteed present for Firestore requests.
            const hasAdminClaim = await isAdminUser(user);

            if (!hasAdminClaim) {
                console.warn('[AdminLogin] User is NOT an admin. Email:', email);
                showNotification('Access denied: you do not have Super Admin privileges.', 'error');
                // Sign out so a non-admin user isn't left authenticated
                try {
                    await firebaseAuthInstance.signOut();
                } catch (e) {}
                return;
            }

            console.log('[AdminLogin] Admin authorization granted for:', email);

            // Clear any other sessions
            clearNonAdminSessions();
            clearProviderAndClientSessions();
            hideProviderAndClientDashboards();

            // Set admin session with auth metadata
            currentAdmin = {
                username: email,
                uid: user.uid,
                loginTime: new Date().toISOString(),
                authenticatedVia: 'firebase-auth',
                hasAdminClaim: hasAdminClaim
            };
            window.currentAdmin = currentAdmin;

            // Update the global auth instance reference if the modular bootstrap exposed one
            if (window.firebaseDbInstance) {
                firebaseDbInstance = window.firebaseDbInstance;
            }

            // Force an immediate admin bootstrap so production loads data on the first
            // admin page render instead of waiting for a later client/provider write event.
            try {
                if (typeof window.bootstrapAdminData === 'function') {
                    await window.bootstrapAdminData();
                }
                // Refresh the panels immediately so the admin dashboard is populated
                // before the listener subscription begins.
                if (typeof renderAdminStats === 'function') renderAdminStats();
                if (typeof renderAdminClients === 'function') renderAdminClients();
                if (typeof renderAdminProviders === 'function') renderAdminProviders();
                if (typeof renderAdminOrders === 'function') renderAdminOrders();
                if (typeof renderAdminSubscriptions === 'function') renderAdminSubscriptions();
            } catch (adminBootstrapErr) {
                console.warn('[AdminLogin] Immediate admin bootstrap failed:', adminBootstrapErr && adminBootstrapErr.message ? adminBootstrapErr.message : adminBootstrapErr);
            }

            // Hide login, show dashboard
            const adminLoginSection = document.getElementById('adminLoginSection');
            const adminDashboard = document.getElementById('adminDashboard');

            if (adminLoginSection) adminLoginSection.classList.add('hidden');
            if (adminDashboard) adminDashboard.classList.remove('hidden');

            // Clear form
            const form = document.getElementById('adminLogForm');
            if (form) form.reset();

            showNotification('Welcome Super Admin!', 'success');

            // Update header badge
            updateHeaderUserBadge();

            // Render initial dashboard data
            renderAdminStats();

            // ============ RELOAD SETTINGS FROM FIRESTORE ============
            // After admin login, explicitly reload the Firestore settings caches
            // and re-render the settings panel + footer so the latest saved
            // values are immediately available (fixes "settings revert on refresh").
            try {
                if (typeof window.__loadSiteSettingsFromFirestore === 'function') {
                    await window.__loadSiteSettingsFromFirestore();
                }
                if (typeof window.__loadSubscriptionRatesFromFirestore === 'function') {
                    await window.__loadSubscriptionRatesFromFirestore();
                }
                if (typeof window.__loadSubscriptionPlansFromFirestore === 'function') {
                    await window.__loadSubscriptionPlansFromFirestore();
                }
                // Re-render the footer with the freshly loaded text
                if (typeof renderFooter === 'function') renderFooter();
                // Set up (or refresh) the real-time settings listener so any
                // subsequent saves update all browsers instantly.
                if (typeof window.__setupSiteSettingsListener === 'function') {
                    window.__setupSiteSettingsListener();
                }
            } catch (settingsErr) {
                console.warn('[AdminLogin] Error reloading settings from Firestore:', settingsErr.message);
            }
            // ============ END RELOAD SETTINGS FROM FIRESTORE ============

            // ============ SET UP ADMIN REAL-TIME LISTENERS ============
            // This ensures the admin dashboard auto-refreshes when new clients or
            // providers register, without requiring a manual page refresh.
            // firebase-integration.js exposes window.setupAdminRealTimeListeners
            // which uses onSnapshot() to listen for changes to the 'clients' and
            // 'providers' Firestore collections. When changes are detected, it
            // automatically calls bootstrapAdminData() + refreshAllAdminPanels().
            try {
                if (typeof window.setupAdminRealTimeListeners === 'function') {
                    console.log('[AdminLogin] Setting up admin real-time listeners...');
                    window.setupAdminRealTimeListeners();
                } else {
                    console.warn('[AdminLogin] setupAdminRealTimeListeners not available (firebase-integration.js not loaded yet)');
                    // Retry after a short delay to allow firebase-integration.js to finish loading
                    setTimeout(() => {
                        if (typeof window.setupAdminRealTimeListeners === 'function') {
                            console.log('[AdminLogin] Setting up admin real-time listeners (delayed)');
                            window.setupAdminRealTimeListeners();
                        } else {
                            console.warn('[AdminLogin] setupAdminRealTimeListeners still not available after delay');
                        }
                    }, 2000);
                }
            } catch (listenerErr) {
                console.warn('[AdminLogin] Error setting up admin real-time listeners:', listenerErr);
            }
            // ============ END ADMIN REAL-TIME LISTENERS ============
        })
        .catch(function(error) {
            console.error('[AdminLogin] Firebase Auth sign-in failed:', error.code, error.message);

            let message = 'Invalid admin credentials. Please try again.';
            if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                message = 'Invalid email or password. Please try again.';
            } else if (error.code === 'auth/invalid-email') {
                message = 'Please enter a valid email address.';
            } else if (error.code === 'auth/too-many-requests') {
                message = 'Too many failed login attempts. Please try again later.';
            } else if (error.code === 'auth/network-request-failed') {
                message = 'Network error. Please check your connection and try again.';
            } else if (error.code === 'auth/user-disabled') {
                message = 'This account has been disabled.';
            } else if (error.code === 'auth/firebase-app-check-token-is-invalid' || /firebase-app-check-token-is-invalid/i.test(String(error.message || ''))) {
                message = 'Firebase App Check is blocking this login. Disable App Check enforcement in Firebase Console or register a valid App Check key for this site, then try again.';
            }

            showNotification(message, 'error');
        })
        .finally(function() {
            // Re-enable the login button
            if (loginBtn) {
                loginBtn.disabled = false;
                loginBtn.textContent = 'Login';
            }
            window.__lastAdminLoginAttempt = 0;
        });
}

// Make admin login function global
window.performAdminLogin = performAdminLogin;

function initClientLogout() {
    const btn = document.getElementById('clientLogoutBtn');
    if (!btn) return;

    btn.addEventListener('click', () => {
        // Clear in-memory session first
        window.__forceGuestModeUntilNextLogin = true;
        currentClient = null;
        window.__lastKnownAuthUid = '';
        window.__lastKnownAuthEmail = '';
        hideClientDashboard();

        // CRITICAL FIX: Terminate the persisted Firebase Auth session so the
        // user does NOT appear logged in after a page refresh. Firebase Auth
        // persists the session by default; without signOut() the onAuthStateChanged
        // handler re-restores the session on reload.
        const doSignOut = () => {
            if (typeof window.authService !== 'undefined' && typeof window.authService.signOut === 'function') {
                return window.authService.signOut();
            }
            if (typeof hybridSignOut === 'function') {
                return hybridSignOut();
            }
            return Promise.resolve();
        };

        Promise.resolve(window.disablePushNotificationPermission?.('client'))
            .catch(() => false)
            .then(doSignOut)
            .then(() => {
            // Ensure header badge reflects logged-out state
            if (typeof updateHeaderUserBadge === 'function') updateHeaderUserBadge();
            if (typeof renderSearchResults === 'function') renderSearchResults();
            showNotification('Logged out successfully!', 'success');
            }).catch((err) => {
            console.warn('[ClientLogout] Firebase signOut error:', err);
            if (typeof updateHeaderUserBadge === 'function') updateHeaderUserBadge();
            if (typeof renderSearchResults === 'function') renderSearchResults();
            showNotification('Could not complete Firebase sign out. Please try again.', 'error');
            });
    });
}
function showClientDashboard() {
    if (typeof window.initClientFavorites === 'function') {
        window.initClientFavorites(getCurrentAuthUser());
    }
    const loginSection = document.getElementById('clientLoginSection');
    const dashboard = document.getElementById('clientDashboard');
    const userInfo = document.getElementById('userInfo');
    const authToggle = loginSection ? loginSection.querySelector('.auth-toggle') : null;

    if (loginSection) loginSection.classList.add('hidden');
    if (authToggle) authToggle.classList.add('hidden');
    if (dashboard) {
        dashboard.classList.remove('hidden');
        // Show welcome message
        const welcomeEl = dashboard.querySelector('h2');
        if (welcomeEl) welcomeEl.textContent = `My Orders - Welcome, ${currentClient.name}!`;
        // Render client order history
        renderClientOrders();
    }
    if (userInfo) {
        userInfo.innerHTML = `<span class="user-badge">👤 ${currentClient.name}</span>`;
    }

    // Update header user badge
    updateHeaderUserBadge();

    // Re-render search results with proper restrictions
    renderSearchResults();

    // Check for pending reward offers and show badge
    setTimeout(checkClientRewardsBadge, 600);
    // Pre-load suggestions so the tab is ready (slight delay to let orders load first)
    setTimeout(() => {
        if (typeof renderMealSuggestions === 'function') renderMealSuggestions();
    }, 1500);

    // Initialise social gifting features when we have a signed-in client
    setTimeout(() => {
        const db = window.firebaseDbRaw;
        const authUser = window.firebaseAuthInstance ? window.firebaseAuthInstance.currentUser : null;
        if (db && authUser && currentClient && typeof window.initSocialFeatures === 'function') {
            window.initSocialFeatures(db, authUser, currentClient);
        }
    }, 800);
}
// Render Client Order History
/**
 * Bridge Firestore `orders` data into the local `clientMessages` array.
 * Called by firebase-integration.js after each real-time orders snapshot
 * so the DOM (client orders, provider orders, admin orders) updates instantly
 * across all open instances/apps without requiring a full page refresh.
 *
 * @param {Array} ordersData - orders pulled from the Firestore `orders` collection
 */
window.bridgeFirestoreOrdersToLocal = function (ordersData) {
    try {
        if (!Array.isArray(ordersData) || ordersData.length === 0) return;

        const existingIds = new Set(clientMessages.map(o => String(o && o.id != null ? o.id : '')));
        let changed = false;

        ordersData.forEach(order => {
            if (!order || order.id == null) return;
            const orderId = String(order.id);
            if (existingIds.has(orderId)) return;

            // Normalize Firestore Timestamp createdAt -> ISO string for display
            const normalized = { ...order };
            if (normalized.createdAt && typeof normalized.createdAt === 'object' && typeof normalized.createdAt.toDate === 'function') {
                normalized.createdAt = normalized.createdAt.toDate().toISOString();
            }
            // Ensure statusHistory entries have ISO timestamps too
            if (Array.isArray(normalized.statusHistory)) {
                normalized.statusHistory = normalized.statusHistory.map(entry => {
                    if (entry && entry.timestamp && typeof entry.timestamp === 'object' && typeof entry.timestamp.toDate === 'function') {
                        return { ...entry, timestamp: entry.timestamp.toDate().toISOString() };
                    }
                    return entry;
                });
            }

            clientMessages.push(normalized);
            existingIds.add(orderId);
            changed = true;
        });

        if (changed) {
            // Re-render all order views so the new order(s) appear immediately
            if (typeof renderClientOrders === 'function') renderClientOrders();
            if (typeof renderProviderOrders === 'function') renderProviderOrders();
            if (typeof isAdminLoggedIn === 'function' && isAdminLoggedIn() && typeof renderAdminOrders === 'function') {
                renderAdminOrders();
            }
        }
    } catch (e) {
        console.warn('[Bridge] Failed to merge Firestore orders into local array:', e.message);
    }
};

function buildOrderPaymentBadge(order, providerView = false) {
    const isCard = String(order.paymentMethod || '').toLowerCase() === 'card';
    const isPaid = String(order.paymentStatus || '').toLowerCase() === 'paid';
    const background = isCard ? '#dbeafe' : (isPaid ? '#dcfce7' : '#fef3c7');
    const color = isCard ? '#1d4ed8' : (isPaid ? '#166534' : '#92400e');
    const method = isCard ? 'Card' : `Cash${providerView ? '' : ` on ${escapeHtml(order.orderType || 'pickup')}`}`;
    const payment = isPaid ? 'Paid' : (isCard ? 'Awaiting payment' : 'Not paid');
    return `<span style="background:${background};color:${color};border-radius:4px;padding:1px 7px;font-size:0.82em;font-weight:600;">${method} · ${payment}</span>`;
}

function renderClientOrders() {
    const ordersContainer = document.getElementById('clientOrders');
    if (!ordersContainer || !currentClient) return;

    // Get messages sent by this client
    const clientOrders = clientMessages.filter(msg =>
        msg.clientEmail && msg.clientEmail.toLowerCase() === currentClient.email.toLowerCase()
    );


    if (clientOrders.length === 0) {
        ordersContainer.innerHTML = `
            <div class="empty-state">
                <p>No orders yet. Search for meals and contact providers to place orders!</p>
            </div>
        `;
        return;
    }

    // Sort by newest first
    clientOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const displayCount = paginationDisplay.clientOrders;
    const visibleOrders = clientOrders.slice(0, displayCount);
    const firestoreHasMore = window.__firestorePaginationState && window.__firestorePaginationState['orders'] && window.__firestorePaginationState['orders'].hasMore;
    const hasMore = clientOrders.length > displayCount || firestoreHasMore;

    ordersContainer.innerHTML = visibleOrders.map(order => {
        const statusInfo = getStatusInfo(order.status || 'pending');
        const statusBadge = `
            <span class="order-status-badge" style="background: ${statusInfo.bg}; color: ${statusInfo.color};">
                ${statusInfo.text}
            </span>
        `;

        const progressTracker = buildProgressTracker(order.status);

        const cancelNote = getCancellationPolicyNote(order.status || 'pending');

        const canCancel = canCancelOrder(order.status || 'pending');
        const cancelButton = canCancel && (order.status === 'pending' || order.status === 'confirmed')
            ? `
                <button class="order-cancel-btn" onclick="cancelOrder('${order.id}')">Cancel Order</button>
            `
            : '';

        // Check if order is completed and not yet reviewed

        const isCompleted = order.status === 'completed';
        const isReviewed = order.reviewed === true;
        const mealComponentsSummary = formatOrderMealComponents(order.mealComponents);

        // Cancellation note (policy)
        const cancellationNote = getCancellationPolicyNote(order.status || 'pending');


        // Build review button or reviewed badge
        let reviewButton = '';
        if (isCompleted && !isReviewed) {
            reviewButton = `
                <button class="leave-review-btn" onclick="openClientOrderReviewModal('${order.id}')">
                    Leave a Review
                </button>
            `;
        } else if (isReviewed) {
            const orderReview = reviews.find(r => r.orderId === order.id);
            if (orderReview) {
                reviewButton = `
                    <div class="reviewed-badge">
                        <span class="reviewed-rating">You rated: ${'★'.repeat(orderReview.rating)}${'☆'.repeat(5 - orderReview.rating)}</span>
                        ${orderReview.comment ? `<span class="reviewed-comment">"${escapeHtml(orderReview.comment)}"</span>` : ''}
                    </div>
                `;
            }
        }

        return `
            <div class="order-card">
                <div class="order-header">
                    <span class="order-provider">${escapeHtml(order.providerName)}</span>
                    <span class="order-date">${formatDateTime(order.createdAt)}</span>
                </div>
                <div class="order-food">${escapeHtml(order.foodItem)}</div>
                <div class="order-details">
                    ${order.fulfillmentDate ? `<span>${escapeHtml(t('fulfillmentDate', 'Scheduled for'))}: ${escapeHtml(order.fulfillmentDate)}</span>` : ''}
                    <span>Portion: ${escapeHtml(order.portion)}</span>
                    ${mealComponentsSummary ? `<span>${escapeHtml(mealComponentsSummary)}</span>` : ''}
                    <span>Drink: ${escapeHtml(order.drink || 'Standard')}</span>
                    <span>Qty: ${Number(order.quantity || 1)}</span>
                    <span>Unit: $${Number(order.unitPrice || 0).toFixed(2)}</span>
                    <span>Total: $${Number(order.price || 0).toFixed(2)}</span>
                    <span>Type: ${escapeHtml(order.orderType)}</span>
                </div>
                <div class="order-payment-badge" style="margin:4px 0;">${buildOrderPaymentBadge(order)}</div>
                <div class="order-message">${escapeHtml(order.message)}</div>
                <div class="order-status">
                    ${statusBadge}
                </div>
        ${progressTracker}
                ${cancelButton}
                <div class="order-cancel-note" style="margin-top:6px; font-size:12px; color:#666;">${escapeHtml(cancelNote)}</div>
                ${reviewButton}
                ${typeof window.favoriteProviderButton === 'function' ? window.favoriteProviderButton(order) : ''}
            </div>
        `;
}).join('');

    if (hasMore) {
        ordersContainer.innerHTML += `
            <div style="text-align:center; margin-top:16px;">
                <button class="load-more-btn" onclick="loadMoreClientOrders()" style="padding:8px 24px;background:#f97316;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:0.9em;">
                    Load More Orders
                </button>
                <div style="font-size:0.78em;color:#888;margin-top:4px;">Showing ${visibleOrders.length} of ${clientOrders.length}${firestoreHasMore ? '+' : ''}</div>
            </div>
        `;
    }
}


// Open Client Order Review Modal
function openClientOrderReviewModal(orderId) {
    const order = clientMessages.find(msg => String(msg.id) === String(orderId));
    if (!order || !currentClient) return;

    selectedOrderReviewRating = 0;
    selectedReviewOrderId = orderId;

    // Set provider name
    document.getElementById('reviewProviderName').textContent = order.providerName;

    // Reset stars
    const starElements = document.querySelectorAll('#clientOrderRatingStars .star');
    starElements.forEach(star => {
        star.classList.remove('active');
    });

    // Reset comment
    document.getElementById('clientOrderReviewComment').value = '';

    // Show modal
    const modal = document.getElementById('clientOrderReviewModal');
    modal.style.display = 'flex';

    // Setup star click handlers
    setupClientOrderRatingStars();
}

// Close Client Order Review Modal
function closeClientOrderReviewModal() {
    document.getElementById('clientOrderReviewModal').style.display = 'none';
    selectedOrderReviewRating = 0;
    selectedReviewOrderId = null;
}

// Setup Client Order Rating Stars
function setupClientOrderRatingStars() {
    const starElements = document.querySelectorAll('#clientOrderRatingStars .star');
    starElements.forEach(star => {
        star.onclick = function() {
            selectedOrderReviewRating = parseInt(this.dataset.rating);

            // Update visual state
            starElements.forEach(s => {
                const rating = parseInt(s.dataset.rating);
                s.classList.toggle('active', rating <= selectedOrderReviewRating);
            });
        };
    });
}

// Submit Client Order Review
async function submitClientOrderReview() {
    if (!selectedOrderReviewRating || !selectedReviewOrderId || !currentClient) {
        alert('Please select a rating before submitting.');
        return;
    }

    const comment = document.getElementById('clientOrderReviewComment').value.trim();
    const order = clientMessages.find(msg => String(msg.id) === String(selectedReviewOrderId));

    if (!order || String(order.status || '').toLowerCase() !== 'completed') {
        alert('Order not found.');
        return;
    }

    try {
        const result = await callProtectedFunction('submitOrderReview', {
            orderId: selectedReviewOrderId,
            rating: selectedOrderReviewRating,
            comment
        });
        if (!result || !result.review) throw new Error('Review service returned no review.');
        reviews.push(result.review);
        order.reviewed = true;
    } catch (error) {
        console.error('[Review] Server-authoritative submission failed:', error);
        alert(error.message || 'Your review could not be saved. Please try again.');
        return;
    }

    // Close modal
    closeClientOrderReviewModal();

    // Re-render orders to show review
    renderClientOrders();

    // Show success message
    alert('Thank you for your review! Your feedback helps other clients.');
}

// Make client order review functions global
window.openClientOrderReviewModal = openClientOrderReviewModal;
window.closeClientOrderReviewModal = closeClientOrderReviewModal;
window.submitClientOrderReview = submitClientOrderReview;

// Format date/time for display
function formatDateTime(isoString) {
    if (!isoString) return '';
    // Handle Firestore Timestamp objects (they have a toDate() method)
    let date;
    if (typeof isoString === 'object' && typeof isoString.toDate === 'function') {
        date = isoString.toDate();
    } else {
        date = new Date(isoString);
    }
    // Guard against invalid dates
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
}
function hideClientDashboard() {
    const loginSection = document.getElementById('clientLoginSection');
    const dashboard = document.getElementById('clientDashboard');
    const userInfo = document.getElementById('userInfo');
    const authToggle = loginSection ? loginSection.querySelector('.auth-toggle') : null;
    const clientRegisterForm = document.getElementById('clientRegisterForm');
    const clientLoginForm = document.getElementById('clientLoginForm');

    if (loginSection) loginSection.classList.remove('hidden');
    if (authToggle) authToggle.classList.remove('hidden');
    if (clientRegisterForm) clientRegisterForm.classList.remove('hidden');
    if (clientLoginForm) clientLoginForm.classList.add('hidden');
    if (dashboard) dashboard.classList.add('hidden');
    if (userInfo) userInfo.innerHTML = '';

    // Update header user badge to show logged out status
    updateHeaderUserBadge();

    // Re-render search results with restricted view for unregistered users
    renderSearchResults();
}
// Provider Auth Toggle
function initProviderAuthToggle() {
    const toggleBtns = document.querySelectorAll('#providerLoginSection .auth-toggle-btn');
    const registerForm = document.getElementById('providerRegisterForm');
    const loginForm = document.getElementById('providerLoginForm');

    toggleBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            const authType = btn.dataset.auth;

            // Update toggle buttons
            toggleBtns.forEach(b => b.classList.remove('active'));
            btn.classList.add('active');

            // Show/hide forms
            if (authType === 'register') {
                registerForm.classList.remove('hidden');
                loginForm.classList.add('hidden');
                // Render reCAPTCHA widget now that the register form is visible
            } else {
                registerForm.classList.add('hidden');
                loginForm.classList.remove('hidden');
            }
        });
    });
}
// Provider Registration
function initProviderRegisterForm() {
    const form = document.getElementById('providerRegForm');
    if (!form) return;

    // Render the reCAPTCHA widget for this form (when visible)
    initProviderLocationAssist({
        timezoneInputId: 'providerRegTimezone',
        latitudeInputId: 'providerRegLatitude',
        longitudeInputId: 'providerRegLongitude',
        locationButtonId: 'providerRegUseCurrentLocationBtn'
    });

    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        if (!enforcePhase1ControlledBeta('provider-registration')) {
            return;
        }

        const type = document.getElementById('providerType').value;
        const name = document.getElementById('providerRegName').value.trim();
        const phone = document.getElementById('providerRegPhone').value.trim();
        const email = document.getElementById('providerRegEmail').value.trim();
        const password = document.getElementById('providerRegPassword').value;
        const address = document.getElementById('providerRegAddress').value.trim();
        const timezoneEl = document.getElementById('providerRegTimezone');
        const latitudeEl = document.getElementById('providerRegLatitude');
        const longitudeEl = document.getElementById('providerRegLongitude');
        const countryEl = document.getElementById('providerCountry');
        const countryRaw = countryEl ? countryEl.value : '';
        const country = countryRaw ? normalizeCountryCode(countryRaw) : '';

        if (!name || !phone || !email || !password) {
            showNotification('Please fill in all required fields', 'error');
            return;
        }

        if (!country) {
            showNotification('Please select your country so clients can find you', 'error');
            countryEl.focus();
            return;
        }

        const launchContext = getProviderLaunchContext(country);
        if (!launchContext.policy.enabled) {
            const launchReason = launchContext.policy.reason || t('countryRestriction', 'This country is currently restricted for launch.');
            showNotification(`${launchReason} ${t('supportedCountries', 'Supported countries')}: ${getSupportedLaunchCountries().join(', ')}`, 'error');
            return;
        }

        const passwordError = getPasswordStrengthError(password);
        if (passwordError) {
            showNotification(passwordError, 'error');
            return;
        }

        const normalizedPhone = normalizePhoneNumber(phone, country);
        if (!normalizedPhone || !isValidE164Phone(normalizedPhone)) {
            showNotification(t('phoneValidation', 'Phone number must use international format.'), 'error');
            return;
        }
        const timezoneSeed = timezoneEl && timezoneEl.value ? timezoneEl.value : detectProviderTimezone();
        const timezone = normalizeProviderTimezone(timezoneSeed || launchContext.timezone);
        const normalizedAddress = normalizeInternationalAddress(address, country);
        const coordinates = normalizeProviderCoordinates(
            latitudeEl ? latitudeEl.value : null,
            longitudeEl ? longitudeEl.value : null
        );

        // Check if provider already exists before creating.
        // Uses the same authenticated-aware helper as clients: browserStorage is always
        // checked, Firestore is only queried when a user IS authenticated, and any
        // permission error is caught so it NEVER aborts registration.
        // createUserWithEmailAndPassword is the authoritative duplicate check.
        const providerEmailExists = await checkEmailExistsBeforeRegister('providers', email);
        if (providerEmailExists) {
            showNotification('Email already registered. Please login.', 'error');
            // Switch to login
            document.querySelector('#providerLoginSection .auth-toggle-btn[data-auth="login"]').click();
            return;
        }

// Register new provider (trim password to remove whitespace)
        // IMPORTANT: New providers are registered as PENDING and need Super Admin approval
        const acceptsCustomCheckbox = document.getElementById('providerAcceptsCustomRequests');
        const acceptsCustomRequests = acceptsCustomCheckbox ? acceptsCustomCheckbox.checked : false;

// Get default subscription
        const subscription = getDefaultSubscription();

        // Get selected subscription plan from registration form (dropdown instead of radio buttons)
        const selectedPlanEl = document.getElementById('providerPlan');
        const selectedPlan = selectedPlanEl ? selectedPlanEl.value : 'free';

        // Validate plan selection (free plan can only be used once)
        const planValidation = validatePlanSelection(selectedPlan, email, providers);
        if (!planValidation.valid) {
            showNotification(planValidation.error, 'error');
            return;
        }

        // Update subscription with selected plan
        subscription.plan = selectedPlan;

        const provider = {
            id: window.generateUUID(),
            type,
            name,
            phone: normalizedPhone,
            email: email.toLowerCase(),
            // IMPORTANT: Production stores no passwords in browserStorage/offline mode.
            password: '',
            address: normalizedAddress.fullAddress,
            addressComponents: normalizedAddress,
            country,
            currency: launchContext.currency,
            timezone,
            providerCoordinates: coordinates,
            launchCountryPolicy: {
                enabled: launchContext.policy.enabled,
                allowProviderPayouts: launchContext.policy.allowProviderPayouts,
                evaluatedAt: new Date().toISOString()
            },
            acceptsCustomRequests: acceptsCustomRequests,
            createdAt: new Date().toISOString(),
            // Subscription fields
            subscriptionStatus: subscription.status,
            subscriptionPlan: subscription.plan,
            subscriptionStart: subscription.startDate,
            subscriptionEnd: subscription.endDate,
            subscriptionApprovedBy: subscription.approvedBy,
            subscriptionApprovedAt: subscription.approvedAt,
            subscriptionRejectionReason: subscription.rejectionReason,
            subscriptionSuspendedReason: subscription.suspendedReason,
            subscriptionNotes: subscription.notes,
            verificationStatus: 'pending',
            accountStatus: 'pending',
            payoutStatus: 'pending'
        };
        applyProviderState(provider);

        // Try Firebase Auth first if available
        if (FIREBASE_AUTH_ENABLED && firebaseAuthInstance && firebaseDbInstance) {
            console.log('[ProviderRegister] Creating Firebase Auth user...');

            firebaseAuthInstance.createUserWithEmailAndPassword(email, password)
                .then(function(userCredential) {
                    // Firebase user created successfully
                    console.log('[ProviderRegister] Firebase user created:', userCredential.user.uid);

                    // CRITICAL FIX: Use the Firebase UID as the canonical provider ID.
                    // This keeps browserStorage session, Firestore doc ID, and in-memory
                    // session all in sync, so provider login/session restore works.
                    provider.id = userCredential.user.uid;
                    provider.uid = userCredential.user.uid;

                    // Create provider profile server-side first so Firestore triggers
                    // (welcome/admin emails) run from a canonical backend write path.
                    return Promise.resolve()
                        .then(function() {
                            return registerPendingProviderViaBackend(provider);
                        })
                        .then(function() {
                            return sendVerificationEmailForUser(userCredential.user);
                        })
                        .then(function() {
                            if (typeof hybridSignOut === 'function') {
                                return hybridSignOut().catch(function() {});
                            }
                        })
                        .then(function() {
                            return userCredential;
                        });
                })
                .then(function(userCredential) {
                    // Continue with local registration after Firebase success
                    completeProviderRegistration(provider, form);
                    if (userCredential && userCredential.user) {
                        showNotification('Verification email sent. Verify your email, then log in to finish provider onboarding.', 'success');
                    }
                })
                .catch(function(error) {
                    console.log('[ProviderRegister] Firebase registration failed:', error.code, error.message);

                    // If email is already in use, show a clear message
                    if (error.code === 'auth/email-already-in-use') {
                        return resumePendingProviderRegistration(email, password, provider, form)
                            .catch(function(resumeError) {
                                console.error('[ProviderRegister] Could not resume pending registration:', resumeError);
                                showNotification(`Registration could not be resumed: ${resumeError.message || 'Please contact support.'}`, 'error');
                            });
                    }
                    showNotification('Registration failed. Please try again in a moment.', 'error');
                });
        } else {
            console.error('[ProviderRegister] Firebase is unavailable. Blocking registration to avoid partial account creation.');
            showNotification('Registration is temporarily unavailable. Please try again shortly.', 'error');
        }
    });
}

// Complete provider registration - saves to browserStorage + calls backend when available
// FIXED: Strips plaintext password, deduplicates by email, uses auth UID as doc ID
async function completeProviderRegistration(provider, form) {
    // SECURITY: Strip password before any storage - NEVER store plaintext passwords client-side
    delete provider.password;

    // REQUIRED by firestore.rules create rule: role identifies this doc as a provider.
    // Without this, Firestore denies the create and the provider never appears for Super Admin approval.
    provider.role = 'provider';
    provider.subscriptionStatus = provider.subscriptionStatus || 'pending';
    provider.verificationStatus = provider.verificationStatus || 'pending';
    provider.accountStatus = provider.accountStatus || 'pending';
    provider.payoutStatus = provider.payoutStatus || 'pending';
    applyProviderState(provider);

    // Update or add to providers array (deduplicate by email)
    const existingIdx = providers.findIndex(p => p.email && p.email.toLowerCase() === provider.email.toLowerCase());
    if (existingIdx !== -1) {
        providers[existingIdx] = provider;
    } else {
        providers.push(provider);
    }

// IMPORTANT: Do NOT auto-login after registration
    // Show pending approval message instead

    // Send welcome email notification to provider
    try {
        if (typeof sendProviderWelcomeEmail === 'function') {
            sendProviderWelcomeEmail({
                name: provider.name,
                email: provider.email
            });
        }
    } catch (e) {
        console.warn('[Email] Could not send welcome email:', e);
    }

    form.reset();
    initProviderLocationAssist({
        timezoneInputId: 'providerRegTimezone',
        latitudeInputId: 'providerRegLatitude',
        longitudeInputId: 'providerRegLongitude',
        locationButtonId: 'providerRegUseCurrentLocationBtn'
    });
    showNotification('Registration submitted! Your request is pending approval by Super Admin. You will be notified once approved.', 'success');

    // Switch to login tab and show message
    document.querySelector('#providerLoginSection .auth-toggle-btn[data-auth="login"]').click();
    const providerLoginEmail = document.getElementById('providerLogEmail');
    const providerLoginPassword = document.getElementById('providerLogPassword');
    if (providerLoginEmail) providerLoginEmail.value = provider.email || '';
    if (providerLoginPassword) providerLoginPassword.focus();

    // Show info about pending status
    const loginForm = document.getElementById('providerLoginForm');
    if (loginForm) {
        const existingMessage = loginForm.querySelector('.pending-message');
        if (!existingMessage) {
            const messageDiv = document.createElement('div');
            messageDiv.className = 'pending-message';
            messageDiv.style.cssText = 'background:#fef9e7;border:1px solid #f39c12;padding:15px;margin-top:15px;border-radius:8px;color:#d68910;';
            messageDiv.innerHTML = '<p><strong>⏳ Your registration is pending approval.</strong></p><p>Super Admin will review your request. Please check back later or contact support.</p><p><small>Email verification can take a few minutes. Check Inbox/Spam for the verification email before attempting provider login.</small></p>';
            loginForm.insertBefore(messageDiv, loginForm.firstChild);
        }
    }

    // Log out provider if was logged in
    currentProvider = null;
    hideProviderDashboard();
}
// Provider Login
// Firebase-only (no browserStorage password verification)
function initProviderLoginForm() {
    const form = document.getElementById('providerLogForm');
    if (!form) return;

    form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Delegate to performProviderLogin() which handles Firebase Auth,
        // subscription status checks, and session setup.
        performProviderLogin();

        return false;
    });
}
function initProviderLogout() {
    const btn = document.getElementById('providerLogoutBtn');
    if (!btn) return;

    btn.addEventListener('click', () => {
        // Clear in-memory session first
        currentProvider = null;
        hideProviderDashboard();

        // CRITICAL FIX: Terminate the persisted Firebase Auth session so the
        // user does NOT appear logged in after a page refresh.
        const doSignOut = () => {
            if (typeof window.authService !== 'undefined' && typeof window.authService.signOut === 'function') {
                return window.authService.signOut();
            }
            if (typeof hybridSignOut === 'function') {
                return hybridSignOut();
            }
            return Promise.resolve();
        };

        Promise.resolve(window.disablePushNotificationPermission?.('provider'))
            .catch(() => false)
            .then(doSignOut)
            .then(() => {
            // Ensure header badge reflects logged-out state
            if (typeof updateHeaderUserBadge === 'function') updateHeaderUserBadge();
            showNotification('Logged out successfully!', 'success');
            }).catch((err) => {
            console.warn('[ProviderLogout] Firebase signOut error:', err);
            showNotification('Logged out successfully!', 'success');
            });
    });
}
function showProviderDashboard() {
    const loginSection = document.getElementById('providerLoginSection');
    const dashboard = document.getElementById('providerDashboard');
    const userInfo = document.getElementById('userInfo');
    const authToggle = loginSection ? loginSection.querySelector('.auth-toggle') : null;

    // Check subscription access before showing dashboard
    const accessCheck = checkProviderSubscriptionAccess(currentProvider);
    if (!accessCheck.allowed) {
        showNotification(accessCheck.reason, 'error');
        hideProviderDashboard();
        return;
    }

    if (loginSection) loginSection.classList.add('hidden');
    if (authToggle) authToggle.classList.add('hidden');
    if (dashboard) {
        dashboard.classList.remove('hidden');
        if (typeof renderProviderProfileView === 'function') {
            renderProviderProfileView();
        }
        // Auto-populate provider info in hidden fields for menu items
        if (currentProvider) {
            const providerNameInput = document.getElementById('providerName');
            const providerPhoneInput = document.getElementById('providerPhone');
            const providerEmailInput = document.getElementById('providerEmail');
            const providerAddressInput = document.getElementById('providerAddress');

            if (providerNameInput) providerNameInput.value = currentProvider.name;
            if (providerPhoneInput) providerPhoneInput.value = currentProvider.phone;
            if (providerEmailInput) providerEmailInput.value = currentProvider.email;
            if (providerAddressInput) providerAddressInput.value = currentProvider.address || '';

            // Render menu items, orders, and reviews for this provider
            renderMenuList();
            renderProviderOrders();
            renderProviderReviews();
            startProviderOrderCounterTicker();
            ensureProviderBusinessSettings().catch(() => {});
        }
    }
    if (userInfo) {
        userInfo.innerHTML = `<span class="user-badge">🍽️ ${currentProvider.name}</span>`;
    }
}
function hideProviderDashboard() {
    if (typeof window.clearProviderInsights === 'function') window.clearProviderInsights();
    const loginSection = document.getElementById('providerLoginSection');
    const dashboard = document.getElementById('providerDashboard');
    const userInfo = document.getElementById('userInfo');
    const authToggle = loginSection ? loginSection.querySelector('.auth-toggle') : null;
    const providerRegisterForm = document.getElementById('providerRegisterForm');
    const providerLoginForm = document.getElementById('providerLoginForm');

    if (loginSection) loginSection.classList.remove('hidden');
    if (authToggle) authToggle.classList.remove('hidden');
    if (providerRegisterForm) providerRegisterForm.classList.remove('hidden');
    if (providerLoginForm) providerLoginForm.classList.add('hidden');
    if (dashboard) dashboard.classList.add('hidden');
    if (userInfo) userInfo.innerHTML = '';
    if (typeof resetProviderOrderCounters === 'function') resetProviderOrderCounters();
}
// Tab Navigation
function initTabs() {
    try {
        const tabBtns = document.querySelectorAll('.tab-btn');
        const tabContents = document.querySelectorAll('.tab-content');

        // Safety check - return if no tabs found
        if (!tabBtns || tabBtns.length === 0) {
            console.log('[Tabs] No tab buttons found, skipping initTabs');
            return;
        }

        console.log('[Tabs] Found', tabBtns.length, 'tab buttons, initializing...');

        tabBtns.forEach(btn => {
            // Remove any existing click handlers to prevent duplicates
            const newBtn = btn.cloneNode(true);
            btn.parentNode.replaceChild(newBtn, btn);

            newBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const targetTab = newBtn.dataset.tab;
                console.log('[Tabs] Tab clicked:', targetTab);

                try {
                    // Update active button
                    document.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
                    newBtn.classList.add('active');

                    // Update active content
                    document.querySelectorAll('.tab-content').forEach(content => {
                        content.classList.remove('active');
                        content.style.display = 'none';
                        if (content.id === targetTab + '-tab') {
                            content.classList.add('active');
                            content.style.display = '';
                        }
                    });

                    // Render reCAPTCHA widgets when switching to client/provider registration tabs
                    if (targetTab === 'clients' || targetTab === 'providers') {
                    }
                } catch (err) {
                    console.error('[Tabs] Error switching tab:', err);
                }
            });
        });

        console.log('[Tabs] Tab navigation initialized successfully');
    } catch (err) {
        console.error('[Tabs] Error initializing tabs:', err);
    }
}
// Photo Upload Handler
function initPhotoUpload() {
    const photoInput = document.getElementById('foodPhoto');
    const photoDataInput = document.getElementById('foodPhotoData');
    const photoPreview = document.getElementById('photoPreview');

    // Some layouts/pages don’t include the upload controls.
    if (!photoInput || !photoDataInput || !photoPreview) return;

    const MAX_BYTES = 2 * 1024 * 1024; // 2 MB

    function setPhotoStatus(msg, isError) {
        const el = document.getElementById('photoUploadStatus');
        if (!el) return;
        el.textContent = msg;
        el.style.color = isError ? '#c0392b' : '#555';
    }

    photoInput.addEventListener('change', (e) => {
        const file = e.target.files[0];

        // Clear previous state
        photoDataInput.value = '';
        photoInput._pendingPhotoFile = null;
        setPhotoStatus('');

        if (!file) return;

        if (file.size > MAX_BYTES) {
            photoPreview.innerHTML = '';
            photoInput.value = '';
            setPhotoStatus(`Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Please choose an image under 2 MB.`, true);
            return;
        }

        // Show a local preview immediately (does not touch Firestore).
        // The actual Storage upload happens on form submit.
        const reader = new FileReader();
        reader.onload = function(event) {
            const dataUrl = event.target.result;
            photoPreview.innerHTML = `<img src="${dataUrl}" alt="Meal Preview" style="max-width: 200px; max-height: 150px; border-radius: 8px;">`;
            // Keep the file reference so the submit handler can upload to Storage.
            photoInput._pendingPhotoFile = file;
            // Store the temporary data URL as a fallback in case Storage is unavailable.
            photoDataInput.value = dataUrl;
            setPhotoStatus('Photo selected. It will be uploaded when you save the menu item.', false);
        };
        reader.readAsDataURL(file);
    });
}
// State for portion sizes and drink options
var currentPortionSizes = [];
var currentDrinkOptions = [];
var currentMealComponents = [];

// Menu Form - For Restaurants/Caterers
function initMenuForm() {
    const form = document.getElementById('menuForm');
    if (!form) return;

    // Initialize portion sizes/drink options functionality
    initPortionSizes();
    initDrinkOptions();
    initMealComponents();

    form.addEventListener('submit', (e) => {
        e.preventDefault();
        _submitMenuForm(form);
    });
}

/**
 * Async handler for the menu form submit.
 * Uploads any newly selected photo to Firebase Storage first,
 * then saves the item with only the download URL (never base64) to Firestore.
 */
async function _submitMenuForm(form) {
    // Get the provider's country from their profile
    const providerCountryRaw = currentProvider ? currentProvider.country : '';
    const providerCountry = providerCountryRaw ? normalizeCountryCode(providerCountryRaw) : '';
    const providerLaunchContext = getProviderLaunchContext(providerCountry || 'US');
    if (!providerLaunchContext.policy.enabled) {
        const launchReason = providerLaunchContext.policy.reason || t('countryRestriction', 'This country is currently restricted for launch.');
        showNotification(`${launchReason} ${t('supportedCountries', 'Supported countries')}: ${getSupportedLaunchCountries().join(', ')}`, 'error');
        return;
    }

    // Get portion sizes from the hidden input
    const portionSizesData = document.getElementById('portionSizesData').value;
    let portions = [];
    try {
        portions = portionSizesData ? JSON.parse(portionSizesData) : [];
    } catch (e) {
        portions = [];
    }

    const mealComponentsData = document.getElementById('mealComponentsData');
    const mealComponents = mealComponentsData
        ? parseMealComponents(mealComponentsData.value || JSON.stringify(currentMealComponents))
        : currentMealComponents;

    const editingItemIdEl = document.getElementById('editingItemId');
    const editingItemId = editingItemIdEl && editingItemIdEl.value ? String(editingItemIdEl.value).trim() : null;

    if (!editingItemId && currentProvider) {
        const accessCheck = checkProviderSubscriptionAccess(currentProvider);
        if (!accessCheck.canAddMenuItems) {
            showNotification(accessCheck.reason || 'You cannot add menu items with your current subscription status.', 'error');
            return;
        }
    }

    const providerPlanId = currentProvider ? currentProvider.subscriptionPlan || 'free' : 'free';
    const canonicalProviderId = currentProvider ? String(currentProvider.uid || currentProvider.id || '').trim() : (window.firebaseAuthInstance && window.firebaseAuthInstance.currentUser ? String(window.firebaseAuthInstance.currentUser.uid).trim() : '');
    const planMax = getPlanMaxItems(providerPlanId);
    const existingProviderMenuCount = currentProvider ? menuItems.filter((item) => {
        const itemProviderId = String(item && (item.providerId || item.providerUid || item.uid) || '').trim();
        return itemProviderId && canonicalProviderId && itemProviderId.toLowerCase() === canonicalProviderId.toLowerCase();
    }).length : 0;

    if (!editingItemId && currentProvider && planMax !== 999999 && existingProviderMenuCount >= planMax) {
        showNotification(`Your current plan allows up to ${planMax} menu items. Remove an existing item or upgrade to a higher plan before adding more.`, 'error');
        return;
    }

    const drinkOptionsData = document.getElementById('drinkOptionsData');
    const drinkOptions = drinkOptionsData ? parseDrinkOptions(drinkOptionsData.value || JSON.stringify(currentDrinkOptions)) : currentDrinkOptions;
    const legacyDrinksField = document.getElementById('drinks');
    const legacyDrinkText = legacyDrinksField ? legacyDrinksField.value.trim() : '';
    const drinkSummary = formatDrinkOptionsForDisplay(drinkOptions) || legacyDrinkText;

    // ── Photo: upload to Firebase Storage; only store download URL in Firestore ──
    const photoInput = document.getElementById('foodPhoto');
    const photoDataInput = document.getElementById('foodPhotoData');

    function setPhotoStatus(msg, isError) {
        const el = document.getElementById('photoUploadStatus');
        if (!el) return;
        el.textContent = msg;
        el.style.color = isError ? '#c0392b' : '#555';
    }

    // Determine the photo value to save:
    //   1. If a new file was selected → upload to Storage → use download URL
    //   2. If editing and no new file was chosen → keep the existing URL as-is
    //   3. If nothing → empty string
    let photoValue = '';
    const pendingFile = photoInput && photoInput._pendingPhotoFile;
    const existingPhotoUrl = photoDataInput ? photoDataInput.value : '';

    if (pendingFile) {
        // New file selected — upload it
        const providerUid = currentProvider ? (currentProvider.uid || currentProvider.id) : null;
        const tempItemId = editingItemId || window.generateUUID();

        if (typeof window.firebaseUploadPhoto === 'function' && providerUid) {
            const submitBtn = form.querySelector('button[type="submit"]');
            const originalBtnText = submitBtn ? submitBtn.textContent : '';
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.textContent = 'Uploading photo…';
            }
            setPhotoStatus('Uploading photo to storage…', false);

            try {
                const dataUrl = photoDataInput.value; // set by FileReader in initPhotoUpload
                const uploadResult = await window.firebaseUploadPhoto({
                    base64Data: dataUrl,
                    category: 'menu-items',
                    ownerId: providerUid,
                    itemId: tempItemId,
                });
                photoValue = uploadResult.downloadURL;
                setPhotoStatus('Photo uploaded successfully.', false);
                console.log('[Photo Upload] Stored to Storage:', uploadResult.storagePath);
            } catch (uploadErr) {
                console.warn('[Photo Upload] Storage upload failed, using base64 fallback:', uploadErr.message);
                // Fallback: use base64 data URL so the save still completes, but warn the user
                photoValue = photoDataInput.value;
                setPhotoStatus('Photo upload to cloud failed — saved locally as fallback. Please try again.', true);
            } finally {
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.textContent = originalBtnText;
                }
            }
        } else {
            // Storage not available yet — use base64 as a safe fallback
            photoValue = existingPhotoUrl;
            console.warn('[Photo Upload] window.firebaseUploadPhoto not available; storing base64 as fallback.');
        }
    } else if (existingPhotoUrl && existingPhotoUrl.startsWith('http')) {
        // Edit flow: no new file chosen, keep the existing Storage URL
        photoValue = existingPhotoUrl;
    }
    // If existingPhotoUrl is a base64 string left over from a legacy item, drop it
    // so the document doesn't grow past Firestore's 1 MB limit.

    const providerUidForMenuItem = currentProvider ? String(currentProvider.uid || currentProvider.id || '').trim() : (window.firebaseAuthInstance && window.firebaseAuthInstance.currentUser ? String(window.firebaseAuthInstance.currentUser.uid).trim() : '');
    const providerTimezoneForMenuItem = currentProvider && currentProvider.timezone
        ? normalizeProviderTimezone(currentProvider.timezone)
        : providerLaunchContext.timezone;
    const availabilityStatus = document.getElementById('availabilityStatus')
        ? document.getElementById('availabilityStatus').value
        : 'today';
    const availabilityDate = getMenuAvailabilityDate(availabilityStatus, providerTimezoneForMenuItem);
    const menuItem = {
        id: window.generateUUID(),
        providerId: providerUidForMenuItem || (currentProvider ? currentProvider.id : null),
        providerType: currentProvider ? currentProvider.type : 'Restaurant',
        providerName: document.getElementById('providerName').value.trim(),
        providerPhone: document.getElementById('providerPhone').value.trim(),
        providerEmail: document.getElementById('providerEmail').value.trim(),
        providerAddress: document.getElementById('providerAddress').value.trim(),
        country: providerCountry, // Save the provider's country
        currency: providerLaunchContext.currency || 'USD',
        providerTimezone: providerTimezoneForMenuItem,
        providerCoordinates: currentProvider && currentProvider.providerCoordinates ? currentProvider.providerCoordinates : null,
        mealType: document.getElementById('mealType') ? document.getElementById('mealType').value : '',
        availabilityStatus,
        availabilityDate,
        foodName: document.getElementById('foodName').value.trim(),
        foodDesc: document.getElementById('foodDesc').value.trim(),
        ingredients: document.getElementById('ingredients').value.trim(),
        mealComponents: mealComponents,
        drinks: drinkSummary || legacyDrinkText,
        drinkOptions: drinkOptions,
        photo: photoValue,
        price: parseFloat(document.getElementById('price').value),
        servingsLeft: parseInt(document.getElementById('servingsLeft').value),
        is247: document.getElementById('is247').checked,
        openTime: document.getElementById('openTime').value,
        closeTime: document.getElementById('closeTime').value,
        afterHours: document.getElementById('afterHours').checked,
        offersDelivery: document.getElementById('offersDelivery').checked,
        offersPickup: document.getElementById('offersPickup').checked,
        dailySpecial: document.getElementById('dailySpecial') ? document.getElementById('dailySpecial').checked : false,
        specialPrice: (function() {
            const el = document.getElementById('specialPrice');
            if (!el) return null;
            const v = parseFloat(el.value);
            return (!isNaN(v) && v > 0) ? v : null;
        })(),
        specialNote: sanitizeText((document.getElementById('specialNote') || {value: ''}).value, 150),
        specialEndTime: (function() {
            const el = document.getElementById('specialEndTime');
            if (!el || !el.value) return null;
            const d = new Date(el.value);
            return isNaN(d.getTime()) ? null : d.toISOString();
        })(),
        portions: portions,
        // Active/Inactive toggle - new items are active by default
        isActive: true,
        createdAt: new Date().toISOString()
    };

    console.log('=== MENU ITEM CREATED ===');
    console.log('Menu Item ID:', menuItem.id);
    console.log('Provider ID:', menuItem.providerId);
    console.log('Provider Name:', menuItem.providerName);

    // CROSS-BROWSER SYNC FIX:
    // - Persist to Firestore via window.firebaseSaveMenuItem (writes to the
    //   `menuItems` collection that all browsers read), with a firestoreService
    //   fallback if the modular integration's provider role isn't ready yet.

    // Sanitize all user-supplied text fields before any storage write.
    // This strips HTML tags and caps field lengths so nothing malicious
    // reaches Firestore, regardless of how it is rendered later.
    const sanitizedItem = sanitizeMenuItemFields(menuItem);

    // Check subscription access before allowing menu item addition
    if (!editingItemId && currentProvider) {
        const accessCheck = checkProviderSubscriptionAccess(currentProvider);
        if (!accessCheck.canAddMenuItems) {
            showNotification(accessCheck.reason || 'You cannot add menu items with your current subscription status.', 'error');
            return;
        }
    }

    if (editingItemId) {
        sanitizedItem.id = editingItemId;
        const existingIdx = menuItems.findIndex(mi => String(mi.id) === String(editingItemId));
        if (existingIdx !== -1) {
            menuItems[existingIdx] = { ...menuItems[existingIdx], ...sanitizedItem };
        } else {
            menuItems.push(sanitizedItem);
        }
    } else {
        sanitizedItem.id = window.generateUUID();
        menuItems.push(sanitizedItem);
    }

    console.log('=== MENU ITEM SAVED ===');
    console.log('Menu Item ID:', sanitizedItem.id, '(editing:', !!editingItemId, ')');
    console.log('Provider ID:', sanitizedItem.providerId);
    console.log('Provider Name:', sanitizedItem.providerName);

    // The modular provider save path is authoritative for menu item writes.
    // Do not also call the legacy saveMenuItems() helper here; it triggers a
    // duplicate Firestore .set() and produces the permission-denied noise seen in
    // the console even when the primary save succeeded.
    if (typeof window.firebaseSaveMenuItem === 'function') {
        window.firebaseSaveMenuItem(sanitizedItem).catch(e => {
            if (e && e.code === 'MENU_LIMIT_EXCEEDED') {
                // Roll back the optimistic local push — the item was never saved to Firestore.
                menuItems = menuItems.filter(mi => String(mi.id) !== String(sanitizedItem.id));
                showNotification(
                    `⚠️ Menu item limit reached for your plan (${e.currentCount}/${e.limit} items). ` +
                    `Upgrade your subscription to add more items.`,
                    'error'
                );
                return;
            }
            console.warn('[Firestore] firebaseSaveMenuItem failed, falling back to firestoreService:', e.message);
            if (firestoreService.initialized && firestoreService.db) {
                firestoreService.save('menuItems', sanitizedItem.id, sanitizedItem).catch(e2 => {
                    console.error('[Firestore] Error saving menu item (fallback):', e2);
                });
            }
        });
    } else if (firestoreService.initialized && firestoreService.db) {
        firestoreService.save('menuItems', sanitizedItem.id, sanitizedItem).catch(e => {
            console.error('[Firestore] Error saving menu item:', e);
        });
    }

    form.reset();
    document.getElementById('editingItemId').value = '';
    document.getElementById('openTime').value = '08:00';
    document.getElementById('closeTime').value = '22:00';
    document.getElementById('offersPickup').checked = true;
    document.getElementById('dailySpecial').checked = false;
    const spPriceReset = document.getElementById('specialPrice');
    const spNoteReset = document.getElementById('specialNote');
    const spEndReset = document.getElementById('specialEndTime');
    if (spPriceReset) spPriceReset.value = '';
    if (spNoteReset) spNoteReset.value = '';
    if (spEndReset) spEndReset.value = '';
    toggleSpecialFields();
    document.getElementById('photoPreview').innerHTML = '';
    setPhotoStatus('');
    if (photoInput) photoInput._pendingPhotoFile = null;
    document.getElementById('drinkName').value = '';
    document.getElementById('drinkPrice').value = '';
    document.getElementById('drinkOptionsData').value = '[]';
    currentMealComponents = [];
    document.getElementById('mealComponentsData').value = '[]';
    document.getElementById('mealComponentName').value = '';
    document.getElementById('mealComponentPrice').value = '';
    document.getElementById('mealComponentType').value = 'included';
    document.getElementById('mealComponentPrice').disabled = true;
    renderMealComponentsList();

    // Clear portion sizes
    currentPortionSizes = [];
    currentDrinkOptions = [];
    document.getElementById('portionSizesData').value = '[]';
    document.getElementById('portionList').innerHTML = '';
    renderDrinkOptionsList();

    // Reset submit button text after an edit
    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.textContent = 'Add to Menu';

    renderMenuList();
    renderSearchResults();

    showNotification(editingItemId ? 'Menu item updated!' : 'Menu item added successfully!', 'success');
}
// ==================== PORTION SIZES FUNCTIONALITY ====================
// Collection to store portion sizes for the current menu item
function normalizeDrinkOption(entry) {
    if (!entry) return null;

    if (typeof entry === 'string') {
        const text = entry.trim();
        if (!text) return null;

        const priceMatch = text.match(/\$?\s*(\d+(?:\.\d{1,2})?)\s*$/);
        if (priceMatch) {
            const name = text.replace(priceMatch[0], '').replace(/[-:]/g, '').trim();
            return {
                name: name || text,
                price: Number(priceMatch[1]) || 0
            };
        }

        return { name: text, price: 0 };
    }

    if (typeof entry === 'object') {
        const name = (entry.name || entry.label || entry.drink || '').trim();
        if (!name) return null;
        return {
            name,
            price: Number(entry.price || entry.amount || 0) || 0
        };
    }

    return null;
}

function parseMealComponents(components) {
    let parsed = components;
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch (e) {
            return [];
        }
    }
    if (!Array.isArray(parsed)) return [];
    return parsed.map(component => {
        if (!component || typeof component !== 'object') return null;
        const id = String(component.id || '').trim();
        const name = String(component.name || '').trim();
        const type = component.type === 'extra' ? 'extra' : 'included';
        const price = Number(component.price || 0);
        if (!id || !name || !Number.isFinite(price) || price < 0) return null;
        return { id, name, type, price: type === 'extra' ? Number(price.toFixed(2)) : 0 };
    }).filter(Boolean).slice(0, 25);
}

function initMealComponents() {
    const addButton = document.getElementById('addMealComponentBtn');
    const nameInput = document.getElementById('mealComponentName');
    const typeSelect = document.getElementById('mealComponentType');
    const priceInput = document.getElementById('mealComponentPrice');
    const dataInput = document.getElementById('mealComponentsData');
    if (!addButton || !nameInput || !typeSelect || !priceInput || !dataInput) return;
    currentMealComponents = Array.isArray(currentMealComponents) ? currentMealComponents : [];

    const updatePriceField = () => {
        const isExtra = typeSelect.value === 'extra';
        priceInput.disabled = !isExtra;
        if (!isExtra) priceInput.value = '';
    };
    typeSelect.addEventListener('change', updatePriceField);
    addButton.addEventListener('click', () => {
        const name = sanitizeText(nameInput.value, 80);
        const type = typeSelect.value === 'extra' ? 'extra' : 'included';
        const price = type === 'extra' ? Number(priceInput.value) : 0;
        if (!name) {
            showNotification('Enter a component name.', 'error');
            return;
        }
        if (type === 'extra' && (!Number.isFinite(price) || price < 0)) {
            showNotification('Enter a valid extra price.', 'error');
            return;
        }
        if (currentMealComponents.length >= 25) {
            showNotification('A meal can have up to 25 components.', 'error');
            return;
        }
        if (currentMealComponents.some(component => component.name.toLowerCase() === name.toLowerCase())) {
            showNotification('That component is already listed.', 'error');
            return;
        }
        currentMealComponents.push({
            id: window.generateUUID(),
            name,
            type,
            price: type === 'extra' ? Number(price.toFixed(2)) : 0
        });
        nameInput.value = '';
        priceInput.value = '';
        dataInput.value = JSON.stringify(currentMealComponents);
        renderMealComponentsList();
    });
    updatePriceField();
    renderMealComponentsList();
}

function renderMealComponentsList() {
    const list = document.getElementById('mealComponentList');
    const dataInput = document.getElementById('mealComponentsData');
    if (!list || !dataInput) return;
    dataInput.value = JSON.stringify(currentMealComponents);
    list.innerHTML = currentMealComponents.length
        ? currentMealComponents.map((component, index) => `
            <div class="meal-component-item">
                <span>${escapeHtml(component.name)} · ${component.type === 'extra' ? 'Extra - ' + formatCurrency(component.price, currentProvider && currentProvider.country || 'US') : 'Included'}</span>
                <button type="button" data-remove-meal-component="${index}" aria-label="Remove ${escapeHtml(component.name)}">Remove</button>
            </div>
        `).join('')
        : '<div class="empty-state">No meal components added.</div>';
    list.querySelectorAll('[data-remove-meal-component]').forEach(button => {
        button.addEventListener('click', () => {
            currentMealComponents.splice(Number(button.dataset.removeMealComponent), 1);
            renderMealComponentsList();
        });
    });
}

function formatOrderMealComponents(components) {
    if (!Array.isArray(components) || components.length === 0) return '';
    const included = components.filter(component => component.type !== 'extra').map(component => component.name);
    const extras = components.filter(component => component.type === 'extra').map(component => {
        const price = Number(component.price || 0);
        return price > 0 ? `${component.name} (+$${price.toFixed(2)})` : component.name;
    });
    return [included.length ? `Included: ${included.join(', ')}` : '', extras.length ? `Extras: ${extras.join(', ')}` : '']
        .filter(Boolean).join(' · ');
}

function parseDrinkOptions(drinks) {
    if (!drinks) return [];

    if (Array.isArray(drinks)) {
        return drinks.map(normalizeDrinkOption).filter(Boolean);
    }

    if (typeof drinks === 'object') {
        return Object.values(drinks).map(normalizeDrinkOption).filter(Boolean);
    }

    if (typeof drinks !== 'string') return [];

    const trimmed = drinks.trim();
    if (!trimmed) return [];

    try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
            return parsed.map(normalizeDrinkOption).filter(Boolean);
        }
    } catch (e) {
        // Fall back to string parsing below.
    }

    return trimmed
        .split(/[\r\n,]+/)
        .map(item => item.trim())
        .filter(Boolean)
        .map(normalizeDrinkOption)
        .filter(Boolean);
}

function formatDrinkOptionsForDisplay(options) {
    const sanitized = Array.isArray(options) ? options : parseDrinkOptions(options);
    return sanitized.map(option => {
        const name = option && option.name ? option.name : option;
        const price = Number(option && option.price !== undefined ? option.price : 0) || 0;
        return price > 0 ? `${name} - $${price.toFixed(2)}` : name;
    }).filter(Boolean).join(', ');
}

function initDrinkOptions() {
    const addBtn = document.getElementById('addDrinkOptionBtn');
    const drinkNameInput = document.getElementById('drinkName');
    const drinkPriceInput = document.getElementById('drinkPrice');
    const drinkList = document.getElementById('drinkList');
    const drinkDataInput = document.getElementById('drinkOptionsData');

    if (!addBtn || !drinkNameInput || !drinkPriceInput || !drinkList || !drinkDataInput) {
        return;
    }

    const storedDrinkData = drinkDataInput.value;
    if (storedDrinkData) {
        try {
            currentDrinkOptions = JSON.parse(storedDrinkData);
        } catch (e) {
            currentDrinkOptions = parseDrinkOptions(storedDrinkData);
        }
    }

    addBtn.addEventListener('click', () => {
        const name = drinkNameInput.value.trim();
        const price = parseFloat(drinkPriceInput.value) || 0;

        if (!name) {
            showNotification('Please enter a drink name', 'error');
            return;
        }

        const alreadyExists = currentDrinkOptions.some(option => String(option.name || '').toLowerCase() === name.toLowerCase());
        if (alreadyExists) {
            showNotification('This drink is already on the list', 'error');
            return;
        }

        currentDrinkOptions.push({ name, price });
        drinkDataInput.value = JSON.stringify(currentDrinkOptions);
        drinkNameInput.value = '';
        drinkPriceInput.value = '';
        renderDrinkOptionsList();
    });

    renderDrinkOptionsList();
}

function renderDrinkOptionsList() {
    const drinkList = document.getElementById('drinkList');
    const drinkDataInput = document.getElementById('drinkOptionsData');
    if (!drinkList || !drinkDataInput) return;

    const options = Array.isArray(currentDrinkOptions) ? currentDrinkOptions : [];
    drinkDataInput.value = JSON.stringify(options);

    if (options.length === 0) {
        drinkList.innerHTML = '<div class="empty-state">No drink options added yet.</div>';
        return;
    }

    drinkList.innerHTML = options.map((option, index) => `
        <div class="drink-item">
            <span class="drink-name">${escapeHtml(option.name)}</span>
            <span class="drink-price">${Number(option.price || 0) > 0 ? '$' + Number(option.price).toFixed(2) : 'No extra charge'}</span>
            <button type="button" class="remove-drink-btn" onclick="removeDrinkOption(${index})">×</button>
        </div>
    `).join('');
}

function removeDrinkOption(index) {
    currentDrinkOptions.splice(index, 1);
    const drinkDataInput = document.getElementById('drinkOptionsData');
    if (drinkDataInput) {
        drinkDataInput.value = JSON.stringify(currentDrinkOptions);
    }
    renderDrinkOptionsList();
}

// Initialize portion sizes functionality
function initPortionSizes() {
    const addPortionBtn = document.getElementById('addPortionBtn');
    const portionNameInput = document.getElementById('portionName');
    const portionPriceInput = document.getElementById('portionPrice');
    const portionList = document.getElementById('portionList');
    const portionDataInput = document.getElementById('portionSizesData');

    // Safety check - return if element doesn't exist
    if (!addPortionBtn || !portionNameInput || !portionPriceInput || !portionList || !portionDataInput) {
        console.log('Portion sizes elements not found - skipping initialization');
        return;
    }

    // Add portion button click handler
    addPortionBtn.addEventListener('click', () => {
        const name = portionNameInput.value.trim();
        const price = parseFloat(portionPriceInput.value) || 0;

        if (!name) {
            showNotification('Please enter a portion name', 'error');
            return;
        }

        // Check for duplicate names
        if (currentPortionSizes.some(p => p.name.toLowerCase() === name.toLowerCase())) {
            showNotification('This portion size already exists', 'error');
            return;
        }

        // Add portion to collection
        currentPortionSizes.push({ name, price });

        // Update hidden input
        portionDataInput.value = JSON.stringify(currentPortionSizes);

        // Clear inputs
        portionNameInput.value = '';
        portionPriceInput.value = '';

        // Render portion list
        renderPortionList();

        showNotification('Portion size added!', 'success');
    });
}
// Render portion list display
function renderPortionList() {
    const portionList = document.getElementById('portionList');
    if (!portionList) return;

    if (currentPortionSizes.length === 0) {
        portionList.innerHTML = '';
        return;
    }

    portionList.innerHTML = currentPortionSizes.map((portion, index) => `
        <div class="portion-item">
            <span class="portion-name">${escapeHtml(portion.name)}</span>
            <span class="portion-price">${portion.price > 0 ? '+$' + portion.price.toFixed(2) : 'Base price'}</span>
            <button type="button" class="remove-portion-btn" onclick="removePortion(${index})">×</button>
        </div>
    `).join('');
}
// Remove portion from list
function removePortion(index) {
    currentPortionSizes.splice(index, 1);

    const portionDataInput = document.getElementById('portionSizesData');
    if (portionDataInput) {
        portionDataInput.value = JSON.stringify(currentPortionSizes);
    }

    renderPortionList();
}
// Make removePortion global
window.removePortion = removePortion;
// ==================== MEAL TYPE FILTER ====================
// Global variable to track the currently selected meal type filter
let currentMealTypeFilter = '';

function ensureSearchPerformanceTools() {
    if (scheduleMainSearchRender && scheduleClientSearchRender) return;

    const perf = window.WTEUiPerformance || {};
    const schedulerFactory = typeof perf.createCoalescedRenderScheduler === 'function'
        ? perf.createCoalescedRenderScheduler
        : function fallbackSchedulerFactory(renderer) {
            return function scheduleFallback() { renderer({}); };
        };

    if (!searchMetricTracker && typeof perf.createUiMetricTracker === 'function') {
        searchMetricTracker = perf.createUiMetricTracker({ source: 'search-rendering' });
    }

    function wrapRenderer(renderer, surface) {
        return function(meta = {}) {
            const start = (window.performance && typeof window.performance.now === 'function')
                ? window.performance.now()
                : Date.now();
            renderer();
            const end = (window.performance && typeof window.performance.now === 'function')
                ? window.performance.now()
                : Date.now();
            if (searchMetricTracker && typeof searchMetricTracker.record === 'function') {
                searchMetricTracker.record('search_render', {
                    surface: surface,
                    reason: meta.reason || 'scheduled',
                    durationMs: Number((end - start).toFixed(2)),
                    menuItems: Array.isArray(menuItems) ? menuItems.length : 0
                });
            }
        };
    }

    scheduleMainSearchRender = schedulerFactory(
        wrapRenderer(renderSearchResults, 'public'),
        { label: 'public-search', minDelayMs: 80, maxDelayMs: 250 }
    );
    scheduleClientSearchRender = schedulerFactory(
        wrapRenderer(renderClientSearchResults, 'client'),
        { label: 'client-search', minDelayMs: 80, maxDelayMs: 250 }
    );
}

function requestMainSearchRender(reason, immediate = false) {
    ensureSearchPerformanceTools();
    scheduleMainSearchRender({ reason: reason || 'unknown', immediate: immediate === true });
}

function requestClientSearchRender(reason, immediate = false) {
    ensureSearchPerformanceTools();
    scheduleClientSearchRender({ reason: reason || 'unknown', immediate: immediate === true });
}

function trackSearchInteraction(surface, action) {
    ensureSearchPerformanceTools();
    if (searchMetricTracker && typeof searchMetricTracker.record === 'function') {
        searchMetricTracker.record('search_interaction', {
            surface: surface,
            action: action,
            menuItems: Array.isArray(menuItems) ? menuItems.length : 0
        });
    }
}

// Set the meal type filter (called by the filter buttons)
function setMealFilter(mealType) {
    currentMealTypeFilter = mealType || '';

    // Update active state on all meal type filter buttons
    document.querySelectorAll('.meal-type-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.mealType === currentMealTypeFilter);
    });

    // Re-render both search result sets
    requestMainSearchRender('meal-filter');
    requestClientSearchRender('meal-filter');
}

// Make setMealFilter globally accessible (used by onclick in HTML)
window.setMealFilter = setMealFilter;

// Search Functionality - FIXED VERSION
function initSearch() {
    console.log('[Search] Initializing main search...');
    ensureSearchPerformanceTools();

    const searchBtn = document.getElementById('searchBtn');
    const searchInput = document.getElementById('searchInput');
    const availabilityFilter = document.getElementById('availabilityFilter');
    const searchCountrySelect = document.getElementById('searchCountry');
    const providerFilter = document.getElementById('providerFilter');
    const resultsContainer = document.getElementById('searchResults');

    // Attach click handler to search button - use onclick directly for immediate response
    if (searchBtn) {
        // Get fresh reference after any cloning
        const btn = document.getElementById('searchBtn');
        if (btn) {
            // Remove all existing onclick handlers by resetting the property
            btn.onclick = null;

            // Add click handler DIRECTLY - most reliable method
            btn.onclick = function(e) {
                // Prevent any default behavior
                if (e) {
                    e.preventDefault ? e.preventDefault() : null;
                    e.stopPropagation ? e.stopPropagation() : null;
                }
                console.log('[Search] Main search button clicked');
                trackSearchInteraction('public', 'button-click');
                // Also show feedback
                const resultsDiv = document.getElementById('searchResults');
                if (resultsDiv) {
                    resultsDiv.innerHTML = '<div class="loading">Searching...</div>';
                }
                requestMainSearchRender('search-button-click', true);
                return false;
            };

            console.log('[Search] Main search button handler attached');
        }
    } else {
        console.log('[Search] WARNING: searchBtn element not found');
    }

    // Attach Enter key handler to search input
    if (searchInput) {
        searchInput.addEventListener('keypress', function(e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                console.log('[Search] Enter pressed in search input');
                trackSearchInteraction('public', 'enter-key');
                requestMainSearchRender('search-enter-key', true);
                return false;
            }
        });
    }

    // Filter change - availability
    if (availabilityFilter) {
        availabilityFilter.addEventListener('change', function() {
            trackSearchInteraction('public', 'availability-filter');
            requestMainSearchRender('availability-filter-change');
        });
    }

    // Filter change - country (for travel search)
    if (searchCountrySelect) {
        searchCountrySelect.addEventListener('change', function() {
            trackSearchInteraction('public', 'country-filter');
            requestMainSearchRender('country-filter-change');
        });
    }

    // Filter change - provider
    if (providerFilter) {
        providerFilter.addEventListener('change', function() {
            trackSearchInteraction('public', 'provider-filter');
            requestMainSearchRender('provider-filter-change');
        });
    }

    // Populate provider filter dropdown
    updateProviderFilter();

    // Initial render of search results
    setTimeout(() => {
        if (resultsContainer) {
            requestMainSearchRender('init-main-search', true);
        }
    }, 100);

    console.log('[Search] Main search initialized');
}
// Populate Provider Filter Dropdown
function updateProviderFilter() {
    const providerFilter = document.getElementById('providerFilter');
    if (!providerFilter) return;

    // Preserve current selection
    const selectedProvider = providerFilter.value;

    // Get unique provider names
    const providers = [...new Set(menuItems.map(item => item.providerName))].sort();

    // Build options HTML
    providerFilter.innerHTML = '<option value="">' + escapeHtml(t('allProviders', 'All Restaurants/Caterers')) + '</option>' +
        providers.map(provider => `<option value="${escapeHtml(provider)}">${escapeHtml(provider)}</option>`).join('');

    // Restore selection if it still exists
    if (selectedProvider) {
        providerFilter.value = selectedProvider;
    }
}
// ==================== CLIENT SEARCH FUNCTIONALITY ====================
// Initialize Client Search
function initClientSearch() {
    console.log('[ClientSearch] Initializing client dashboard search...');
    ensureSearchPerformanceTools();

    // Use clientDashboard-prefixed IDs ONLY (matching HTML)
    const searchBtn = document.getElementById('clientDashboardSearchBtn');
    if (!searchBtn) {
        console.log('[ClientSearch] WARNING: Client search button not found');
        return;
    }

    // Get fresh reference
    const btn = document.getElementById('clientDashboardSearchBtn');
    if (btn) {
        // Remove any existing handlers
        btn.onclick = null;

        // Attach click handler DIRECTLY - most reliable method
        btn.onclick = function(e) {
            if (e) {
                e.preventDefault ? e.preventDefault() : null;
                e.stopPropagation ? e.stopPropagation() : null;
            }
            console.log('[ClientSearch] Client dashboard search button clicked');
            trackSearchInteraction('client', 'button-click');
            // Also show feedback
            const resultsDiv = document.getElementById('clientDashboardSearchResults');
            if (resultsDiv) {
                resultsDiv.innerHTML = '<div class="loading">Searching...</div>';
            }
            requestClientSearchRender('client-search-button-click', true);
            return false;
        };

        console.log('[ClientSearch] Client dashboard search button handler attached');
    }

    // Search on Enter key - use clientDashboard-prefixed ID
    const searchInput = document.getElementById('clientDashboardSearchInput');
    if (searchInput) {
        searchInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                console.log('[ClientSearch] Enter pressed in client search input');
                trackSearchInteraction('client', 'enter-key');
                requestClientSearchRender('client-search-enter-key', true);
                return false;
            }
        });
    }

    // Filter change - availability - use clientDashboard-prefixed ID
    const availabilityFilter = document.getElementById('clientDashboardAvailabilityFilter');
    if (availabilityFilter) {
        availabilityFilter.addEventListener('change', () => {
            trackSearchInteraction('client', 'availability-filter');
            requestClientSearchRender('client-availability-filter-change');
        });
    }

    // Filter change - country (for travel search) - use clientDashboard-prefixed ID
    const searchCountrySelect = document.getElementById('clientDashboardSearchCountry');
    if (searchCountrySelect) {
        searchCountrySelect.addEventListener('change', () => {
            trackSearchInteraction('client', 'country-filter');
            requestClientSearchRender('client-country-filter-change');
        });
    }

    // Filter change - provider - use clientDashboard-prefixed ID
    const providerFilter = document.getElementById('clientDashboardProviderFilter');
    if (providerFilter) {
        providerFilter.addEventListener('change', () => {
            trackSearchInteraction('client', 'provider-filter');
            requestClientSearchRender('client-provider-filter-change');
        });
    }

    // Populate provider filter dropdown
    updateClientProviderFilter();

    // Initial render
    setTimeout(() => {
        requestClientSearchRender('init-client-search', true);
    }, 100);

    console.log('[ClientSearch] Client search initialized');
}
// Update Client Provider Filter Dropdown
function updateClientProviderFilter() {
    // Use clientDashboard-prefixed ID to match HTML
    const providerFilter = document.getElementById('clientDashboardProviderFilter');
    if (!providerFilter) return;

    // Preserve current selection
    const selectedProvider = providerFilter.value;

    // Get unique provider names
    const uniqueProviders = [...new Set(menuItems.map(item => item.providerName))].sort();

    // Build options HTML
    providerFilter.innerHTML = '<option value="">' + escapeHtml(t('allProviders', 'All Restaurants/Caterers')) + '</option>' +
        uniqueProviders.map(provider => `<option value="${escapeHtml(provider)}">${escapeHtml(provider)}</option>`).join('');

    // Restore selection if it still exists
    if (selectedProvider) {
        providerFilter.value = selectedProvider;
    }
}
// Render Client Search Results
window.mergeFavoriteMenuItems = function mergeFavoriteMenuItems(items) {
    for (const item of items) {
        const index = menuItems.findIndex(existing => String(existing.id) === String(item.id));
        if (index === -1) menuItems.push(item);
        else menuItems[index] = item;
    }
    window.menuItems = menuItems;
};

function renderClientSearchResults(options = {}) {
    const isFavoriteMenu = Array.isArray(options.items);
    // Use clientDashboard-prefixed IDs to match HTML
    const searchInput = document.getElementById('clientDashboardSearchInput');
    const searchInputVal = !isFavoriteMenu && searchInput ? searchInput.value.toLowerCase().trim() : '';

    const searchCountrySelect = document.getElementById('clientDashboardSearchCountry');
    const searchCountry = !isFavoriteMenu && searchCountrySelect ? searchCountrySelect.value : '';

    const availabilityFilterSelect = document.getElementById('clientDashboardAvailabilityFilter');
    const availabilityFilter = !isFavoriteMenu && availabilityFilterSelect ? availabilityFilterSelect.value : 'all';

    const providerFilterSelect = document.getElementById('clientDashboardProviderFilter');
    const providerFilter = !isFavoriteMenu && providerFilterSelect ? providerFilterSelect.value : '';

    const resultsContainer = document.getElementById(options.containerId || 'clientDashboardSearchResults');

    // SAFETY CHECK: Don't render if container doesn't exist (e.g., on admin page)
    if (!resultsContainer) {
        console.log('[ClientSearch] Results container not found - skipping render');
        return;
    }

    if (!isFavoriteMenu) updateClientProviderFilter();

    // Check if user is logged in (registered) - strict validation
    const isLoggedIn = isUserLoggedIn();

    // Get user's registered country (for prioritizing results)
    const userCountry = currentClient ? currentClient.country : '';

    let filtered = (isFavoriteMenu ? options.items : menuItems).filter(item => {
        // Only show items that are marked as active (default to true if not set)
        const isActive = item.isActive !== false;
        if (!isActive) return false;
        const itemCountry = normalizeCountryCode(item.country || item.providerCountry || 'US');
        if (!isSupportedLaunchCountry(itemCountry)) return false;

        const matchesSearch = !searchInputVal ||
            item.foodName.toLowerCase().includes(searchInputVal) ||
            item.providerName.toLowerCase().includes(searchInputVal) ||
            item.foodDesc.toLowerCase().includes(searchInputVal);

        if (!matchesSearch) return false;

        // Filter by country (if selected in search)
        if (searchCountry && item.country !== searchCountry) return false;

        if (providerFilter && item.providerName !== providerFilter) return false;

        // Filter by meal type (if a meal type filter is selected)
        if (!isFavoriteMenu && currentMealTypeFilter && item.mealType !== currentMealTypeFilter) return false;

        if (availabilityFilter === 'today' || availabilityFilter === 'tomorrow') {
            return getMenuAvailabilityStatus(item) === availabilityFilter;
        } else if (availabilityFilter === '247') {
            return item.is247 === true;
        } else if (availabilityFilter === 'open') {
            return isCurrentlyOpen(item);
        } else if (availabilityFilter === 'afterHours') {
            return item.afterHours === true && !isCurrentlyOpen(item);
        } else if (availabilityFilter === 'dailySpecial') {
            return isDailySpecial(item);
        }

        return true;
    });

    filtered = rankSearchResults(filtered, {
        searchInput: searchInputVal,
        userCountry,
        prioritizeUserCountry: !searchCountry
    });

    resultsContainer.innerHTML = filtered.map(item => {
        const status = getAvailabilityStatus(item);
        const ratingData = getProviderRating(item.providerName, item.providerId);

        let photoHtml = item.photo ? '<div class="meal-photo"><img src="' + item.photo + '" alt="' + escapeHtml(item.foodName) + '"></div>' : '';

        let ratingHtml = '';
        if (ratingData) {
            ratingHtml = '<div class="rating-summary" onclick="showProviderReviews(\'' + escapeHtml(item.providerName) + '\')">' +
                '<span class="avg-rating">★ ' + ratingData.average + '</span>' +
                '<span class="review-count">(' + ratingData.count + ' ' + escapeHtml(t('reviews', 'reviews')) + ')</span>' +
            '</div>';
        }

        // Portion sizes display - only show for logged in users
        let portionsHtml = '';
        if (isLoggedIn && item.portions && item.portions.length > 0) {
            let portionSpans = item.portions.map(p => {
                const totalPrice = (item.price || 0) + p.price;
                const itemCurrency = item.currency || getMarketRule(item.country || 'US').currency;
                return '<span class="portion-tag">' + p.name + ' - ' + formatCurrency(totalPrice, item.country || 'US', itemCurrency) + '</span>';
            }).join('');
            portionsHtml = '<div class="portion-options"><strong>' + escapeHtml(t('availablePortions', 'Available Portions')) + ':</strong> ' + portionSpans + '</div>';
        }

        // Only show detailed info if user is registered/logged in
        let restrictedInfoHtml = '';
        let registerPromptHtml = '';
        let contactButtonHtml = '';

        if (isLoggedIn) {
            // Show ALL RESTRICTED INFO for registered users
            let restrictedParts = [];
            if (item.ingredients) {
                restrictedParts.push('<div class="ingredients-info"><strong>' + escapeHtml(t('ingredientsAllergens', 'Ingredients/Allergens')) + ':</strong> ' + escapeHtml(item.ingredients) + '</div>');
            }
            if (item.drinks) {
                restrictedParts.push('<div class="drinks-info"><strong>' + escapeHtml(t('drinksLabel', 'Drinks')) + ':</strong> ' + escapeHtml(item.drinks) + '</div>');
            }
            const configuredComponents = Array.isArray(item.mealComponents)
                ? item.mealComponents.filter(component => component && typeof component.name === 'string' && component.name.trim())
                : [];
            if (configuredComponents.length) {
                const includedComponents = configuredComponents.filter(component => component.type === 'included').map(component => component.name);
                const extraComponents = configuredComponents.filter(component => component.type === 'extra').map(component => `${component.name} (+${formatCurrency(component.price, item.country || 'US', item.currency || getMarketRule(item.country || 'US').currency)})`);
                const componentParts = [];
                if (includedComponents.length) componentParts.push('<strong>Included:</strong> ' + escapeHtml(includedComponents.join(', ')));
                if (extraComponents.length) componentParts.push('<strong>Extras:</strong> ' + escapeHtml(extraComponents.join(', ')));
                restrictedParts.push('<div class="meal-components-info">' + componentParts.join(' &nbsp; ') + '</div>');
            }
            if (isDailySpecial(item)) {
                const countdown = getDealCountdown(item.specialEndTime);
                const itemCurrency = item.currency || getMarketRule(item.country || 'US').currency;
                const dealPriceHtml = item.specialPrice > 0
                    ? '<span class="deal-price-tag">🏷️ Deal Price: ' + formatCurrency(Number(item.specialPrice), item.country || 'US', itemCurrency) + ' <span class="original-price">(reg. ' + formatCurrency(Number(item.price || 0), item.country || 'US', itemCurrency) + ')</span></span>'
                    : '';
                const dealNoteHtml = item.specialNote ? '<span class="deal-note">' + escapeHtml(item.specialNote) + '</span>' : '';
                const countdownHtml = countdown ? '<span class="deal-countdown">' + countdown + '</span>' : '';
                restrictedParts.push('<div class="special-deal-info special-deal-client">' + dealPriceHtml + dealNoteHtml + countdownHtml + '</div>');
            }
            restrictedParts.push(portionsHtml);
            restrictedParts.push(ratingHtml);
            const trustSignals = getProviderTrustSignals(item, ratingData);
            restrictedParts.push(buildProviderTrustSignalsHtml(trustSignals));
            const itemCurrency = item.currency || getMarketRule(item.country || 'US').currency;
            const distanceKm = calculateDistanceKm(clientSearchGeo, resolveItemCoordinates(item));
            restrictedParts.push('<div class="meal-details">' +
                (isDailySpecial(item) && item.specialPrice > 0
                    ? '<span class="detail-tag price deal-active-price">' + formatCurrency(Number(item.specialPrice), item.country || 'US', itemCurrency) + ' <span class="original-price">' + formatCurrency(Number(item.price || 0), item.country || 'US', itemCurrency) + '</span></span>'
                    : '<span class="detail-tag price">' + formatCurrency(Number(item.price || 0), item.country || 'US', itemCurrency) + '</span>') +
                '<span class="detail-tag servings">' + item.servingsLeft + ' ' + escapeHtml(t('servingsLeft', 'servings left')) + '</span>' +
                '<span class="detail-tag ' + status.class + '">' + status.text + '</span>' +
                (Number.isFinite(distanceKm) ? '<span class="detail-tag location">📍 ' + distanceKm.toFixed(1) + ' km</span>' : '') +
                (item.afterHours ? '<span class="detail-tag after-hours">' + escapeHtml(t('afterHoursOk', 'After Hours OK')) + '</span>' : '') +
            '</div>');
            if (item.is247) {
                restrictedParts.push('<span class="detail-tag status-247">' + escapeHtml(t('filter247', '24/7 Available')) + '</span>');
            }
            let delOpts = [];
            if (item.offersDelivery && item.offersPickup) delOpts.push('<span class="delivery-tag both">' + escapeHtml(t('deliveryAndPickup', 'Delivery & Pickup')) + '</span>');
            if (item.offersDelivery && !item.offersPickup) delOpts.push('<span class="delivery-tag delivery">' + escapeHtml(t('deliveryOnly', 'Delivery')) + '</span>');
            if (!item.offersDelivery && item.offersPickup) delOpts.push('<span class="delivery-tag pickup">' + escapeHtml(t('pickupOnly', 'Pickup Only')) + '</span>');
            restrictedParts.push('<div class="delivery-options">' + delOpts.join('') + '</div>');

            restrictedInfoHtml = restrictedParts.join('');
            // Show Contact to Order button ONLY for registered users
            // CRITICAL FIX: item.id is a UUID string - MUST be wrapped in quotes inside
            // the onclick attribute, otherwise the browser throws a SyntaxError and the
            // modal never opens.
            const itemJsonDash = encodeURIComponent(JSON.stringify({
                id: item.id,
                title: item.foodName,
                foodName: item.foodName,
                price: item.price,
                providerId: item.providerId || item.providerName,
                providerName: item.providerName,
                currency: item.currency || getMarketRule(item.country || 'US').currency
            }));
            contactButtonHtml = '<button class="contact-btn" onclick="openContactModal(\'' + item.id + '\')">' +
                (getMenuAvailabilityStatus(item) === 'tomorrow' ? escapeHtml(t('preorderForTomorrow', 'Preorder for tomorrow')) : 'Contact to Order') +
                '</button>' +
                '<button class="btn-send-gift" onclick="window.openGiftOrderModal(JSON.parse(decodeURIComponent(\'' + itemJsonDash + '\')))">🎁 Gift a Friend</button>' +
                '<button class="btn-request-meal" onclick="window.openMealRequestModal(JSON.parse(decodeURIComponent(\'' + itemJsonDash + '\')))">🍔 Request from Friend</button>' +
                (typeof window.favoriteProviderButton === 'function' ? window.favoriteProviderButton(item) : '');
        } else {
            // Show registration prompt for unregistered users - HIDE ALL RESTRICTED INFO including button
            registerPromptHtml = '<div class="register-prompt">' +
                '<p>🔒 ' + escapeHtml(t('registerToViewDetails', 'Register to view price, servings, open/closed status, ingredients, drinks, delivery/pickup options & order!')) + '</p>' +
            '</div>';
        }

        return '<div class="result-card ' + (item.dailySpecial ? 'daily-special-card' : '') + '">' +
            photoHtml +
            '<div class="provider-name">' + escapeHtml(item.providerName) + '</div>' +
            '<div class="food-name">' + escapeHtml(item.foodName) + ' ' + getMenuAvailabilityBadge(item) + (item.dailySpecial ? ' <span class="special-badge-inline">🔥 Daily Special</span>' : '') + '</div>' +
            restrictedInfoHtml +
            registerPromptHtml +
            '<div class="card-actions" style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">' + contactButtonHtml + '</div>' +
        '</div>';
    }).join('');
}

// Check if currently open
function isCurrentlyOpen(item) {
    if (item.is247) return true;
    if (!item) return false;

    let schedule = item.hours || item.businessHours || item.openingHours || item.availability || null;
    if (!schedule && item.openTime && item.closeTime) {
        const dailyRange = `${item.openTime}-${item.closeTime}`;
        schedule = {
            mon: dailyRange,
            tue: dailyRange,
            wed: dailyRange,
            thu: dailyRange,
            fri: dailyRange,
            sat: dailyRange,
            sun: dailyRange
        };
    }

    const itemCountry = normalizeCountryCode(item.country || item.providerCountry || 'US');
    const policy = getLaunchCountryPolicy(itemCountry);
    const timezone = normalizeProviderTimezone(item.providerTimezone || item.timezone || policy.timezone || 'UTC');
    return isProviderOpenNow({
        isOpenNow: item.isOpenNow === true,
        timezone,
        hours: schedule
    }, new Date(), timezone);
}
// Get availability status
function getAvailabilityStatus(item) {
    if (item.is247) {
        return { class: 'status-247', text: '24/7' };
    }

    if (isCurrentlyOpen(item)) {
        return { class: 'status-open', text: t('openNow', 'Open now') };
    }

    if (item.afterHours) {
        return { class: 'status-closed', text: t('afterHoursOnly', 'After Hours Only') };
    }

    return { class: 'status-closed', text: t('closedNow', 'Closed now') };
}

function getMenuAvailabilityStatus(item) {
    const storedStatus = item && item.availabilityStatus === 'tomorrow' ? 'tomorrow' : 'today';
    if (!item || !item.availabilityDate) return storedStatus;

    const country = normalizeCountryCode(item.country || item.providerCountry || 'US');
    const policy = getLaunchCountryPolicy(country);
    const timezone = normalizeProviderTimezone(item.providerTimezone || item.timezone || policy.timezone || 'UTC');
    const today = getMenuAvailabilityDate('today', timezone);
    if (item.availabilityDate === today) return 'today';
    if (item.availabilityDate === getMenuAvailabilityDate('tomorrow', timezone)) return 'tomorrow';
    return 'expired';
}

function getMenuAvailabilityDate(status, timeZone, now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(now);
    const dateParts = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
    const date = new Date(Date.UTC(Number(dateParts.year), Number(dateParts.month) - 1, Number(dateParts.day)));
    if (status === 'tomorrow') date.setUTCDate(date.getUTCDate() + 1);
    return date.toISOString().slice(0, 10);
}

function getMenuAvailabilityBadge(item) {
    const status = getMenuAvailabilityStatus(item);
    const labels = {
        today: t('availableToday', 'Available today'),
        tomorrow: t('availableTomorrow', 'Available tomorrow'),
        expired: t('availabilityNeedsUpdate', 'Availability needs updating')
    };
    return `<span class="detail-tag menu-availability ${status}">${escapeHtml(labels[status])}</span>`;
}
// Contact Modal Functions
let selectedProviderId = null;
// Global function to close the contact modal
function closeContactModal() {
    const modal = document.getElementById('contactModal');
    if (modal) {
        modal.classList.remove('show');
        // Restore the form (hidden when showing the order confirmation panel)
        const form = document.getElementById('contactForm');
        if (form) form.style.display = '';
        const directContact = modal.querySelector('.direct-contact');
        if (directContact) directContact.style.display = '';
        const confirmPanel = document.getElementById('orderConfirmationPanel');
        if (confirmPanel) confirmPanel.remove();
        const reviewSection = document.getElementById('reviewSection');
        if (reviewSection) reviewSection.classList.add('hidden');
    }
}
// Make function globally accessible
window.closeContactModal = closeContactModal;
window.submitClientMessage = submitClientMessage;
function initContactModal() {
    const modal = document.getElementById('contactModal');

    // Safety check - return if modal doesn't exist
    if (!modal) {
        console.log('Contact modal not found - skipping initialization');
        return;
    }

    const closeBtn = document.querySelector('#contactModal .close-modal');

    // Add click event to the close button (X)
    if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            closeContactModal();
        });
    }

    // Close modal when clicking outside the modal content (on the dark overlay)
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            closeContactModal();
        }
    });

    // Handle Escape key to close modal
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            const modal = document.getElementById('contactModal');
            if (modal && modal.classList.contains('show')) {
                closeContactModal();
            }
        }
    });

// Contact form submission - prevent default form submit and use our handler
    const contactForm = document.getElementById('contactForm');
    if (contactForm) {
        contactForm.addEventListener('submit', (e) => {
            e.preventDefault();
            console.log('Contact form submitted - calling submitClientMessage');
            submitClientMessage();
        });
    }
}

// Global function to open the contact modal
function openContactModal(providerId) {
    // Hard restriction: unregistered users cannot start ordering flow
    if (!isUserLoggedIn()) {
        showNotification('Please register/login to contact and place an order', 'error');
        return;
    }

    selectedProviderId = providerId;
    // CRITICAL FIX: Compare IDs as strings. Menu item IDs can be numeric (e.g. 1)
    // or UUID strings (e.g. "123e4567-..."). The onclick attribute always passes a
    // string now, so strict equality (===) fails for numeric IDs and the modal
    // silently doesn't open.
    const item = menuItems.find(i => String(i.id) === String(providerId));

    if (!item) return;

    const modal = document.getElementById('contactModal');
    if (modal) {
        modal.classList.add('show');
    }
    const providerInfo = document.getElementById('modalProviderInfo');
    const directButtons = document.getElementById('directContactButtons');

    // PRE-FILL client form fields with logged-in client's info
    const clientNameEl = document.getElementById('clientName');
    const clientPhoneEl = document.getElementById('clientPhone');
    const clientEmailEl = document.getElementById('clientEmail');

    if (clientNameEl) clientNameEl.value = currentClient.name || '';
    if (clientPhoneEl) clientPhoneEl.value = currentClient.phone || '';
    if (clientEmailEl) clientEmailEl.value = currentClient.email || '';

    // CRITICAL FIX: Clear the order message field when opening the modal for a
    // different menu item. The modal uses a single shared #clientMessage textarea,
    // so without clearing it, a message typed for one item would carry over to
    // the next item's order form.
    const clientMessageEl = document.getElementById('clientMessage');
    if (clientMessageEl) clientMessageEl.value = '';

    // Also hide the review section (it may have been shown after a previous order)
    const reviewSection = document.getElementById('reviewSection');
    if (reviewSection) reviewSection.classList.add('hidden');

    console.log('=== MODAL OPENED WITH CLIENT INFO ===');
    console.log('Client Name:', currentClient.name);
    console.log('Client Email:', currentClient.email);

    providerInfo.innerHTML = `
        <p><strong>${escapeHtml(item.providerName)}</strong></p>
        <p>${escapeHtml(item.foodName)} - $${item.price.toFixed(2)}</p>
        ${getMenuAvailabilityStatus(item) === 'tomorrow' ? `<p class="preorder-notice">${escapeHtml(t('availableTomorrow', 'Available tomorrow'))} — your order will be scheduled for tomorrow.</p>` : ''}
        ${item.providerAddress ? `<p>${escapeHtml(item.providerAddress)}</p>` : ''}
    `;

    // Direct contact buttons
    let buttonsHtml = '';
    if (item.providerPhone) {
        buttonsHtml += `<a href="tel:${escapeHtml(item.providerPhone)}" class="contact-direct-btn call">Call ${escapeHtml(item.providerPhone)}</a>`;
    }
    if (item.providerEmail) {
        buttonsHtml += `<a href="mailto:${escapeHtml(item.providerEmail)}" class="contact-direct-btn email">Email</a>`;
    }
directButtons.innerHTML = buttonsHtml;

    // Update delivery/pickup options based on what provider offers
    const deliveryPickupSelect = document.getElementById('deliveryPickupChoice');
    if (deliveryPickupSelect) {
        const hasDelivery = item.offersDelivery;
        const hasPickup = item.offersPickup;

        deliveryPickupSelect.innerHTML = '';

        if (hasPickup) {
            const pickupOption = document.createElement('option');
            pickupOption.value = 'pickup';
            pickupOption.textContent = 'Pickup at Restaurant';
            deliveryPickupSelect.appendChild(pickupOption);
        }

        if (hasDelivery) {
            const deliveryOption = document.createElement('option');
            deliveryOption.value = 'delivery';
            deliveryOption.textContent = 'Delivery';
            deliveryPickupSelect.appendChild(deliveryOption);
        }

        // If only one option, select it by default
        if (deliveryPickupSelect.options.length === 1) {
            deliveryPickupSelect.selectedIndex = 0;
        }
    }

    // Add portion size selection if available
    const portionSelectContainer = document.getElementById('portionSelectContainer');
    if (portionSelectContainer) {
        if (item.portions && item.portions.length > 0) {
            // Build portion options
            let portionOptionsHtml = '';
            item.portions.forEach((portion, index) => {
                const totalPrice = (item.price || 0) + portion.price;
                portionOptionsHtml += `<option value="${escapeHtml(portion.name)}">${escapeHtml(portion.name)} - $${totalPrice.toFixed(2)}</option>`;
            });
            portionSelectContainer.innerHTML = `
                <div class="portion-select">
                    <label>Portion Size:</label>
                    <select id="portionChoice">
                        ${portionOptionsHtml}
                    </select>
                </div>
            `;
        } else {
            // No portions - hide the container
            portionSelectContainer.innerHTML = '';
        }
    }

    // Add drink choices if the provider offers drinks
    const drinkSelectContainer = document.getElementById('drinkSelectContainer');
    if (drinkSelectContainer) {
        const drinkOptions = Array.isArray(item.drinkOptions) && item.drinkOptions.length
            ? item.drinkOptions
            : parseDrinkOptions(item.drinks || item.drinkOptions || '');

        if (drinkOptions.length > 0) {
            const drinkOptionsHtml = drinkOptions.map(drink => {
                const normalized = normalizeDrinkOption(drink);
                const priceText = normalized && normalized.price > 0 ? ` - $${normalized.price.toFixed(2)}` : '';
                const value = normalized ? `${normalized.name}|${Number(normalized.price || 0).toFixed(2)}` : String(drink);
                return `<option value="${escapeHtml(value)}">${escapeHtml(normalized ? normalized.name + priceText : drink)}</option>`;
            }).join('');

            drinkSelectContainer.innerHTML = `
                <div class="drink-select">
                    <label for="drinkChoice">Choose Drink:</label>
                    <select id="drinkChoice">
                        <option value="">No drink selected</option>
                        ${drinkOptionsHtml}
                    </select>
                    <label for="drinkQuantity">Drink Quantity:</label>
                    <input type="number" id="drinkQuantity" min="0" value="0" step="1">
                </div>
            `;
        } else {
            drinkSelectContainer.innerHTML = '';
        }
    }

    const mealComponentsSelectContainer = document.getElementById('mealComponentsSelectContainer');
    if (mealComponentsSelectContainer) {
        const components = parseMealComponents(item.mealComponents);
        const included = components.filter(component => component.type === 'included');
        const extras = components.filter(component => component.type === 'extra');
        const renderChoices = (choices, checked, heading) => choices.length ? `
            <section class="meal-component-group">
                <h4>${heading}</h4>
                <div class="meal-component-choice-list">
                    ${choices.map(component => {
                        const extraPrice = component.type === 'extra'
                            ? ` (+${formatCurrency(component.price, item.country || 'US', item.currency || getMarketRule(item.country || 'US').currency)})`
                            : '';
                        return `<label class="meal-component-choice">
                            <input class="meal-component-choice-input" type="checkbox" value="${escapeHtml(component.id)}" data-component-type="${component.type}" ${checked ? 'checked' : ''}>
                            <span>${escapeHtml(component.name)}${extraPrice}</span>
                        </label>`;
                    }).join('')}
                </div>
            </section>` : '';
        mealComponentsSelectContainer.innerHTML = components.length
            ? `<div class="meal-components-picker"><h3>Customize your meal</h3>
                ${renderChoices(included, true, 'Included with the meal (uncheck to leave out)')}
                ${renderChoices(extras, false, 'Optional extras')}</div>`
            : '';
    }

    const quantityInput = document.getElementById('orderQuantity');
    if (quantityInput) {
        quantityInput.value = '1';
    }

    // ── Payment method selector ──────────────────────────────────────────────
    // Always show "Cash on Pickup / Arrange with Provider".
    // Show "Pay Online with Card" only when Stripe Connect is fully configured.
    const paymentMethodContainer = document.getElementById('paymentMethodContainer');
    if (paymentMethodContainer) {
        const cardOptionHtml = stripeConnectEnabled()
            ? `<label class="payment-radio-label">
                 <input type="radio" name="paymentMethod" value="card" id="pmCard">
                 💳 Pay Online with Card (secure checkout)
               </label>`
            : '';
        paymentMethodContainer.innerHTML = `
            <div class="payment-method-group">
                <div class="payment-method-heading">💰 How will you pay?</div>
                <label class="payment-radio-label">
                    <input type="radio" name="paymentMethod" value="cash" id="pmCash" checked>
                    💵 Cash on Pickup / Arrange with Provider
                </label>
                ${cardOptionHtml}
            </div>`;
    }

    updateOrderModalTotal(item);
    attachOrderModalListeners(item);
}

function attachOrderModalListeners(item) {
    const quantityInput = document.getElementById('orderQuantity');
    const portionSelect = document.getElementById('portionChoice');
    const drinkChoice = document.getElementById('drinkChoice');
    const drinkQuantity = document.getElementById('drinkQuantity');
    const mealComponentChoices = document.querySelectorAll('#mealComponentsSelectContainer .meal-component-choice-input');

    if (quantityInput) {
        quantityInput.oninput = () => updateOrderModalTotal(item);
    }
    if (portionSelect) {
        portionSelect.onchange = () => updateOrderModalTotal(item);
    }
    if (drinkChoice) {
        drinkChoice.onchange = () => updateOrderModalTotal(item);
    }
    if (drinkQuantity) {
        drinkQuantity.oninput = () => updateOrderModalTotal(item);
    }
    mealComponentChoices.forEach(choice => {
        choice.onchange = () => updateOrderModalTotal(item);
    });
}

function updateOrderModalTotal(item) {
    const totalDisplay = document.getElementById('orderTotalDisplay');
    if (!totalDisplay || !item) return;

    const quantityInput = document.getElementById('orderQuantity');
    const quantity = Math.max(1, parseInt(quantityInput ? quantityInput.value : '1', 10) || 1);

    let portionValue = 0;
    const portionSelect = document.getElementById('portionChoice');
    const portionChoice = portionSelect ? portionSelect.value : '';
    if (portionChoice && item.portions && item.portions.length > 0) {
        const selectedPortion = item.portions.find(p => p.name === portionChoice);
        if (selectedPortion) portionValue = Number(selectedPortion.price || 0);
    }

    let drinkValue = 0;
    const drinkChoice = document.getElementById('drinkChoice');
    const drinkQuantity = document.getElementById('drinkQuantity');
    const drinkQty = Math.max(0, parseInt(drinkQuantity ? drinkQuantity.value : '0', 10) || 0);
    if (drinkChoice && drinkChoice.value) {
        const [drinkName, drinkPrice] = drinkChoice.value.split('|');
        drinkValue = Number(drinkPrice || 0) * drinkQty;
    }

    const selectedComponentIds = Array.from(document.querySelectorAll('#mealComponentsSelectContainer .meal-component-choice-input:checked'))
        .map(choice => choice.value);
    const components = parseMealComponents(item.mealComponents);
    const extraUnitPrice = components
        .filter(component => component.type === 'extra' && selectedComponentIds.includes(component.id))
        .reduce((total, component) => total + component.price, 0);

    const basePrice = isDailySpecial(item) && Number(item.specialPrice) > 0
        ? Number(item.specialPrice)
        : Number(item.price || 0);
    const unitPrice = basePrice + Number(portionValue || 0) + extraUnitPrice;
    const totalPrice = (unitPrice * quantity) + Number(drinkValue || 0);

    totalDisplay.textContent = `Total: $${totalPrice.toFixed(2)} (${quantity} meal(s) × $${unitPrice.toFixed(2)})${drinkQty > 0 ? ` + ${drinkQty} drink(s) × $${Number(drinkChoice && drinkChoice.value ? drinkChoice.value.split('|')[1] : 0).toFixed(2)}` : ''}`;
}

async function submitClientMessage() {
    // Hard restriction: unregistered users cannot submit orders
    if (!isUserLoggedIn()) {
        showNotification('Please register/login to place an order', 'error');
        return;
    }

    if (!selectedProviderId) {
        console.error('No provider selected');
        showNotification('Error: No provider selected', 'error');
        return;
    }

    const item = menuItems.find(i => String(i.id) === String(selectedProviderId));
    if (!item) {
        console.error('Menu item not found for id:', selectedProviderId);
        showNotification('Error: Menu item not found', 'error');
        return;
    }

    const deliveryPickupChoice = document.getElementById('deliveryPickupChoice');
    const choiceText = deliveryPickupChoice && deliveryPickupChoice.value === 'delivery' ? 'Delivery' : 'Pickup';

    // ── Read selected payment method ─────────────────────────────────────────
    const pmCardEl = document.getElementById('pmCard');
    const selectedPaymentMethod = (pmCardEl && pmCardEl.checked && stripeConnectEnabled())
        ? 'card'
        : 'cash';

    // Get portion choice if available
    let portionChoice = '';
    let portionPriceAdjustment = 0;
    const portionSelect = document.getElementById('portionChoice');
    if (portionSelect) {
        portionChoice = portionSelect.value;
        if (portionChoice && item.portions && item.portions.length > 0) {
            const selectedPortion = item.portions.find(p => p.name === portionChoice);
            if (selectedPortion) {
                portionPriceAdjustment = Number(selectedPortion.price || 0);
            }
        }
    }

    const quantityInput = document.getElementById('orderQuantity');
    const quantity = Math.max(1, parseInt(quantityInput ? quantityInput.value : '1', 10) || 1);

    const drinkChoiceInput = document.getElementById('drinkChoice');
    const drinkQuantityInput = document.getElementById('drinkQuantity');
    let selectedDrink = '';
    let selectedDrinkPrice = 0;
    let drinkQuantity = 0;

    if (drinkChoiceInput && drinkChoiceInput.value) {
        const [drinkName, drinkPrice] = drinkChoiceInput.value.split('|');
        selectedDrink = drinkName || drinkChoiceInput.value;
        selectedDrinkPrice = Number(drinkPrice || 0) || 0;
    }

    if (drinkQuantityInput) {
        drinkQuantity = Math.max(0, parseInt(drinkQuantityInput.value, 10) || 0);
    }

    const selectedMealComponentIds = Array.from(document.querySelectorAll('#mealComponentsSelectContainer .meal-component-choice-input:checked'))
        .map(choice => choice.value);
    const configuredMealComponents = parseMealComponents(item.mealComponents);
    const mealComponentExtraUnitPrice = configuredMealComponents
        .filter(component => component.type === 'extra' && selectedMealComponentIds.includes(component.id))
        .reduce((total, component) => total + component.price, 0);

    // Calculate final price based on portion, meal quantity, and drink quantity
    const basePrice = isDailySpecial(item) && Number(item.specialPrice) > 0
        ? Number(item.specialPrice)
        : Number(item.price || 0);
    const unitPrice = basePrice + Number(portionPriceAdjustment || 0) + mealComponentExtraUnitPrice;
    const drinkTotal = Number(selectedDrinkPrice || 0) * Number(drinkQuantity || 0);
    const totalPrice = (unitPrice * quantity) + drinkTotal;

    if (getMenuAvailabilityStatus(item) !== 'tomorrow' && !item.afterHours) {
        try {
            const availability = await evaluateProviderAvailabilityServer(item);
            if (availability && availability.openNow === false) {
                showNotification(`${item.providerName} is currently ${t('closedNow', 'Closed now')}. Please try again during open hours.`, 'error');
                return;
            }
        } catch (availabilityError) {
            console.warn('[Availability] Server check failed, falling back to local timezone check:', availabilityError);
            if (!isCurrentlyOpen(item)) {
                showNotification(`${item.providerName} is currently ${t('closedNow', 'Closed now')}. Please try again during open hours.`, 'error');
                return;
            }
        }
    }

    // Get client info from the logged-in client (no fallback for unregistered users)
    const clientMessageEl = document.getElementById('clientMessage');
    const clientMessage = clientMessageEl ? clientMessageEl.value.trim() : '';

    const clientName = currentClient.name || '';
    const clientPhone = currentClient.phone || '';
    const clientEmail = currentClient.email || '';

    if (!clientEmail) {
        showNotification('Client session is invalid. Please login again.', 'error');
        return;
    }

// Create order with status tracking - include providerId for reliable matching
    // Use providerId from menu item (most reliable), and name
    const providerIdToUse = item.providerId || (currentProvider ? currentProvider.id : null) || selectedProviderId;
    const clientMarketCountry = (currentClient && currentClient.country) || item.clientCountry || 'US';
    const providerMarketCountry = (item && (item.country || item.providerCountry || currentProvider && currentProvider.country)) || 'US';
    const providerCurrency = item.currency || getMarketRule(providerMarketCountry).currency;
    const marketCompliance = calculateOrderCompliance({
        country: clientMarketCountry,
        price: totalPrice,
        currency: providerCurrency,
        clientCountry: clientMarketCountry,
        providerCountry: providerMarketCountry
    });

    console.log('=== ORDER SUBMISSION DETAILS ===');
    console.log('Menu Item providerId:', item.providerId);
    console.log('Menu Item id:', item.id);
    console.log('selectedProviderId:', selectedProviderId);
    console.log('Using providerId:', providerIdToUse);
    console.log('[Compliance] Order market rules:', marketCompliance);

    const message = {
        id: window.generateUUID(),
        menuItemId: String(item.id),
        providerId: providerIdToUse, // Use providerId from menu item or current provider if available
        providerName: item.providerName, // Already sanitized at menu-item write time
        clientName: sanitizeText(clientName, 100),
        clientPhone: sanitizeText(clientPhone, 30),
        clientEmail: clientEmail.toLowerCase(),
        clientCountry: clientMarketCountry,
        providerCountry: providerMarketCountry,
        message: sanitizeText(clientMessage, 500),
        foodItem: item.foodName, // Already sanitized at menu-item write time
        portion: sanitizeText(portionChoice || 'Regular', 60),
        mealComponentIds: selectedMealComponentIds,
        drink: sanitizeText(selectedDrink, 100),
        drinkPrice: selectedDrinkPrice,
        drinkQuantity: drinkQuantity,
        quantity: quantity,
        unitPrice: unitPrice,
        price: totalPrice,
        taxRate: marketCompliance.taxRate,
        taxAmount: marketCompliance.taxAmount,
        currency: marketCompliance.currency,
        refundWindowHours: marketCompliance.refundWindowHours,
        cancellationWindowHours: marketCompliance.cancellationWindowHours,
        // payoutDelayDays / payoutFeePercent / payoutFeeAmount are informational only
        // until Stripe Connect is live. They do not trigger real money movement.
        payoutDelayDays: marketCompliance.payoutDelayDays,
        payoutFeePercent: marketCompliance.payoutFeePercent,
        payoutFeeAmount: marketCompliance.payoutFeeAmount,
        refundPolicy: marketCompliance.refundPolicy,
        cancellationPolicy: marketCompliance.cancellationPolicy,
        payoutPolicy: marketCompliance.payoutPolicy,
        compliance: marketCompliance,
        orderType: choiceText,
        // ── Payment method ──────────────────────────────────────────────────
        // 'cash'  = client pays provider directly (cash, transfer, etc.)
        // 'card'  = online card payment via Stripe Connect Payment Intent
        paymentMethod: selectedPaymentMethod,
        // 'unpaid'        = cash order awaiting in-person payment
        // 'pending_card'  = card payment intent created, awaiting confirmation
        // 'paid'          = card payment confirmed by Stripe webhook
        paymentStatus: selectedPaymentMethod === 'cash' ? 'unpaid' : 'pending_card',
        stripePaymentIntentId: '',   // filled in by _initStripeOrderPayment if card
        stripePaymentIntentSecret: '', // client secret — only held in memory, NOT stored
        items: [{
            menuItemId: String(item.id),
            quantity,
            portion: sanitizeText(portionChoice || 'Regular', 60),
            mealComponentIds: selectedMealComponentIds,
            drink: sanitizeText(selectedDrink, 100),
            drinkQuantity
        }],
        // Order status fields
        status: 'pending',
        statusHistory: [
            { status: 'pending', timestamp: new Date().toISOString(), note: 'Order placed' }
        ],
        providerNotes: '',
        createdAt: new Date().toISOString()
    };

    console.log('=== SUBMITTING ORDER ===');
    console.log('Provider ID:', message.providerId);
    console.log('Provider Name:', message.providerName);
    console.log('Client:', message.clientName, message.clientEmail);
    console.log('Food:', message.foodItem);
    console.log('Payment method:', message.paymentMethod);
    console.log('Total messages before:', clientMessages.length);

    // The backend owns prices, tax, availability, stock reservation, and audit
    // history. Never write order or ledger data directly from the browser.
    try {
        const created = await callProtectedFunction('createOrder', message);
        if (!created || !created.order) throw new Error('Order service returned no order.');
        Object.assign(message, created.order);
        clientMessages.push(message);
    } catch (e) {
        console.error('[Order] Server-authoritative creation failed:', e);
        showNotification('Your order could not be saved. No payment was started. Please try again.', 'error');
        return;
    }

    // Build order summary string for notification
    let orderSummary = `${item.foodName}`;
    if (portionChoice) {
        orderSummary += ` (${portionChoice} - $${totalPrice.toFixed(2)})`;
    } else {
        orderSummary += ` ($${totalPrice.toFixed(2)})`;
    }
    if (selectedDrink) {
        orderSummary += ` + ${drinkQuantity} ${selectedDrink}`;
    }

    // ── Branch: cash or card ─────────────────────────────────────────────────
    if (selectedPaymentMethod === 'card' && stripeConnectEnabled()) {
        // Show confirmation panel first, then initiate Stripe Payment Intent
        _showOrderConfirmation(message, item, 'pending_card');
        _initStripeOrderPayment(message, item).catch(err => {
            console.error('[Stripe] Order payment init failed:', err.message);
            showNotification('Card payment could not be started. Please pay cash instead.', 'error');
            // Degrade gracefully: switch confirmation to cash instructions
            _showOrderConfirmation(message, item, 'cash_fallback');
        });
    } else {
        // Cash on Pickup — show receipt with clear payment instructions
        _showOrderConfirmation(message, item, 'cash');
        showNotification(`Order placed: ${orderSummary}. Pay cash when you pick up / meet the provider.`, 'success');
    }

    // Refresh order lists
    if (currentProvider) renderProviderOrders();
    if (currentClient) renderClientOrders();

    // Send new order notification to provider (Email + SMS)
    try {
        if (typeof sendNewOrderEmail === 'function') {
            sendNewOrderEmail({
                providerName: message.providerName,
                providerEmail: item.providerEmail,
                clientName: message.clientName,
                clientEmail: message.clientEmail,
                foodItem: message.foodItem,
                portion: message.portion,
                mealComponents: formatOrderMealComponents(message.mealComponents),
                price: message.price,
                orderType: message.orderType,
                paymentMethod: message.paymentMethod,
                message: message.message
            });
        }
    } catch (e) {
        console.warn('[Email] Could not send new order email:', e);
    }

    // Also send SMS notification to provider if phone is available
    try {
        if (typeof sendNewOrderSms === 'function') {
            const providerPhone = item.providerPhone;
            sendNewOrderSms({
                providerPhone: providerPhone,
                providerName: message.providerName,
                clientName: message.clientName,
                foodItem: message.foodItem,
                mealComponents: formatOrderMealComponents(message.mealComponents),
                portion: message.portion,
                price: message.price,
                orderType: message.orderType
            });
        }
    } catch (e) {
        console.warn('[Twilio] Could not send new order SMS:', e);
    }
}

/**
 * Replace the contact modal's form with a clear order confirmation / receipt panel.
 * @param {Object} order  - the order object
 * @param {Object} item   - the menu item
 * @param {'cash'|'pending_card'|'cash_fallback'} mode
 */
function _showOrderConfirmation(order, item, mode) {
    const modal = document.getElementById('contactModal');
    const form = document.getElementById('contactForm');
    const directContact = modal && modal.querySelector('.direct-contact');

    // Build payment instructions block
    let paymentBlock = '';
    if (mode === 'cash' || mode === 'cash_fallback') {
        const isFallback = mode === 'cash_fallback';
        paymentBlock = `
            <div class="order-payment-info cash-payment" style="background:#f0fdf4;border:1px solid #86efac;border-radius:8px;padding:12px;margin:12px 0;">
                <div style="font-size:1.1em;font-weight:700;color:#166534;margin-bottom:6px;">
                    💵 Payment Method: Cash on ${order.orderType === 'Delivery' ? 'Delivery' : 'Pickup'}
                </div>
                ${isFallback ? '<div style="color:#b45309;font-size:0.85em;margin-bottom:6px;">⚠️ Online payment could not be started — please pay cash instead.</div>' : ''}
                <ul style="margin:6px 0 0 16px;font-size:0.9em;color:#166534;">
                    <li>Have <strong>$${Number(order.price || 0).toFixed(2)}</strong> ready when you ${order.orderType === 'Delivery' ? 'receive your delivery' : 'pick up your order'}.</li>
                    <li>You can also arrange payment directly with the provider via phone or email.</li>
                    <li>Your order is confirmed — the provider has been notified.</li>
                </ul>
            </div>`;
    } else if (mode === 'pending_card') {
        paymentBlock = `
            <div class="order-payment-info card-payment" style="background:#eff6ff;border:1px solid #93c5fd;border-radius:8px;padding:12px;margin:12px 0;">
                <div style="font-size:1.1em;font-weight:700;color:#1d4ed8;margin-bottom:6px;">
                    💳 Online Payment — Loading Checkout…
                </div>
                <div id="stripeOrderElements" style="margin-top:8px;min-height:60px;">
                    <div style="color:#555;font-size:0.9em;">Please wait while we set up secure payment…</div>
                </div>
                <div id="stripeOrderPayBtn" style="display:none;margin-top:10px;">
                    <button type="button" id="confirmStripeOrderBtn"
                        style="background:#1d4ed8;color:#fff;border:none;border-radius:6px;padding:10px 24px;font-size:1em;font-weight:600;cursor:pointer;width:100%;">
                        Pay $${Number(order.price || 0).toFixed(2)} Securely
                    </button>
                </div>
                <div id="stripeOrderError" style="color:#dc2626;font-size:0.85em;margin-top:6px;display:none;"></div>
            </div>`;
    }

    const confirmHtml = `
        <div id="orderConfirmationPanel" style="padding:4px 0;">
            <div style="background:#dcfce7;border:1px solid #86efac;border-radius:8px;padding:10px 14px;margin-bottom:10px;">
                <strong style="color:#166534;">✅ Order Placed Successfully!</strong>
            </div>
            <div class="order-receipt" style="font-size:0.93em;line-height:1.7;">
                <div><strong>🍽 Item:</strong> ${escapeHtml(order.foodItem)} — ${escapeHtml(order.portion)}</div>
                ${order.fulfillmentDate ? `<div><strong>${escapeHtml(t('fulfillmentDate', 'Scheduled for'))}:</strong> ${escapeHtml(order.fulfillmentDate)}</div>` : ''}
                ${formatOrderMealComponents(order.mealComponents) ? `<div><strong>Meal components:</strong> ${escapeHtml(formatOrderMealComponents(order.mealComponents))}</div>` : ''}
                ${order.drink ? `<div><strong>🥤 Drink:</strong> ${escapeHtml(order.drink)} × ${order.drinkQuantity}</div>` : ''}
                <div><strong>📦 Quantity:</strong> ${order.quantity}</div>
                <div><strong>🚚 Option:</strong> ${escapeHtml(order.orderType)}</div>
                <div><strong>💰 Total:</strong> $${Number(order.price || 0).toFixed(2)} ${String(order.currency || 'USD').toUpperCase()}</div>
                <div><strong>📋 Order ID:</strong> <span style="font-family:monospace;font-size:0.85em;">${escapeHtml(order.id)}</span></div>
            </div>
            ${paymentBlock}
            <p style="font-size:0.82em;color:#666;margin-top:8px;">
                You will receive updates as the provider processes your order.
                Contact the provider directly if you need to make changes.
            </p>
            <button type="button" onclick="closeContactModal()"
                style="margin-top:8px;background:#e5e7eb;border:none;border-radius:6px;padding:8px 20px;cursor:pointer;font-size:0.95em;">
                Close
            </button>
        </div>`;

    if (form) form.style.display = 'none';
    if (directContact) directContact.style.display = 'none';

    // Remove any prior confirmation panel before inserting a fresh one
    const existing = document.getElementById('orderConfirmationPanel');
    if (existing) existing.remove();

    const modalContent = modal && modal.querySelector('.modal-content');
    if (modalContent) {
        const tmp = document.createElement('div');
        tmp.innerHTML = confirmHtml;
        modalContent.appendChild(tmp.firstElementChild);
    }

    // Show the review section below
    const reviewSection = document.getElementById('reviewSection');
    if (reviewSection) reviewSection.classList.add('hidden');
}

/**
 * Create a Stripe Payment Intent for the order via Cloud Function,
 * then mount the Stripe Elements card form inside #stripeOrderElements.
 *
 * This function is a no-op skeleton when Stripe Connect is not configured.
 * To activate: set window.stripeConnectConfig.enabled = true and provide a publishableKey.
 *
 * @param {Object} order - the order object (already saved to Firestore)
 * @param {Object} item  - the menu item
 */
async function _initStripeOrderPayment(order, item) {
    const cfg = window.stripeConnectConfig || {};
    if (!cfg.enabled || !cfg.publishableKey) {
        console.warn('[Stripe Connect] Not configured — skipping card payment init.');
        return;
    }

    // Lazy-load Stripe.js if not already present
    if (!window.Stripe) {
        await new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = 'https://js.stripe.com/v3/';
            s.onload = resolve;
            s.onerror = () => reject(new Error('Failed to load Stripe.js'));
            document.head.appendChild(s);
        });
    }

    const stripe = window.Stripe(cfg.publishableKey);

    // Call the backend Cloud Function to create a Payment Intent
    let idToken = '';
    try { idToken = await getFirebaseIdToken(); } catch (_) {}
    const appCheckToken = await getAppCheckToken();

    const fnUrl = _getConnectFunctionUrl('createOrderPaymentIntent');
    const resp = await fetch(fnUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Firebase-AppCheck': appCheckToken,
            ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
        },
        body: JSON.stringify({
            orderId: order.id
        })
    });

    const data = await resp.json();

    if (!resp.ok || !data.clientSecret) {
        const errMsg = data && data.code === 'PROVIDER_CONNECT_INCOMPLETE'
            ? 'This provider has not finished payout onboarding yet. Please pay cash for now.'
            : data && data.code === 'PROVIDER_CONNECT_ONBOARDING_REQUIRED'
                ? 'This provider is not onboarded for online card payments yet. Please pay cash for now.'
                : data && data.code === 'PROVIDER_NOT_ACTIVE'
                    ? 'This provider account is not active for card payments right now. Please pay cash for now.'
                    : data && data.code === 'PROVIDER_CONNECT_STATUS_UNAVAILABLE'
                        ? 'We could not verify provider payout status right now. Please try again or pay cash.'
                        : data.error || 'Payment Intent creation failed';
        const errEl = document.getElementById('stripeOrderError');
        if (errEl) { errEl.textContent = errMsg; errEl.style.display = ''; }
        throw new Error(errMsg);
    }

    if (data.pricing) {
        order.price = Number(data.pricing.total);
        order.subtotal = Number(data.pricing.subtotal);
        order.discountAmount = Number(data.pricing.discountAmount);
        order.taxAmount = Number(data.pricing.taxAmount);
        order.taxRate = Number(data.pricing.taxRate);
        order.currency = String(data.pricing.currency || order.currency || 'USD');
        const payButton = document.getElementById('confirmStripeOrderBtn');
        if (payButton) {
            payButton.textContent = `Pay ${formatCurrency(order.price, order.providerCountry || 'US', order.currency)} Securely`;
        }
    }

    // Mount Stripe Elements
    const elements = stripe.elements({ clientSecret: data.clientSecret });
    const paymentElement = elements.create('payment');
    const mountEl = document.getElementById('stripeOrderElements');
    if (mountEl) {
        mountEl.innerHTML = '';
        paymentElement.mount(mountEl);
    }

    // Show Pay button
    const payBtnWrapper = document.getElementById('stripeOrderPayBtn');
    if (payBtnWrapper) payBtnWrapper.style.display = '';

    const payBtn = document.getElementById('confirmStripeOrderBtn');
    if (payBtn) {
        payBtn.addEventListener('click', async () => {
            payBtn.disabled = true;
            payBtn.textContent = 'Processing…';
            const { error } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: window.location.href + (window.location.href.includes('?') ? '&' : '?') + `payment=success&orderId=${encodeURIComponent(order.id)}`
                }
            });
            if (error) {
                const errEl = document.getElementById('stripeOrderError');
                if (errEl) { errEl.textContent = error.message; errEl.style.display = ''; }
                payBtn.disabled = false;
                payBtn.textContent = `Pay $${Number(order.price || 0).toFixed(2)} Securely`;
            }
        });
    }
}

// ==================== PROVIDER LIVE ORDER COUNTERS ====================
// Orders are grouped so providers can see at a glance what still needs action.
const PROVIDER_ORDER_GROUPS = {
    received: { label: 'New Orders', hint: 'Waiting to be confirmed', icon: '🔔', statuses: ['pending', 'new', 'queued'] },
    inProgress: { label: 'Pending Completion', hint: 'Confirmed · Preparing · Ready', icon: '⏳', statuses: ['confirmed', 'preparing', 'ready'] },
    completed: { label: 'Completed', hint: 'Fulfilled orders', icon: '✅', statuses: ['completed'] }
};
const PROVIDER_COUNTER_TICK_MS = 30000;
let providerOrderFilter = 'all';
let providerOrderCounterTimer = null;
let providerOrderCounterState = { providerKey: null, sinceMs: 0, seenReceivedIds: new Set() };
let providerBaseDocumentTitle = null;

function normalizeOrderStatus(status) {
    const value = String(status || 'pending').toLowerCase();
    return value === 'canceled' ? 'cancelled' : value;
}

function getOrderGroupKey(order) {
    const status = normalizeOrderStatus(order && order.status);
    return Object.keys(PROVIDER_ORDER_GROUPS).find(key => PROVIDER_ORDER_GROUPS[key].statuses.includes(status)) || null;
}

function orderTimestampMs(value) {
    if (!value) return NaN;
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    return new Date(value).getTime();
}

function formatWaitDuration(ms) {
    const minutes = Math.max(0, Math.floor(ms / 60000));
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m`;
    return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

// Orders sent to the logged-in provider (matched by ID or name)
function getCurrentProviderOrders() {
    if (!currentProvider || !Array.isArray(clientMessages)) return [];
    const providerId = currentProvider.id || currentProvider.providerId || null;
    const providerName = currentProvider.name ? currentProvider.name.trim() : '';
    return clientMessages.filter(msg => {
        if (!msg) return false;
        const matchesProviderId = msg.providerId && providerId && String(msg.providerId) === String(providerId);
        const matchesProviderName = msg.providerName && providerName &&
            String(msg.providerName).toLowerCase() === providerName.toLowerCase();
        return matchesProviderId || matchesProviderName;
    });
}

function playNewOrderChime() {
    try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        [880, 1175].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            const start = ctx.currentTime + i * 0.18;
            osc.type = 'sine';
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.0001, start);
            gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.16);
            osc.connect(gain).connect(ctx.destination);
            osc.start(start);
            osc.stop(start + 0.17);
        });
        setTimeout(() => { try { ctx.close(); } catch (e) { /* ignore */ } }, 800);
    } catch (e) {
        // Autoplay may be blocked until the user interacts with the page
    }
}

function updateProviderDocumentTitle(receivedCount) {
    if (providerBaseDocumentTitle === null) providerBaseDocumentTitle = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = receivedCount > 0 ? `(${receivedCount}) ${providerBaseDocumentTitle}` : providerBaseDocumentTitle;
}

function updateProviderOrdersTabBadge(actionableCount, hasNew) {
    const ordersGroupTab = document.querySelector('.provider-group-tab[data-group="orders"]');
    if (ordersGroupTab) setProviderTabBadge(ordersGroupTab, actionableCount, hasNew);
    const ordersTab = document.querySelector('.provider-panel-tab[data-panel="orders"]');
    if (!ordersTab) return;
    setProviderTabBadge(ordersTab, actionableCount, hasNew);
}

function setProviderTabBadge(ordersTab, actionableCount, hasNew) {
    let badge = ordersTab.querySelector('.provider-tab-badge');
    if (actionableCount > 0) {
        if (!badge) {
            badge = document.createElement('span');
            badge.className = 'provider-tab-badge';
            ordersTab.appendChild(badge);
        }
        badge.textContent = actionableCount > 99 ? '99+' : String(actionableCount);
        badge.classList.toggle('urgent', hasNew);
        badge.setAttribute('aria-label', `${actionableCount} orders need attention`);
    } else if (badge) {
        badge.remove();
    }
}

function updateProviderOrderCounters(providerOrders) {
    const container = document.getElementById('providerOrderCounters');
    if (!container) return;

    if (!currentProvider) {
        container.innerHTML = '';
        return;
    }

    const orders = Array.isArray(providerOrders) ? providerOrders : getCurrentProviderOrders();
    const now = Date.now();
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const counts = { received: 0, inProgress: 0, completed: 0 };
    let completedToday = 0;
    let oldestReceivedMs = NaN;
    let oldestInProgressMs = NaN;
    const receivedIds = [];

    orders.forEach(order => {
        const group = getOrderGroupKey(order);
        if (!group) return;
        counts[group] += 1;
        const createdMs = orderTimestampMs(order.createdAt);
        if (group === 'received') {
            receivedIds.push({ id: String(order.id), createdMs });
            if (Number.isFinite(createdMs) && !(createdMs >= oldestReceivedMs)) oldestReceivedMs = createdMs;
        } else if (group === 'inProgress') {
            if (Number.isFinite(createdMs) && !(createdMs >= oldestInProgressMs)) oldestInProgressMs = createdMs;
        } else if (group === 'completed') {
            const doneMs = orderTimestampMs(order.updatedAt || order.completedAt || order.createdAt);
            if (Number.isFinite(doneMs) && doneMs >= todayStart.getTime()) completedToday += 1;
        }
    });

    // Detect orders that arrived after the dashboard was opened
    const providerKey = String(currentProvider.id || currentProvider.providerId || currentProvider.name || '');
    if (providerOrderCounterState.providerKey !== providerKey) {
        providerOrderCounterState = { providerKey, sinceMs: now, seenReceivedIds: new Set() };
    }
    const newArrivals = receivedIds.filter(({ id, createdMs }) =>
        !providerOrderCounterState.seenReceivedIds.has(id) &&
        Number.isFinite(createdMs) && createdMs >= providerOrderCounterState.sinceMs - 5000
    );
    receivedIds.forEach(({ id }) => providerOrderCounterState.seenReceivedIds.add(id));

    const waitText = (ms) => Number.isFinite(ms) ? `Oldest waiting ${formatWaitDuration(now - ms)}` : '';
    const subtext = {
        received: counts.received > 0 ? waitText(oldestReceivedMs) : 'All caught up',
        inProgress: counts.inProgress > 0 ? waitText(oldestInProgressMs) : 'Nothing in the kitchen',
        completed: `${completedToday} today`
    };

    container.innerHTML = Object.keys(PROVIDER_ORDER_GROUPS).map(key => {
        const group = PROVIDER_ORDER_GROUPS[key];
        const count = counts[key];
        const classes = ['order-counter-card', `order-counter-${key}`];
        if (count > 0 && key !== 'completed') classes.push('has-items');
        if (providerOrderFilter === key) classes.push('selected');
        return `
            <button type="button" class="${classes.join(' ')}" data-counter="${key}"
                onclick="showProviderOrdersByGroup('${key}')"
                title="${escapeHtml(group.hint)} — click to view"
                aria-label="${escapeHtml(group.label)}: ${count}. ${escapeHtml(subtext[key])}">
                <span class="order-counter-icon" aria-hidden="true">${group.icon}</span>
                <span class="order-counter-body">
                    <span class="order-counter-label">${escapeHtml(group.label)}</span>
                    <span class="order-counter-value">${count}</span>
                    <span class="order-counter-sub">${escapeHtml(subtext[key])}</span>
                </span>
            </button>
        `;
    }).join('');

    updateProviderOrdersTabBadge(counts.received + counts.inProgress, counts.received > 0);
    updateProviderDocumentTitle(counts.received);

    if (newArrivals.length > 0) {
        const receivedCard = container.querySelector('.order-counter-received');
        if (receivedCard) {
            receivedCard.classList.add('just-arrived');
            setTimeout(() => receivedCard.classList.remove('just-arrived'), 4000);
        }
        playNewOrderChime();
        showNotification(`🔔 ${newArrivals.length} new order${newArrivals.length > 1 ? 's' : ''} received!`, 'success');
    }
}

function startProviderOrderCounterTicker() {
    stopProviderOrderCounterTicker();
    providerOrderCounterTimer = setInterval(() => {
        if (!currentProvider) {
            stopProviderOrderCounterTicker();
            return;
        }
        updateProviderOrderCounters();
    }, PROVIDER_COUNTER_TICK_MS);
}

function stopProviderOrderCounterTicker() {
    if (providerOrderCounterTimer) {
        clearInterval(providerOrderCounterTimer);
        providerOrderCounterTimer = null;
    }
}

function resetProviderOrderCounters() {
    stopProviderOrderCounterTicker();
    providerOrderFilter = 'all';
    providerOrderCounterState = { providerKey: null, sinceMs: 0, seenReceivedIds: new Set() };
    const container = document.getElementById('providerOrderCounters');
    if (container) container.innerHTML = '';
    updateProviderOrdersTabBadge(0, false);
    updateProviderDocumentTitle(0);
}

// Open the Orders tab filtered to a counter group ('received' | 'inProgress' | 'completed' | 'all')
function showProviderOrdersByGroup(groupKey) {
    providerOrderFilter = PROVIDER_ORDER_GROUPS[groupKey] ? groupKey : 'all';
    const editForm = document.getElementById('providerEditProfileForm');
    if (editForm && !editForm.classList.contains('hidden') && typeof hideProviderEditForm === 'function') {
        hideProviderEditForm();
    }
    const ordersTab = document.querySelector('.provider-panel-tab[data-panel="orders"]');
    if (ordersTab) {
        ordersTab.click();
    } else {
        renderProviderOrders();
    }
    const ordersPanel = document.getElementById('orders-panel');
    if (ordersPanel && typeof ordersPanel.scrollIntoView === 'function') {
        ordersPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
}
window.showProviderOrdersByGroup = showProviderOrdersByGroup;
window.updateProviderOrderCounters = updateProviderOrderCounters;

// ==================== ORDER PROCESSING SYSTEM ====================
// Render Provider Orders (Incoming Orders with Status Controls)
function renderProviderOrders() {
    const ordersContainer = document.getElementById('providerOrders');
    if (!currentProvider) {
        updateProviderOrderCounters([]);
        return;
    }

    // CRITICAL FIX: Filter orders to show ONLY those sent to THIS specific provider
    // Each restaurant/caterer should only see orders sent to them
    const allProviderOrders = getCurrentProviderOrders();

    // Live counters are refreshed on every render (Firestore onSnapshot triggers this function)
    updateProviderOrderCounters(allProviderOrders);

    if (!ordersContainer) return;

    if (allProviderOrders.length === 0) {
        ordersContainer.innerHTML = `
            <div class="empty-state">
                <p>No orders yet. Clients will appear here when they place orders.</p>
            </div>
        `;
        return;
    }

    const activeGroup = PROVIDER_ORDER_GROUPS[providerOrderFilter] ? providerOrderFilter : 'all';
    let providerOrders = activeGroup === 'all'
        ? allProviderOrders.slice()
        : allProviderOrders.filter(order => getOrderGroupKey(order) === activeGroup);

    const filterBarHtml = activeGroup === 'all' ? '' : `
        <div class="provider-order-filter-bar">
            <span>Showing: <strong>${escapeHtml(PROVIDER_ORDER_GROUPS[activeGroup].label)}</strong> (${providerOrders.length})</span>
            <button type="button" class="provider-order-filter-clear" onclick="showProviderOrdersByGroup('all')">Show all orders</button>
        </div>
    `;

    if (providerOrders.length === 0) {
        ordersContainer.innerHTML = `${filterBarHtml}
            <div class="empty-state">
                <p>No orders in this category right now.</p>
            </div>
        `;
        return;
    }

    // Sort by newest first
    providerOrders.sort((a, b) => orderTimestampMs(b.createdAt) - orderTimestampMs(a.createdAt));

    const displayCount = paginationDisplay.providerOrders;
    const visibleOrders = providerOrders.slice(0, displayCount);
    const firestoreHasMore = window.__firestorePaginationState && window.__firestorePaginationState['orders'] && window.__firestorePaginationState['orders'].hasMore;
    const hasMore = providerOrders.length > displayCount || firestoreHasMore;

    ordersContainer.innerHTML = filterBarHtml + visibleOrders.map(order => {
        const statusInfo = getStatusInfo(order.status || 'pending');
        const isCompleted = order.status === 'completed';
        const mealComponentsSummary = formatOrderMealComponents(order.mealComponents);

        // Build status badge with color
        const statusBadge = `
            <span class="order-status-badge" style="background: ${statusInfo.bg}; color: ${statusInfo.color};">
                ${statusInfo.text}
            </span>
        `;

        // Build action button based on status
        let actionButton = '';
        if (!isCompleted && statusInfo.next) {
            actionButton = `
                <button class="order-action-btn" onclick="updateOrderStatus('${order.id}')">
                    ${statusInfo.nextText}
                </button>
            `;
        }
        const paymentActionButton = order.paymentMethod !== 'card' &&
            order.paymentStatus !== 'paid' && order.status !== 'cancelled'
            ? `<button class="order-action-btn" onclick="markOrderPaid('${order.id}')">Mark paid</button>`
            : '';

        // Provider cancel button (only allowed while Pending/Confirmed)
        const canCancel = canCancelOrder(order.status || 'pending');
        const providerCancelButton = (canCancel && (order.status === 'pending' || order.status === 'confirmed'))
            ? `
                <button class="order-cancel-btn" onclick="cancelOrder('${order.id}')">Cancel Order</button>
            `
            : '';


        // Build provider notes section
        const notesValue = order.providerNotes || '';
        const notesHtml = `
            <div class="order-notes">
                <input type="text" class="provider-note-input"
                    id="note-${order.id}"
                    placeholder="Add internal note..."
                    value="${escapeHtml(notesValue)}"
                    onchange="saveProviderNote('${order.id}', this.value)">
            </div>
        `;

        // Build progress tracker for client view
        const progressHtml = buildProgressTracker(order.status);

        return `
            <div class="provider-order-card">
                <div class="order-header">
                    <span class="order-client">${escapeHtml(order.clientName)}</span>
                    <span class="order-date">${formatDateTime(order.createdAt)}</span>
                </div>
                <div class="order-food">${escapeHtml(order.foodItem)}</div>
                <div class="order-details">
                    ${order.fulfillmentDate ? `<span>${escapeHtml(t('fulfillmentDate', 'Scheduled for'))}: ${escapeHtml(order.fulfillmentDate)}</span>` : ''}
                    <span>Portion: ${escapeHtml(order.portion)}</span>
                    ${mealComponentsSummary ? `<span>${escapeHtml(mealComponentsSummary)}</span>` : ''}
                    <span>Drink: ${escapeHtml(order.drink || 'Standard')}</span>
                    <span>Qty: ${Number(order.quantity || 1)}</span>
                    <span>Unit: $${Number(order.unitPrice || 0).toFixed(2)}</span>
                    <span>Total: $${Number(order.price || 0).toFixed(2)}</span>
                    <span>Type: ${escapeHtml(order.orderType)}</span>
                </div>
                <div class="order-payment-row" style="display:flex;align-items:center;gap:8px;margin:4px 0;">
                    <div class="order-payment-badge">${buildOrderPaymentBadge(order, true)}</div>
                    ${paymentActionButton}
                </div>
                <div class="order-message">${escapeHtml(order.message)}</div>
                <div class="order-status-row">
                    ${statusBadge}
                    ${actionButton}
                    ${providerCancelButton}
                </div>
                ${notesHtml}
                <div class="order-client-contact">
                    <small>Client: ${escapeHtml(order.clientEmail)}</small>
                    ${order.clientPhone ? `<small> | ${escapeHtml(order.clientPhone)}</small>` : ''}
                </div>
            </div>
        `;
    }).join('');

    if (hasMore) {
        ordersContainer.innerHTML += `
            <div style="text-align:center; margin-top:16px;">
                <button class="load-more-btn" onclick="loadMoreProviderOrders()" style="padding:8px 24px;background:#f97316;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:0.9em;">
                    Load More Orders
                </button>
                <div style="font-size:0.78em;color:#888;margin-top:4px;">Showing ${visibleOrders.length} of ${providerOrders.length}${firestoreHasMore ? '+' : ''}</div>
            </div>
        `;
    }
}
// Build progress tracker for client order view
function buildProgressTracker(currentStatus) {
    const stages = [
        { status: 'pending', label: 'Order Placed' },
        { status: 'confirmed', label: 'Confirmed' },
        { status: 'preparing', label: 'Preparing' },
        { status: 'ready', label: 'Ready' },
        { status: 'completed', label: 'Completed' }
    ];

    const currentIndex = stages.findIndex(s => s.status === currentStatus);

    return `
        <div class="order-progress">
            ${stages.map((stage, index) => {
                const isActive = index <= currentIndex;
                const isCurrent = index === currentIndex;
                return `
                    <div class="progress-step ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}">
                        <div class="step-dot">✓</div>
                        <div class="step-label">${stage.label}</div>
                    </div>
                `;
            }).join('')}
        </div>
    `;
}
// Update order status
async function updateOrderStatus(orderId) {
    const orderIndex = clientMessages.findIndex(msg => String(msg.id) === String(orderId));
    if (orderIndex === -1) {
        showNotification('Order not found', 'error');
        return;
    }

    const order = clientMessages[orderIndex];
    const nextStatus = getNextStatus(order.status || 'pending');

    if (!nextStatus) {
        showNotification('Order is already completed', 'error');
        return;
    }

    try {
        const result = await callProtectedFunction('transitionOrder', {
            orderId,
            nextStatus,
            reason: `Status changed to ${getStatusInfo(nextStatus).text}`
        });
        clientMessages[orderIndex].status = result.order.nextStatus;
        clientMessages[orderIndex].updatedAt = result.order.updatedAt;
    } catch (error) {
        console.error('[Order] Server-authoritative transition failed:', error);
        showNotification(error.message || 'Order status update failed. Please try again.', 'error');
        return;
    }

showNotification(`Order status updated to ${getStatusInfo(nextStatus).text}`, 'success');

    // Re-render orders
    renderProviderOrders();

    // If client is viewing their orders, refresh that too
    if (currentClient) {
        renderClientOrders();
    }

// Send order status update notification to client (Email + SMS)
    try {
        if (typeof sendOrderStatusEmail === 'function') {
            sendOrderStatusEmail({
                providerName: order.providerName,
                clientEmail: order.clientEmail,
                clientName: order.clientName,
                foodItem: order.foodItem,
                portion: order.portion,
                orderType: order.orderType,
                oldStatus: order.status,
                newStatus: nextStatus
            });
        }
    } catch (e) {
        console.warn('[Email] Could not send order status email:', e);
    }

    // Also send SMS notification to client if phone is available
    try {
        if (typeof sendOrderStatusSms === 'function') {
            const clientPhone = order.clientPhone;
            if (clientPhone) {
                sendOrderStatusSms({
                    clientPhone: clientPhone,
                    clientName: order.clientName,
                    providerName: order.providerName,
                    foodItem: order.foodItem,
                    portion: order.portion,
                    orderType: order.orderType,
                    status: nextStatus
                });
            }
        }
    } catch (e) {
        console.warn('[Twilio] Could not send order status SMS:', e);
    }
}

async function markOrderPaid(orderId) {
    const orderIndex = clientMessages.findIndex(msg => String(msg.id) === String(orderId));
    if (orderIndex === -1) {
        showNotification('Order not found', 'error');
        return;
    }

    try {
        const result = await callProtectedFunction('markOrderPaid', { orderId });
        Object.assign(clientMessages[orderIndex], result.order);
        showNotification('Payment recorded as received.', 'success');
        renderProviderOrders();
        if (currentClient) renderClientOrders();
        if (isProviderPanelActive('history-panel')) renderDailyCloseReports();
        if (isProviderPanelActive('sales-panel')) renderSalesReport();
    } catch (error) {
        console.error('[Order] Could not record payment:', error);
        showNotification(error.message || 'Payment could not be recorded. Please try again.', 'error');
    }
}

// Cancel order (server-validated action)
async function cancelOrder(orderId) {
    const orderIndex = clientMessages.findIndex(msg => String(msg.id) === String(orderId));
    if (orderIndex === -1) {
        showNotification('Order not found', 'error');
        return;
    }

    const order = clientMessages[orderIndex];
    const currentStatus = order.status || 'pending';

    if (currentStatus === 'cancelled') {
        showNotification('Order is already cancelled', 'error');
        return;
    }

    if (!canCancelOrder(currentStatus)) {
        showNotification('Cancellation is not available for this order status', 'error');
        return;
    }

    try {
        const result = await callProtectedFunction('transitionOrder', {
            orderId,
            nextStatus: 'cancelled',
            reason: 'Order cancelled by account holder'
        });

        clientMessages[orderIndex].status = result.order.nextStatus;
        if (!clientMessages[orderIndex].statusHistory) {
            clientMessages[orderIndex].statusHistory = [];
        }
        clientMessages[orderIndex].statusHistory.push({
            status: 'cancelled',
            timestamp: new Date().toISOString(),
            note: result.message || 'Order cancelled by server-validated action'
        });

        showNotification(result.message || 'Order cancelled successfully!', 'success');

        renderProviderOrders();
        if (currentClient) {
            renderClientOrders();
        }
    } catch (error) {
        console.error('[cancelOrder] Server action failed:', error);
        showNotification(error.message || 'Order cancellation failed. Please try again.', 'error');
    }
}

// Save provider note
function saveProviderNote(orderId, note) {
    const orderIndex = clientMessages.findIndex(msg => String(msg.id) === String(orderId));
    if (orderIndex === -1) return;

    clientMessages[orderIndex].providerNotes = note;
    showNotification('Note saved', 'success');
}
// Make functions global
window.updateOrderStatus = updateOrderStatus;
window.markOrderPaid = markOrderPaid;
window.saveProviderNote = saveProviderNote;
window.cancelOrder = cancelOrder;
// ==================== SERVINGS ADJUSTMENT FUNCTIONALITY ====================
// Quick adjust servings - increase by 1
function quickAdjustServings(id, amount) {
    const itemIndex = menuItems.findIndex(item => String(item.id) === String(id));
    if (itemIndex === -1) {
        showNotification('Menu item not found', 'error');
        return;
    }

    const item = menuItems[itemIndex];
    const newServings = item.servingsLeft + amount;

    // Don't allow negative servings
    if (newServings < 0) {
        showNotification('Servings cannot be negative', 'error');
        return;
    }

    // Update the servings
    menuItems[itemIndex].servingsLeft = newServings;
    menuItems[itemIndex].updatedAt = new Date().toISOString();
    saveMenuItems();

    // Persist to Firestore (cloud storage for shared access) - Firebase-only (no browserStorage).
    // PRIMARY: Write through firebase-integration.js so all browsers see the updated
    // servings instantly via the onSnapshot listeners.
    if (typeof window.firebaseSaveMenuItem === 'function') {
        window.firebaseSaveMenuItem(menuItems[itemIndex]).catch(e => {
            console.warn('[Firestore] firebaseSaveMenuItem (quickAdjustServings) failed:', e.message);
        });
    }

    // Re-render to show updated servings
    renderMenuList();
    renderSearchResults();
    renderClientSearchResults();

    // Show notification
    const action = amount > 0 ? 'increased' : 'decreased';
    showNotification(`${item.foodName} servings ${action} to ${newServings}`, 'success');
}
// Direct edit servings input
function updateServingsDirectly(id, newValue) {
    const itemIndex = menuItems.findIndex(item => String(item.id) === String(id));
    if (itemIndex === -1) {
        showNotification('Menu item not found', 'error');
        return;
    }

    const newServings = parseInt(newValue);

    // Validate
    if (isNaN(newServings) || newServings < 0) {
        showNotification('Please enter a valid number (0 or more)', 'error');
        // Re-render to reset the input
        renderMenuList();
        return;
    }

    const item = menuItems[itemIndex];

    // Update the servings
    menuItems[itemIndex].servingsLeft = newServings;
    menuItems[itemIndex].updatedAt = new Date().toISOString();
    saveMenuItems();

    // Persist to Firestore (cloud storage for shared access) - Firebase-only (no browserStorage).
    // PRIMARY: Write through firebase-integration.js so all browsers see the updated
    // servings instantly via the onSnapshot listeners.
    if (typeof window.firebaseSaveMenuItem === 'function') {
        window.firebaseSaveMenuItem(menuItems[itemIndex]).catch(e => {
            console.warn('[Firestore] firebaseSaveMenuItem (updateServingsDirectly) failed:', e.message);
        });
    }

    // Re-render
    renderMenuList();
    renderSearchResults();
    renderClientSearchResults();

    showNotification(`${item.foodName} servings updated to ${newServings}`, 'success');
}
// Toggle Menu Item Active/Inactive (Show/Hide from customers)
async function toggleMenuItemActive(id, button) {
    const itemIndex = menuItems.findIndex(item => String(item.id) === String(id));
    if (itemIndex === -1) {
        showNotification('Menu item not found', 'error');
        return;
    }

    const item = menuItems[itemIndex];
    const currentStatus = item.isActive !== false; // Default to true
    const nextStatus = !currentStatus;

    if (button) {
        button.disabled = true;
        button.textContent = 'Saving...';
    }

    try {
        if (typeof window.firebaseUpdateMenuItemActive !== 'function') {
            throw new Error('Menu visibility service is unavailable. Please try again.');
        }
        await window.firebaseUpdateMenuItemActive(id, nextStatus);

        const latestItem = menuItems.find(menuItem => String(menuItem.id) === String(id));
        if (latestItem) {
            latestItem.isActive = nextStatus;
            latestItem.updatedAt = new Date().toISOString();
        }

        renderMenuList();
        renderSearchResults();
        renderClientSearchResults();
        showNotification(`${item.foodName} ${nextStatus ? 'now displaying' : 'now hidden'} from customers`, 'success');
    } catch (error) {
        console.error('[Menu] Could not update menu visibility:', error);
        if (button) {
            button.disabled = false;
            button.textContent = currentStatus ? 'Turn Off' : 'Turn On';
        }
        showNotification(error.message || 'Could not update menu visibility. Please try again.', 'error');
    }
}

// Make servings functions global (and deleteMenuItem for inline onclick handlers)
window.quickAdjustServings = quickAdjustServings;
window.updateServingsDirectly = updateServingsDirectly;
window.toggleMenuItemActive = toggleMenuItemActive;
window.toggleDailySpecial = toggleDailySpecial;
window._openDailySpecialModal = _openDailySpecialModal;
window._saveDailySpecialModal = _saveDailySpecialModal;
window.toggleSpecialFields = toggleSpecialFields;
window.deleteMenuItem = deleteMenuItem;
// Render Menu List (Provider Panel)
function renderMenuList() {
    const menuList = document.getElementById('menuList');

    if (!menuList) return;

    // If no provider is logged in, there is no menu to show yet
    if (!currentProvider) {
        menuList.innerHTML = `
            <div class="empty-state">
                <p>No menu items yet. Add your first item above!</p>
            </div>
        `;
        return;
    }

    if (menuItems.length === 0) {
        menuList.innerHTML = `
            <div class="empty-state">
                <p>No menu items yet. Add your first item above!</p>
            </div>
        `;
        return;
    }

// Filter to only show current provider's menu items using the canonical auth UID
// when available, while retaining the legacy provider-name fallback.
const currentProviderUid = currentProvider ? String(currentProvider.uid || currentProvider.id || '').trim().toLowerCase() : '';
const currentProviderName = currentProvider && currentProvider.name ? currentProvider.name.trim().toLowerCase() : '';
const providerMenuItems = menuItems.filter((item) => {
    const itemProviderId = String(item && (item.providerId || item.providerUid || item.uid) || '').trim().toLowerCase();
    const itemProviderName = String(item && item.providerName || '').trim().toLowerCase();
    if (currentProviderUid && itemProviderId && itemProviderId === currentProviderUid) return true;
    if (currentProviderName && itemProviderName && itemProviderName === currentProviderName) return true;
    return false;
});

menuList.innerHTML = providerMenuItems.map(item => {
        const status = getAvailabilityStatus(item);

        // Active/Inactive status display
        const isActive = item.isActive !== false; // Default to true if not set
        const activeClass = isActive ? 'active-item' : 'inactive-item';
        const activeBadge = isActive
            ? '<span class="status-badge active-display">✓ Displaying</span>'
            : '<span class="status-badge inactive-display">✗ Hidden</span>';
        const specialBadge = isDailySpecial(item) ? '<span class="status-badge special-display">🔥 Daily Special</span>' : '';
        const specialToggleBtnText = isDailySpecial(item) ? '✨ Remove Special' : '🔥 Set Daily Special';
        const specialToggleBtnClass = isDailySpecial(item) ? 'special-off-btn' : 'special-on-btn';
        const toggleBtnText = isActive ? 'Turn Off' : 'Turn On';
        const toggleBtnClass = isActive ? 'toggle-off-btn' : 'toggle-on-btn';

        // Build additional info sections
        let photoHtml = '';
        if (item.photo) {
            photoHtml = `<div class="meal-photo"><img src="${item.photo}" alt="${escapeHtml(item.foodName)}"></div>`;
        }

        let ingredientsHtml = '';
        if (item.ingredients) {
            ingredientsHtml = `<div class="ingredients-info"><strong>Ingredients/Allergens:</strong> ${escapeHtml(item.ingredients)}</div>`;
        }

        let drinksHtml = '';
        if (item.drinks) {
            drinksHtml = `<div class="drinks-info"><strong>Drinks:</strong> ${escapeHtml(item.drinks)}</div>`;
        }

        // Portion sizes display
        let portionsHtml = '';
        if (item.portions && item.portions.length > 0) {
            const itemCurrency = item.currency || getMarketRule(item.country || 'US').currency;
            portionsHtml = `<div class="portion-info"><strong>Portions:</strong> ${item.portions.map(p => `${escapeHtml(p.name)} (${p.price > 0 ? '+' + formatCurrency(Number(p.price || 0), item.country || 'US', itemCurrency) : 'Base'})`).join(', ')}</div>`;
        }

        // Last updated timestamp
        const lastUpdated = item.updatedAt ? formatDateTime(item.updatedAt) : (item.createdAt ? formatDateTime(item.createdAt) : 'N/A');

        // Daily special extra info (deal price, note, countdown)
        let specialInfoHtml = '';
        const itemCurrency = item.currency || getMarketRule(item.country || 'US').currency;
        if (isDailySpecial(item)) {
            const countdown = getDealCountdown(item.specialEndTime);
            specialInfoHtml = `<div class="special-deal-info">` +
                (item.specialPrice > 0 ? `<span class="deal-price-tag">🏷️ Deal: ${formatCurrency(Number(item.specialPrice), item.country || 'US', itemCurrency)} <span class="original-price">(reg. ${formatCurrency(Number(item.price || 0), item.country || 'US', itemCurrency)})</span></span>` : '') +
                (item.specialNote ? `<span class="deal-note">${escapeHtml(item.specialNote)}</span>` : '') +
                (countdown ? `<span class="deal-countdown">${countdown}</span>` : '') +
            `</div>`;
        }

        return `
            <div class="menu-item ${activeClass}${isDailySpecial(item) ? ' daily-special-item' : ''}">
                <div class="food-header">
                    <div class="provider-name">${escapeHtml(item.providerName)}</div>
                    <div class="menu-item-actions">
                        <button class="edit-btn" onclick="editMenuItem('${item.id}')">Edit</button>
                        <button class="${specialToggleBtnClass}" onclick="toggleDailySpecial('${item.id}')" title="${isDailySpecial(item) ? 'Remove daily special status' : 'Mark as daily special / deal of the day'}">${specialToggleBtnText}</button>
                        <button class="${toggleBtnClass}" onclick="toggleMenuItemActive('${item.id}', this)" title="${isActive ? 'Hide from customers' : 'Show to customers'}">${toggleBtnText}</button>
                        <button class="delete-btn" onclick="deleteMenuItem('${item.id}')">Delete</button>
                    </div>
                </div>
                ${photoHtml}
                <div class="food-name">${escapeHtml(item.foodName)} ${getMenuAvailabilityBadge(item)} ${specialBadge} ${activeBadge}</div>
                ${specialInfoHtml}
                <div class="food-desc">${escapeHtml(item.foodDesc)}</div>
                ${ingredientsHtml}
                ${drinksHtml}
                ${portionsHtml}
                <div class="meal-details">
                    <span class="detail-tag price">$${item.price.toFixed(2)}</span>

                    <div class="servings-adjust">
                        <span class="servings-label">Servings:</span>
                        <button class="servings-btn minus" onclick="quickAdjustServings('${item.id}', -1)" title="Decrease by 1">−</button>
                        <input type="number" class="servings-input" value="${item.servingsLeft}" min="0" onchange="updateServingsDirectly('${item.id}', this.value)" onblur="updateServingsDirectly('${item.id}', this.value)">
                        <button class="servings-btn plus" onclick="quickAdjustServings('${item.id}', 1)" title="Increase by 1">+</button>
                    </div>

                    <span class="detail-tag ${status.class}">${status.text}</span>
                </div>
                <div class="last-updated">
                    <small>Last updated: ${lastUpdated}</small>
                </div>
                ${!item.is247 ? `<small>Hours: ${item.openTime} - ${item.closeTime}</small>` : ''}
            </div>
        `;
    }).join('');
}
// Delete Menu Item
function deleteMenuItem(id) {
    if (confirm('Are you sure you want to delete this menu item?')) {
        // FIX: Compare IDs as strings so deletion works whether the stored id is a
        // UUID string or a numeric id. Previously the strict type check (item.id !== id)
        // could fail on number/string mismatch, making delete position-dependent.
        const deleteKey = String(id);
        menuItems = menuItems.filter(item => String(item.id) !== deleteKey);
        syncWindowCollectionState();
        renderMenuList();
        renderSearchResults();

        // Delete from Firestore (cloud storage for shared access) - Firebase-only (no browserStorage).
        // PRIMARY: Write through firebase-integration.js so all browsers drop the item instantly.
        if (typeof window.firebaseDeleteMenuItem === 'function') {
            window.firebaseDeleteMenuItem(deleteKey).catch(e => {
                console.warn('[Firestore] firebaseDeleteMenuItem failed:', e.message);
            });
        }

        showNotification('Menu item deleted!', 'success');
    }
}
// Edit Menu Item - Populate form with existing data
function editMenuItem(id) {
    const item = menuItems.find(i => String(i.id) === String(id));
    if (!item) return;

    // Set editing item ID
    document.getElementById('editingItemId').value = id;

    // Populate form fields
    document.getElementById('foodName').value = item.foodName || '';
    const availabilityStatusEl = document.getElementById('availabilityStatus');
    if (availabilityStatusEl) availabilityStatusEl.value = item.availabilityStatus === 'tomorrow' ? 'tomorrow' : 'today';
    document.getElementById('foodDesc').value = item.foodDesc || '';
    document.getElementById('ingredients').value = item.ingredients || '';
    const legacyDrinksField = document.getElementById('drinks');
    if (legacyDrinksField) {
        legacyDrinksField.value = item.drinks || '';
    }
    currentMealComponents = parseMealComponents(item.mealComponents || []);
    const mealComponentsData = document.getElementById('mealComponentsData');
    if (mealComponentsData) mealComponentsData.value = JSON.stringify(currentMealComponents);
    renderMealComponentsList();
    document.getElementById('price').value = item.price || '';
    document.getElementById('servingsLeft').value = item.servingsLeft || '';
    document.getElementById('is247').checked = item.is247 || false;
    document.getElementById('openTime').value = item.openTime || '08:00';
    document.getElementById('closeTime').value = item.closeTime || '22:00';
    document.getElementById('afterHours').checked = item.afterHours || false;
    document.getElementById('offersDelivery').checked = item.offersDelivery || false;
    document.getElementById('offersPickup').checked = item.offersPickup !== false;
    document.getElementById('dailySpecial').checked = isDailySpecial(item);
    // Load special deal fields and show/hide the group
    const spPriceEl = document.getElementById('specialPrice');
    const spNoteEl = document.getElementById('specialNote');
    const spEndEl = document.getElementById('specialEndTime');
    if (spPriceEl) spPriceEl.value = (item.specialPrice > 0) ? item.specialPrice : '';
    if (spNoteEl) spNoteEl.value = item.specialNote || '';
    if (spEndEl) spEndEl.value = item.specialEndTime ? item.specialEndTime.slice(0, 16) : '';
    toggleSpecialFields();

    // Handle photo
    if (item.photo) {
        document.getElementById('foodPhotoData').value = item.photo;
        document.getElementById('photoPreview').innerHTML = `<img src="${item.photo}" alt="Meal Preview" style="max-width: 200px; max-height: 150px; border-radius: 8px;">`;
    } else {
        document.getElementById('foodPhotoData').value = '';
        document.getElementById('photoPreview').innerHTML = '';
    }
    // Clear any pending file from a previous upload attempt so the submit handler
    // uses the existing photo URL instead of re-uploading a stale file.
    const _photoInputEdit = document.getElementById('foodPhoto');
    if (_photoInputEdit) _photoInputEdit._pendingPhotoFile = null;
    const _photoStatusEdit = document.getElementById('photoUploadStatus');
    if (_photoStatusEdit) _photoStatusEdit.textContent = '';

    // Load portions if they exist
    currentPortionSizes = item.portions && item.portions.length > 0 ? [...item.portions] : [];
    document.getElementById('portionSizesData').value = JSON.stringify(currentPortionSizes);
    renderPortionList();

    // Change button text and scroll to form
    const submitBtn = document.querySelector('#menuForm button[type="submit"]');
    if (submitBtn) {
        submitBtn.textContent = 'Update Menu Item';
    }

    // Scroll to form
    document.getElementById('menuForm').scrollIntoView({ behavior: 'smooth' });

    showNotification('Editing menu item. Make changes and click Update.', 'success');
}
// Cancel Edit
function cancelEdit() {
    const form = document.getElementById('menuForm');
    form.reset();
    currentMealComponents = [];
    document.getElementById('mealComponentsData').value = '[]';
    document.getElementById('mealComponentType').value = 'included';
    document.getElementById('mealComponentPrice').value = '';
    document.getElementById('mealComponentPrice').disabled = true;
    renderMealComponentsList();
    document.getElementById('editingItemId').value = '';
    document.getElementById('openTime').value = '08:00';
    document.getElementById('closeTime').value = '22:00';
    const availabilityStatusReset = document.getElementById('availabilityStatus');
    if (availabilityStatusReset) availabilityStatusReset.value = 'today';
    document.getElementById('offersPickup').checked = true;
    document.getElementById('photoPreview').innerHTML = '';
    // Clear special fields
    const spPriceReset = document.getElementById('specialPrice');
    const spNoteReset = document.getElementById('specialNote');
    const spEndReset = document.getElementById('specialEndTime');
    if (spPriceReset) spPriceReset.value = '';
    if (spNoteReset) spNoteReset.value = '';
    if (spEndReset) spEndReset.value = '';
    toggleSpecialFields();

    // Clear portions
    currentPortionSizes = [];
    document.getElementById('portionSizesData').value = '[]';
    document.getElementById('portionList').innerHTML = '';

    // Reset button text
    const submitBtn = document.querySelector('#menuForm button[type="submit"]');
    if (submitBtn) {
        submitBtn.textContent = 'Add to Menu';
    }
}
// Make functions global
window.editMenuItem = editMenuItem;
window.cancelEdit = cancelEdit;
// Request Form - Custom Meals
function initRequestForm() {
    const form = document.getElementById('requestForm');
    if (!form) return; // requestForm does not exist on provider/client pages

    form.addEventListener('submit', (e) => {
        e.preventDefault();

        const request = {
            id: window.generateUUID(),
            name: document.getElementById('requestName').value.trim(),
            food: document.getElementById('requestFood').value.trim(),
            budget: parseFloat(document.getElementById('budget').value) || 0,
            details: document.getElementById('requestDetails').value.trim(),
            createdAt: new Date().toISOString()
        };

        mealRequests.push(request);
        saveMealRequests();

        form.reset();
        renderRequestsList();

        showNotification('Request submitted! Restaurants will contact you.', 'success');
    });
}
// Render Requests List
function renderRequestsList() {
    const requestsList = document.getElementById('requestsList');
    // SAFETY CHECK: Don't render if the container doesn't exist (e.g., on pages
    // without the custom requests section). Prevents "Cannot set properties of
    // null (setting 'innerHTML')" errors during triggerAppReRender().
    if (!requestsList) return;

    if (mealRequests.length === 0) {
        requestsList.innerHTML = `
            <div class="empty-state">
                <p>No custom requests yet.</p>
            </div>
        `;
        return;
    }

    requestsList.innerHTML = mealRequests.map(req => `
        <div class="request-card">
            <div class="request-name">${escapeHtml(req.name)}</div>
            <div class="request-food">${escapeHtml(req.food)}</div>
            ${req.budget > 0 ? `<div class="detail-tag price">Budget: $${req.budget.toFixed(2)}</div>` : ''}
            <div class="request-details">${escapeHtml(req.details)}</div>
        </div>
    `).join('');
}
// Client Request Form - Custom Meals (in client dashboard)
function initClientRequestForm() {
    const form = document.getElementById('clientRequestForm');
    if (!form) return;

    form.addEventListener('submit', (e) => {
        e.preventDefault();

        if (!currentClient) {
            showNotification('Please login to submit a custom meal request', 'error');
            return;
        }

        const request = {
            id: window.generateUUID(),
            clientId: currentClient.id,
            clientName: currentClient.name,
            clientEmail: currentClient.email,
            food: document.getElementById('clientRequestFood').value.trim(),
            budget: parseFloat(document.getElementById('clientBudget').value) || 0,
            details: document.getElementById('clientRequestDetails').value.trim(),
            createdAt: new Date().toISOString()
        };

        mealRequests.push(request);
        saveMealRequests();

        form.reset();
        renderClientRequestsList();

        showNotification('Custom meal request submitted! Restaurants will contact you.', 'success');
    });
}
// Render Client Requests List (in client dashboard)
function renderClientRequestsList() {
    const requestsList = document.getElementById('clientRequestsList');
    if (!requestsList || !currentClient) return;

    // Get requests from this client
    const clientRequests = mealRequests.filter(req =>
        req.clientId && req.clientId === currentClient.id
    );

    if (clientRequests.length === 0) {
        requestsList.innerHTML = `
            <div class="empty-state">
                <p>No custom requests yet.</p>
            </div>
        `;
        return;
    }

    // Sort by newest first
    clientRequests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    requestsList.innerHTML = clientRequests.map(req => `
        <div class="request-card">
            <div class="request-food">${escapeHtml(req.food)}</div>
            ${req.budget > 0 ? `<div class="detail-tag price">Budget: $${req.budget.toFixed(2)}</div>` : ''}
            <div class="request-details">${escapeHtml(req.details)}</div>
            <div class="order-date">${formatDateTime(req.createdAt)}</div>
        </div>
    `).join('');
}
// Local Storage Functions - WITH FIRESTORE SYNC
function saveMenuItems() {
    // Firebase-only (no browserStorage fallback)
    // The provider flow writes through the modular save path
    // (window.firebaseSaveMenuItem) and the legacy direct Firestore set here
    // causes a second permission-checked write that can fail with
    // "Missing or insufficient permissions" even after the provider item was
    // successfully created.
    if (typeof window.firebaseSaveMenuItem === 'function' && userRole === 'provider') {
        return;
    }

    if (firestoreService.initialized && firestoreService.db) {
        menuItems.forEach(item => {
            firestoreService.save('menuItems', item.id, item).catch(e => {
                console.error('[Firestore] Error saving menu item:', e);
            });
        });
    }
}

function saveMealRequests() {
    // Sync to Firestore
    if (firestoreService.initialized && firestoreService.db) {
        mealRequests.forEach(req => {
            firestoreService.save('mealRequests', req.id, req).catch(e => {
                console.error('[Firestore] Error saving meal request:', e);
            });
        });
    }
}
// Utility Functions
function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Strip all HTML tags from a string so user-supplied text is stored as plain
 * text in Firestore. This is the write-time sanitization layer — it ensures
 * nothing malicious ever reaches the database, regardless of how it gets
 * rendered later.
 *
 * @param {string} text - Raw input value from a form field.
 * @param {number} [maxLength] - Optional character cap (defaults to 500).
 * @returns {string} Plain text, HTML stripped, whitespace trimmed.
 */
function sanitizeText(text, maxLength) {
    if (text === null || text === undefined) return '';
    const cap = maxLength || 500;
    const div = document.createElement('div');
    div.innerHTML = String(text);
    // textContent strips all tags; trim and cap length
    return (div.textContent || div.innerText || '').trim().slice(0, cap);
}

/**
 * Sanitize all user-supplied text fields on a menu item object before it is
 * written to Firestore. Returns a new object with sanitized values.
 */
function sanitizeMenuItemFields(item) {
    return Object.assign({}, item, {
        foodName:        sanitizeText(item.foodName, 120),
        foodDesc:        sanitizeText(item.foodDesc, 500),
        ingredients:     sanitizeText(item.ingredients, 500),
        drinks:          sanitizeText(item.drinks, 300),
        providerName:    sanitizeText(item.providerName, 120),
        providerAddress: sanitizeText(item.providerAddress, 200),
        providerPhone:   sanitizeText(item.providerPhone, 30),
        providerEmail:   sanitizeText(item.providerEmail, 100),
        mealType:        sanitizeText(item.mealType, 60),
        mealComponents: Array.isArray(item.mealComponents)
            ? item.mealComponents.slice(0, 25).map(component => ({
                id: sanitizeText(component.id, 128),
                name: sanitizeText(component.name, 80),
                type: component.type === 'extra' ? 'extra' : 'included',
                price: component.type === 'extra' && Number.isFinite(Number(component.price))
                    ? Math.max(0, Number(Number(component.price).toFixed(2))) : 0
            }))
            : [],
        // Sanitize each portion name (price stays numeric)
        portions: Array.isArray(item.portions)
            ? item.portions.map(p => Object.assign({}, p, { name: sanitizeText(p.name, 60) }))
            : [],
    });
}
function isDailySpecial(item) {
    if (!item) return false;
    if (!(item.dailySpecial === true || item.dailySpecial === 'true' ||
          item.dailySpecial === 1 || item.dailySpecial === '1')) return false;
    // Auto-expire: if a deal end time was set and it has passed, treat as not special
    if (item.specialEndTime) {
        const end = new Date(item.specialEndTime);
        if (!isNaN(end.getTime()) && end < new Date()) return false;
    }
    return true;
}

/**
 * Build a human-readable countdown string for a deal end time.
 * Returns empty string if no end time or already expired.
 */
function getDealCountdown(endTimeStr) {
    if (!endTimeStr) return '';
    const end = new Date(endTimeStr);
    if (isNaN(end.getTime())) return '';
    const diff = end - Date.now();
    if (diff <= 0) return '(expired)';
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (hours >= 24) {
        const days = Math.floor(hours / 24);
        return `⏰ Ends in ${days}d ${hours % 24}h`;
    }
    if (hours > 0) return `⏰ Ends in ${hours}h ${mins}m`;
    return `⏰ Ends in ${mins}m`;
}

/** Show/hide the special detail fields when the checkbox is toggled. */
function toggleSpecialFields() {
    const checked = document.getElementById('dailySpecial') && document.getElementById('dailySpecial').checked;
    const group = document.getElementById('specialFieldsGroup');
    if (group) group.style.display = checked ? 'block' : 'none';
}

/**
 * Quick-toggle a menu item's Daily Special status from the menu card.
 * When turning ON: clears any other item's special flag for this provider first
 * (one special at a time), then opens a small inline dialog to optionally set
 * deal price, note, and end time before saving.
 */
function toggleDailySpecial(id) {
    const itemIndex = menuItems.findIndex(item => String(item.id) === String(id));
    if (itemIndex === -1) { showNotification('Menu item not found', 'error'); return; }

    const item = menuItems[itemIndex];
    const turningOn = !isDailySpecial(item);

    if (turningOn) {
        // Prompt for optional deal details via a small modal
        _openDailySpecialModal(id);
    } else {
        // Turn off — clear all special fields
        menuItems[itemIndex] = Object.assign({}, item, {
            dailySpecial: false,
            specialPrice: null,
            specialNote: '',
            specialEndTime: null,
            updatedAt: new Date().toISOString()
        });
        _persistMenuItem(menuItems[itemIndex]);
        renderMenuList();
        renderSearchResults();
        renderClientSearchResults();
        showNotification(`${item.foodName} is no longer a Daily Special`, 'info');
    }
}

/** Open an inline modal letting the provider optionally set deal price, note, and end time. */
function _openDailySpecialModal(id) {
    // Remove any stale modal
    const existing = document.getElementById('dailySpecialModal');
    if (existing) existing.remove();

    const item = menuItems.find(i => String(i.id) === String(id));
    if (!item) return;

    const modal = document.createElement('div');
    modal.id = 'dailySpecialModal';
    modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;';
    modal.innerHTML = `
        <div style="background:#fff;border-radius:12px;padding:24px;width:92%;max-width:420px;box-shadow:0 8px 32px rgba(0,0,0,0.25);">
            <h3 style="margin:0 0 4px;color:#e65100;">🔥 Set Daily Special</h3>
            <p style="margin:0 0 16px;color:#666;font-size:0.9em;">${escapeHtml(item.foodName)} — fill in deal details or leave blank and just click Save.</p>
            <label style="display:block;margin-bottom:4px;font-weight:600;">🏷️ Deal Price (optional)</label>
            <input type="number" id="dsModalPrice" placeholder="e.g. 7.99" step="0.01" min="0"
                value="${item.specialPrice > 0 ? item.specialPrice : ''}"
                style="width:100%;padding:8px;border:1px solid #ddd;border-radius:6px;box-sizing:border-box;margin-bottom:12px;">
            <label style="display:block;margin-bottom:4px;font-weight:600;">📝 Deal Note (optional)</label>
            <input type="text" id="dsModalNote" placeholder="e.g. 20% off today only" maxlength="150"
                value="${escapeHtml(item.specialNote || '')}"
                style="width:100%;padding:8px;border:1px solid #ddd;border-radius:6px;box-sizing:border-box;margin-bottom:12px;">
            <label style="display:block;margin-bottom:4px;font-weight:600;">⏰ Deal Expires At (optional)</label>
            <input type="datetime-local" id="dsModalEndTime"
                value="${item.specialEndTime ? item.specialEndTime.slice(0, 16) : ''}"
                style="width:100%;padding:8px;border:1px solid #ddd;border-radius:6px;box-sizing:border-box;margin-bottom:20px;">
            <div style="display:flex;gap:10px;justify-content:flex-end;">
                <button onclick="document.getElementById('dailySpecialModal').remove()"
                    style="padding:8px 18px;border:1px solid #ccc;border-radius:6px;background:#f5f5f5;cursor:pointer;">Cancel</button>
                <button onclick="_saveDailySpecialModal('${id}')"
                    style="padding:8px 18px;border:none;border-radius:6px;background:#e65100;color:#fff;font-weight:600;cursor:pointer;">🔥 Save Special</button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);
}

/** Persist the daily special modal values and activate the special. */
function _saveDailySpecialModal(id) {
    const itemIndex = menuItems.findIndex(i => String(i.id) === String(id));
    if (itemIndex === -1) return;

    const rawPrice = parseFloat(document.getElementById('dsModalPrice').value);
    const dealPrice = !isNaN(rawPrice) && rawPrice > 0 ? rawPrice : null;
    const dealNote = sanitizeText(document.getElementById('dsModalNote').value, 150);
    const endVal = document.getElementById('dsModalEndTime').value;
    const dealEnd = endVal ? new Date(endVal).toISOString() : null;

    // Validate: if end time is set, it must be in the future
    if (dealEnd && new Date(dealEnd) <= new Date()) {
        showNotification('Deal end time must be in the future', 'error');
        return;
    }

    // Clear any other item's special flag for this provider (one special at a time)
    if (currentProvider) {
        menuItems.forEach((mi, idx) => {
            if (String(mi.id) !== String(id) &&
                mi.providerName && currentProvider.name &&
                mi.providerName.toLowerCase() === currentProvider.name.toLowerCase() &&
                isDailySpecial(mi)) {
                menuItems[idx] = Object.assign({}, mi, {
                    dailySpecial: false, specialPrice: null, specialNote: '', specialEndTime: null,
                    updatedAt: new Date().toISOString()
                });
                _persistMenuItem(menuItems[idx]);
            }
        });
    }

    menuItems[itemIndex] = Object.assign({}, menuItems[itemIndex], {
        dailySpecial: true,
        specialPrice: dealPrice,
        specialNote: dealNote,
        specialEndTime: dealEnd,
        updatedAt: new Date().toISOString()
    });
    _persistMenuItem(menuItems[itemIndex]);

    document.getElementById('dailySpecialModal').remove();
    renderMenuList();
    renderSearchResults();
    renderClientSearchResults();
    showNotification(`🔥 ${menuItems[itemIndex].foodName} is now the Daily Special!`, 'success');
}

/** Save a single menu item to local state + Firestore. */
function _persistMenuItem(item) {
    saveMenuItems();
    if (typeof window.firebaseSaveMenuItem === 'function') {
        window.firebaseSaveMenuItem(item).catch(e => {
            console.warn('[Firestore] _persistMenuItem failed:', e.message);
        });
    }
}
function showNotification(message, type) {
    const notification = document.createElement('div');
    notification.className = `notification ${type}`;
    notification.textContent = message;

    document.body.appendChild(notification);

    setTimeout(() => {
        notification.remove();
    }, 3000);
}
// Expose to window so bundled scripts (e.g. renderAdminSubscriptions-extract.js) can access it
window.showNotification = showNotification;
// ==================== REVIEWS & RATINGS ====================
// Initialize Review functionality
function initReviewFeature() {
    // Star rating click handlers
    const ratingStars = document.getElementById('ratingStars');
    if (ratingStars) {
        const stars = ratingStars.querySelectorAll('.star');
        stars.forEach(star => {
            star.addEventListener('click', function() {
                selectedReviewRating = parseInt(this.dataset.rating);
                updateStarDisplay(selectedReviewRating);
            });

            star.addEventListener('mouseover', function() {
                const hoverRating = parseInt(this.dataset.rating);
                updateStarDisplay(hoverRating, true);
            });

            star.addEventListener('mouseout', function() {
                updateStarDisplay(selectedReviewRating);
            });
        });
    }

    // Review submit button
    const submitReviewBtn = document.getElementById('submitReviewBtn');
    if (submitReviewBtn) {
        submitReviewBtn.addEventListener('click', submitReview);
    }

    // Add to DOMContentLoaded
    loadReviewScripts();
}
// Load review scripts
function loadReviewScripts() {
    document.addEventListener('DOMContentLoaded', initReviewFeature);
}
// Update star display
function updateStarDisplay(rating, hover = false) {
    const stars = document.querySelectorAll('#ratingStars .star');
    stars.forEach((star, index) => {
        star.classList.remove('filled', 'hover');
        if (index < rating) {
            star.classList.add(hover ? 'hover' : 'filled');
        }
    });
}
// Submit review
function submitReview() {
    showNotification('Reviews can be submitted from a completed order in My Orders.', 'error');
}
// Calculate average rating for a provider
function getProviderRating(providerName, providerId = '') {
    const normalizedProviderId = String(providerId || '').trim();
    const normalizedProviderName = String(providerName || '').trim().toLowerCase();
    const providerReviews = reviews.filter(r =>
        (normalizedProviderId && String(r.providerId || '') === normalizedProviderId) ||
        (!normalizedProviderId && String(r.providerName || '').trim().toLowerCase() === normalizedProviderName)
    );
    if (providerReviews.length === 0) return null;

    const total = providerReviews.reduce((sum, r) => sum + r.rating, 0);
    const avg = total / providerReviews.length;

    return {
        average: avg.toFixed(1),
        count: providerReviews.length,
        reviews: providerReviews
    };
}
// Render provider reviews in dashboard
function renderProviderReviews() {
    const reviewsContainer = document.getElementById('providerReviews');
    if (!reviewsContainer || !currentProvider) return;

    // Use case-insensitive comparison for provider name matching
    const providerId = currentProvider.id || currentProvider.providerId || currentProvider.uid || '';
    const providerReviews = reviews.filter(r =>
        String(r.providerId || '') === String(providerId) ||
        (!providerId && r.providerName && r.providerName.toLowerCase() === currentProvider.name.toLowerCase())
    );

    if (providerReviews.length === 0) {
        reviewsContainer.innerHTML = '<p class="no-reviews">No reviews yet. Keep providing great service!</p>';
        return;
    }

    // Calculate average
    const total = providerReviews.reduce((sum, r) => sum + r.rating, 0);
    const avg = (total / providerReviews.length).toFixed(1);

    reviewsContainer.innerHTML = `
        <div class="rating-summary">
            <span class="avg-rating">★ ${avg}</span>
            <span class="review-count">${providerReviews.length} review${providerReviews.length !== 1 ? 's' : ''}</span>
        </div>
        ${providerReviews.map(review => `
            <div class="review-card">
                <div class="review-header">
                    <span class="review-author">${escapeHtml(review.clientName)}</span>
                    <span class="review-date">${new Date(review.createdAt).toLocaleDateString()}</span>
                </div>
                <div class="review-rating">
                    ${[1,2,3,4,5].map(i => `<span class="star ${i <= review.rating ? 'filled' : ''}">★</span>`).join('')}
                </div>
                ${review.comment ? `<div class="review-comment">${escapeHtml(review.comment)}</div>` : ''}
            </div>
        `).join('')}
    `;
}
// Enhance search results with ratings
function renderSearchResults() {
    const searchInputEl = document.getElementById('searchInput');
    const searchInput = searchInputEl ? searchInputEl.value.toLowerCase().trim() : '';
    const searchCountry = document.getElementById('searchCountry') ? document.getElementById('searchCountry').value : '';
    const availabilityFilterEl = document.getElementById('availabilityFilter');
    const availabilityFilter = availabilityFilterEl ? availabilityFilterEl.value : 'all';
    const providerFilterEl = document.getElementById('providerFilter');
    const providerFilter = providerFilterEl ? providerFilterEl.value : '';
    const resultsContainer = document.getElementById('searchResults');

    // SAFETY CHECK: Don't render if the container doesn't exist (e.g., on admin-only pages)
    if (!resultsContainer) {
        console.log('[Search] Results container not found - skipping render');
        return;
    }

    updateProviderFilter();

    // Check if user is logged in (registered) - strict validation
    const isLoggedIn = isUserLoggedIn();

    // Get user's registered country (for prioritizing results)
    const userCountry = currentClient ? currentClient.country : '';

// First filter the items - only show ACTIVE menu items to customers
    let filtered = menuItems.filter(item => {
        // Only show items that are marked as active (default to true if not set)
        const isActive = item.isActive !== false;
        if (!isActive) return false;
        const itemCountry = normalizeCountryCode(item.country || item.providerCountry || 'US');
        if (!isSupportedLaunchCountry(itemCountry)) return false;

        const matchesSearch = !searchInput ||
            item.foodName.toLowerCase().includes(searchInput) ||
            item.providerName.toLowerCase().includes(searchInput) ||
            item.foodDesc.toLowerCase().includes(searchInput);

        if (!matchesSearch) return false;

        // Filter by country (if selected in search)
        if (searchCountry && item.country !== searchCountry) return false;

        if (providerFilter && item.providerName !== providerFilter) return false;

        // Filter by meal type (if a meal type filter is selected)
        if (currentMealTypeFilter && item.mealType !== currentMealTypeFilter) return false;

        if (availabilityFilter === 'today' || availabilityFilter === 'tomorrow') {
            return getMenuAvailabilityStatus(item) === availabilityFilter;
        } else if (availabilityFilter === '247') {
            return item.is247 === true;
        } else if (availabilityFilter === 'open') {
            return isCurrentlyOpen(item);
        } else if (availabilityFilter === 'afterHours') {
            return item.afterHours === true && !isCurrentlyOpen(item);
        } else if (availabilityFilter === 'dailySpecial') {
            return isDailySpecial(item);
        }

        return true;
    });

    if (filtered.length === 0) {
        const fallbackItems = Array.isArray(window.__fallbackMenuItems) && window.__fallbackMenuItems.length > 0
            ? window.__fallbackMenuItems
            : (Array.isArray(menuItems) && menuItems.length > 0 ? menuItems : []);

        if (fallbackItems.length > 0) {
            if (typeof menuItems !== 'undefined' && Array.isArray(menuItems)) {
                menuItems.length = 0;
                fallbackItems.forEach(item => menuItems.push(item));
                window.menuItems = menuItems;
                window.__wtE_allMenuItems = menuItems;
            }
            filtered = fallbackItems.filter(item => {
                const isActive = item.isActive !== false;
                if (!isActive) return false;
                const itemCountry = normalizeCountryCode(item.country || item.providerCountry || 'US');
                if (!isSupportedLaunchCountry(itemCountry)) return false;
                if (searchInput && !(
                    (item.foodName || '').toLowerCase().includes(searchInput) ||
                    (item.providerName || '').toLowerCase().includes(searchInput) ||
                    (item.foodDesc || '').toLowerCase().includes(searchInput)
                )) {
                    return false;
                }
                if (searchCountry && item.country !== searchCountry) return false;
                if (providerFilter && item.providerName !== providerFilter) return false;
                if (currentMealTypeFilter && item.mealType !== currentMealTypeFilter) return false;
                if (availabilityFilter === 'today' || availabilityFilter === 'tomorrow') return getMenuAvailabilityStatus(item) === availabilityFilter;
                if (availabilityFilter === '247') return item.is247 === true;
                if (availabilityFilter === 'open') return isCurrentlyOpen(item);
                if (availabilityFilter === 'afterHours') return item.afterHours === true && !isCurrentlyOpen(item);
                if (availabilityFilter === 'dailySpecial') return isDailySpecial(item);
                return true;
            });
        }

        if (filtered.length === 0) {
            resultsContainer.innerHTML = '<div class="empty-state"><p>No meals found matching your search.</p></div>';
            return;
        }
    }

    filtered = rankSearchResults(filtered, {
        searchInput,
        userCountry,
        prioritizeUserCountry: !searchCountry
    });

// For unregistered users, show ALMOST NOTHING - just a teaser with registration prompt
// This encourages users to register to see the menu
if (!isLoggedIn) {
    // Show ONLY registration prompt - completely hide all menu details
    resultsContainer.innerHTML = filtered.map(item => {
        return `
            <div class="result-card restricted">
                <div class="register-prompt">
                    <p>🔒 <strong>${escapeHtml(t('registerToViewMenuItem', 'Register to view this menu item'))}</strong></p>
                    <p class="register-subtext">${escapeHtml(t('registerSubtext', 'Create a free account to see prices, descriptions, ingredients, photos, delivery options & order!'))}</p>
                    <button class="register-cta-btn" onclick="goToClientRegister()">Register Now</button>
                </div>
            </div>
        `;
    }).join('');

    return;
}

    // Show full info for logged in users
    resultsContainer.innerHTML = filtered.map(item => {
        const status = getAvailabilityStatus(item);
        const ratingData = getProviderRating(item.providerName, item.providerId);

        let photoHtml = item.photo ? `<div class="meal-photo"><img src="${item.photo}" alt="${escapeHtml(item.foodName)}"></div>` : '';

        let ratingHtml = '';
        if (ratingData) {
            ratingHtml = `
                <div class="rating-summary" onclick="showProviderReviews('${escapeHtml(item.providerName)}', '${escapeHtml(String(item.providerId || ''))}')">
                    <span class="avg-rating">★ ${ratingData.average}</span>
                    <span class="review-count">(${ratingData.count} ${escapeHtml(t('reviews', 'reviews'))})</span>
                </div>
            `;
        }

        const specialBadge = isDailySpecial(item) ? '<span class="special-badge">🔥 Daily Special</span>' : '';
        const itemCurrency = item.currency || getMarketRule(item.country || 'US').currency;
        const distanceKm = calculateDistanceKm(clientSearchGeo, resolveItemCoordinates(item));

        // Deal info for daily specials
        const dealInfoHtml = isDailySpecial(item) ? (() => {
            const countdown = getDealCountdown(item.specialEndTime);
            return '<div class="special-deal-info special-deal-client">' +
                (item.specialPrice > 0 ? `<span class="deal-price-tag">🏷️ Deal: ${formatCurrency(Number(item.specialPrice), item.country || 'US', itemCurrency)} <span class="original-price">(reg. ${formatCurrency(Number(item.price || 0), item.country || 'US', itemCurrency)})</span></span>` : '') +
                (item.specialNote ? `<span class="deal-note">${escapeHtml(item.specialNote)}</span>` : '') +
                (countdown ? `<span class="deal-countdown">${countdown}</span>` : '') +
            '</div>';
        })() : '';

        // Show ALL info for registered users
        const restrictedInfoHtml = `
            ${item.ingredients ? `<div class="ingredients-info"><strong>${escapeHtml(t('ingredientsAllergens', 'Ingredients/Allergens'))}:</strong> ${escapeHtml(item.ingredients)}</div>` : ''}
            ${item.drinks ? `<div class="drinks-info"><strong>${escapeHtml(t('drinksLabel', 'Drinks'))}:</strong> ${escapeHtml(item.drinks)}</div>` : ''}
            ${ratingHtml}
            ${buildProviderTrustSignalsHtml(getProviderTrustSignals(item, ratingData))}
            <div class="meal-details">
                ${isDailySpecial(item) && item.specialPrice > 0
                    ? `<span class="detail-tag price deal-active-price">${formatCurrency(Number(item.specialPrice), item.country || 'US', itemCurrency)} <span class="original-price">${formatCurrency(Number(item.price || 0), item.country || 'US', itemCurrency)}</span></span>`
                    : `<span class="detail-tag price">${formatCurrency(Number(item.price || 0), item.country || 'US', itemCurrency)}</span>`}
                <span class="detail-tag servings">${item.servingsLeft} ${escapeHtml(t('servingsLeft', 'servings left'))}</span>
                <span class="detail-tag ${status.class}">${status.text}</span>
                ${Number.isFinite(distanceKm) ? `<span class="detail-tag location">📍 ${distanceKm.toFixed(1)} km</span>` : ''}
                ${item.afterHours ? `<span class="detail-tag after-hours">${escapeHtml(t('afterHoursOk', 'After Hours OK'))}</span>` : ''}
            </div>
            ${item.is247 ? `<span class="detail-tag status-247">${escapeHtml(t('filter247', '24/7 Available'))}</span>` : ''}
            <div class="delivery-options">
                ${item.offersDelivery && item.offersPickup ? `<span class="delivery-tag both">${escapeHtml(t('deliveryAndPickup', 'Delivery & Pickup'))}</span>` : ''}
                ${item.offersDelivery && !item.offersPickup ? `<span class="delivery-tag delivery">${escapeHtml(t('deliveryOnly', 'Delivery'))}</span>` : ''}
                ${!item.offersDelivery && item.offersPickup ? `<span class="delivery-tag pickup">${escapeHtml(t('pickupOnly', 'Pickup Only'))}</span>` : ''}
            </div>
        `;
        // CRITICAL FIX: item.id is a UUID string (e.g. "123e4567-e89b-12d3-a456-426614174000").
        // It MUST be wrapped in quotes inside the onclick attribute, otherwise the browser
        // throws "Uncaught SyntaxError: Invalid or unexpected token" and the modal never opens.
        const itemJson = encodeURIComponent(JSON.stringify({
            id: item.id,
            title: item.foodName,
            foodName: item.foodName,
            price: item.price,
            providerId: item.providerId || item.providerName,
            providerName: item.providerName,
            currency: itemCurrency
        }));
        const contactButtonHtml = `<button class="contact-btn" onclick="openContactModal('${item.id}')">${getMenuAvailabilityStatus(item) === 'tomorrow' ? escapeHtml(t('preorderForTomorrow', 'Preorder for tomorrow')) : 'Contact to Order'}</button>`;
        const giftButtonHtml = `<button class="btn-send-gift" onclick="window.openGiftOrderModal(JSON.parse(decodeURIComponent('${itemJson}')))">🎁 Gift a Friend</button>`;
        const requestButtonHtml = `<button class="btn-request-meal" onclick="window.openMealRequestModal(JSON.parse(decodeURIComponent('${itemJson}')))">🍔 Request from Friend</button>`;

        return `
            <div class="result-card ${isDailySpecial(item) ? 'daily-special-card' : ''}">
                ${photoHtml}
                <div class="provider-name">${escapeHtml(item.providerName)} ${specialBadge}</div>
                <div class="food-name">${escapeHtml(item.foodName)} ${getMenuAvailabilityBadge(item)} ${isDailySpecial(item) ? '<span class="special-badge-inline">🔥 Daily Special</span>' : ''}</div>
                ${dealInfoHtml}
                <div class="food-desc">${escapeHtml(item.foodDesc)}</div>
                ${restrictedInfoHtml}
                <div class="card-actions" style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">
                    ${contactButtonHtml}
                    ${giftButtonHtml}
                    ${requestButtonHtml}
                    ${typeof window.favoriteProviderButton === 'function' ? window.favoriteProviderButton(item) : ''}
                </div>
            </div>
        `;
    }).join('');
}
async function showProviderReviews(providerName, providerId = '') {
    const modal = document.getElementById('providerReviewsModal');
    const title = document.getElementById('providerReviewsModalTitle');
    const content = document.getElementById('providerReviewsModalContent');
    if (!modal || !title || !content) return;

    title.textContent = `${providerName} Reviews`;
    content.innerHTML = '<p class="no-reviews">Loading reviews...</p>';
    modal.style.display = 'flex';
    let providerReviews = getProviderRating(providerName, providerId)?.reviews || [];
    if (providerId && typeof window.firebaseGetProviderReviews === 'function') {
        try {
            providerReviews = await window.firebaseGetProviderReviews(providerId);
        } catch (error) {
            console.error('[Reviews] Failed to load provider reviews:', error);
            content.innerHTML = '<p class="no-reviews">Unable to load reviews. Please try again.</p>';
            return;
        }
    }
    const total = providerReviews.reduce((sum, review) => sum + Number(review.rating || 0), 0);
    const ratingData = providerReviews.length ? {
        average: (total / providerReviews.length).toFixed(1),
        count: providerReviews.length,
        reviews: providerReviews
    } : null;
    if (!ratingData) {
        content.innerHTML = '<p class="no-reviews">No reviews yet.</p>';
    } else {
        content.innerHTML = `
            <div class="rating-summary">
                <span class="avg-rating">★ ${ratingData.average}</span>
                <span class="review-count">${ratingData.count} review${ratingData.count !== 1 ? 's' : ''}</span>
            </div>
            ${ratingData.reviews.map(review => `
                <div class="review-card">
                    <div class="review-header">
                        <span class="review-author">${escapeHtml(review.clientName || 'Anonymous')}</span>
                        <span class="review-date">${formatDateTime(review.createdAt)}</span>
                    </div>
                    <div class="review-rating">${[1, 2, 3, 4, 5].map(i => `<span class="star ${i <= review.rating ? 'filled' : ''}">★</span>`).join('')}</div>
                    ${review.comment ? `<div class="review-comment">${escapeHtml(review.comment)}</div>` : ''}
                </div>
            `).join('')}
        `;
    }
}

function closeProviderReviewsModal() {
    const modal = document.getElementById('providerReviewsModal');
    if (modal) modal.style.display = 'none';
}
window.closeProviderReviewsModal = closeProviderReviewsModal;
// ==================== CLIENT PROFILE EDITING ====================
// Render read-only profile view (My Profile tab)
function renderClientProfileView() {
    if (!currentClient) return;

    const profilePanel = document.getElementById('client-profile-panel');
    if (!profilePanel) return;

    const viewContainer = document.getElementById('clientEditProfile');
    if (!viewContainer) return;

    // Ensure read-only view content only (no form inputs)
    viewContainer.innerHTML = `
        <div class="profile-details-card">
            <p><strong>👤 Name:</strong> ${escapeHtml(currentClient.name || '')}</p>
            <p><strong>📧 Email:</strong> ${escapeHtml(currentClient.email || '')}</p>
            <p><strong>📱 Phone:</strong> ${escapeHtml(currentClient.phone || '')}</p>
            <p><strong>🌍 Country:</strong> ${escapeHtml(currentClient.country || '')}</p>
            <div style="margin-top: 15px;">
                <button type="button" class="btn btn-secondary edit-profile-btn" onclick="showClientEditProfile()">✏️ Edit Profile</button>
            </div>
        </div>

        <div class="gdpr-privacy-section" style="margin-top: 25px; padding: 15px; border: 1px solid #e2e8f0; border-radius: 8px; background-color: #f8fafc;">
            <h4 style="margin-top: 0; color: #2d3748; display: flex; align-items: center; gap: 8px;">
                <span>🔒</span> Privacy & Data Rights (GDPR / CCPA Compliance)
            </h4>
            <p style="font-size: 0.9em; color: #4a5568; line-height: 1.4; margin-bottom: 15px;">
                Under EU GDPR (Articles 15 & 20) and California CCPA/CPRA, you have the right to export your personal data or request permanent account deletion ("Right to be Forgotten").
            </p>
            <div style="display: flex; gap: 10px; flex-wrap: wrap;">
                <button type="button" class="btn export-data-btn" onclick="exportPersonalData('client')" style="background-color: #2b6cb0; color: white; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;">
                    📥 Export My Personal Data (JSON)
                </button>
                <button type="button" class="btn delete-account-btn" onclick="deleteUserAccount('client')" style="background-color: #e53e3e; color: white; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;">
                    🗑️ Delete My Account
                </button>
            </div>
        </div>
    `;

    // Ensure workflow visibility
    const editForm = document.getElementById('clientEditProfileForm');
    if (editForm) editForm.classList.add('hidden');
    profilePanel.classList.remove('hidden');
}

// Global function to be called from onclick
window.clientEditProfile = function() {
    showClientEditForm();
};
function initClientProfileEdit() {
    // Client profile form submission
    const profileForm = document.getElementById('clientProfileForm');
    if (profileForm) {
        profileForm.addEventListener('submit', (e) => {
            e.preventDefault();
            saveClientProfile();
        });
    }

    // Cancel button for client profile edit
    const cancelClientBtn = document.getElementById('cancelClientEditBtn');
    if (cancelClientBtn) {
        cancelClientBtn.addEventListener('click', function() {
            cancelClientEdit();
        });
    }
}
function showClientEditForm() {
    if (!currentClient) return;
    const dashboard = document.getElementById('clientDashboard');
    const editSection = document.getElementById('clientEditProfileForm');
    const profilePanel = document.getElementById('client-profile-panel');

    if (!editSection) return;

    // Populate current values
    const nameEl = document.getElementById('clientEditName');
    const phoneEl = document.getElementById('clientEditPhone');
    const countryEl = document.getElementById('clientEditCountry');
    if (nameEl) nameEl.value = currentClient.name || '';
    if (phoneEl) phoneEl.value = currentClient.phone || '';
    if (countryEl) countryEl.value = currentClient.country || '';

    // Workflow: switch to EDIT form only
    if (profilePanel) profilePanel.classList.add('hidden');
    editSection.classList.remove('hidden');

    // Hide other panels while editing
    const ordersSection = document.getElementById('clientOrders');
    if (ordersSection) ordersSection.classList.add('hidden');

    const customReqSection = dashboard ? dashboard.querySelector('.custom-request-section') : null;
    if (customReqSection) customReqSection.classList.add('hidden');

    if (dashboard) {
        const altCustom = dashboard.querySelector('#clientCustomRequests, [data-section="custom-requests"], .client-custom-requests');
        if (altCustom) altCustom.classList.add('hidden');
    }

    const headerSection = dashboard ? dashboard.querySelector('.dashboard-header') : null;
    if (headerSection) headerSection.classList.add('hidden');
}
function saveClientProfile() {
    if (!currentClient) return;

    const nameEl = document.getElementById('clientEditName');
    const phoneEl = document.getElementById('clientEditPhone');
    const countryEl = document.getElementById('clientEditCountry');

    const newName = nameEl ? nameEl.value.trim() : '';
    const newPhone = phoneEl ? phoneEl.value.trim() : '';
    const newCountryRaw = countryEl ? countryEl.value : '';
    const newCountry = newCountryRaw ? normalizeCountryCode(newCountryRaw) : '';

    if (!newName) {
        showNotification('Name is required', 'error');
        return;
    }

    if (!newCountry) {
        showNotification('Country is required', 'error');
        return;
    }

    const normalizedPhone = normalizePhoneNumber(newPhone, newCountry);
    if (!normalizedPhone || !isValidE164Phone(normalizedPhone)) {
        showNotification(t('phoneValidation', 'Phone number must use international format.'), 'error');
        return;
    }

    // Capture privacy opt-in from the social features toggle
    const allowFriendRequests = typeof window.getPrivacyOptIn === 'function'
        ? window.getPrivacyOptIn()
        : (currentClient.allowFriendRequests || false);

    // Update current client session
    currentClient.name = newName;
    currentClient.phone = normalizedPhone;
    currentClient.country = newCountry;
    currentClient.allowFriendRequests = allowFriendRequests;
    // Keep a lowercase displayName for Firestore range queries in the friend picker
    currentClient.displayName = newName;
    currentClient.displayNameLower = newName.toLowerCase();

    // Update in browserStorage
    // Update in clients array
    const clientIndex = clients.findIndex(c => c.id === currentClient.id);
    if (clientIndex !== -1) {
        clients[clientIndex] = currentClient;
    }

    // Hide edit form
    const editSection = document.getElementById('clientEditProfileForm');
    if (editSection) editSection.classList.add('hidden');

    // Show profile panel and render read-only view
    const profilePanel = document.getElementById('client-profile-panel');
    if (profilePanel) profilePanel.classList.remove('hidden');
    renderClientProfileView();

    // Update welcome message
    const dashboard = document.getElementById('clientDashboard');
    const welcomeEl = dashboard ? dashboard.querySelector('h2') : null;
    if (welcomeEl) welcomeEl.textContent = `My Orders - Welcome, ${currentClient.name}!`;

    // Update header badge
    const userInfo = document.getElementById('userInfo');
    if (userInfo) userInfo.innerHTML = `<span class="user-badge">👤 ${currentClient.name}</span>`;

    // Persist allowFriendRequests + displayNameLower to Firestore
    if (window.firebaseDbInstance && currentClient.uid) {
        try {
            window.firebaseDbInstance.collection('clients').doc(currentClient.uid).set({
                displayName: currentClient.name,
                displayNameLower: currentClient.name.toLowerCase(),
                allowFriendRequests: !!allowFriendRequests,
            }, { merge: true }).catch((e) => console.warn('[saveClientProfile] Firestore update failed:', e));
        } catch (e) {
            console.warn('[saveClientProfile] Firestore not ready:', e);
        }
    }

    showNotification('Profile updated successfully!', 'success');
}
function cancelClientEdit() {
    const editSection = document.getElementById('clientEditProfileForm');
    const dashboard = document.getElementById('clientDashboard');

    if (editSection) editSection.classList.add('hidden');

    // Hide any header hiding done in showClientEditForm
    const headerSection = dashboard ? dashboard.querySelector('.dashboard-header') : null;
    if (headerSection) headerSection.classList.remove('hidden');

    // Show profile panel (read-only)
    const profilePanel = document.getElementById('client-profile-panel');
    if (profilePanel) {
        profilePanel.classList.remove('hidden');
        renderClientProfileView();
    }

    // Show orders section again
    const ordersSection = document.getElementById('clientOrders');
    const customReq = dashboard ? dashboard.querySelector('.custom-request-section') : null;
    if (ordersSection) ordersSection.classList.remove('hidden');
    if (customReq) customReq.classList.remove('hidden');

    if (dashboard) {
        const altCustom = dashboard.querySelector('#clientCustomRequests, [data-section="custom-requests"], .client-custom-requests');
        if (altCustom) altCustom.classList.remove('hidden');
    }

    try {
        renderClientOrders();
    } catch (e) {
        // no-op
    }
}
// ==================== PROVIDER PROFILE EDITING ====================
// Global function to be called from onclick
window.providerEditProfile = function() {
    showProviderEditForm();
};
// ==================== GDPR / CCPA DATA EXPORT & ACCOUNT DELETION ====================

async function exportPersonalData(role = 'client') {
    try {
        if (typeof showNotification === 'function') {
            showNotification('Preparing your personal data export...', 'info');
        }

        let token = null;
        if (typeof getFirebaseIdToken === 'function') {
            try {
                token = await getFirebaseIdToken();
            } catch (e) {
                console.warn('[exportPersonalData] Failed to get Auth token:', e);
            }
        }

        let exportData = null;

        if (token) {
            try {
                const appCheckToken = await getAppCheckToken();
                const response = await fetch(`https://us-central1-${(window.firebaseConfig && window.firebaseConfig.projectId) || 'what-to-eat-e5ce9'}.cloudfunctions.net/exportUserData`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Firebase-AppCheck': appCheckToken,
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    exportData = await response.json();
                } else {
                    console.warn('[exportPersonalData] Endpoint returned non-OK status:', response.status);
                }
            } catch (err) {
                console.warn('[exportPersonalData] Error fetching exportUserData:', err);
            }
        }

        if (!exportData) {
            const userObj = role === 'provider' ? (typeof currentProvider !== 'undefined' ? currentProvider : null) : (typeof currentClient !== 'undefined' ? currentClient : null);
            const userId = userObj?.id || userObj?.uid;
            const orders = typeof allOrders !== 'undefined' && Array.isArray(allOrders) ? allOrders : [];
            const items = typeof menuItems !== 'undefined' && Array.isArray(menuItems) ? menuItems : [];

            exportData = {
                complianceInfo: {
                    standard: 'GDPR (EU Article 15/20) & CCPA/CPRA (California Civil Code § 1798.100)',
                    exportedAt: new Date().toISOString(),
                    role: role,
                    userId: userId || 'unknown'
                },
                profile: userObj || {},
                orders: orders.filter(o => Boolean(userId && (o.clientId === userId || o.providerId === userId))),
                menuItems: role === 'provider' ? items.filter(m => Boolean(userId && m.providerId === userId)) : []
            };
        }

        if (role === 'client' && typeof window.exportClientFavorites === 'function') {
            exportData.favoriteProviders = await window.exportClientFavorites();
        }
        const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(exportData, null, 2));
        const downloadAnchor = document.createElement('a');
        const timestamp = new Date().toISOString().slice(0, 10);
        downloadAnchor.setAttribute('href', dataStr);
        downloadAnchor.setAttribute('download', `what-to-eat-personal-data-${role}-${timestamp}.json`);
        document.body.appendChild(downloadAnchor);
        downloadAnchor.click();
        downloadAnchor.remove();

        if (typeof showNotification === 'function') {
            showNotification('Personal data exported successfully.', 'success');
        }
    } catch (error) {
        console.error('[exportPersonalData] Error:', error);
        if (typeof showNotification === 'function') {
            showNotification('Failed to export personal data: ' + error.message, 'error');
        }
    }
}
window.exportPersonalData = exportPersonalData;

async function deleteUserAccount(role = 'client') {
    const userObj = role === 'provider' ? (typeof currentProvider !== 'undefined' ? currentProvider : null) : (typeof currentClient !== 'undefined' ? currentClient : null);
    const userName = userObj?.name || userObj?.restaurantName || 'User';

    const confirmFirst = confirm(
        `⚠️ GDPR / CCPA Account Deletion ("Right to be Forgotten")\n\n` +
        `Are you sure you want to permanently delete your account (${userName})?\n\n` +
        `This will permanently purge:\n` +
        `• Your profile records & contact details\n` +
        `• Your complete order history\n` +
        `• Your menu items, meal requests & custom messages\n` +
        `• Your reviews & uploaded photos\n` +
        `• Your authentication account\n\n` +
        `THIS ACTION CANNOT BE UNDONE.`
    );

    if (!confirmFirst) return;

    const confirmSecond = prompt(`To confirm deletion of your account, please type "DELETE" below:`);
    if (!confirmSecond || confirmSecond.trim().toUpperCase() !== 'DELETE') {
        if (typeof showNotification === 'function') {
            showNotification('Account deletion cancelled.', 'info');
        }
        return;
    }

    try {
        if (typeof showNotification === 'function') {
            showNotification('Deleting account and purging personal data...', 'info');
        }

        let token = null;
        if (typeof getFirebaseIdToken === 'function') {
            try {
                token = await getFirebaseIdToken();
            } catch (e) {
                console.warn('[deleteUserAccount] Could not fetch token:', e);
            }
        }

        let success = false;
        let errorMessage = '';

        if (token) {
            try {
                const appCheckToken = await getAppCheckToken();
                const response = await fetch(`https://us-central1-${(window.firebaseConfig && window.firebaseConfig.projectId) || 'what-to-eat-e5ce9'}.cloudfunctions.net/deleteUserData`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Firebase-AppCheck': appCheckToken,
                        'Authorization': `Bearer ${token}`
                    }
                });

                const resData = await response.json();
                if (response.ok && resData.ok) {
                    success = true;
                } else {
                    errorMessage = resData.error || resData.message || 'Server failed to delete user data.';
                }
            } catch (err) {
                console.warn('[deleteUserAccount] Cloud function error:', err);
                errorMessage = err.message;
            }
        }

        if (!success && !token) {
            success = true;
        }

        if (success) {
            if (role === 'client') {
                if (typeof currentClient !== 'undefined') currentClient = null;
            } else {
                if (typeof currentProvider !== 'undefined') currentProvider = null;
            }

            if (typeof firebase !== 'undefined' && firebase.auth) {
                await firebase.auth().signOut().catch(() => {});
            }

            if (typeof showNotification === 'function') {
                showNotification('Your account and personal data have been permanently deleted.', 'success');
            }

            setTimeout(() => {
                window.location.reload();
            }, 1500);
        } else {
            throw new Error(errorMessage || 'Account deletion failed.');
        }
    } catch (error) {
        console.error('[deleteUserAccount] Error:', error);
        if (typeof showNotification === 'function') {
            showNotification('Failed to delete account: ' + error.message, 'error');
        }
    }
}
window.deleteUserAccount = deleteUserAccount;

function renderProviderProfileView() {
    const provider = currentProvider || window.currentProvider || (typeof currentUser !== 'undefined' ? currentUser : null);

    let viewContainer = document.getElementById('providerProfileView');
    if (!viewContainer) return;

    if (!provider) {
        viewContainer.innerHTML = `
            <div class="empty-state" style="padding: 20px; text-align: center; color: #718096;">
                <p>⚠️ Provider profile information is loading or unavailable. Please sign in as a provider.</p>
            </div>
        `;
        return;
    }

    viewContainer.innerHTML = `
        <div class="profile-details-card">
            <p><strong>🏪 Type:</strong> ${escapeHtml(provider.type || 'Provider')}</p>
            <p><strong>🍽️ Name / Restaurant:</strong> ${escapeHtml(provider.name || provider.restaurantName || '')}</p>
            <p><strong>📧 Email:</strong> ${escapeHtml(provider.email || '')}</p>
            <p><strong>📱 Phone:</strong> ${escapeHtml(provider.phone || '')}</p>
            <p><strong>📍 Address:</strong> ${escapeHtml(provider.address || '')}</p>
            <p><strong>🌍 Country:</strong> ${escapeHtml(provider.country || '')}</p>
            <p><strong>🍲 Accepts Custom Requests:</strong> ${provider.acceptsCustomRequests ? 'Yes' : 'No'}</p>
            <div style="margin-top: 15px;">
                <button type="button" class="btn btn-secondary edit-profile-btn" onclick="showProviderEditProfile()">✏️ Edit Profile</button>
            </div>
        </div>

        <div class="gdpr-privacy-section" style="margin-top: 25px; padding: 15px; border: 1px solid #e2e8f0; border-radius: 8px; background-color: #f8fafc;">
            <h4 style="margin-top: 0; color: #2d3748; display: flex; align-items: center; gap: 8px;">
                <span>🔒</span> Privacy & Data Rights (GDPR / CCPA Compliance)
            </h4>
            <p style="font-size: 0.9em; color: #4a5568; line-height: 1.4; margin-bottom: 15px;">
                Under EU GDPR (Articles 15 & 20) and California CCPA/CPRA, you have the right to export your personal data or request permanent account deletion ("Right to be Forgotten").
            </p>
            <div style="display: flex; gap: 10px; flex-wrap: wrap;">
                <button type="button" class="btn export-data-btn" onclick="exportPersonalData('provider')" style="background-color: #2b6cb0; color: white; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;">
                    📥 Export My Personal Data (JSON)
                </button>
                <button type="button" class="btn delete-account-btn" onclick="deleteUserAccount('provider')" style="background-color: #e53e3e; color: white; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;">
                    🗑️ Delete My Account
                </button>
            </div>
        </div>
    `;

    let formContainer = document.getElementById('providerEditProfileForm');
    if (formContainer) formContainer.classList.add('hidden');
}
window.renderProviderProfileView = renderProviderProfileView;

function initProviderProfileEdit() {
    // Provider profile form submission
    const profileForm = document.getElementById('providerProfileForm');
    if (profileForm) {
        profileForm.addEventListener('submit', (e) => {
            e.preventDefault();
            saveProviderProfile();
        });
    }

    // Cancel button for provider profile edit
    const cancelProviderBtn = document.getElementById('cancelProviderEditBtn');
    if (cancelProviderBtn) {
        cancelProviderBtn.addEventListener('click', function() {
            hideProviderEditForm();
        });
    }
}
function showProviderEditForm() {
    if (!currentProvider) return;

    const editForm = document.getElementById('providerEditProfileForm');
    if (!editForm) return;

    // Populate current values
    document.getElementById('providerEditType').value = currentProvider.type || 'Restaurant';
    document.getElementById('providerEditName').value = currentProvider.name || '';
    document.getElementById('providerEditPhone').value = currentProvider.phone || '';
    document.getElementById('providerEditAddress').value = currentProvider.address || '';
    document.getElementById('providerEditCountry').value = currentProvider.country || '';
    const providerTimezoneEl = document.getElementById('providerEditTimezone');
    const providerLatEl = document.getElementById('providerEditLatitude');
    const providerLngEl = document.getElementById('providerEditLongitude');
    if (providerTimezoneEl) {
        populateProviderTimezoneSelect(providerTimezoneEl, currentProvider.timezone || detectProviderTimezone());
    }
    if (providerLatEl) providerLatEl.value = currentProvider.providerCoordinates && Number.isFinite(Number(currentProvider.providerCoordinates.latitude)) ? Number(currentProvider.providerCoordinates.latitude) : '';
    if (providerLngEl) providerLngEl.value = currentProvider.providerCoordinates && Number.isFinite(Number(currentProvider.providerCoordinates.longitude)) ? Number(currentProvider.providerCoordinates.longitude) : '';
    initProviderLocationAssist({
        timezoneInputId: 'providerEditTimezone',
        latitudeInputId: 'providerEditLatitude',
        longitudeInputId: 'providerEditLongitude',
        locationButtonId: 'providerEditUseCurrentLocationBtn'
    });

    // Workflow: show edit form only
    editForm.classList.remove('hidden');

    const dashboard = document.getElementById('providerDashboard');
    const menuForm = document.getElementById('menuForm');
    const menuList = document.getElementById('menuList');
    const ordersSection = document.getElementById('providerOrders');
    const reviewsSection = document.getElementById('providerReviews');
    if (menuForm) menuForm.classList.add('hidden');
    if (menuList) menuList.classList.add('hidden');
    if (ordersSection) ordersSection.classList.add('hidden');
    if (reviewsSection) reviewsSection.classList.add('hidden');

    // Hide all tab panels so only the edit form is visible
    if (dashboard) {
        const panels = dashboard.querySelectorAll('.dashboard-panel-section');
        panels.forEach(p => p.classList.add('hidden'));
    }
}
function saveProviderProfile() {
    if (!currentProvider) return;

    const newType = document.getElementById('providerEditType').value;
    const newName = document.getElementById('providerEditName').value.trim();
    const newPhone = document.getElementById('providerEditPhone').value.trim();
    const newAddress = document.getElementById('providerEditAddress').value.trim();
    const newCountryRaw = document.getElementById('providerEditCountry').value;
    const newCountry = newCountryRaw ? normalizeCountryCode(newCountryRaw) : '';
    const timezoneEl = document.getElementById('providerEditTimezone');
    const latitudeEl = document.getElementById('providerEditLatitude');
    const longitudeEl = document.getElementById('providerEditLongitude');

    if (!newName || !newPhone) {
        showNotification('Name and phone are required', 'error');
        return;
    }

    if (!newCountry) {
        showNotification('Country is required', 'error');
        return;
    }

    const launchContext = getProviderLaunchContext(newCountry);
    if (!launchContext.policy.enabled) {
        const launchReason = launchContext.policy.reason || t('countryRestriction', 'This country is currently restricted for launch.');
        showNotification(`${launchReason} ${t('supportedCountries', 'Supported countries')}: ${getSupportedLaunchCountries().join(', ')}`, 'error');
        return;
    }

    const normalizedPhone = normalizePhoneNumber(newPhone, newCountry);
    if (!normalizedPhone || !isValidE164Phone(normalizedPhone)) {
        showNotification(t('phoneValidation', 'Phone number must use international format.'), 'error');
        return;
    }
    const timezoneSeed = timezoneEl && timezoneEl.value ? timezoneEl.value : detectProviderTimezone();
    const timezone = normalizeProviderTimezone(timezoneSeed || launchContext.timezone);
    const normalizedAddress = normalizeInternationalAddress(newAddress, newCountry);
    const coordinates = normalizeProviderCoordinates(
        latitudeEl ? latitudeEl.value : null,
        longitudeEl ? longitudeEl.value : null
    );

    // Update current provider session
    currentProvider.type = newType;
    currentProvider.name = newName;
    currentProvider.phone = normalizedPhone;
    currentProvider.address = normalizedAddress.fullAddress;
    currentProvider.addressComponents = normalizedAddress;
    currentProvider.country = newCountry;
    currentProvider.currency = launchContext.currency;
    currentProvider.timezone = timezone;
    currentProvider.providerCoordinates = coordinates;
    currentProvider.launchCountryPolicy = {
        enabled: launchContext.policy.enabled,
        allowProviderPayouts: launchContext.policy.allowProviderPayouts,
        evaluatedAt: new Date().toISOString()
    };

    // Update in browserStorage
    // Update in providers array
    const providerIndex = providers.findIndex(p => p.id === currentProvider.id);
    if (providerIndex !== -1) {
        providers[providerIndex] = currentProvider;
    }

    // The server-side end-of-day close reads timezone/currency from Firestore.
    const businessSettings = { timezone };
    if (/^[A-Za-z]{3}$/.test(String(currentProvider.currency || ''))) businessSettings.currency = String(currentProvider.currency).toUpperCase();
    persistProviderBusinessSettings(businessSettings).then((saved) => {
        if (!saved) showNotification('Timezone could not be saved to the server; daily reports may close at the wrong time.', 'warning');
    }).catch(() => {});

    // Hide edit form, show dashboard
    hideProviderEditForm();

    showNotification('Profile updated successfully!', 'success');

    // Update hidden fields for menu items
    const providerNameInput = document.getElementById('providerName');
    const providerPhoneInput = document.getElementById('providerPhone');
    const providerEmailInput = document.getElementById('providerEmail');
    const providerAddressInput = document.getElementById('providerAddress');

    if (providerNameInput) providerNameInput.value = currentProvider.name;
    if (providerPhoneInput) providerPhoneInput.value = currentProvider.phone;
    if (providerEmailInput) providerEmailInput.value = currentProvider.email;
    if (providerAddressInput) providerAddressInput.value = currentProvider.address || '';

    // Update header badge
    const userInfo = document.getElementById('userInfo');
    if (userInfo) userInfo.innerHTML = `<span class="user-badge">🍽️ ${currentProvider.name}</span>`;
}
function hideProviderEditForm() {
    const editSection = document.getElementById('providerEditProfileForm');
    if (editSection) editSection.classList.add('hidden');

    const dashboard = document.getElementById('providerDashboard');

    // Restore provider UI: show all tab panels back (tabs controller will display correct one)
    if (dashboard) {
        const panels = dashboard.querySelectorAll('.dashboard-panel-section');
        panels.forEach(p => p.classList.remove('hidden'));
    }

    const menuForm = document.getElementById('menuForm');
    const menuList = document.getElementById('menuList');
    const ordersSection = document.getElementById('providerOrders');
    const reviewsSection = document.getElementById('providerReviews');

    if (menuForm) menuForm.classList.remove('hidden');
    if (menuList) menuList.classList.remove('hidden');
    if (ordersSection) ordersSection.classList.remove('hidden');
    if (reviewsSection) reviewsSection.classList.remove('hidden');
}
// Make review functions global
window.deleteMenuItem = deleteMenuItem;
window.openContactModal = openContactModal;
window.showProviderReviews = showProviderReviews;
window.saveClientProfile = saveClientProfile;
window.cancelClientEdit = cancelClientEdit;
window.saveProviderProfile = saveProviderProfile;
window.hideProviderEditForm = hideProviderEditForm;
// Make profile edit functions global for onclick handlers
// NOTE: client profile edit is handled by showClientEditForm() (single source of truth)
// This alias exists only for compatibility with existing inline onclick="showClientEditProfile()".
window.showClientEditProfile = function() {
    showClientEditForm();
};
window.showProviderEditProfile = function() {
    if (!currentProvider) return;

    const dashboard = document.getElementById('providerDashboard');
    const editForm = document.getElementById('providerEditProfileForm');

    if (!editForm) return;

    // Populate current values
    document.getElementById('providerEditType').value = currentProvider.type || 'Restaurant';
    document.getElementById('providerEditName').value = currentProvider.name || '';
    document.getElementById('providerEditPhone').value = currentProvider.phone || '';
    document.getElementById('providerEditAddress').value = currentProvider.address || '';
    document.getElementById('providerEditCountry').value = currentProvider.country || '';
    const providerTimezoneEl = document.getElementById('providerEditTimezone');
    const providerLatEl = document.getElementById('providerEditLatitude');
    const providerLngEl = document.getElementById('providerEditLongitude');
    if (providerTimezoneEl) providerTimezoneEl.value = currentProvider.timezone || detectProviderTimezone();
    if (providerLatEl) providerLatEl.value = currentProvider.providerCoordinates && Number.isFinite(Number(currentProvider.providerCoordinates.latitude)) ? Number(currentProvider.providerCoordinates.latitude) : '';
    if (providerLngEl) providerLngEl.value = currentProvider.providerCoordinates && Number.isFinite(Number(currentProvider.providerCoordinates.longitude)) ? Number(currentProvider.providerCoordinates.longitude) : '';
    initProviderLocationAssist({
        timezoneInputId: 'providerEditTimezone',
        latitudeInputId: 'providerEditLatitude',
        longitudeInputId: 'providerEditLongitude',
        locationButtonId: 'providerEditUseCurrentLocationBtn'
    });

    // Hide menu form and other content, show edit form
    const menuForm = document.getElementById('menuForm');
    const menuList = document.getElementById('menuList');
    const ordersSection = document.getElementById('providerOrders');
    const reviewsSection = document.getElementById('providerReviews');
    const headings = dashboard.querySelectorAll('h3');

    if (menuForm) menuForm.classList.add('hidden');
    headings.forEach(h => h.classList.add('hidden'));
    if (menuList) menuList.classList.add('hidden');
    if (ordersSection) ordersSection.classList.add('hidden');
    if (reviewsSection) reviewsSection.classList.add('hidden');

    editForm.classList.remove('hidden');
};
// ==================== FORGOT PASSWORD FUNCTIONALITY ====================
// Initialize forgot password functionality
// ==================== HISTORY / SALES (Provider Dashboard) ====================
// End-of-day close: every business day runs from midnight to midnight in the
// provider's time zone. The scheduled `closeProviderBusinessDaysScheduled`
// Cloud Function freezes each finished day into an immutable report in
// `providerDailyCloses`. These views read those reports, show today live, and
// compute missing days in the browser when the backend can't be reached.

const DAILY_CLOSE_MAX_BACKFILL_DAYS = 366;
const DAILY_CLOSE_LOOKBACK_DAYS = 14;
const dailyCloseState = {
    key: null,
    reports: new Map(),
    coveredStart: null,
    coveredEnd: null,
    allTimeLoaded: false,
    backfillAttempted: false,
    backfilledFrom: null,
    fetchedOrders: new Map(),
    walkIns: new Map(),
    walkInsAvailable: false,
    pending: null,
    source: 'none',
    coverageWarnings: [],
};
let dailyCloseRenderToken = 0;
let salesRenderToken = 0;

function getDailyCloseCore() {
    return (typeof window !== 'undefined' && window.WTEDailyClose) ? window.WTEDailyClose : null;
}

function getCurrentProviderKey() {
    return currentProvider ? (currentProvider.id || currentProvider.providerId || null) : null;
}

function getProviderBusinessTimezone() {
    const core = getDailyCloseCore();
    const tz = currentProvider && currentProvider.timezone;
    // Same resolution as the server (saved timezone → country → default) so both agree on "today".
    return core ? core.resolveProviderTimezone(currentProvider) : (tz || 'UTC');
}

let providerBusinessSettingsSyncedFor = null;

/**
 * Providers registered before timezones were stored have no timezone in Firestore,
 * so the server would close their day at the platform default instead of their
 * local midnight. Save the browser's timezone (and currency) once for them.
 */
async function ensureProviderBusinessSettings() {
    const providerId = getCurrentProviderKey();
    if (!currentProvider || !providerId || providerBusinessSettingsSyncedFor === providerId) return;
    providerBusinessSettingsSyncedFor = providerId;
    const core = getDailyCloseCore();
    const updates = {};
    const savedTz = core ? core.normalizeTimeZone(currentProvider.timezone, null) : currentProvider.timezone;
    if (!savedTz) updates.timezone = detectProviderTimezone();
    if (!/^[A-Za-z]{3}$/.test(String(currentProvider.currency || ''))) {
        const currency = getProviderLaunchContext(currentProvider.country || 'US').currency;
        if (currency) updates.currency = String(currency).toUpperCase();
    }
    if (Object.keys(updates).length === 0) return;
    const saved = await persistProviderBusinessSettings(updates);
    if (saved) {
        Object.assign(currentProvider, updates);
        if (updates.timezone && isProviderPanelActive('history-panel')) renderDailyCloseReports();
    }
}

async function persistProviderBusinessSettings(updates) {
    const db = typeof _getDb === 'function' ? _getDb() : null;
    const providerId = getCurrentProviderKey();
    if (!db || !providerId) return false;
    try {
        await db.collection('providers').doc(String(providerId)).update({ ...updates, updatedAt: new Date().toISOString() });
        return true;
    } catch (error) {
        console.warn('[Provider] Could not save business timezone/currency:', error && error.message ? error.message : error);
        return false;
    }
}

function getProviderReportCurrency() {
    const own = currentProvider && currentProvider.currency;
    if (/^[A-Za-z]{3}$/.test(String(own || ''))) return String(own).toUpperCase();
    const orders = typeof getCurrentProviderOrders === 'function' ? getCurrentProviderOrders() : [];
    const withCurrency = orders.find((order) => /^[A-Za-z]{3}$/.test(String(order && order.currency || '')));
    if (withCurrency) return String(withCurrency.currency).toUpperCase();
    return String(getProviderLaunchContext((currentProvider && currentProvider.country) || 'US').currency || 'USD').toUpperCase();
}

function getProviderTodayKey() {
    const core = getDailyCloseCore();
    return core ? core.getLocalDateKey(Date.now(), getProviderBusinessTimezone()) : new Date().toISOString().slice(0, 10);
}

function syncDailyCloseStateOwner() {
    const key = `${getCurrentProviderKey() || ''}|${getProviderBusinessTimezone()}`;
    if (dailyCloseState.key === key) return;
    if (typeof window.clearProviderInsights === 'function') window.clearProviderInsights();
    Object.assign(dailyCloseState, {
        key,
        reports: new Map(),
        coveredStart: null,
        coveredEnd: null,
        allTimeLoaded: false,
        backfillAttempted: false,
        backfilledFrom: null,
        fetchedOrders: new Map(),
        walkIns: new Map(),
        walkInsAvailable: false,
        pending: null,
        source: 'none',
        coverageWarnings: [],
    });
}

function getOrdersForDailyReports() {
    const merged = new Map();
    dailyCloseState.fetchedOrders.forEach((order, id) => merged.set(id, order));
    getCurrentProviderOrders().forEach((order, index) => {
        merged.set(order.id ? String(order.id) : `local-${index}`, order);
    });
    return Array.from(merged.values());
}

function isProviderPanelActive(panelId) {
    const panel = document.getElementById(panelId);
    return !!panel && panel.classList.contains('active');
}

function formatReportMoney(value) {
    const amount = Number(value) || 0;
    const currency = getProviderReportCurrency();
    try {
        return new Intl.NumberFormat(getCurrentLocale(), { style: 'currency', currency }).format(amount);
    } catch (error) {
        return `${currency} ${amount.toFixed(2)}`;
    }
}

function formatBusinessDateLabel(dateKey, options = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) {
    if (!dateKey) return '';
    const date = new Date(`${dateKey}T12:00:00Z`);
    return Number.isNaN(date.getTime()) ? dateKey : date.toLocaleDateString(getCurrentLocale(), { ...options, timeZone: 'UTC' });
}

// 0 = Sunday … 6 = Saturday, following the user's locale (Monday in most of the world, Sunday in the Americas).
function getLocaleFirstDayOfWeek() {
    try {
        const tag = (typeof navigator !== 'undefined' && navigator.language) || getCurrentLocale();
        const locale = new Intl.Locale(tag);
        const info = typeof locale.getWeekInfo === 'function' ? locale.getWeekInfo() : locale.weekInfo;
        if (info && Number.isInteger(info.firstDay)) return info.firstDay % 7;
    } catch (error) {
        // Older browsers lack week info.
    }
    return 1;
}

function getLocalizedWeekdayName(dayOfWeek, style = 'long') {
    // 2026-03-01 is a Sunday, so day N of that week has dayOfWeek N.
    return formatBusinessDateLabel(`2026-03-0${1 + (Number(dayOfWeek) || 0)}`, { weekday: style });
}

function daysBetweenKeys(startKey, endKey) {
    return Math.round((Date.parse(`${endKey}T00:00:00Z`) - Date.parse(`${startKey}T00:00:00Z`)) / 86400000);
}

/**
 * Make sure closed-day reports for [startDate, yesterday] are loaded.
 * startDate === null means all time. Results are cached per provider so the
 * frequent realtime re-renders never re-query Firestore.
 */
async function ensureDailyCloseRange(startDate, { forceBackfill = false } = {}) {
    const core = getDailyCloseCore();
    if (!core || !currentProvider) return;
    syncDailyCloseStateOwner();
    if (dailyCloseState.pending) {
        try { await dailyCloseState.pending; } catch (error) { /* handled by the original caller */ }
    }

    const today = getProviderTodayKey();
    const yesterday = core.addDaysToKey(today, -1);
    if (dailyCloseState.coveredEnd && dailyCloseState.coveredEnd !== yesterday) {
        // A new business day closed since the last load; refresh the cache.
        dailyCloseState.coveredStart = null;
        dailyCloseState.allTimeLoaded = false;
        dailyCloseState.backfillAttempted = false;
    }
    const covered = startDate === null
        ? dailyCloseState.allTimeLoaded
        : (dailyCloseState.allTimeLoaded ||
            (dailyCloseState.coveredStart && core.compareDateKeys(startDate, dailyCloseState.coveredStart) >= 0));
    if (covered && !forceBackfill) return;

    const providerId = getCurrentProviderKey();
    const task = (async () => {
        let serverOk = false;
        let backfillOk = false;
        const warnings = [];
        const wantedDays = startDate === null
            ? DAILY_CLOSE_MAX_BACKFILL_DAYS
            : Math.min(DAILY_CLOSE_MAX_BACKFILL_DAYS, Math.max(35, daysBetweenKeys(startDate, today)));

        if (providerId && typeof window.firebaseGetProviderDailyCloses === 'function') {
            const backfillNeeded = forceBackfill || !dailyCloseState.backfillAttempted ||
                !dailyCloseState.backfilledFrom ||
                (startDate !== null && core.compareDateKeys(startDate, dailyCloseState.backfilledFrom) < 0);
            if (backfillNeeded && typeof window.callProtectedFunction === 'function') {
                dailyCloseState.backfillAttempted = true;
                try {
                    const result = await window.callProtectedFunction('backfillProviderDailyCloses', {
                        days: forceBackfill ? DAILY_CLOSE_MAX_BACKFILL_DAYS : wantedDays,
                    });
                    backfillOk = !!(result && result.ok);
                    if (!backfillOk) throw new Error('The backfill service did not confirm complete reports.');
                    if (backfillOk) {
                        dailyCloseState.backfilledFrom = core.addDaysToKey(today, -(result.days || wantedDays));
                        dailyCloseState.lastBackfill = result;
                    }
                } catch (error) {
                    dailyCloseState.backfillAttempted = false;
                    warnings.push('Report backfill failed. Missing days may not be fully represented.');
                    console.warn('[DailyClose] Backfill unavailable, using available data:', error && error.message);
                }
            } else if (dailyCloseState.backfilledFrom) {
                backfillOk = true;
            }

            try {
                const docs = await window.firebaseGetProviderDailyCloses({
                    providerId,
                    startDate: startDate || undefined,
                    endDate: yesterday,
                });
                docs.forEach((report) => {
                    if (report && report.businessDate) {
                        dailyCloseState.reports.set(report.businessDate, { ...report, source: 'closed' });
                    }
                });
                serverOk = true;
            } catch (error) {
                warnings.push('Closed-day reports could not be loaded. Cached reports and available orders may be incomplete.');
                console.warn('[DailyClose] Could not read closed-day reports:', error && error.message);
            }
        }

        dailyCloseState.walkInsAvailable = false;
        if (providerId && typeof window.firebaseGetProviderWalkInSales === 'function') {
            try {
                const entries = await window.firebaseGetProviderWalkInSales({
                    providerId,
                    startDate: startDate || undefined,
                    endDate: today,
                });
                entries.forEach((entry) => {
                    if (entry && entry.businessDate) dailyCloseState.walkIns.set(entry.businessDate, entry);
                });
                dailyCloseState.walkInsAvailable = true;
            } catch (error) {
                warnings.push('Walk-in POS records could not be loaded. Combined receipts may omit sales or use cached entries.');
                console.warn('[DailyClose] Could not read walk-in sales:', error && error.message);
            }
        } else {
            warnings.push('Walk-in POS records are unavailable. Combined receipts may omit walk-in sales.');
        }

        // Days without a stored report are computed from orders unless the
        // server backfill already confirmed they had no activity.
        const needOrders = !serverOk || !backfillOk ||
            (startDate !== null && dailyCloseState.backfilledFrom &&
                core.compareDateKeys(startDate, dailyCloseState.backfilledFrom) < 0);
        if (needOrders && providerId && typeof window.firebaseGetProviderSalesOrders === 'function') {
            try {
                const tz = getProviderBusinessTimezone();
                const startAt = startDate
                    ? new Date(core.getBusinessDayBounds(startDate, tz).startMs - DAILY_CLOSE_LOOKBACK_DAYS * core.DAY_MS)
                    : null;
                const orders = await window.firebaseGetProviderSalesOrders({ providerId, startAt, endAt: null });
                orders.forEach((order) => {
                    if (order && order.id) dailyCloseState.fetchedOrders.set(String(order.id), order);
                });
            } catch (error) {
                warnings.push('Source orders could not be loaded. Browser orders are paginated and must not be treated as complete.');
                console.warn('[DailyClose] Could not load orders for local day reports:', error && error.message);
            }
        } else if (needOrders) {
            warnings.push('Source orders are unavailable. Browser orders are paginated and must not be treated as complete.');
        }

        dailyCloseState.source = serverOk ? (backfillOk ? 'server' : 'mixed') : 'local';
        if (!serverOk) warnings.push('Official closed-day reports are unavailable; displayed figures are provisional.');
        else if (!backfillOk) warnings.push('Missing closed days have not been confirmed by the server.');
        dailyCloseState.coverageWarnings = warnings;
        dailyCloseState.serverConfirmedFrom = serverOk && backfillOk ? dailyCloseState.backfilledFrom : null;
        if (warnings.length === 0) {
            dailyCloseState.coveredEnd = yesterday;
            if (startDate === null) {
                dailyCloseState.allTimeLoaded = true;
            } else if (!dailyCloseState.coveredStart || core.compareDateKeys(startDate, dailyCloseState.coveredStart) < 0) {
                dailyCloseState.coveredStart = startDate;
            }
        } else {
            dailyCloseState.coveredStart = null;
            dailyCloseState.coveredEnd = null;
            dailyCloseState.allTimeLoaded = false;
        }
    })();

    dailyCloseState.pending = task;
    try {
        await task;
    } finally {
        if (dailyCloseState.pending === task) dailyCloseState.pending = null;
    }
}

function getEarliestKnownBusinessDate() {
    const core = getDailyCloseCore();
    const tz = getProviderBusinessTimezone();
    let earliest = getProviderTodayKey();
    dailyCloseState.reports.forEach((_, key) => {
        if (core.compareDateKeys(key, earliest) < 0) earliest = key;
    });
    dailyCloseState.walkIns.forEach((_, key) => {
        if (core.compareDateKeys(key, earliest) < 0) earliest = key;
    });
    getOrdersForDailyReports().forEach((order) => {
        const key = core.getLocalDateKey(order.createdAt, tz);
        if (key && core.compareDateKeys(key, earliest) < 0) earliest = key;
    });
    return earliest;
}

/** Reports for every business day in [startDate, endDate]; today is always live. */
function getDailyReportsForRange(startDate, endDate) {
    const core = getDailyCloseCore();
    const tz = getProviderBusinessTimezone();
    const today = getProviderTodayKey();
    const orders = getOrdersForDailyReports();
    const providerId = getCurrentProviderKey();
    const confirmedFrom = dailyCloseState.serverConfirmedFrom;
    const now = Date.now();
    const baseOptions = {
        providerId,
        providerName: currentProvider ? currentProvider.name : '',
        timezone: tz,
        currency: currentProvider ? currentProvider.currency : null,
        now,
    };

    return core.listDateKeys(startDate, endDate).map((businessDate) => {
        if (core.compareDateKeys(businessDate, today) > 0) return null;
        const walkIn = dailyCloseState.walkIns.get(businessDate) || null;
        if (businessDate !== today && dailyCloseState.reports.has(businessDate)) {
            return core.applyWalkInSales(dailyCloseState.reports.get(businessDate), walkIn);
        }
        if (businessDate !== today && confirmedFrom && core.compareDateKeys(businessDate, confirmedFrom) >= 0) {
            return core.applyWalkInSales({ ...core.buildDailyCloseReport([], { ...baseOptions, businessDate }), source: 'empty' }, walkIn);
        }
        const report = core.buildDailyCloseReport(orders, { ...baseOptions, businessDate });
        let source = 'empty';
        if (businessDate === today) source = 'live';
        else if (report.hasActivity) source = 'computed';
        return core.applyWalkInSales({ ...report, source }, walkIn);
    }).filter(Boolean);
}

function renderReportItemList(items, emptyText) {
    if (!Array.isArray(items) || items.length === 0) {
        return `<p class="daily-close-muted">${escapeHtml(emptyText)}</p>`;
    }
    return `
        <ol class="daily-close-items">
            ${items.map((item) => `
                <li>
                    <span class="daily-close-item-name">${escapeHtml(item.name)}</span>
                    <span class="daily-close-item-qty">${Number(item.quantity) || 0} sold</span>
                    <span class="daily-close-item-sales">${formatReportMoney(item.grossSales)}</span>
                </li>
            `).join('')}
        </ol>
    `;
}

function getDailyReportStatusChip(report) {
    switch (report.source) {
        case 'live':
            return '<span class="daily-close-chip open">● Open – closes at midnight</span>';
        case 'closed':
            return '<span class="daily-close-chip closed">✓ Closed</span>';
        case 'computed':
            return '<span class="daily-close-chip pending" title="Calculated from your orders. The official close report has not been generated yet.">Ended – awaiting close report</span>';
        default:
            return '<span class="daily-close-chip closed">✓ Closed</span>';
    }
}

function renderWalkInButton(report, label) {
    return `<button type="button" class="walkin-row-btn" data-walkin-date="${escapeHtml(report.businessDate)}">${escapeHtml(label)}</button>`;
}

function renderWalkInDetail(report) {
    const walkIn = report.walkIn;
    if (!walkIn) {
        return `
            <div class="walkin-detail is-missing">
                <h5>Walk-in sales (POS)</h5>
                <p class="daily-close-muted">No walk-in sales recorded for this day.</p>
                ${renderWalkInButton(report, '+ Add walk-in sales')}
            </div>
        `;
    }
    const payments = walkIn.payments || {};
    const paymentParts = ['cash', 'card', 'other']
        .filter((key) => Number(payments[key]) > 0)
        .map((key) => `${key[0].toUpperCase()}${key.slice(1)} ${formatReportMoney(payments[key])}`);
    return `
        <div class="walkin-detail">
            <h5>Walk-in sales (POS)</h5>
            <div class="daily-close-detail-grid">
                <div><span>Walk-in total</span><strong>${formatReportMoney(walkIn.grossSales)}</strong></div>
                <div><span>Receipts</span><strong>${Number(walkIn.transactions) || 0}</strong></div>
                <div><span>Tax</span><strong>${formatReportMoney(walkIn.tax)}</strong></div>
            </div>
            ${walkIn.salesSplit ? `<p class="daily-close-muted">Food ${formatReportMoney(walkIn.mealSales)} · Drinks ${formatReportMoney(walkIn.drinkSales)} (excl. tax)</p>` : ''}
            ${paymentParts.length ? `<p class="daily-close-muted">Payments: ${escapeHtml(paymentParts.join(' · '))}</p>` : ''}
            ${walkIn.notes ? `<p class="daily-close-muted">Note: ${escapeHtml(walkIn.notes)}</p>` : ''}
            <p class="daily-close-muted">Entered manually${walkIn.updatedAt ? ` · last updated ${escapeHtml(formatDateTime(walkIn.updatedAt))}` : ''}${walkIn.revision > 1 ? ` · edited ${walkIn.revision - 1}×` : ''}</p>
            ${renderWalkInButton(report, 'Edit walk-in sales')}
        </div>
    `;
}

function renderDailyReportRow(report) {
    const totals = report.totals || {};
    const isToday = report.source === 'live';
    const openCount = Number(totals.ordersOpenAtClose) || 0;
    const openLabel = isToday ? 'still open' : 'open at close';
    const walkInSales = Number(totals.walkInSales) || 0;
    const walkInCount = Number(totals.walkInTransactions) || 0;
    const hasWalkIn = !!report.walkIn;
    const hasActivity = report.hasActivity !== false &&
        ((Number(totals.ordersReceived) || 0) + (Number(totals.ordersCompleted) || 0) +
            (Number(totals.ordersPaid) || 0) + openCount +
            (Number(totals.ordersCancelled) || 0) + walkInCount + (walkInSales > 0 ? 1 : 0)) > 0;

    if (!hasActivity && !isToday && !hasWalkIn) {
        return `
            <div class="daily-close-row is-empty">
                <div class="daily-close-row-main">
                    <span class="daily-close-date">${escapeHtml(formatBusinessDateLabel(report.businessDate))}</span>
                    ${getDailyReportStatusChip(report)}
                    <span class="daily-close-muted">No orders</span>
                    <span class="daily-close-row-end">${renderWalkInButton(report, '+ Walk-in sales')}</span>
                </div>
            </div>
        `;
    }

    const openOrders = Array.isArray(report.openOrders) ? report.openOrders : [];
    const closedMeta = report.source === 'closed' && report.closedAt
        ? `<p class="daily-close-muted">Online orders closed ${escapeHtml(formatDateTime(report.closedAt))}${report.closedBy ? ` · ${escapeHtml(report.closedBy)}` : ''}</p>`
        : '';

    return `
        <details class="daily-close-row${isToday ? ' is-today' : ''}${openCount > 0 ? ' has-open' : ''}"${isToday ? ' open' : ''} data-business-date="${escapeHtml(report.businessDate)}">
            <summary class="daily-close-row-main">
                <span class="daily-close-date">${isToday ? 'Today · ' : ''}${escapeHtml(formatBusinessDateLabel(report.businessDate))}</span>
                ${getDailyReportStatusChip(report)}
                <span class="daily-close-stats">
                    <span title="Online orders received this day"><strong>${Number(totals.ordersReceived) || 0}</strong> received</span>
                    <span title="Online payments received this day"><strong>${Number(totals.ordersPaid === undefined ? totals.ordersCompleted : totals.ordersPaid) || 0}</strong> paid</span>
                    <span title="Orders marked ready for pickup and collected"><strong>${Number(totals.ordersCompleted) || 0}</strong> completed</span>
                    ${Number(totals.ordersCancelled) ? `<span><strong>${Number(totals.ordersCancelled)}</strong> cancelled</span>` : ''}
                    ${openCount ? `<span class="daily-close-open-warning"><strong>${openCount}</strong> ${openLabel}</span>` : ''}
                    ${hasWalkIn
                        ? `<span class="walkin-chip" title="Walk-in sales entered from your POS"><strong>${walkInCount}</strong> walk-in · ${formatReportMoney(walkInSales)}</span>`
                        : (isToday ? '' : '<span class="walkin-chip is-missing" title="No walk-in POS total entered for this day">No walk-in total</span>')}
                </span>
                <span class="daily-close-sales" title="Online ${formatReportMoney(totals.onlineSales)} + walk-in ${formatReportMoney(walkInSales)}">${formatReportMoney(totals.grossSales)}</span>
            </summary>
            <div class="daily-close-detail">
                <div class="daily-close-detail-grid">
                    <div class="is-total"><span>Total daily sales</span><strong>${formatReportMoney(totals.grossSales)}</strong></div>
                    <div><span>Paid online orders</span><strong>${Number(totals.ordersPaid === undefined ? totals.ordersCompleted : totals.ordersPaid) || 0}</strong></div>
                    <div><span>Online sales</span><strong>${formatReportMoney(totals.onlineSales)}</strong></div>
                    <div><span>Walk-in (POS)</span><strong>${formatReportMoney(walkInSales)}</strong></div>
                    <div><span>Food sales${Number(totals.walkInUnsplitSales) > 0 ? ' (online + split walk-in)' : ''}</span><strong>${formatReportMoney(totals.mealSales)}</strong></div>
                    <div><span>Drink sales${Number(totals.walkInUnsplitSales) > 0 ? ' (online + split walk-in)' : ''}</span><strong>${formatReportMoney(totals.drinkSales)}</strong></div>
                    ${Number(totals.walkInUnsplitSales) > 0 ? `<div title="Walk-in sales (excl. tax) not split into food and drinks"><span>Walk-in not split</span><strong>${formatReportMoney(totals.walkInUnsplitSales)}</strong></div>` : ''}
                    <div><span>Tax</span><strong>${formatReportMoney(totals.tax)}</strong></div>
                    <div><span>Transactions</span><strong>${Number(totals.transactions) || 0}</strong></div>
                    <div><span>Avg. sale</span><strong>${formatReportMoney(totals.averageOrderValue)}</strong></div>
                </div>
                <div class="daily-close-detail-columns">
                    <div>
                        <h5>What sold</h5>
                        ${renderReportItemList(report.items, 'No paid online sales.')}
                        ${Array.isArray(report.drinks) && report.drinks.length ? `<h5>Drinks (online)</h5>${renderReportItemList(report.drinks, '')}` : ''}
                    </div>
                    <div>
                        <h5>${isToday ? 'Orders still open' : 'Orders open at close'}</h5>
                        ${openOrders.length === 0
                            ? `<p class="daily-close-muted">${isToday ? 'Nothing waiting – all caught up.' : 'All orders were finished before close.'}</p>`
                            : `<ul class="daily-close-open-list">
                                ${openOrders.map((order) => {
                                    const statusInfo = getStatusInfo(order.status || 'pending');
                                    return `
                                    <li>
                                        <span>${escapeHtml(order.foodItem || 'Order')} × ${Number(order.quantity) || 1}</span>
                                        <span class="daily-close-muted">${escapeHtml(order.clientName || '')}${order.carriedOver ? ' · from an earlier day' : ''}</span>
                                        <span class="order-status-badge" style="background:${statusInfo.bg}; color:${statusInfo.color}">${escapeHtml(statusInfo.text)}</span>
                                    </li>
                                `;
                                }).join('')}
                            </ul>`}
                        ${renderWalkInDetail(report)}
                    </div>
                </div>
                ${closedMeta}
            </div>
        </details>
    `;
}

// ---------- Walk-in (external POS) sales entry ----------
let walkInSaving = false;

function getWalkInFormEls() {
    const byId = (id) => document.getElementById(id);
    return {
        form: byId('walkInSalesForm'),
        title: byId('walkInSalesTitle'),
        date: byId('walkInDate'),
        gross: byId('walkInGross'),
        transactions: byId('walkInTransactions'),
        tax: byId('walkInTax'),
        cash: byId('walkInCash'),
        card: byId('walkInCard'),
        other: byId('walkInOther'),
        paymentCheck: byId('walkInPaymentCheck'),
        meal: byId('walkInMeal'),
        drink: byId('walkInDrink'),
        splitCheck: byId('walkInSplitCheck'),
        menuNames: byId('walkInMenuNames'),
        items: byId('walkInItems'),
        notes: byId('walkInNotes'),
        errors: byId('walkInErrors'),
        meta: byId('walkInMeta'),
        save: byId('walkInSave'),
        cancel: byId('walkInCancel'),
        remove: byId('walkInDelete'),
        addItem: byId('walkInAddItem'),
        open: byId('walkInSalesOpen'),
    };
}

function formatInputNumber(value) {
    const n = Number(value);
    return Number.isFinite(n) && n !== 0 ? String(n) : '';
}

function addWalkInItemRow(item = {}) {
    const { items } = getWalkInFormEls();
    if (!items) return;
    const core = getDailyCloseCore();
    const max = core ? core.WALK_IN_LIMITS.MAX_ITEMS : 30;
    if (items.children.length >= max) {
        showWalkInErrors([`You can list up to ${max} items.`]);
        return;
    }
    const row = document.createElement('div');
    row.className = 'walkin-item-row';
    row.innerHTML = `
        <input type="text" class="walkin-item-name" maxlength="80" list="walkInMenuNames" autocomplete="off" placeholder="Item name" aria-label="Item name">
        <input type="number" class="walkin-item-qty" min="1" step="1" inputmode="numeric" placeholder="Qty" aria-label="Quantity sold">
        <input type="number" class="walkin-item-amount" min="0" step="0.01" inputmode="decimal" placeholder="Sales amount" aria-label="Item sales amount">
        <button type="button" class="walkin-item-remove" aria-label="Remove item">✕</button>
    `;
    row.querySelector('.walkin-item-name').value = item.name || '';
    row.querySelector('.walkin-item-qty').value = item.quantity ? String(item.quantity) : '';
    row.querySelector('.walkin-item-amount').value = formatInputNumber(item.grossSales);
    row.querySelector('.walkin-item-remove').addEventListener('click', () => row.remove());
    items.appendChild(row);
}

function showWalkInErrors(errors) {
    const { errors: box } = getWalkInFormEls();
    if (!box) return;
    if (!errors || errors.length === 0) {
        box.classList.add('hidden');
        box.innerHTML = '';
        return;
    }
    box.innerHTML = `<ul>${errors.map((e) => `<li>${escapeHtml(e)}</li>`).join('')}</ul>`;
    box.classList.remove('hidden');
}

function updateWalkInPaymentCheck() {
    const els = getWalkInFormEls();
    if (!els.paymentCheck) return;
    const total = Number(els.gross.value) || 0;
    const paid = ['cash', 'card', 'other'].reduce((sum, key) => sum + (Number(els[key].value) || 0), 0);
    if (paid === 0) {
        els.paymentCheck.textContent = '';
        els.paymentCheck.classList.remove('is-mismatch');
        return;
    }
    const diff = Math.round((total - paid) * 100) / 100;
    els.paymentCheck.textContent = Math.abs(diff) <= 0.01
        ? `✓ Payments match the total (${formatReportMoney(paid)}).`
        : `Payments add up to ${formatReportMoney(paid)} – ${formatReportMoney(Math.abs(diff))} ${diff > 0 ? 'short of' : 'over'} the total.`;
    els.paymentCheck.classList.toggle('is-mismatch', Math.abs(diff) > 0.01);
}

function getCurrentProviderMenuNames() {
    if (!currentProvider || !Array.isArray(menuItems)) return [];
    const uid = String(currentProvider.uid || currentProvider.id || '').trim().toLowerCase();
    const name = String(currentProvider.name || '').trim().toLowerCase();
    const names = new Map();
    menuItems.forEach((item) => {
        if (!item) return;
        const itemProvider = String(item.providerId || item.providerUid || item.uid || '').trim().toLowerCase();
        const itemProviderName = String(item.providerName || '').trim().toLowerCase();
        const mine = (uid && itemProvider === uid) || (!itemProvider && name && itemProviderName === name);
        const label = String(item.name || item.foodItem || '').trim();
        if (mine && label) names.set(label.toLowerCase(), label);
    });
    return Array.from(names.values()).sort((a, b) => a.localeCompare(b));
}

function populateWalkInMenuNames() {
    const { menuNames } = getWalkInFormEls();
    if (!menuNames) return;
    menuNames.innerHTML = getCurrentProviderMenuNames()
        .map((name) => `<option value="${escapeHtml(name)}"></option>`).join('');
}

function updateWalkInSplitCheck(event) {
    const els = getWalkInFormEls();
    if (!els.splitCheck) return;
    const net = Math.round(((Number(els.gross.value) || 0) - (Number(els.tax.value) || 0)) * 100) / 100;
    const mealSet = els.meal.value !== '';
    const drinkSet = els.drink.value !== '';
    // Typing one side fills in the other so the split always balances.
    if (event && event.target === els.meal && mealSet && net >= 0) {
        els.drink.value = String(Math.max(0, Math.round((net - Number(els.meal.value)) * 100) / 100));
    } else if (event && event.target === els.drink && drinkSet && net >= 0) {
        els.meal.value = String(Math.max(0, Math.round((net - Number(els.drink.value)) * 100) / 100));
    }
    if (els.meal.value === '' && els.drink.value === '') {
        els.splitCheck.textContent = net > 0 ? `Not split – ${formatReportMoney(net)} (excl. tax) will show as "walk-in not split".` : '';
        els.splitCheck.classList.remove('is-mismatch');
        return;
    }
    const diff = Math.round((net - (Number(els.meal.value) || 0) - (Number(els.drink.value) || 0)) * 100) / 100;
    els.splitCheck.textContent = Math.abs(diff) <= 0.01
        ? `✓ Food + drinks match sales excl. tax (${formatReportMoney(net)}).`
        : `Food + drinks should equal ${formatReportMoney(net)} (total minus tax).`;
    els.splitCheck.classList.toggle('is-mismatch', Math.abs(diff) > 0.01);
}

function fillWalkInForm(entry) {
    const els = getWalkInFormEls();
    els.gross.value = entry ? formatInputNumber(entry.grossSales) : '';
    els.transactions.value = entry && entry.transactions ? String(entry.transactions) : '';
    els.tax.value = entry ? formatInputNumber(entry.tax) : '';
    const payments = (entry && entry.payments) || {};
    els.cash.value = formatInputNumber(payments.cash);
    els.card.value = formatInputNumber(payments.card);
    els.other.value = formatInputNumber(payments.other);
    const split = !!(entry && entry.salesSplit);
    els.meal.value = split ? String(Number(entry.mealSales) || 0) : '';
    els.drink.value = split ? String(Number(entry.drinkSales) || 0) : '';
    els.notes.value = entry && entry.notes ? entry.notes : '';
    els.items.innerHTML = '';
    (entry && Array.isArray(entry.items) ? entry.items : []).forEach((item) => addWalkInItemRow(item));
    updateWalkInFormMode();
    updateWalkInPaymentCheck();
    updateWalkInSplitCheck();
}

function updateWalkInFormMode() {
    const els = getWalkInFormEls();
    const entry = dailyCloseState.walkIns.get(els.date.value) || null;
    els.remove.classList.toggle('hidden', !entry);
    els.save.textContent = entry ? 'Update walk-in sales' : 'Save walk-in sales';
    els.title.textContent = els.date.value
        ? `Walk-in sales for ${formatBusinessDateLabel(els.date.value)}`
        : 'Walk-in sales (from your POS)';
    els.meta.textContent = entry && entry.updatedAt
        ? `Saved entry · last updated ${formatDateTime(entry.updatedAt)}${entry.revision > 1 ? ` · edited ${entry.revision - 1}×` : ''}`
        : '';
}

function openWalkInSalesForm(dateKey) {
    const els = getWalkInFormEls();
    if (!els.form) return;
    bindWalkInForm();
    populateWalkInMenuNames();
    const today = getProviderTodayKey();
    const core = getDailyCloseCore();
    els.date.max = today;
    if (core) els.date.min = core.addDaysToKey(today, -core.WALK_IN_LIMITS.MAX_PAST_DAYS);
    els.date.value = dateKey || today;
    fillWalkInForm(dailyCloseState.walkIns.get(els.date.value) || null);
    showWalkInErrors([]);
    els.form.classList.remove('hidden');
    if (els.open) els.open.setAttribute('aria-expanded', 'true');
    els.form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    els.gross.focus({ preventScroll: true });
}

function closeWalkInSalesForm() {
    const els = getWalkInFormEls();
    if (!els.form) return;
    els.form.classList.add('hidden');
    showWalkInErrors([]);
    if (els.open) els.open.setAttribute('aria-expanded', 'false');
}

function collectWalkInPayload() {
    const els = getWalkInFormEls();
    const items = Array.from(els.items.querySelectorAll('.walkin-item-row')).map((row) => ({
        name: row.querySelector('.walkin-item-name').value,
        quantity: row.querySelector('.walkin-item-qty').value,
        grossSales: row.querySelector('.walkin-item-amount').value,
    }));
    return {
        businessDate: els.date.value,
        grossSales: els.gross.value,
        transactions: els.transactions.value,
        tax: els.tax.value,
        payments: { cash: els.cash.value, card: els.card.value, other: els.other.value },
        mealSales: els.meal.value,
        drinkSales: els.drink.value,
        items,
        notes: els.notes.value,
    };
}

function refreshReportsAfterWalkInChange() {
    renderDailyCloseReports();
    if (isProviderPanelActive('sales-panel')) renderSalesReport();
}

async function submitWalkInSales(event) {
    if (event) event.preventDefault();
    if (walkInSaving) return;
    const core = getDailyCloseCore();
    const els = getWalkInFormEls();
    if (!core || !currentProvider) return;

    const check = core.validateWalkInEntry(collectWalkInPayload(), { todayKey: getProviderTodayKey() });
    if (!check.ok) {
        showWalkInErrors(check.errors);
        return;
    }
    showWalkInErrors([]);
    walkInSaving = true;
    els.save.disabled = true;
    const originalText = els.save.textContent;
    els.save.textContent = 'Saving…';
    try {
        const result = await callProtectedFunction('saveProviderWalkInSales', check.value);
        const saved = (result && result.entry) || { ...check.value, updatedAt: new Date().toISOString(), revision: 1 };
        dailyCloseState.walkIns.set(saved.businessDate, saved);
        closeWalkInSalesForm();
        showNotification(`Walk-in sales saved for ${formatBusinessDateLabel(saved.businessDate)}.`, 'success');
        refreshReportsAfterWalkInChange();
    } catch (error) {
        showWalkInErrors([error && error.message ? error.message : 'Could not save walk-in sales. Please try again.']);
    } finally {
        walkInSaving = false;
        els.save.disabled = false;
        els.save.textContent = originalText;
    }
}

async function deleteWalkInSales() {
    const els = getWalkInFormEls();
    const businessDate = els.date.value;
    if (!businessDate || !dailyCloseState.walkIns.has(businessDate) || walkInSaving) return;
    if (!window.confirm(`Remove the walk-in sales entry for ${formatBusinessDateLabel(businessDate)}? This day's report will show online orders only.`)) return;
    walkInSaving = true;
    els.remove.disabled = true;
    try {
        await callProtectedFunction('saveProviderWalkInSales', { businessDate, delete: true });
        dailyCloseState.walkIns.delete(businessDate);
        closeWalkInSalesForm();
        showNotification('Walk-in sales entry removed.', 'success');
        refreshReportsAfterWalkInChange();
    } catch (error) {
        showWalkInErrors([error && error.message ? error.message : 'Could not remove the entry. Please try again.']);
    } finally {
        walkInSaving = false;
        els.remove.disabled = false;
    }
}

function bindWalkInForm() {
    const els = getWalkInFormEls();
    if (!els.form || els.form.dataset.bound) return;
    els.form.dataset.bound = 'true';
    els.form.addEventListener('submit', submitWalkInSales);
    els.cancel.addEventListener('click', closeWalkInSalesForm);
    els.remove.addEventListener('click', deleteWalkInSales);
    els.addItem.addEventListener('click', () => {
        addWalkInItemRow();
        const rows = els.items.querySelectorAll('.walkin-item-name');
        if (rows.length) rows[rows.length - 1].focus();
    });
    ['gross', 'cash', 'card', 'other'].forEach((key) => els[key].addEventListener('input', updateWalkInPaymentCheck));
    ['gross', 'tax', 'meal', 'drink'].forEach((key) => els[key].addEventListener('input', updateWalkInSplitCheck));
    els.date.addEventListener('change', () => {
        // Load a saved entry for the chosen date; otherwise keep what was typed.
        const entry = dailyCloseState.walkIns.get(els.date.value);
        if (entry) fillWalkInForm(entry);
        else updateWalkInFormMode();
        showWalkInErrors([]);
    });
    if (els.open) {
        els.open.setAttribute('aria-controls', 'walkInSalesForm');
        els.open.setAttribute('aria-expanded', 'false');
        els.open.addEventListener('click', () => {
            if (els.form.classList.contains('hidden')) openWalkInSalesForm(getProviderTodayKey());
            else closeWalkInSalesForm();
        });
    }
    const list = document.getElementById('dailyCloseReports');
    if (list) {
        list.addEventListener('click', (event) => {
            const button = event.target.closest('.walkin-row-btn');
            if (!button) return;
            event.preventDefault();
            event.stopPropagation();
            openWalkInSalesForm(button.dataset.walkinDate);
        });
    }
}

function describeDailyCloseSource() {
    switch (dailyCloseState.source) {
        case 'server': return 'Closed days are official end-of-day reports. Walk-in POS totals you enter are added on top.';
        case 'mixed': return 'Days marked “awaiting close report” are calculated from your orders until the official report is generated.';
        case 'local': return 'Offline view: days are calculated from the orders loaded in this browser.';
        default: return '';
    }
}

function renderDailyCloseCoverage(summary, includesToday = false) {
    const warnings = [...dailyCloseState.coverageWarnings];
    if (!dailyCloseState.coveredEnd && warnings.length === 0) {
        warnings.push('Report coverage has not yet been confirmed. Figures are provisional.');
    }
    if (summary.legacyItemReportDays > 0) {
        warnings.push(`${summary.legacyItemReportDays} older daily reports may omit sold items and include tax/drinks in item amounts. Use detailed rankings for complete meal comparisons.`);
    }
    if (includesToday) {
        warnings.push("Today's daily preview uses orders loaded in this browser and may be paginated. Use detailed insights for server-backed receipts and rankings.");
    }
    if (warnings.length === 0) return '';
    return `<div class="insights-warning" role="alert"><strong>Data coverage warning</strong><ul>${warnings.map((warning) => `<li>${escapeHtml(warning)}</li>`).join('')}</ul></div>`;
}

async function renderDailyCloseReports() {
    const container = document.getElementById('dailyCloseReports');
    const core = getDailyCloseCore();
    if (!container || !currentProvider) return;
    if (!core) {
        container.innerHTML = '<div class="empty-state"><p>End-of-day reports are unavailable.</p></div>';
        return;
    }
    syncDailyCloseStateOwner();
    bindDailyCloseControls();
    bindWalkInForm();

    const tz = getProviderBusinessTimezone();
    const note = document.getElementById('dailyCloseTimezoneNote');
    if (note) {
        note.textContent = `Each business day closes automatically at midnight (${tz}) – change your timezone in Edit Profile. Amounts in ${getProviderReportCurrency()}. ${describeDailyCloseSource()}`.trim();
    }

    const periodEl = document.getElementById('dailyClosePeriod');
    const days = Math.max(1, Number(periodEl && periodEl.value) || 14);
    const today = getProviderTodayKey();
    const startDate = core.addDaysToKey(today, -(days - 1));
    const token = ++dailyCloseRenderToken;

    const paint = () => {
        if (token !== dailyCloseRenderToken) return;
        const reports = getDailyReportsForRange(startDate, today).reverse();
        const closedDays = reports.filter((r) => r.source !== 'live');
        const summary = core.summarizeDailyCloses(closedDays, { startDate, endDate: core.addDaysToKey(today, -1) });
        const carriedOver = (reports[0] && reports[0].totals && reports[0].totals.ordersOpenAtClose) || 0;
        container.innerHTML = `
            ${renderDailyCloseCoverage(summary, reports.some((report) => report.source === 'live'))}
            <div class="daily-close-summary">
                <div><span>Closed days</span><strong>${closedDays.length}</strong></div>
                <div><span>Total sales (closed days)</span><strong>${formatReportMoney(summary.totals.grossSales)}</strong></div>
                <div><span>Online / walk-in</span><strong class="daily-close-split">${formatReportMoney(summary.totals.onlineSales)} <small>/ ${formatReportMoney(summary.totals.walkInSales)}</small></strong></div>
                <div><span>Orders paid</span><strong>${summary.totals.ordersPaid}</strong></div>
                <div><span>Orders completed</span><strong>${summary.totals.ordersCompleted}</strong></div>
                <div><span>Avg. per day</span><strong>${formatReportMoney(summary.averageDailySales)}</strong></div>
                <div class="${carriedOver ? 'needs-attention' : ''}"><span>Open right now</span><strong>${carriedOver}</strong></div>
            </div>
            ${reports.map(renderDailyReportRow).join('')}
        `;
        const latestNote = document.getElementById('dailyCloseTimezoneNote');
        if (latestNote) {
            latestNote.textContent = `Each business day closes automatically at midnight (${tz}) – change your timezone in Edit Profile. Amounts in ${getProviderReportCurrency()}. ${describeDailyCloseSource()}`.trim();
        }
    };

    paint();
    try {
        await ensureDailyCloseRange(startDate);
    } catch (error) {
        dailyCloseState.coverageWarnings = ['Reports could not be loaded. Displayed figures may be incomplete.'];
        console.warn('[DailyClose] Failed to load reports:', error);
    }
    paint();
}

function bindDailyCloseControls() {
    const periodEl = document.getElementById('dailyClosePeriod');
    if (periodEl && !periodEl.dataset.bound) {
        periodEl.addEventListener('change', renderDailyCloseReports);
        periodEl.dataset.bound = 'true';
    }
    const rebuildBtn = document.getElementById('dailyCloseRebuild');
    if (rebuildBtn && !rebuildBtn.dataset.bound) {
        rebuildBtn.addEventListener('click', async () => {
            const core = getDailyCloseCore();
            if (!core || !currentProvider) return;
            rebuildBtn.disabled = true;
            const originalText = rebuildBtn.textContent;
            rebuildBtn.textContent = 'Rebuilding…';
            try {
                const startDate = core.addDaysToKey(getProviderTodayKey(), -DAILY_CLOSE_MAX_BACKFILL_DAYS);
                await ensureDailyCloseRange(startDate, { forceBackfill: true });
                if (dailyCloseState.coverageWarnings.length > 0) {
                    throw new Error('Report coverage is incomplete.');
                }
                const created = dailyCloseState.lastBackfill && Array.isArray(dailyCloseState.lastBackfill.created)
                    ? dailyCloseState.lastBackfill.created.length : null;
                if (typeof showNotification === 'function') {
                    showNotification(created === null
                        ? 'Reports refreshed from the orders available on this device.'
                        : `End-of-day reports are up to date (${created} new day${created === 1 ? '' : 's'} closed).`, 'success');
                }
            } catch (error) {
                if (typeof showNotification === 'function') showNotification('Could not rebuild reports. Please try again.', 'error');
            } finally {
                rebuildBtn.disabled = false;
                rebuildBtn.textContent = originalText;
                renderDailyCloseReports();
                if (isProviderPanelActive('sales-panel')) renderSalesReport();
            }
        });
        rebuildBtn.dataset.bound = 'true';
    }
}

function renderOrdersHistory() {
    const ordersHistoryEl = document.getElementById('ordersHistory');
    if (!ordersHistoryEl || !currentProvider) return;

    if (isProviderPanelActive('history-panel')) renderDailyCloseReports();

    const core = getDailyCloseCore();
    const tz = getProviderBusinessTimezone();
    const dateFilterEl = document.getElementById('ordersHistoryDate');
    const dateFilterVal = dateFilterEl ? dateFilterEl.value : '';
    if (dateFilterEl && !dateFilterEl.dataset.bound) {
        dateFilterEl.addEventListener('change', renderOrdersHistory);
        dateFilterEl.dataset.bound = 'true';
    }

    let providerOrders = getCurrentProviderOrders();

    // Filter by the provider's business day (local time zone), not UTC.
    if (dateFilterVal) {
        providerOrders = providerOrders.filter((o) => {
            if (!o.createdAt) return false;
            if (core) return core.getLocalDateKey(o.createdAt, tz) === dateFilterVal;
            const ts = new Date(o.createdAt).getTime();
            return ts >= new Date(dateFilterVal + 'T00:00:00').getTime() && ts <= new Date(dateFilterVal + 'T23:59:59.999').getTime();
        });
    }

    providerOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    if (providerOrders.length === 0) {
        ordersHistoryEl.innerHTML = '<div class="empty-state"><p>No orders history yet.</p></div>';
        return;
    }

    ordersHistoryEl.innerHTML = providerOrders
        .map(order => {
            const statusInfo = getStatusInfo(order.status || 'pending');
            const progressHtml = buildProgressTracker(order.status);
            return `
                <div class="history-order-card">
                    <div class="history-order-header">
                        <span class="history-order-client">${escapeHtml(order.clientName)}</span>
                        <span class="history-order-date">${formatDateTime(order.createdAt)}</span>
                    </div>
                    <div class="history-order-food">${escapeHtml(order.foodItem)}</div>
                    <div class="history-order-meta">
                        ${order.fulfillmentDate ? `<span>${escapeHtml(t('fulfillmentDate', 'Scheduled for'))}: ${escapeHtml(order.fulfillmentDate)}</span>` : ''}
                        <span>Portion: ${escapeHtml(order.portion)}</span>
                        <span>Drink: ${escapeHtml(order.drink || 'Standard')}</span>
                        <span>Qty: ${Number(order.quantity || 1)}</span>
                        <span>Unit: $${Number(order.unitPrice || order.price || 0).toFixed(2)}</span>
                        <span>Total: $${Number(order.price || 0).toFixed(2)}</span>
                        <span>Type: ${escapeHtml(order.orderType)}</span>
                    </div>
                    <div class="history-order-status">
                        <span class="order-status-badge" style="background:${statusInfo.bg}; color:${statusInfo.color}">
                            ${escapeHtml(statusInfo.text)}
                        </span>
                    </div>
                    <div class="history-order-progress">${progressHtml}</div>
                </div>
            `;
        })
        .join('');
}

function resolveSalesRange() {
    const core = getDailyCloseCore();
    const presetEl = document.getElementById('salesPeriodPreset');
    const startEl = document.getElementById('salesReportStartDate');
    const endEl = document.getElementById('salesReportEndDate');
    const preset = presetEl ? presetEl.value : '30';
    const today = getProviderTodayKey();

    if (preset === 'custom') {
        const startDate = startEl && startEl.value ? startEl.value : core.addDaysToKey(today, -29);
        let endDate = endEl && endEl.value ? endEl.value : today;
        if (core.compareDateKeys(endDate, today) > 0) endDate = today;
        return { preset, startDate, endDate, invalid: core.compareDateKeys(endDate, startDate) < 0 };
    }
    if (preset === 'all') return { preset, startDate: null, endDate: today };
    const days = Math.max(1, Number(preset) || 30);
    return { preset, startDate: core.addDaysToKey(today, -(days - 1)), endDate: today };
}

function renderSalesTrend(reports) {
    if (reports.length === 0) return '';
    const byMonth = reports.length > 92;
    const buckets = new Map();
    reports.forEach((report) => {
        const key = byMonth ? report.businessDate.slice(0, 7) : report.businessDate;
        const totals = report.totals || {};
        const row = buckets.get(key) || { key, sales: 0, walkIn: 0, orders: 0 };
        row.sales += Number(totals.grossSales) || 0;
        row.walkIn += Number(totals.walkInSales) || 0;
        row.orders += Number(totals.ordersPaid === undefined ? totals.ordersCompleted : totals.ordersPaid) || 0;
        buckets.set(key, row);
    });
    const rows = Array.from(buckets.values());
    const max = Math.max(...rows.map((r) => r.sales), 0);
    const hasWalkIn = rows.some((r) => r.walkIn > 0);
    const pct = (value) => (max > 0 ? (value / max) * 100 : 0);
    const label = (key) => byMonth
        ? formatBusinessDateLabel(`${key}-15`, { month: 'short', year: '2-digit' })
        : formatBusinessDateLabel(key, { weekday: 'short', month: 'short', day: 'numeric' });

    return `
        <div class="sales-insight-card">
            <h4>${byMonth ? 'Monthly' : 'Daily'} sales</h4>
            <div class="sales-trend" role="img" aria-label="${byMonth ? 'Monthly' : 'Daily'} sales chart">
                ${rows.map((row) => `
                    <div class="sales-trend-bar" title="${escapeHtml(label(row.key))}: ${formatReportMoney(row.sales)} · ${row.orders} online orders paid${row.walkIn > 0 ? ` · walk-in ${formatReportMoney(row.walkIn)}` : ''}">
                        <span class="sales-trend-stack" style="height:${max > 0 ? Math.max(2, Math.round(pct(row.sales))) : 2}%">
                            ${row.walkIn > 0 ? `<span class="sales-trend-walkin" style="height:${Math.round((row.walkIn / row.sales) * 100)}%"></span>` : ''}
                        </span>
                    </div>
                `).join('')}
            </div>
            ${hasWalkIn ? `
            <div class="sales-trend-legend">
                <span><i class="legend-online"></i>Online orders</span>
                <span><i class="legend-walkin"></i>Walk-in (POS)</span>
            </div>` : ''}
            <div class="sales-trend-axis">
                <span>${escapeHtml(label(rows[0].key))}</span>
                <span>${escapeHtml(label(rows[rows.length - 1].key))}</span>
            </div>
        </div>
    `;
}

function renderTopItemChips(items) {
    if (!Array.isArray(items) || items.length === 0) return '<span class="daily-close-muted">—</span>';
    return items.slice(0, 3).map((item) =>
        `<span class="sales-item-chip">${escapeHtml(item.name)} <small>×${Number(item.quantity) || 0}</small></span>`
    ).join('');
}

async function renderSalesReport() {
    const salesEl = document.getElementById('salesReport');
    const core = getDailyCloseCore();
    if (!salesEl || !currentProvider) {
        if (typeof window.clearProviderInsights === 'function') window.clearProviderInsights();
        return;
    }
    if (!core) {
        salesEl.innerHTML = '<div class="empty-state"><p>Sales insights are unavailable.</p></div>';
        return;
    }
    bindSalesControls();
    syncDailyCloseStateOwner();
    // Realtime snapshots re-render every view; skip the work while hidden.
    if (!isProviderPanelActive('sales-panel')) return;

    const range = resolveSalesRange();
    const startEl = document.getElementById('salesReportStartDate');
    const endEl = document.getElementById('salesReportEndDate');
    if (range.preset !== 'custom') {
        if (startEl) startEl.value = range.startDate || '';
        if (endEl) endEl.value = range.endDate;
    }
    if (range.invalid) {
        if (typeof window.clearProviderInsights === 'function') window.clearProviderInsights();
        salesEl.innerHTML = '<div class="empty-state"><p>The end date must be on or after the start date.</p></div>';
        return;
    }

    const token = ++salesRenderToken;
    if (typeof window.renderProviderInventory === 'function') {
        window.renderProviderInventory({
            providerId: String(currentProvider.uid || getCurrentProviderKey()),
            escape: escapeHtml,
            locale: getCurrentLocale()
        });
    }
    if (typeof window.renderProviderInsights === 'function') {
        window.renderProviderInsights({
            providerId: String(currentProvider.uid || getCurrentProviderKey()),
            startDate: range.startDate,
            endDate: range.endDate,
            days: range.startDate ? daysBetweenKeys(range.startDate, range.endDate) + 1 : null,
            escape: escapeHtml,
            locale: getCurrentLocale(),
            weekday: (day) => getLocalizedWeekdayName(day, 'short'),
            promote: (id) => _openDailySpecialModal(id)
        });
    }
    const tz = getProviderBusinessTimezone();
    const note = document.getElementById('salesTimezoneNote');
    if (note) {
        note.textContent = `Built from your end-of-day reports (business days close at midnight, ${tz}). Sales count on the day payment is received; pickup completion is tracked separately.`;
    }

    if (!dailyCloseState.coveredEnd) {
        salesEl.innerHTML = '<div class="empty-state"><p>Loading sales insights…</p></div>';
    }
    try {
        await ensureDailyCloseRange(range.startDate);
    } catch (error) {
        dailyCloseState.coverageWarnings = ['Daily reports could not be loaded. Displayed figures may be incomplete.'];
        console.warn('[Sales] Failed to load daily reports:', error);
    }
    if (token !== salesRenderToken) return;

    const startDate = range.startDate || getEarliestKnownBusinessDate();
    if (range.preset === 'all' && startEl) startEl.value = startDate;
    const reports = getDailyReportsForRange(startDate, range.endDate);
    const summary = core.summarizeDailyCloses(reports, { startDate, endDate: range.endDate });

    const coverage = renderDailyCloseCoverage(summary, reports.some((report) => report.source === 'live'));
    if (summary.totals.ordersReceived === 0 && summary.totals.ordersCompleted === 0 &&
        summary.totals.ordersPaid === 0 && summary.totals.walkInSales === 0) {
        salesEl.innerHTML = `${coverage}<div class="empty-state"><p>No sales in the loaded daily reports for this period. Detailed rankings and preorder planning load independently below.</p></div>`;
        return;
    }

    const weekdayMax = Math.max(...summary.weekdays.map((w) => w.averageSales), 0);
    const bestWeekdayIndex = summary.bestWeekday ? summary.bestWeekday.dayOfWeek : -1;
    const firstDay = getLocaleFirstDayOfWeek();
    const weekdayOrder = [0, 1, 2, 3, 4, 5, 6].map((offset) => summary.weekdays[(firstDay + offset) % 7]);

    salesEl.innerHTML = `
        ${coverage}
        <div class="sales-summary-cards">
            <div class="is-total"><span>Total sales</span><strong>${formatReportMoney(summary.totals.grossSales)}</strong></div>
            <div><span>Online orders</span><strong>${formatReportMoney(summary.totals.onlineSales)}</strong><small>${summary.totals.ordersPaid} paid · ${summary.totals.ordersCompleted} completed</small></div>
            <div><span>Walk-in (POS)</span><strong>${formatReportMoney(summary.totals.walkInSales)}</strong><small>${summary.totals.walkInTransactions} sales · ${summary.walkInDays} day${summary.walkInDays === 1 ? '' : 's'} entered</small></div>
            <div><span>Items sold</span><strong>${summary.totals.itemsSold}</strong></div>
            <div><span>Avg. sale</span><strong>${formatReportMoney(summary.totals.averageOrderValue)}</strong><small>${summary.totals.transactions} transactions</small></div>
            <div><span>Avg. per day</span><strong>${formatReportMoney(summary.averageDailySales)}</strong></div>
            <div><span>Days with sales</span><strong>${summary.salesDays} / ${summary.calendarDays}</strong></div>
        </div>
        <p class="daily-close-muted">
            ${escapeHtml(formatBusinessDateLabel(startDate))} – ${escapeHtml(formatBusinessDateLabel(range.endDate))}
            · Food ${formatReportMoney(summary.totals.mealSales)} · Drinks ${formatReportMoney(summary.totals.drinkSales)}${summary.totals.walkInUnsplitSales > 0 ? ` · Walk-in not split ${formatReportMoney(summary.totals.walkInUnsplitSales)}` : ''} · Tax ${formatReportMoney(summary.totals.tax)}
            ${summary.totals.ordersCancelled ? ` · ${summary.totals.ordersCancelled} cancelled` : ''}
        </p>

        ${renderSalesTrend(reports)}

        <div class="sales-insight-grid">
            <div class="sales-insight-card">
                <h4>🏆 Best selling days</h4>
                ${summary.bestDays.length === 0 ? '<p class="daily-close-muted">No completed sales yet.</p>' : `
                <ol class="sales-best-days">
                    ${summary.bestDays.map((day) => `
                        <li>
                            <div class="sales-best-day-head">
                                <span>${escapeHtml(formatBusinessDateLabel(day.businessDate))}</span>
                                <strong>${formatReportMoney(day.grossSales)}</strong>
                            </div>
                            <div class="daily-close-muted">${day.walkInSales > 0
                                ? `Online ${formatReportMoney(day.onlineSales)} (${day.ordersPaid} paid · ${day.ordersCompleted} completed) · Walk-in ${formatReportMoney(day.walkInSales)}`
                                : `${day.ordersPaid} paid · ${day.ordersCompleted} completed`} · ${day.itemsSold} items</div>
                            <div class="sales-item-chips">${renderTopItemChips(day.topItems)}</div>
                        </li>
                    `).join('')}
                </ol>`}
            </div>

            <div class="sales-insight-card">
                <h4>📅 Best days of the week</h4>
                ${summary.bestWeekday ? `<p class="sales-highlight"><strong>${escapeHtml(getLocalizedWeekdayName(summary.bestWeekday.dayOfWeek, 'long'))}</strong> is your strongest day, averaging ${formatReportMoney(summary.bestWeekday.averageSales)} in sales.</p>` : ''}
                <div class="sales-weekdays">
                    ${weekdayOrder.map((day) => `
                        <div class="sales-weekday${day.dayOfWeek === bestWeekdayIndex ? ' is-best' : ''}">
                            <div class="sales-weekday-head">
                                <span>${escapeHtml(getLocalizedWeekdayName(day.dayOfWeek, 'short'))}</span>
                                <span class="sales-weekday-bar"><span style="width:${weekdayMax > 0 ? Math.round((day.averageSales / weekdayMax) * 100) : 0}%"></span></span>
                                <strong>${formatReportMoney(day.averageSales)}</strong>
                            </div>
                            <div class="sales-item-chips">${renderTopItemChips(day.topItems)}</div>
                        </div>
                    `).join('')}
                </div>
                <p class="daily-close-muted">Average sales per calendar day. Chips show what sells most on that day.</p>
            </div>
        </div>

        <div class="sales-insight-card">
            <h4>⭐ Daily-report item summary</h4>
            ${renderReportItemList(summary.topItems, 'No completed sales yet.')}
            <p class="daily-close-muted">New daily reports retain every sold item and exclude tax/drinks from online item amounts. Older immutable reports may not. POS item amounts are as entered. Use detailed rankings below for meal comparisons and recorded margins.</p>
        </div>
        <div class="sales-report-note">
            <small>Total sales = paid online orders + walk-in totals entered from your POS (record them in History → End-of-Day Reports). Totals include tax; food and drink sales exclude tax and include walk-in sales only when you split them in the walk-in form. Today is included live and closes at midnight. Historical summaries may be incomplete if report loading or backfill is unavailable; detailed rankings below load independently.</small>
        </div>
    `;
}

function bindSalesControls() {
    const presetEl = document.getElementById('salesPeriodPreset');
    const startEl = document.getElementById('salesReportStartDate');
    const endEl = document.getElementById('salesReportEndDate');
    if (presetEl && !presetEl.dataset.bound) {
        presetEl.addEventListener('change', renderSalesReport);
        presetEl.dataset.bound = 'true';
    }
    [startEl, endEl].forEach((element) => {
        if (element && !element.dataset.bound) {
            element.addEventListener('change', () => {
                if (presetEl) presetEl.value = 'custom';
                renderSalesReport();
            });
            element.dataset.bound = 'true';
        }
    });
}

// ==================== CLIENT ORDER HISTORY & REWARDS ====================
// Firestore-backed: all reward settings stored in providers/{providerId} doc
// Client monthly order counts derived from the orders/clientMessages collection.
// No browserStorage is used anywhere in this feature.

/**
 * Get the active Firestore DB instance (same helper pattern used throughout app).
 */
function _getDb() {
    return firebaseDbInstance || window.firebaseDbInstance ||
        (window.firestoreService && window.firestoreService.db) || null;
}

/**
 * Save the provider's reward programme settings to Firestore.
 * Settings are stored on the provider's own document under the key `rewardSettings`.
 */
async function saveProviderRewardSettings() {
    if (!currentProvider) { showNotification('Not logged in as provider', 'error'); return; }
    const db = _getDb();
    if (!db) { showNotification('Database not available', 'error'); return; }

    const thresholdEl = document.getElementById('rewardOrderThreshold');
    const descEl = document.getElementById('rewardDescription');
    const enabledEl = document.getElementById('rewardEnabled');

    const threshold = parseInt(thresholdEl ? thresholdEl.value : '5', 10) || 5;
    const description = descEl ? descEl.value.trim() : '';
    const enabled = enabledEl ? enabledEl.checked : false;

    const settings = { threshold, description, enabled, updatedAt: new Date().toISOString() };

    try {
        const providerId = currentProvider.uid || currentProvider.id;
        await db.collection('providers').doc(providerId).set(
            { rewardSettings: settings }, { merge: true }
        );
        // Also update in-memory provider object so UI reflects immediately
        currentProvider.rewardSettings = settings;
        showNotification('Reward settings saved!', 'success');
        renderClientOrderHistory(); // refresh history to show updated badges
    } catch (e) {
        console.error('[Rewards] Failed to save reward settings:', e);
        showNotification('Failed to save reward settings', 'error');
    }
}
window.saveProviderRewardSettings = saveProviderRewardSettings;

/**
 * Load the provider's reward settings from Firestore and populate the form.
 */
async function loadProviderRewardSettings() {
    if (!currentProvider) return;
    const db = _getDb();
    if (!db) return;

    try {
        const providerId = currentProvider.uid || currentProvider.id;
        const snap = await db.collection('providers').doc(providerId).get();
        if (!snap.exists) return;
        const data = snap.data() || {};
        const s = data.rewardSettings || {};

        const thresholdEl = document.getElementById('rewardOrderThreshold');
        const descEl = document.getElementById('rewardDescription');
        const enabledEl = document.getElementById('rewardEnabled');

        if (thresholdEl) thresholdEl.value = s.threshold || 5;
        if (descEl) descEl.value = s.description || '';
        if (enabledEl) enabledEl.checked = s.enabled === true;

        // Cache on currentProvider
        currentProvider.rewardSettings = s;
    } catch (e) {
        console.warn('[Rewards] Could not load reward settings:', e);
    }
}
window.loadProviderRewardSettings = loadProviderRewardSettings;

/**
 * Compute per-client order stats from the shared clientMessages array.
 * Returns an array of { clientId, clientName, clientEmail, totalOrders, monthlyOrders }
 * sorted by monthly order count descending.
 */
function computeClientOrderStats() {
    if (!currentProvider) return [];

    const providerId = currentProvider.id || currentProvider.providerId || null;
    const providerName = currentProvider.name ? currentProvider.name.trim() : '';

    // Filter orders that belong to this provider
    const mine = clientMessages.filter(msg => {
        const byId = msg.providerId && providerId && String(msg.providerId) === String(providerId);
        const byName = msg.providerName && providerName &&
            msg.providerName.toLowerCase() === providerName.toLowerCase();
        return byId || byName;
    });

    // Current month boundaries (local time)
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).getTime();

    // Aggregate by client
    const map = new Map();
    for (const order of mine) {
        const key = order.clientEmail ? order.clientEmail.toLowerCase() : (order.clientName || 'unknown');
        if (!map.has(key)) {
            map.set(key, {
                clientId: order.clientId || key,
                clientName: order.clientName || 'Unknown',
                clientEmail: order.clientEmail || '',
                totalOrders: 0,
                monthlyOrders: 0,
                totalSpend: 0,
                lastOrderAt: null,
                orders: []
            });
        }
        const entry = map.get(key);
        entry.totalOrders += 1;
        entry.totalSpend += Number(order.price || 0);

        const ts = order.createdAt ? new Date(order.createdAt).getTime() : 0;
        if (ts >= monthStart && ts <= monthEnd) {
            entry.monthlyOrders += 1;
        }
        if (!entry.lastOrderAt || ts > new Date(entry.lastOrderAt).getTime()) {
            entry.lastOrderAt = order.createdAt || null;
        }
        entry.orders.push(order);
    }

    return Array.from(map.values()).sort((a, b) => b.monthlyOrders - a.monthlyOrders);
}
window.computeClientOrderStats = computeClientOrderStats;

/**
 * Render the client order history & rewards panel.
 * Shows per-client order counts and whether they have earned a reward this month.
 */
function renderClientOrderHistory() {
    const container = document.getElementById('clientOrderHistory');
    if (!container || !currentProvider) return;

    const stats = computeClientOrderStats();
    const rewardSettings = currentProvider.rewardSettings || {};
    const rewardEnabled = rewardSettings.enabled === true;
    const threshold = Number(rewardSettings.threshold) || 5;
    const rewardDesc = rewardSettings.description || '';

    const now = new Date();
    const monthLabel = now.toLocaleString('default', { month: 'long', year: 'numeric' });

    if (stats.length === 0) {
        container.innerHTML = '<div class="empty-state"><p>No client orders yet. Clients will appear here once they place orders.</p></div>';
        return;
    }

    container.innerHTML = `
        <div class="client-history-meta">
            <span class="client-history-period">📅 Period: <strong>${monthLabel}</strong></span>
            <span class="client-history-count">${stats.length} unique client${stats.length !== 1 ? 's' : ''}</span>
        </div>
        <div class="client-history-table">
            <div class="client-history-header">
                <span>Client</span>
                <span>This Month</span>
                <span>All Time</span>
                <span>Total Spend</span>
                <span>Last Order</span>
                ${rewardEnabled ? '<span>Reward Status</span>' : ''}
            </div>
            ${stats.map(s => {
                const earned = rewardEnabled && s.monthlyOrders >= threshold;
                const progress = rewardEnabled ? Math.min(Math.round((s.monthlyOrders / threshold) * 100), 100) : 0;
                const rewardBadge = rewardEnabled ? `
                    <span class="reward-status-cell">
                        ${earned
                            ? `<span class="reward-badge earned" title="${escapeHtml(rewardDesc)}">🏆 Reward Earned!</span>`
                            : `<span class="reward-progress-wrap">
                                <span class="reward-progress-bar" style="width:${progress}%"></span>
                                <span class="reward-progress-label">${s.monthlyOrders}/${threshold}</span>
                               </span>`
                        }
                    </span>` : '';
                return `
                <div class="client-history-row ${earned ? 'reward-earned' : ''}">
                    <span class="client-history-name">
                        <strong>${escapeHtml(s.clientName)}</strong>
                        ${s.clientEmail ? `<small>${escapeHtml(s.clientEmail)}</small>` : ''}
                    </span>
                    <span class="client-history-monthly"><strong>${s.monthlyOrders}</strong> orders</span>
                    <span class="client-history-total">${s.totalOrders} orders</span>
                    <span class="client-history-spend">$${s.totalSpend.toFixed(2)}</span>
                    <span class="client-history-last">${s.lastOrderAt ? formatDateTime(s.lastOrderAt) : '—'}</span>
                    ${rewardBadge}
                </div>`;
            }).join('')}
        </div>
        ${rewardEnabled ? `
        <div class="rewards-legend">
            <span class="reward-badge earned">🏆</span>
            <span> = client has placed <strong>${threshold}</strong> or more orders this month</span>
            ${rewardDesc ? `<span class="rewards-legend-desc"> — Reward: <em>${escapeHtml(rewardDesc)}</em></span>` : ''}
        </div>` : '<div class="rewards-legend-disabled">Reward programme is currently <strong>disabled</strong>. Enable it in Reward Settings.</div>'}
    `;
}
window.renderClientOrderHistory = renderClientOrderHistory;

// ==================== REWARD OFFER SYSTEM (Provider → Client, Firestore-backed) ====================
// Firestore collection: clientRewards/{autoId}
// Document shape: {
//   id, providerId, providerName, clientEmail, clientName,
//   offerType ('menu_item'|'discount'), offerTitle, offerDetails,
//   expiresAt (ISO), status ('pending'|'accepted'|'declined'),
//   sentAt, respondedAt
// }

/**
 * Load the top clients for this provider from order history and render
 * the "Send Reward" sub-panel inside the Rewards tab.
 */
async function renderSendRewardPanel() {
    const container = document.getElementById('sendRewardClientList');
    if (!container || !currentProvider) return;

    const stats = computeClientOrderStats();
    if (stats.length === 0) {
        container.innerHTML = '<p class="reward-no-clients">No clients have ordered yet.</p>';
        return;
    }

    // Show top 20 by total orders
    const top = stats.slice(0, 20);
    container.innerHTML = top.map((s, i) => `
        <label class="reward-client-select-item">
            <input type="checkbox" class="reward-client-checkbox" value="${escapeHtml(s.clientEmail)}"
                data-client-name="${escapeHtml(s.clientName)}">
            <span class="reward-client-rank">#${i + 1}</span>
            <span class="reward-client-info">
                <strong>${escapeHtml(s.clientName)}</strong>
                <small>${escapeHtml(s.clientEmail)}</small>
            </span>
            <span class="reward-client-stats">${s.totalOrders} orders · $${s.totalSpend.toFixed(2)}</span>
        </label>
    `).join('');
}
window.renderSendRewardPanel = renderSendRewardPanel;

/**
 * Send a reward offer to all selected clients. Each offer is written as a
 * separate document in the `clientRewards` Firestore collection.
 */
async function sendRewardOffer() {
    if (!currentProvider) { showNotification('Not logged in as provider', 'error'); return; }
    const db = _getDb();
    if (!db) { showNotification('Database not available', 'error'); return; }

    // Gather selected clients
    const checkboxes = document.querySelectorAll('.reward-client-checkbox:checked');
    if (checkboxes.length === 0) {
        showNotification('Please select at least one client', 'error');
        return;
    }

    const offerTypeEl = document.getElementById('rewardOfferType');
    const offerTitleEl = document.getElementById('rewardOfferTitle');
    const offerDetailsEl = document.getElementById('rewardOfferDetails');
    const offerExpiryEl = document.getElementById('rewardOfferExpiry');

    const offerType = offerTypeEl ? offerTypeEl.value : 'discount';
    const offerTitle = offerTitleEl ? offerTitleEl.value.trim() : '';
    const offerDetails = offerDetailsEl ? offerDetailsEl.value.trim() : '';
    const expiresAt = offerExpiryEl && offerExpiryEl.value
        ? new Date(offerExpiryEl.value + 'T23:59:59').toISOString()
        : null;

    if (!offerTitle) { showNotification('Please enter an offer title', 'error'); return; }

    const providerId = currentProvider.uid || currentProvider.id;
    const providerName = currentProvider.name || '';
    const sentAt = new Date().toISOString();
    let sent = 0;
    const errors = [];

    for (const cb of checkboxes) {
        const clientEmail = cb.value;
        const clientName = cb.dataset.clientName || clientEmail;
        try {
            const docRef = db.collection('clientRewards').doc();
            const offer = {
                id: docRef.id,
                providerId,
                providerName,
                clientEmail,
                clientName,
                offerType,
                offerTitle,
                offerDetails,
                expiresAt,
                status: 'pending',
                sentAt,
                respondedAt: null
            };
            await docRef.set(offer);
            sent++;
        } catch (e) {
            console.error('[Rewards] Failed to send offer to', clientEmail, e);
            errors.push(clientEmail);
        }
    }

    if (sent > 0) {
        showNotification(`Reward offer sent to ${sent} client${sent > 1 ? 's' : ''}!`, 'success');
        // Reset form
        const form = document.getElementById('sendRewardForm');
        if (form) form.reset();
        // Uncheck all
        document.querySelectorAll('.reward-client-checkbox').forEach(c => { c.checked = false; });
        // Refresh sent list
        loadSentRewards();
    }
    if (errors.length) {
        showNotification(`Failed to send to: ${errors.join(', ')}`, 'error');
    }
}
window.sendRewardOffer = sendRewardOffer;

/**
 * Load all reward offers sent by this provider from Firestore and render them
 * in the #sentRewardsList container, showing the client's response.
 */
async function loadSentRewards() {
    const container = document.getElementById('sentRewardsList');
    if (!container || !currentProvider) return;

    container.innerHTML = '<p class="reward-loading">Loading sent offers…</p>';
    const db = _getDb();
    if (!db) { container.innerHTML = '<p class="reward-error">Database not available.</p>'; return; }

    try {
        const providerId = currentProvider.uid || currentProvider.id;
        const snap = await db.collection('clientRewards')
            .where('providerId', '==', providerId)
            .orderBy('sentAt', 'desc')
            .get();

        if (snap.empty) {
            container.innerHTML = '<div class="empty-state"><p>No reward offers sent yet.</p></div>';
            return;
        }

        const offers = [];
        snap.forEach(doc => offers.push(doc.data()));

        container.innerHTML = offers.map(o => {
            const statusLabel = o.status === 'accepted' ? '✅ Accepted'
                : o.status === 'declined' ? '❌ Declined' : '⏳ Pending';
            const statusClass = o.status === 'accepted' ? 'accepted'
                : o.status === 'declined' ? 'declined' : 'pending';
            const typeBadge = o.offerType === 'menu_item' ? '🍽️ Menu Item' : '💸 Discount';
            const expiry = o.expiresAt ? `Expires: ${new Date(o.expiresAt).toLocaleDateString()}` : 'No expiry';
            const respondedLine = o.respondedAt
                ? `<span class="sent-reward-responded">Responded: ${formatDateTime(o.respondedAt)}</span>` : '';
            return `
            <div class="sent-reward-card reward-status-${statusClass}">
                <div class="sent-reward-header">
                    <span class="sent-reward-client"><strong>${escapeHtml(o.clientName)}</strong> <small>${escapeHtml(o.clientEmail)}</small></span>
                    <span class="reward-status-badge ${statusClass}">${statusLabel}</span>
                </div>
                <div class="sent-reward-offer">
                    <span class="reward-type-badge">${typeBadge}</span>
                    <strong>${escapeHtml(o.offerTitle)}</strong>
                </div>
                ${o.offerDetails ? `<div class="sent-reward-details">${escapeHtml(o.offerDetails)}</div>` : ''}
                <div class="sent-reward-meta">
                    <span>${expiry}</span>
                    <span>Sent: ${formatDateTime(o.sentAt)}</span>
                    ${respondedLine}
                </div>
            </div>`;
        }).join('');
    } catch (e) {
        console.error('[Rewards] Failed to load sent rewards:', e);
        container.innerHTML = '<p class="reward-error">Could not load sent offers. Please try again.</p>';
    }
}
window.loadSentRewards = loadSentRewards;

// ==================== CLIENT SIDE: RECEIVE & RESPOND TO REWARD OFFERS ====================

/**
 * Load reward offers addressed to the current client from Firestore and render
 * them in the #clientRewardsInbox container ("Just For You" panel).
 */
async function loadClientRewards() {
    const container = document.getElementById('clientRewardsInbox');
    if (!container || !currentClient) return;

    container.innerHTML = '<p class="reward-loading">Loading your rewards…</p>';
    const db = _getDb();
    if (!db) { container.innerHTML = '<p class="reward-error">Database not available.</p>'; return; }

    try {
        const snap = await db.collection('clientRewards')
            .where('clientEmail', '==', currentClient.email.toLowerCase())
            .orderBy('sentAt', 'desc')
            .get();

        if (snap.empty) {
            container.innerHTML = `
                <div class="empty-state">
                    <p>🎁 No special offers yet. Keep ordering to unlock personalised rewards from your favourite restaurants!</p>
                </div>`;
            return;
        }

        const offers = [];
        snap.forEach(doc => offers.push(doc.data()));

        // Separate pending from responded
        const pending = offers.filter(o => o.status === 'pending');
        const responded = offers.filter(o => o.status !== 'pending');

        const now = Date.now();

        const renderOffer = (o) => {
            const expired = o.expiresAt && new Date(o.expiresAt).getTime() < now;
            const typeBadge = o.offerType === 'menu_item' ? '🍽️ Menu Item' : '💸 Discount';
            const expiry = o.expiresAt
                ? (expired ? `<span class="offer-expired">⚠️ Expired ${new Date(o.expiresAt).toLocaleDateString()}</span>`
                           : `<span class="offer-expiry">⏰ Valid until ${new Date(o.expiresAt).toLocaleDateString()}</span>`)
                : '';

            if (o.status === 'pending' && !expired) {
                return `
                <div class="client-reward-card pending">
                    <div class="client-reward-badge-row">
                        <span class="reward-type-badge">${typeBadge}</span>
                        <span class="client-reward-from">From <strong>${escapeHtml(o.providerName)}</strong></span>
                    </div>
                    <div class="client-reward-title">🎁 ${escapeHtml(o.offerTitle)}</div>
                    ${o.offerDetails ? `<div class="client-reward-details">${escapeHtml(o.offerDetails)}</div>` : ''}
                    <div class="client-reward-meta">${expiry}<span>Sent: ${formatDateTime(o.sentAt)}</span></div>
                    <div class="client-reward-actions">
                        <button class="reward-accept-btn" onclick="respondToReward('${o.id}', 'accepted')">✅ Accept Offer</button>
                        <button class="reward-decline-btn" onclick="respondToReward('${o.id}', 'declined')">❌ Decline</button>
                    </div>
                </div>`;
            } else {
                const statusLabel = o.status === 'accepted' ? '✅ Accepted' : o.status === 'declined' ? '❌ Declined' : '⚠️ Expired';
                const statusClass = o.status === 'accepted' ? 'accepted' : o.status === 'declined' ? 'declined' : 'expired';
                return `
                <div class="client-reward-card ${statusClass}">
                    <div class="client-reward-badge-row">
                        <span class="reward-type-badge">${typeBadge}</span>
                        <span class="client-reward-from">From <strong>${escapeHtml(o.providerName)}</strong></span>
                        <span class="reward-status-badge ${statusClass}">${statusLabel}</span>
                    </div>
                    <div class="client-reward-title">${escapeHtml(o.offerTitle)}</div>
                    ${o.offerDetails ? `<div class="client-reward-details">${escapeHtml(o.offerDetails)}</div>` : ''}
                    <div class="client-reward-meta">
                        ${expiry}
                        <span>Sent: ${formatDateTime(o.sentAt)}</span>
                        ${o.respondedAt ? `<span>Responded: ${formatDateTime(o.respondedAt)}</span>` : ''}
                    </div>
                </div>`;
            }
        };

        let html = '';
        if (pending.length > 0) {
            html += `<div class="client-rewards-section-label">🎉 New Offers (${pending.length})</div>`;
            html += pending.map(renderOffer).join('');
        }
        if (responded.length > 0) {
            html += `<div class="client-rewards-section-label history-label">Past Offers</div>`;
            html += responded.map(renderOffer).join('');
        }

        container.innerHTML = html;

        // Update tab badge count
        _updateClientRewardsBadge(pending.length);

    } catch (e) {
        console.error('[Rewards] Failed to load client rewards:', e);
        container.innerHTML = '<p class="reward-error">Could not load your rewards. Please try again.</p>';
    }
}
window.loadClientRewards = loadClientRewards;

/**
 * Client responds to a reward offer (accepted or declined).
 * Updates the `clientRewards` document in Firestore.
 */
async function respondToReward(rewardId, response) {
    if (!currentClient) return;
    const db = _getDb();
    if (!db) { showNotification('Database not available', 'error'); return; }

    try {
        await db.collection('clientRewards').doc(rewardId).update({
            status: response,
            respondedAt: new Date().toISOString()
        });
        showNotification(
            response === 'accepted' ? '🎉 Offer accepted! Enjoy your reward.' : 'Offer declined.',
            response === 'accepted' ? 'success' : 'success'
        );
        // Refresh client inbox
        loadClientRewards();
    } catch (e) {
        console.error('[Rewards] Failed to respond to reward:', e);
        showNotification('Could not respond to offer. Please try again.', 'error');
    }
}
window.respondToReward = respondToReward;

/**
 * Show/hide a numeric badge on the "Just For You" tab when there are pending offers.
 */
function _updateClientRewardsBadge(count) {
    const tab = document.querySelector('.client-dashboard-tab[data-dashboard-tab="rewards"]');
    if (!tab) return;
    let badge = tab.querySelector('.rewards-tab-badge');
    if (count > 0) {
        if (!badge) {
            badge = document.createElement('span');
            badge.className = 'rewards-tab-badge';
            tab.appendChild(badge);
        }
        badge.textContent = count;
    } else if (badge) {
        badge.remove();
    }
}

/**
 * Poll for new pending reward offers when client dashboard is visible.
 * Called once when the client logs in and on tab switch.
 */
async function checkClientRewardsBadge() {
    if (!currentClient) return;
    const db = _getDb();
    if (!db) return;
    try {
        const snap = await db.collection('clientRewards')
            .where('clientEmail', '==', currentClient.email.toLowerCase())
            .where('status', '==', 'pending')
            .get();
        _updateClientRewardsBadge(snap.size);
    } catch (e) {
        // non-critical — silently ignore
    }
}
window.checkClientRewardsBadge = checkClientRewardsBadge;

// ==================== PROVIDER DASHBOARD PANEL TABS (FIX) ====================
// ==================== FORGOT PASSWORD FUNCTIONALITY ====================
// Initialize forgot password functionality
// ==================== PROVIDER DASHBOARD PANEL TABS (FIX) ====================
const providerLastSubTabByGroup = {};

// Show the sub-menu for a main dashboard section and mark it selected.
function syncProviderGroupNav(groupKey) {
    if (!groupKey) return;
    document.querySelectorAll('.provider-group-tab').forEach((groupTab) => {
        const isActive = groupTab.dataset.group === groupKey;
        groupTab.classList.toggle('active', isActive);
        groupTab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    document.querySelectorAll('.provider-panel-tab').forEach((subTab) => {
        subTab.hidden = !!subTab.dataset.group && subTab.dataset.group !== groupKey;
    });
}

function initProviderGroupTabs() {
    const groupTabs = document.querySelectorAll('.provider-group-tab');
    groupTabs.forEach((groupTab) => {
        if (groupTab.dataset.bound === 'true') return;
        groupTab.dataset.bound = 'true';
        groupTab.addEventListener('click', (e) => {
            e.preventDefault();
            const groupKey = groupTab.dataset.group;
            const remembered = providerLastSubTabByGroup[groupKey];
            const target = (remembered && document.querySelector(`.provider-panel-tab[data-panel="${remembered}"]`))
                || document.querySelector(`.provider-panel-tab[data-group="${groupKey}"]`);
            if (target) target.click();
        });
    });
}

function initProviderPanelTabs() {
    const panelTabs = document.querySelectorAll('.provider-panel-tab');
    if (!panelTabs || panelTabs.length === 0) return;
    initProviderGroupTabs();

    panelTabs.forEach(tab => {
        tab.addEventListener('click', (e) => {
            e.preventDefault();
            const targetPanel = tab.dataset.panel;
            const editForm = document.getElementById('providerEditProfileForm');
            if (editForm && !editForm.classList.contains('hidden') && typeof hideProviderEditForm === 'function') {
                hideProviderEditForm();
            }
            if (tab.dataset.group) {
                providerLastSubTabByGroup[tab.dataset.group] = targetPanel;
                syncProviderGroupNav(tab.dataset.group);
            }
            if (targetPanel !== 'orders' && typeof providerOrderFilter !== 'undefined') {
                providerOrderFilter = 'all';
                if (typeof updateProviderOrderCounters === 'function') updateProviderOrderCounters();
            }

            try {
                panelTabs.forEach(t => {
                    t.classList.remove('active');
                    t.setAttribute('aria-selected', 'false');
                });
                tab.classList.add('active');
                tab.setAttribute('aria-selected', 'true');

                // Hide all dashboard panel sections first
                const allPanels = document.querySelectorAll('.dashboard-panel-section');
                allPanels.forEach(panel => {
                    panel.classList.remove('active');
                    panel.style.display = 'none';
                });

                // Show the specific panel based on targetPanel
                const targetPanelEl = document.getElementById(`${targetPanel}-panel`);
                if (targetPanelEl) {
                    targetPanelEl.classList.add('active');
                    targetPanelEl.style.display = '';
                }

                // Always refresh content for the selected panel (if elements/functions exist)
                if (targetPanel === 'menu' || targetPanel === 'addmenu') {
                    if (typeof renderMenuList === 'function') renderMenuList();
                } else if (targetPanel === 'orders') {
                    renderProviderOrders();
                } else if (targetPanel === 'reviews') {
                    if (typeof renderProviderReviews === 'function') renderProviderReviews();
                } else if (targetPanel === 'history' || targetPanel === 'orderhistory') {
                    if (typeof renderOrdersHistory === 'function') {
                        renderOrdersHistory();
                    } else {
                        const el = document.getElementById('ordersHistory');
                        if (el) el.innerHTML = '<div class="empty-state"><p>No orders history yet.</p></div>';
                    }
                } else if (targetPanel === 'sales') {
                    if (typeof renderSalesReport === 'function') {
                        renderSalesReport();
                    } else {
                        const el = document.getElementById('salesReport');
                        if (el) el.innerHTML = '<div class="empty-state"><p>No sales report yet.</p></div>';
                    }
                } else if (targetPanel === 'subscription') {
                    if (typeof renderProviderSubscriptionStatus === 'function') {
                        renderProviderSubscriptionStatus();
                    } else {
                        const el = document.getElementById('providerSubscriptionStatus');
                        if (el) el.innerHTML = '<div class="empty-state"><p>Subscription & billing information is loading...</p></div>';
                    }
                } else if (targetPanel === 'rewards') {
                    loadProviderRewardSettings().then(() => {
                        renderClientOrderHistory();
                        renderSendRewardPanel();
                        loadSentRewards();
                    });
                } else if (targetPanel === 'profile') {
                    if (typeof renderProviderProfileView === 'function') {
                        renderProviderProfileView();
                    }
                }
            } catch (err) {
                console.error('[ProviderTabs debug] error while switching panel', targetPanel, err);
            }
        });
    });
}


function initCollapsibleSections() {
    const headers = document.querySelectorAll('.collapsible-header, .form-section-header, .collapsible-form-header');

    headers.forEach(header => {
        if (header.dataset.bbBound === '1') return;
        header.dataset.bbBound = '1';

        header.addEventListener('click', () => {
            const body = header.nextElementSibling;
            if (!body) return;

            if (body.classList.contains('collapsible-body') || body.classList.contains('collapsible-form-content')) {
                header.classList.toggle('collapsed');
                body.classList.toggle('collapsed');
            }
        });
    });
}

// Make functions global
window.initProviderPanelTabs = initProviderPanelTabs;
window.initCollapsibleSections = initCollapsibleSections;

function initForgotPassword() {
    // Use setTimeout to ensure DOM is fully ready
    setTimeout(() => {
        // Client forgot password
        const clientForgotBtn = document.getElementById('clientForgotPasswordBtn');
        if (clientForgotBtn) {
            clientForgotBtn.onclick = null;
            clientForgotBtn.addEventListener('click', showClientForgotPasswordForm);
        }

        const clientBackBtn = document.getElementById('clientBackToLoginBtn');
        if (clientBackBtn) {
            clientBackBtn.onclick = null;
            clientBackBtn.addEventListener('click', showClientLoginFromForgot);
        }

        // Don't clone - just add event listener directly to existing form
        const clientResetForm = document.getElementById('clientResetForm');
        if (clientResetForm) {
            // Remove old event listeners to prevent duplicates
            clientResetForm.removeEventListener('submit', resetClientPassword);
            clientResetForm.addEventListener('submit', resetClientPassword);
        }

        // Provider forgot password
        const providerForgotBtn = document.getElementById('providerForgotPasswordBtn');
        if (providerForgotBtn) {
            providerForgotBtn.onclick = null;
            providerForgotBtn.addEventListener('click', showProviderForgotPasswordForm);
        }

        const providerBackBtn = document.getElementById('providerBackToLoginBtn');
        if (providerBackBtn) {
            providerBackBtn.onclick = null;
            providerBackBtn.addEventListener('click', showProviderLoginFromForgot);
        }

        // Don't clone - just add event listener directly to existing form
        const providerResetForm = document.getElementById('providerResetForm');
        if (providerResetForm) {
            // Remove old event listeners to prevent duplicates
            providerResetForm.removeEventListener('submit', resetProviderPassword);
            providerResetForm.addEventListener('submit', resetProviderPassword);
        }
    }, 100);
}
// Show forgot password form for clients
function showClientForgotPasswordForm() {
    const loginForm = document.getElementById('clientLoginForm');
    const forgotForm = document.getElementById('clientForgotPasswordForm');

    if (loginForm) loginForm.classList.add('hidden');
    if (forgotForm) forgotForm.classList.remove('hidden');
}
// Show login form from client forgot password
function showClientLoginFromForgot() {
    const loginForm = document.getElementById('clientLoginForm');
    const forgotForm = document.getElementById('clientForgotPasswordForm');

    if (loginForm) loginForm.classList.remove('hidden');
    if (forgotForm) forgotForm.classList.add('hidden');

    // Clear the form
    const resetForm = document.getElementById('clientResetForm');
    if (resetForm) resetForm.reset();
}
function getPasswordResetMessage(error) {
    const errorCode = String(error && error.code || '');
    if (errorCode === 'auth/invalid-email') {
        return 'Enter a valid email address.';
    }
    if (errorCode === 'auth/too-many-requests') {
        return 'Too many reset attempts. Please wait and try again later.';
    }
    if (errorCode === 'auth/network-request-failed') {
        return 'Unable to reach Firebase. Check your connection and try again.';
    }
    if (errorCode === 'auth/operation-not-allowed') {
        return 'Password reset is not enabled. Please contact support.';
    }
    if (errorCode === 'auth/user-not-found') {
        return 'For security, account availability cannot be confirmed. If this email is registered, use the secure reset email from Firebase.';
    }
    return error && error.message ? error.message : 'Unable to request a password reset. Please try again.';
}

async function requestVerifiedPasswordReset(email, formId, returnToLogin) {
    if (!window.authService || typeof window.authService.requestPasswordReset !== 'function') {
        throw new Error('Secure password reset is not available. Please try again in a moment.');
    }

    await window.authService.requestPasswordReset(email);
    showNotification(
        'Check your email for a secure Firebase reset link. Your password will not change until you verify ownership of the email and complete that link.',
        'success'
    );

    const resetForm = document.getElementById(formId);
    if (resetForm) resetForm.reset();
    returnToLogin();
}

// Request an email-verified client password reset.
async function resetClientPassword(e) {
    e.preventDefault();

    const email = document.getElementById('clientResetEmail').value.trim();

    if (!email) {
        showNotification('Enter your email address.', 'error');
        return;
    }

    try {
        await requestVerifiedPasswordReset(email, 'clientResetForm', showClientLoginFromForgot);
    } catch (error) {
        console.error('[ForgotPassword] Client reset request failed:', error);
        showNotification(getPasswordResetMessage(error), 'error');
    }
}
// Show forgot password form for providers
function showProviderForgotPasswordForm() {
    const loginForm = document.getElementById('providerLoginForm');
    const forgotForm = document.getElementById('providerForgotPasswordForm');

    if (loginForm) loginForm.classList.add('hidden');
    if (forgotForm) forgotForm.classList.remove('hidden');
}
// Show login form from provider forgot password
function showProviderLoginFromForgot() {
    const loginForm = document.getElementById('providerLoginForm');
    const forgotForm = document.getElementById('providerForgotPasswordForm');

    if (loginForm) loginForm.classList.remove('hidden');
    if (forgotForm) forgotForm.classList.add('hidden');

    // Clear the form
    const resetForm = document.getElementById('providerResetForm');
    if (resetForm) resetForm.reset();
}
// Request an email-verified provider password reset.
async function resetProviderPassword(e) {
    e.preventDefault();

    const email = document.getElementById('providerResetEmail').value.trim();

    if (!email) {
        showNotification('Enter your email address.', 'error');
        return;
    }

    try {
        await requestVerifiedPasswordReset(email, 'providerResetForm', showProviderLoginFromForgot);
    } catch (error) {
        console.error('[ForgotPassword] Provider reset request failed:', error);
        showNotification(getPasswordResetMessage(error), 'error');
    }
}
// Make forgot password functions global
window.showClientForgotPasswordForm = showClientForgotPasswordForm;
window.showClientLoginFromForgot = showClientLoginFromForgot;
window.showProviderForgotPasswordForm = showProviderForgotPasswordForm;
window.showProviderLoginFromForgot = showProviderLoginFromForgot;

// ==================== CUSTOM MEAL REQUESTS - PROVIDER OPT-IN FEATURE ====================
// Check if any provider accepts custom meal requests
function hasProviderAcceptingCustomRequests() {
    return providers.some(p => p.acceptsCustomRequests === true);
}

// Show/hide custom meal request form based on provider settings
function updateCustomMealRequestVisibility() {
    // Check main page request form
    const requestForm = document.getElementById('requestForm');
    const requestSection = requestForm ? requestForm.closest('.request-section') : null;

    // Check client dashboard custom request section
    const clientCustomRequestSection = document.querySelector('.custom-request-section');

    // Check if any provider accepts custom requests
    const hasCustomRequests = hasProviderAcceptingCustomRequests();

    // Update visibility
    if (requestSection) {
        if (hasCustomRequests) {
            requestSection.classList.remove('hidden');
        } else {
            requestSection.classList.add('hidden');
        }
    }

    if (clientCustomRequestSection) {
        if (hasCustomRequests) {
            clientCustomRequestSection.classList.remove('hidden');
        } else {
            clientCustomRequestSection.classList.add('hidden');
        }
    }

    // Also update the custom requests tab/button visibility
    const customRequestsTab = document.querySelector('.client-dashboard-tab[data-dashboard-tab="requests"]');
    if (customRequestsTab) {
        if (hasCustomRequests) {
            customRequestsTab.classList.remove('hidden');
        } else {
            customRequestsTab.classList.add('hidden');
        }
    }
}

// Initialize custom meal requests visibility check
function initCustomMealRequestsFeature() {
    // Run on page load
    updateCustomMealRequestVisibility();

    // Also run when providers change (after registration/login)
    // This ensures new providers accepting custom requests are reflected
}

// Make function global
window.hasProviderAcceptingCustomRequests = hasProviderAcceptingCustomRequests;
window.updateCustomMealRequestVisibility = updateCustomMealRequestVisibility;

// ==================== MEAL SUGGESTIONS ENGINE ====================
/**
 * "Because You Ate & Liked It — Try This!"
 *
 * Scoring factors (all Firebase-backed — no localStorage):
 *  1. Same mealType as items the client has ordered and rated 4+ stars     (+5 per match)
 *  2. Same provider the client rated 4+ stars                              (+4 per match)
 *  3. Same mealType as any past order (even unrated)                       (+2 per match)
 *  4. Same provider as any past order                                      (+1)
 *  5. Is a Daily Special today                                             (+3)
 *  6. Provider has avg rating >= 4.0                                       (+2)
 *  7. Currently open                                                       (+1)
 *  8. Client's country matches item's country                              (+2)
 *  9. Positive feedback from client on a past suggestion for same provider (+3)
 * 10. Negative feedback on a past suggestion removes item entirely         (skip)
 * 11. Already ordered this exact item recently (< 7 days)                  (skip — avoid repeats)
 * 12. Not already in the current suggestion set (deduplicate)
 *
 * Returns up to MAX_SUGGESTIONS scored items with a human-readable reason.
 */
const MAX_SUGGESTIONS = 3;

function computeMealSuggestions(pastSuggestionFeedback) {
    if (!currentClient || !Array.isArray(menuItems) || menuItems.length === 0) return [];

    const clientEmail = currentClient.email ? currentClient.email.toLowerCase() : null;
    if (!clientEmail) return [];

    // --- Build signals from the client's order history ---
    const myOrders = clientMessages.filter(m =>
        m.clientEmail && m.clientEmail.toLowerCase() === clientEmail
    );

    // Liked mealTypes: from orders where the review rating >= 4
    const likedMealTypes = new Set();
    const likedProviders = new Set();
    const orderedMealTypes = new Set();
    const orderedProviders = new Set();
    // Items ordered in the last 7 days (avoid re-suggesting too soon)
    const recentItemIds = new Set();
    const sevenDaysAgo = Date.now() - 7 * 24 * 3600 * 1000;

    myOrders.forEach(order => {
        if (order.mealType) orderedMealTypes.add(order.mealType);
        if (order.providerName) orderedProviders.add(order.providerName.toLowerCase());

        // Check if there's a high-rated review for this order
        const orderReview = reviews.find(r => r.orderId === order.id);
        if (orderReview && orderReview.rating >= 4) {
            if (order.mealType) likedMealTypes.add(order.mealType);
            if (order.providerName) likedProviders.add(order.providerName.toLowerCase());
        }

        // Track recently ordered items
        if (order.menuItemId && new Date(order.createdAt).getTime() > sevenDaysAgo) {
            recentItemIds.add(String(order.menuItemId));
        }
        // Also match by food name + provider as fallback
        if (order.foodItem && order.providerName && new Date(order.createdAt).getTime() > sevenDaysAgo) {
            recentItemIds.add((order.foodItem + '|' + order.providerName).toLowerCase());
        }
    });

    // Build a set of providers the client gave negative feedback on in suggestions
    const negativeProviders = new Set();
    const positiveProviders = new Set();
    if (Array.isArray(pastSuggestionFeedback)) {
        pastSuggestionFeedback.forEach(fb => {
            if (fb.helpful === false && fb.providerName) negativeProviders.add(fb.providerName.toLowerCase());
            if (fb.helpful === true && fb.providerName) positiveProviders.add(fb.providerName.toLowerCase());
        });
    }

    // --- Score every active menu item ---
    const scored = [];
    const activeItems = menuItems.filter(item => item.isActive !== false);

    activeItems.forEach(item => {
        // Skip items already ordered recently
        const recentKey = ((item.foodName || '') + '|' + (item.providerName || '')).toLowerCase();
        if (recentItemIds.has(String(item.id)) || recentItemIds.has(recentKey)) return;

        // Skip items from providers with negative suggestion feedback
        if (item.providerName && negativeProviders.has(item.providerName.toLowerCase())) return;

        // Skip items this client has already ordered from (unless liked) — avoid re-suggesting unknowns
        // Only skip if NOT a liked provider and NOT liked mealType and they've ordered from there
        const alreadyOrdered = item.providerName && orderedProviders.has(item.providerName.toLowerCase());
        const isLikedProvider = item.providerName && likedProviders.has(item.providerName.toLowerCase());
        const isLikedType = item.mealType && likedMealTypes.has(item.mealType);
        if (alreadyOrdered && !isLikedProvider && !isLikedType) return;

        let score = 0;
        const reasons = [];

        // Factor 1: Liked mealType
        if (item.mealType && likedMealTypes.has(item.mealType)) {
            score += 5;
            reasons.push(`You loved ${item.mealType} before`);
        }

        // Factor 2: Liked provider
        if (isLikedProvider) {
            score += 4;
            reasons.push(`You rated ${escapeHtml(item.providerName)} highly`);
        }

        // Factor 3: Ordered mealType (not necessarily rated)
        if (item.mealType && orderedMealTypes.has(item.mealType) && !likedMealTypes.has(item.mealType)) {
            score += 2;
            reasons.push(`Matches your taste in ${item.mealType}`);
        }

        // Factor 4: Ordered from same provider (unrated)
        if (alreadyOrdered && !isLikedProvider) {
            score += 1;
        }

        // Factor 5: Daily special
        if (isDailySpecial(item)) {
            score += 3;
            reasons.push('🔥 On special today');
        }

        // Factor 6: Highly rated provider (avg >= 4.0)
        const ratingData = getProviderRating(item.providerName, item.providerId);
        if (ratingData && parseFloat(ratingData.average) >= 4.0) {
            score += 2;
            reasons.push(`★ ${ratingData.average} rated provider`);
        }

        // Factor 7: Currently open
        if (isCurrentlyOpen(item) || item.is247) {
            score += 1;
            reasons.push('Open now');
        }

        // Factor 8: Country match
        if (currentClient.country && item.country && item.country === currentClient.country) {
            score += 2;
            reasons.push('Available in your country');
        }

        // Factor 9: Positive suggestion feedback for this provider
        if (item.providerName && positiveProviders.has(item.providerName.toLowerCase())) {
            score += 3;
            reasons.push('You found a suggestion from here helpful before');
        }

        // Only include items with meaningful signal
        if (score > 0 && reasons.length > 0) {
            scored.push({ item, score, reasons });
        }
    });

    // Sort by score descending; break ties by dailySpecial then by providerRating
    scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        if (isDailySpecial(b.item) !== isDailySpecial(a.item)) return isDailySpecial(b.item) ? 1 : -1;
        const ra = getProviderRating(a.item.providerName);
        const rb = getProviderRating(b.item.providerName);
        return (rb ? parseFloat(rb.average) : 0) - (ra ? parseFloat(ra.average) : 0);
    });

    // Deduplicate by provider — don't suggest two items from the exact same provider
    const seenProviders = new Set();
    const top = [];
    for (const entry of scored) {
        const pKey = (entry.item.providerName || '').toLowerCase();
        if (seenProviders.has(pKey)) continue;
        seenProviders.add(pKey);
        top.push(entry);
        if (top.length >= MAX_SUGGESTIONS) break;
    }

    return top;
}

/**
 * Render the suggestion cards into #clientSuggestionsContainer.
 * Loads past feedback from Firestore first, then runs the scoring engine.
 */
async function renderMealSuggestions() {
    const container = document.getElementById('clientSuggestionsContainer');
    if (!container) return;
    if (!currentClient) {
        container.innerHTML = '<div class="empty-state"><p>Please log in to see your personalised suggestions.</p></div>';
        return;
    }

    container.innerHTML = '<div class="empty-state"><p>⏳ Computing your suggestions…</p></div>';

    // Load past suggestion feedback from Firestore
    let pastFeedback = [];
    try {
        if (typeof window.firebaseLoadSuggestionFeedback === 'function') {
            pastFeedback = await window.firebaseLoadSuggestionFeedback(currentClient.uid || currentClient.id);
        }
    } catch (e) {
        console.warn('[Suggestions] Could not load past feedback:', e.message);
    }

    const suggestions = computeMealSuggestions(pastFeedback);

    if (suggestions.length === 0) {
        container.innerHTML = `
            <div class="empty-state suggestions-empty">
                <p>🍽️ We're still learning your taste! Once you place and rate a few orders, we'll suggest meals you'll love.</p>
                <button class="cta-search-btn" onclick="showClientDashboardTab('search')">Browse Meals</button>
            </div>`;
        return;
    }

    container.innerHTML = suggestions.map(({ item, score, reasons }) => {
        const mainReason = reasons.slice(0, 2).join(' · ');
        const itemCurrency = item.currency || getMarketRule(item.country || 'US').currency;
        const dealHtml = isDailySpecial(item) && item.specialPrice > 0
            ? `<span class="deal-price-tag">🏷️ ${formatCurrency(Number(item.specialPrice), item.country || 'US', itemCurrency)} <span class="original-price">${formatCurrency(Number(item.price || 0), item.country || 'US', itemCurrency)}</span></span>`
            : `<span class="detail-tag price">${formatCurrency(Number(item.price || 0), item.country || 'US', itemCurrency)}</span>`;
        const dealNote = (isDailySpecial(item) && item.specialNote) ? `<div class="deal-note">${escapeHtml(item.specialNote)}</div>` : '';
        const photoHtml = item.photo ? `<div class="suggestion-photo"><img src="${item.photo}" alt="${escapeHtml(item.foodName)}"></div>` : '';
        const ratingData = getProviderRating(item.providerName, item.providerId);
        const ratingHtml = ratingData ? `<span class="detail-tag">★ ${ratingData.average} (${ratingData.count})</span>` : '';
        const suggestionId = window.generateUUID();

        return `
            <div class="suggestion-card" id="suggestion-card-${suggestionId}" data-item-id="${item.id}" data-provider="${escapeHtml(item.providerName)}">
                ${photoHtml}
                <div class="suggestion-body">
                    <div class="suggestion-reason">💡 ${escapeHtml(mainReason)}</div>
                    <div class="suggestion-food-name">${escapeHtml(item.foodName)}</div>
                    <div class="suggestion-provider">by ${escapeHtml(item.providerName)}</div>
                    ${dealNote}
                    <div class="suggestion-meta">
                        ${dealHtml}
                        ${ratingHtml}
                        ${isDailySpecial(item) ? '<span class="special-badge-inline">🔥 Special</span>' : ''}
                        ${(item.is247 || isCurrentlyOpen(item)) ? '<span class="detail-tag status-open">Open</span>' : ''}
                    </div>
                    <div class="suggestion-desc">${escapeHtml((item.foodDesc || '').slice(0, 100))}${(item.foodDesc || '').length > 100 ? '…' : ''}</div>
                    <div class="suggestion-actions">
                        <button class="contact-btn suggestion-order-btn" onclick="openContactModal('${item.id}')">🛒 Order This</button>
                    </div>
                    <div class="suggestion-feedback">
                        <span class="suggestion-feedback-label">Did this suggestion help?</span>
                        <button class="feedback-yes-btn" onclick="recordSuggestionFeedback('${suggestionId}','${item.id}','${escapeHtml(item.providerName)}','${escapeHtml(item.mealType || '')}',true)" title="Yes, this helped!">👍 Yes</button>
                        <button class="feedback-no-btn" onclick="recordSuggestionFeedback('${suggestionId}','${item.id}','${escapeHtml(item.providerName)}','${escapeHtml(item.mealType || '')}',false)" title="Not helpful">👎 No</button>
                    </div>
                </div>
            </div>`;
    }).join('');
}

/** Called by the Refresh button — just re-renders */
function refreshMealSuggestions() {
    renderMealSuggestions();
}

/**
 * Record whether a suggestion was helpful.
 * Writes to Firestore suggestions_feedback collection.
 * Updates the card UI immediately.
 */
async function recordSuggestionFeedback(suggestionId, itemId, providerName, mealType, helpful) {
    if (!currentClient) return;

    const card = document.getElementById('suggestion-card-' + suggestionId);
    if (card) {
        const fb = card.querySelector('.suggestion-feedback');
        if (fb) {
            fb.innerHTML = helpful
                ? '<span class="feedback-thanks feedback-positive">👍 Great! We\'ll show you more like this.</span>'
                : '<span class="feedback-thanks feedback-negative">👎 Got it! We\'ll tune your suggestions.</span>';
        }
    }

    const feedbackDoc = {
        id: suggestionId,
        clientId: currentClient.uid || currentClient.id,
        clientEmail: currentClient.email,
        menuItemId: itemId,
        providerName: sanitizeText(providerName, 120),
        mealType: sanitizeText(mealType, 60),
        helpful: helpful,
        createdAt: new Date().toISOString()
    };

    // Show aggregate thanks message
    const summary = document.getElementById('suggestionFeedbackSummary');
    if (summary) summary.style.display = 'block';

    // Persist to Firestore
    if (typeof window.firebaseSaveSuggestionFeedback === 'function') {
        try {
            await window.firebaseSaveSuggestionFeedback(feedbackDoc);
        } catch (e) {
            console.warn('[Suggestions] Could not save feedback:', e.message);
        }
    }
}

window.renderMealSuggestions = renderMealSuggestions;
window.refreshMealSuggestions = refreshMealSuggestions;
window.recordSuggestionFeedback = recordSuggestionFeedback;

// ==================== CLIENT DASHBOARD TABS ====================
// Initialize Client Dashboard Tab Navigation
function initClientDashboardTabs() {
    const tabs = document.querySelectorAll('.client-dashboard-tab');

    // Always clear admin/provider/client user badge precedence issues.
    // (Admin login is fully separate and only toggles #adminDashboard.)

    // Safety check - return if no tabs found
    if (!tabs || tabs.length === 0) {
        console.log('Client dashboard tabs not found - skipping initialization');
        return;
    }

    tabs.forEach(tab => {
        tab.addEventListener('click', (e) => {
            e.preventDefault();
            const targetPanel = tab.dataset.dashboardTab;
            console.log('[ClientDashboardTabs] click targetPanel=', targetPanel);

            try {
                // Update active tab button
                tabs.forEach(t => t.classList.remove('active'));
                tab.classList.add('active');

                // Hide all dashboard panels first
                const allPanels = document.querySelectorAll('.client-dashboard-panel');
                allPanels.forEach(panel => {
                    panel.classList.remove('active');
                    panel.style.display = 'none';
                });

                // Always hide edit form when switching tabs (workflow rule)
                const editForm = document.getElementById('clientEditProfileForm');
                if (editForm) editForm.classList.add('hidden');

                // Show the specific panel based on targetPanel
                const targetPanelEl = document.getElementById(`client-${targetPanel}-panel`);
                if (targetPanelEl) {
                    targetPanelEl.classList.add('active');
                    targetPanelEl.style.display = '';
                }

                // Refresh content for specific panels
                if (targetPanel === 'search') {
                    renderClientSearchResults();
                } else if (targetPanel === 'orders') {
                    renderClientOrders();
                } else if (targetPanel === 'profile') {
                    renderClientProfileView();
                } else if (targetPanel === 'rewards') {
                    loadClientRewards();
                } else if (targetPanel === 'suggestions') {
                    renderMealSuggestions();
                }
            } catch (err) {
                console.error('[ClientDashboardTabs] error while switching panel', targetPanel, err);
            }
        });
    });
}

// Global function to switch client dashboard tabs programmatically
function showClientDashboardTab(tabName) {
    const tabs = document.querySelectorAll('.client-dashboard-tab');
    const targetTab = Array.from(tabs).find(t => t.dataset.dashboardTab === tabName);

    if (targetTab) {
        targetTab.click();
    }
}

// ==================== ADMIN PANEL RENDERING ====================
async function renderAdminStats() {
    console.log('[Admin] Rendering stats...');
    const preferredContainer = document.getElementById('adminStats') || document.getElementById('admin-stats-panel');
    if (!preferredContainer) return;

    const targetContainer = document.getElementById('adminStats') || preferredContainer;

    try {
        const statsClients = Array.isArray(window.clients) ? window.clients : (Array.isArray(clients) ? clients : []);
        const statsProviders = Array.isArray(window.providers) ? window.providers : (Array.isArray(providers) ? providers : []);
        const statsMenuItems = Array.isArray(window.menuItems) ? window.menuItems : (Array.isArray(menuItems) ? menuItems : []);
        const statsOrders = Array.isArray(window.orders) ? window.orders : (Array.isArray(orders) ? orders : (Array.isArray(window.clientMessages) ? window.clientMessages : (Array.isArray(clientMessages) ? clientMessages : [])));
        const statsReviews = Array.isArray(window.reviews) ? window.reviews : (Array.isArray(reviews) ? reviews : []);

        const pendingOrders = statsOrders.filter((order) => {
            const status = String(order && order.status ? order.status : 'pending').toLowerCase();
            return status === 'pending' || status === 'queued' || status === 'new';
        }).length;

        const completedOrders = statsOrders.filter((order) => {
            const status = String(order && order.status ? order.status : 'pending').toLowerCase();
            return status === 'completed';
        }).length;

        const totalRevenue = statsOrders
            .filter((order) => {
                const status = String(order && order.status ? order.status : 'pending').toLowerCase();
                return status === 'completed';
            })
            .reduce((sum, order) => sum + (Number(order && order.price) || 0), 0);

        const activeProviders = new Set(statsMenuItems.map((m) => m && (m.providerId || m.provider_id)).filter(Boolean)).size;

        const statsHtml = `
            <div class="stats-grid">
                <div class="stat-card">
                    <h3>Platform Statistics</h3>
                    <p class="stat-number">${statsClients.length + statsProviders.length}</p>
                </div>
                <div class="stat-card">
                    <h3>Total Clients</h3>
                    <p class="stat-number">${statsClients.length}</p>
                </div>
                <div class="stat-card">
                    <h3>Total Providers</h3>
                    <p class="stat-number">${statsProviders.length}</p>
                </div>
                <div class="stat-card">
                    <h3>Menu Items</h3>
                    <p class="stat-number">${statsMenuItems.length}</p>
                </div>
                <div class="stat-card">
                    <h3>Total Orders</h3>
                    <p class="stat-number">${statsOrders.length}</p>
                </div>
                <div class="stat-card">
                    <h3>Pending Orders</h3>
                    <p class="stat-number">${pendingOrders}</p>
                </div>
                <div class="stat-card">
                    <h3>Completed Orders</h3>
                    <p class="stat-number">${completedOrders}</p>
                </div>
                <div class="stat-card">
                    <h3>Revenue</h3>
                    <p class="stat-number">$${totalRevenue.toFixed(2)}</p>
                </div>
                <div class="stat-card">
                    <h3>Active Providers</h3>
                    <p class="stat-number">${activeProviders}</p>
                </div>
                <div class="stat-card">
                    <h3>Reviews</h3>
                    <p class="stat-number">${statsReviews.length}</p>
                </div>
            </div>
        `;

        targetContainer.innerHTML = statsHtml;
    } catch (error) {
        console.error('[Admin] Error rendering stats:', error);
        targetContainer.innerHTML = '<div class="empty-state"><p>Stats unavailable.</p></div>';
    }
}
window.renderAdminStats = renderAdminStats;

async function renderAdminSubscriptions() {
    console.log('[Admin] Rendering subscriptions...');
    const container = document.getElementById('adminProviderSubscriptionsList') || document.getElementById('admin-subscriptions-panel');
    if (!container) return;

    try {
        const subscriptions = Array.isArray(window.providers) ? window.providers : (Array.isArray(providers) ? providers : []);
        let html = '<div class="data-list"><h3>Active Subscriptions</h3>';

        if (!subscriptions.length) {
            html += '<div class="data-item"><span>No subscriptions found</span></div>';
        } else {
            subscriptions.forEach((provider) => {
                const status = String(provider?.subscriptionStatus || provider?.accountStatus || 'pending');
                const plan = provider?.subscriptionPlan || provider?.plan || 'Free';
                html += `
                    <div class="data-item">
                        <span>${escapeHtml(provider?.restaurantName || provider?.name || 'Unknown Provider')} - ${escapeHtml(plan)}</span>
                        <span>${escapeHtml(status)}</span>
                    </div>
                `;
            });
        }

        html += '</div>';
        container.innerHTML = html;
    } catch (error) {
        console.error('[Admin] Error rendering subscriptions:', error);
    }
}

async function renderAdminClients() {
    console.log('[Admin] Rendering clients...');
    const container = document.getElementById('admin-clients-panel');
    if (!container) return;

    try {
        const sourceClients = Array.isArray(window.clients) ? window.clients : (Array.isArray(clients) ? clients : []);
        const displayCount = paginationDisplay.adminClients;
        const visible = sourceClients.slice(0, displayCount);
        const firestoreHasMore = window.__firestorePaginationState && window.__firestorePaginationState['clients'] && window.__firestorePaginationState['clients'].hasMore;
        const hasMore = sourceClients.length > displayCount || firestoreHasMore;

        let html = '<div class="data-list"><h3>All Clients</h3>';

        if (!visible.length) {
            html += '<div class="data-item"><span>No clients found</span></div>';
        } else {
            visible.forEach((client) => {
                html += `
                    <div class="data-item">
                        <span>${escapeHtml(client?.name || 'Unknown')}</span>
                        <span>${escapeHtml(client?.email || 'No email')}</span>
                    </div>
                `;
            });
        }

        if (hasMore) {
            html += `
                <div style="text-align:center;margin-top:12px;">
                    <button class="load-more-btn" onclick="loadMoreAdminClients()" style="padding:7px 20px;background:#3b82f6;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:0.88em;">
                        Load More Clients
                    </button>
                    <div style="font-size:0.75em;color:#888;margin-top:3px;">Showing ${visible.length} of ${sourceClients.length}${firestoreHasMore ? '+' : ''}</div>
                </div>
            `;
        }

        html += '</div>';
        container.innerHTML = html;
    } catch (error) {
        console.error('[Admin] Error rendering clients:', error);
    }
}

async function renderAdminProviders() {
    console.log('[Admin] Rendering providers...');
    const container = document.getElementById('admin-providers-panel');
    if (!container) return;

    try {
        const sourceProviders = Array.isArray(window.providers) ? window.providers : (Array.isArray(providers) ? providers : []);
        const displayCount = paginationDisplay.adminProviders;
        const visible = sourceProviders.slice(0, displayCount);
        const firestoreHasMore = window.__firestorePaginationState && window.__firestorePaginationState['providers'] && window.__firestorePaginationState['providers'].hasMore;
        const hasMore = sourceProviders.length > displayCount || firestoreHasMore;

        let html = '<div class="data-list"><h3>All Providers</h3>';

        if (!visible.length) {
            html += '<div class="data-item"><span>No providers found</span></div>';
        } else {
            visible.forEach((provider) => {
                html += `
                    <div class="data-item">
                        <span>${escapeHtml(provider?.restaurantName || provider?.name || 'Unknown')}</span>
                        <span>${escapeHtml(provider?.email || 'No email')}</span>
                    </div>
                `;
            });
        }

        if (hasMore) {
            html += `
                <div style="text-align:center;margin-top:12px;">
                    <button class="load-more-btn" onclick="loadMoreAdminProviders()" style="padding:7px 20px;background:#3b82f6;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:0.88em;">
                        Load More Providers
                    </button>
                    <div style="font-size:0.75em;color:#888;margin-top:3px;">Showing ${visible.length} of ${sourceProviders.length}${firestoreHasMore ? '+' : ''}</div>
                </div>
            `;
        }

        html += '</div>';
        container.innerHTML = html;
    } catch (error) {
        console.error('[Admin] Error rendering providers:', error);
    }
}

async function renderAdminOrders() {
    console.log('[Admin] Rendering orders...');
    const container = document.getElementById('admin-orders-panel');
    if (!container) return;

    try {
        const sourceOrders = Array.isArray(window.orders) ? window.orders : (Array.isArray(orders) ? orders : []);
        const displayCount = paginationDisplay.adminOrders;
        const visible = sourceOrders.slice(0, displayCount);
        const firestoreHasMore = window.__firestorePaginationState && window.__firestorePaginationState['orders'] && window.__firestorePaginationState['orders'].hasMore;
        const hasMore = sourceOrders.length > displayCount || firestoreHasMore;

        let html = '<div class="data-list"><h3>All Orders</h3>';

        if (!visible.length) {
            html += '<div class="data-item"><span>No orders found</span></div>';
        } else {
            visible.forEach((order) => {
                const orderId = order?.id || order?.orderId || 'Unknown';
                const status = String(order?.status || 'pending');
                html += `
                    <div class="data-item">
                        <span>Order #${escapeHtml(orderId)}</span>
                        <span>${escapeHtml(status)}</span>
                    </div>
                `;
            });
        }

        if (hasMore) {
            html += `
                <div style="text-align:center;margin-top:12px;">
                    <button class="load-more-btn" onclick="loadMoreAdminOrders()" style="padding:7px 20px;background:#3b82f6;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:0.88em;">
                        Load More Orders
                    </button>
                    <div style="font-size:0.75em;color:#888;margin-top:3px;">Showing ${visible.length} of ${sourceOrders.length}${firestoreHasMore ? '+' : ''}</div>
                </div>
            `;
        }

        html += '</div>';
        container.innerHTML = html;
    } catch (error) {
        console.error('[Admin] Error rendering orders:', error);
    }
}

async function renderAdminReviews() {
    console.log('[Admin] Rendering reviews...');
    const container = document.getElementById('admin-reviews-panel');
    if (!container) return;

    try {
        const sourceReviews = Array.isArray(window.reviews) ? window.reviews : (Array.isArray(reviews) ? reviews : []);
        let html = '<div class="data-list"><h3>All Reviews</h3>';

        if (!sourceReviews.length) {
            html += '<div class="data-item"><span>No reviews found</span></div>';
        } else {
            sourceReviews.forEach((review) => {
                const reviewText = review?.text || review?.comment || 'No text';
                const rating = review?.rating || 0;
                html += `
                    <div class="data-item">
                        <span>${escapeHtml(reviewText)}</span>
                        <span>${escapeHtml(String(rating))}/5</span>
                    </div>
                `;
            });
        }

        html += '</div>';
        container.innerHTML = html;
    } catch (error) {
        console.error('[Admin] Error rendering reviews:', error);
    }
}

function renderAdminSettings() {
    console.log('[Admin] Rendering settings...');
    const container = document.getElementById('admin-settings-panel');
    if (!container) return;

    container.innerHTML = `
        <div class="settings-section">
            <h3>Admin Settings</h3>
            <p>Configure system settings here.</p>
        </div>
    `;
}

// ==================== SUPER ADMIN TAB SWITCHING ====================
// Switch admin dashboard tabs
function switchAdminTab(tabName) {
    console.log('[Admin] switchAdminTab called:', tabName);

    // Get all tab buttons
    const tabs = document.querySelectorAll('.admin-panel-tab');

    // Update active tab button
    tabs.forEach(t => {
        t.classList.remove('active');
        if (t.dataset.adminPanel === tabName) {
            t.classList.add('active');
        }
    });

    // Hide all admin panel sections
    const allSections = document.querySelectorAll('.admin-panel-section');
    allSections.forEach(section => {
        section.classList.remove('active');
        section.style.display = 'none';
    });

    // Show target section
    const targetSection = document.getElementById(`admin-${tabName}-panel`);
    if (targetSection) {
        targetSection.classList.add('active');
        targetSection.style.display = '';
    }

    // Render data for specific panel
    if (tabName === 'stats') {
        renderAdminStats();
    } else if (tabName === 'subscriptions') {
        renderAdminSubscriptions();
    } else if (tabName === 'clients') {
        renderAdminClients();
    } else if (tabName === 'providers') {
        renderAdminProviders();
    } else if (tabName === 'orders') {
        renderAdminOrders();
    } else if (tabName === 'reviews') {
        renderAdminReviews();
    } else if (tabName === 'settings') {
        renderAdminSettings();
    }

    console.log('[Admin] Switched to tab:', tabName);
}

// Make functions global
window.initClientDashboardTabs = initClientDashboardTabs;
window.showClientDashboardTab = showClientDashboardTab;
window.switchAdminTab = switchAdminTab;
window.loadMoreClientOrders = loadMoreClientOrders;
window.loadMoreProviderOrders = loadMoreProviderOrders;
window.loadMoreAdminClients = loadMoreAdminClients;
window.loadMoreAdminProviders = loadMoreAdminProviders;
window.loadMoreAdminOrders = loadMoreAdminOrders;
window.resetPaginationDisplay = resetPaginationDisplay;

// ==================== SUPER ADMIN PANEL ====================
// Initialize Admin Panel - FIXED VERSION
function initAdminPanel() {
    console.log('[Admin] Initializing admin panel...');

    // Find the admin login form
    const adminLogForm = document.getElementById('adminLogForm');
    if (!adminLogForm) {
        console.log('[Admin] adminLogForm not found!');
        return;
    }

    // Make form not submit normally - prevent page reload/redirect
    adminLogForm.addEventListener('submit', function(e) {
        e.preventDefault();
        e.stopPropagation();
        console.log('[Admin] Form submit intercepted');
        performAdminLogin();
        return false;
    });

    // Also handle button click directly
    const loginBtn = adminLogForm.querySelector('button[type="submit"]');
    if (loginBtn) {
        loginBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            console.log('[Admin] Button click intercepted');
            performAdminLogin();
            return false;
        });
    }

    console.log('[Admin] Admin panel initialized');

    // Admin logout button
    const adminLogoutBtn = document.getElementById('adminLogoutBtn');
    if (adminLogoutBtn) {
        adminLogoutBtn.onclick = function(e) {
            if (e) {
                e.preventDefault();
            }
            console.log('[Admin] Logging out...');

            // Clear admin session
            currentAdmin = null;

            // Ensure provider/client sessions are cleared (no stale dashboard)
            clearProviderAndClientSessions();
            hideProviderAndClientDashboards();

            // CRITICAL FIX: Terminate the persisted Firebase Auth session so the
            // admin does NOT appear logged in after a page refresh.
            const doSignOut = () => {
                if (typeof window.authService !== 'undefined' && typeof window.authService.signOut === 'function') {
                    return window.authService.signOut();
                }
                if (typeof hybridSignOut === 'function') {
                    return hybridSignOut();
                }
                return Promise.resolve();
            };

            doSignOut().catch((err) => {
                console.warn('[AdminLogout] Firebase signOut error:', err);
            });

            // Hide dashboard, show login
            const adminDashboard = document.getElementById('adminDashboard');
            const adminLoginSection = document.getElementById('adminLoginSection');

            if (adminDashboard) adminDashboard.classList.add('hidden');
            if (adminLoginSection) adminLoginSection.classList.remove('hidden');

            // Update header badge after logout so we don't show stale role
            updateHeaderUserBadge();

            console.log('[Admin] Logged out successfully');
            alert('Logged out successfully!');
        };
    }

    // Admin panel tabs
    const adminPanelTabs = document.querySelectorAll('.admin-panel-tab');
    if (adminPanelTabs && adminPanelTabs.length > 0) {
        adminPanelTabs.forEach(tab => {
            tab.addEventListener('click', () => {
                const targetPanel = tab.dataset.adminPanel;
                console.log('[Admin] Tab clicked:', targetPanel);

                // Update active tab
                adminPanelTabs.forEach(t => t.classList.remove('active'));
                tab.classList.add('active');

                // Hide all admin panel sections
                const allSections = document.querySelectorAll('.admin-panel-section');
                allSections.forEach(section => section.classList.remove('active'));

                // Show target section
                const targetSection = document.getElementById(`admin-${targetPanel}-panel`);
                if (targetSection) {
                    targetSection.classList.add('active');
                }

// Render data for specific panel
                if (targetPanel === 'stats') {
                    renderAdminStats();
                } else if (targetPanel === 'subscriptions') {
                    renderAdminSubscriptions();
                } else if (targetPanel === 'clients') {
                    renderAdminClients();
                } else if (targetPanel === 'providers') {
                    renderAdminProviders();
                } else if (targetPanel === 'orders') {
                    renderAdminOrders();
                } else if (targetPanel === 'reviews') {
                    renderAdminReviews();
                } else if (targetPanel === 'settings') {
                    renderAdminSettings();
                }
            });
        });
    }
}

// Handle Admin Logout - FIXED: Make global so onclick in HTML works
async function handleAdminLogout() {
    console.log('[Admin] Logging out...');

    // Clear admin session
    currentAdmin = null;
    if (typeof window.stopAdminRealTimeListeners === 'function') {
        window.stopAdminRealTimeListeners();
    }

    // CRITICAL FIX: Terminate the persisted Firebase Auth session so the
    // admin does NOT appear logged in after a page refresh.
    const doSignOut = () => {
        if (typeof window.authService !== 'undefined' && typeof window.authService.signOut === 'function') {
            return window.authService.signOut();
        }
        if (typeof hybridSignOut === 'function') {
            return hybridSignOut();
        }
        return Promise.resolve();
    };

    try {
        await window.disablePushNotificationPermission?.('admin');
        await doSignOut();
    } catch (err) {
        console.warn('[AdminLogout] Firebase signOut error:', err);
    }

    // Hide dashboard, show login (with null safety checks)
    const adminDashboard = document.getElementById('adminDashboard');
    const adminLoginSection = document.getElementById('adminLoginSection');

    if (adminDashboard) adminDashboard.classList.add('hidden');
    if (adminLoginSection) adminLoginSection.classList.remove('hidden');

    // Update header badge after logout to show correct status
    updateHeaderUserBadge();

    console.log('[Admin] Logged out successfully');
    showNotification('Logged out successfully!', 'success');
}

// Make handleAdminLogout globally accessible for onclick in HTML
window.handleAdminLogout = handleAdminLogout;

// Legacy stub superseded by async renderAdminStats above
function renderAdminStatsLegacyStub() {
    if (!isAdminLoggedIn()) return;
    const statsContainer = document.getElementById('adminStats');
    if (!statsContainer) return;

    const totalClients = clients.length;

    const totalProviders = providers.length;
    const totalMenuItems = menuItems.length;
    const totalOrders = clientMessages.length;
    const totalReviews = reviews.length;

    // Calculate completed orders
    const completedOrders = clientMessages.filter(o => o.status === 'completed').length;
    const pendingOrders = clientMessages.filter(o => o.status === 'pending').length;

    // Calculate total revenue (from completed orders)
    const totalRevenue = clientMessages
        .filter(o => o.status === 'completed')
        .reduce((sum, o) => sum + (o.price || 0), 0);

    // Calculate active providers (have menu items)
    const activeProviders = new Set(menuItems.map(m => m.providerId)).size;

    statsContainer.innerHTML = `
        <div class="stat-card">
            <div class="stat-value">${totalClients}</div>
            <div class="stat-label">Total Clients</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${totalProviders}</div>
            <div class="stat-label">Total Restaurants/Caterers</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${activeProviders}</div>
            <div class="stat-label">Active Providers</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${totalMenuItems}</div>
            <div class="stat-label">Menu Items</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${totalOrders}</div>
            <div class="stat-label">Total Orders</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${pendingOrders}</div>
            <div class="stat-label">Pending Orders</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${completedOrders}</div>
            <div class="stat-label">Completed Orders</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">$${totalRevenue.toFixed(2)}</div>
            <div class="stat-label">Total Revenue</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${totalReviews}</div>
            <div class="stat-label">Total Reviews</div>
        </div>
    `;
}

// ES module bundle compatibility: the deployed app loads as a module, so any
// legacy code or runtime callbacks that expect these functions on window must be
// explicitly exposed. This is required for the public search cards to render on
// the live Hosting build.
if (typeof renderSearchResults === 'function') {
    window.renderSearchResults = renderSearchResults;
}
if (typeof renderClientSearchResults === 'function') {
    window.renderClientSearchResults = renderClientSearchResults;
}
if (typeof initSearch === 'function') {
    window.initSearch = initSearch;
}
if (typeof initClientSearch === 'function') {
    window.initClientSearch = initClientSearch;
}

// Legacy stub superseded by async renderAdminClients above
function renderAdminClientsLegacyStub() {
    if (!isAdminLoggedIn()) return;
    const clientsList = document.getElementById('adminClientsList');
    if (!clientsList) return;

    if (clients.length === 0) {
        clientsList.innerHTML = '<div class="empty-state"><p>No clients registered yet.</p></div>';
        return;
    }

    // Sort by newest first
    const sortedClients = [...clients].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });

    clientsList.innerHTML = sortedClients.map(client => {
        const createdDate = client.createdAt ? formatDateTime(client.createdAt) : 'N/A';
        const ordersCount = clientMessages.filter(m => m.clientEmail === client.email).length;

        return `
            <div class="admin-item">
                <div class="admin-item-header">
                    <span class="admin-item-name">${escapeHtml(client.name)}</span>
                    <span class="admin-item-date">Registered: ${createdDate}</span>
                </div>
                <div class="admin-item-details">
                    <span>📧 ${escapeHtml(client.email)}</span>
                    <span>📞 ${escapeHtml(client.phone || 'N/A')}</span>
                    <span>🌍 ${escapeHtml(client.country || 'N/A')}</span>
                    <span>📦 ${ordersCount} orders</span>
                </div>
                <div class="admin-item-actions">
                    <button class="delete-btn" onclick="deleteAdminClient('${client.id || client.uid}')">Delete</button>
                </div>
            </div>
        `;
    }).join('');
}

// Legacy stub superseded by async renderAdminProviders above
function renderAdminProvidersLegacyStub() {
    if (!isAdminLoggedIn()) return;
    const providersList = document.getElementById('adminProvidersList');
    if (!providersList) return;

    if (providers.length === 0) {
        providersList.innerHTML = '<div class="empty-state"><p>No restaurants/caterers registered yet.</p></div>';
        return;
    }

    // Sort by newest first
    const sortedProviders = [...providers].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });

    providersList.innerHTML = sortedProviders.map(provider => {
        const createdDate = provider.createdAt ? formatDateTime(provider.createdAt) : 'N/A';
        const menuCount = menuItems.filter(m => m.providerId === provider.id).length;
        const planMaxItems = getPlanMaxItems(provider.subscriptionPlan || 'free');
        const ordersCount = clientMessages.filter(m => m.providerId === provider.id).length;

        // Get subscription status badge
        const subStatus = provider.subscriptionStatus || 'pending';
        let statusBadge = '';
        if (subStatus === 'pending') {
            statusBadge = '<span class="sub-status-badge pending">⏳ Pending</span>';
        } else if (subStatus === 'approved') {
            statusBadge = '<span class="sub-status-badge approved">✅ Active</span>';
        } else if (subStatus === 'rejected') {
            statusBadge = '<span class="sub-status-badge rejected">❌ Rejected</span>';
        } else if (subStatus === 'suspended') {
            statusBadge = '<span class="sub-status-badge suspended">⛔ Suspended</span>';
        }

        return `
            <div class="admin-item">
                <div class="admin-item-header">
                    <span class="admin-item-name">${escapeHtml(provider.name)}</span>
                    <span class="admin-item-type">${escapeHtml(provider.type)}</span>
                    ${statusBadge}
                </div>
                <div class="admin-item-details">
                    <span>📧 ${escapeHtml(provider.email)}</span>
                    <span>📞 ${escapeHtml(provider.phone)}</span>
                    <span>📍 ${escapeHtml(provider.address || 'N/A')}</span>
                    <span>🌍 ${escapeHtml(provider.country)}</span>
                </div>
                <div class="admin-item-stats">
                    <span>🍽️ ${menuCount} / ${planMaxItems === 999999 ? 'Unlimited' : planMaxItems} menu items</span>
                    <span>📦 ${ordersCount} orders</span>
                    <span>Joined: ${createdDate}</span>
                </div>
                <div class="admin-item-actions">
                    <button class="delete-btn" onclick="deleteAdminProvider('${provider.id || provider.uid}')">Delete</button>
                </div>
            </div>
        `;
    }).join('');
}

// ==================== SUBSCRIPTION PLANS CONFIGURATION (SUPER ADMIN) ====================
// Load subscription plans from Firestore cache or use defaults
let __subscriptionPlansCache = (window && Array.isArray(window.__subscriptionPlansCache)) ? window.__subscriptionPlansCache : null;
var subscriptionPlans = loadSubscriptionPlans();

function getDefaultSubscriptionRates() {
    const fallbackRates = { free: 0, basic: 100.50, premium: 200.00 };
    const liveRates = (typeof window !== 'undefined' && window.__subscriptionRatesCache && typeof window.__subscriptionRatesCache === 'object')
        ? window.__subscriptionRatesCache
        : {};
    const globalRates = (typeof DEFAULT_SUBSCRIPTION_RATES === 'object' && DEFAULT_SUBSCRIPTION_RATES)
        ? DEFAULT_SUBSCRIPTION_RATES
        : {};

    const basic = Number.isFinite(Number(liveRates.basic ?? globalRates.basic ?? fallbackRates.basic))
        ? Number(liveRates.basic ?? globalRates.basic ?? fallbackRates.basic)
        : fallbackRates.basic;
    const premium = Number.isFinite(Number(liveRates.premium ?? globalRates.premium ?? fallbackRates.premium))
        ? Number(liveRates.premium ?? globalRates.premium ?? fallbackRates.premium)
        : fallbackRates.premium;

    return { free: 0, basic, premium };
}

function applyLiveSubscriptionRateOverrides(planCatalog = [], rateState = {}) {
    if (!Array.isArray(planCatalog)) return planCatalog;

    const defaultRates = getDefaultSubscriptionRates();
    const sourceRates = {
        free: 0,
        basic: defaultRates.basic,
        premium: defaultRates.premium,
        ...rateState
    };

    return planCatalog.map(plan => {
        if (!plan || typeof plan !== 'object') return plan;
        const updated = { ...plan };

        if (updated.id === 'free') {
            updated.price = 0;
            return updated;
        }

        if (updated.id === 'basic' && Number.isFinite(Number(sourceRates.basic))) {
            updated.price = Number(sourceRates.basic);
        } else if (updated.id === 'premium' && Number.isFinite(Number(sourceRates.premium))) {
            updated.price = Number(sourceRates.premium);
        }

        return updated;
    });
}

function syncSubscriptionPlanGlobals() {
    const candidatePlans = Array.isArray(subscriptionPlans)
        ? subscriptionPlans
        : (Array.isArray(window.subscriptionPlans) ? window.subscriptionPlans : (Array.isArray(__subscriptionPlansCache) ? __subscriptionPlansCache : (Array.isArray(window.__subscriptionPlansCache) ? window.__subscriptionPlansCache : [])));

    if (Array.isArray(candidatePlans) && candidatePlans.length) {
        subscriptionPlans = candidatePlans.map(plan => ({ ...plan }));
    }

    if (Array.isArray(subscriptionPlans)) {
        subscriptionPlans = applyLiveSubscriptionRateOverrides(subscriptionPlans, window.__subscriptionRatesCache || {});
        __subscriptionPlansCache = subscriptionPlans.map(plan => ({ ...plan }));
        window.subscriptionPlans = subscriptionPlans;
        window.SUBSCRIPTION_PLANS = subscriptionPlans;
        window.__subscriptionPlansCache = __subscriptionPlansCache;
    }
}

syncSubscriptionPlanGlobals();

function normalizeSubscriptionPlan(plan, defaultPlans) {
    const normalized = {
        id: String(plan.id || '').trim(),
        name: String(plan.name || plan.id || '').trim() || 'Unnamed Plan',
        price: Number.isFinite(Number(plan.price)) ? Number(plan.price) : 0,
        durationDays: Number.isFinite(Number(plan.durationDays)) ? Number(plan.durationDays) : 30,
        maxItems: Number.isFinite(Number(plan.maxItems)) ? Math.max(0, Number(plan.maxItems)) : 10,
        features: Array.isArray(plan.features)
            ? plan.features.map(f => String(f).trim()).filter(Boolean)
            : typeof plan.features === 'string'
                ? plan.features.split(',').map(f => f.trim()).filter(Boolean)
                : [],
        isActive: plan.isActive !== false
    };

    if (!normalized.id) {
        return null;
    }

    if (normalized.durationDays < 1) {
        normalized.durationDays = 30;
    }

    return normalized;
}

function loadSubscriptionPlans() {
    const defaultRates = getDefaultSubscriptionRates();
    const defaultPlans = [
        { id: 'free', name: 'Free', price: 0, durationDays: 30, maxItems: 10, features: ['basic_menu', 'limited_orders'], isActive: true },
        { id: 'basic', name: 'Basic', price: Number(defaultRates.basic || 100.50), durationDays: 30, maxItems: 50, features: ['full_menu', 'unlimited_orders', 'analytics'], isActive: true },
        { id: 'premium', name: 'Premium', price: Number(defaultRates.premium || 200.00), durationDays: 30, maxItems: 999999, features: ['full_menu', 'unlimited_orders', 'analytics', 'priority_support', 'custom_branding'], isActive: true }
    ];

    const cache = (__subscriptionPlansCache && Array.isArray(__subscriptionPlansCache))
        ? __subscriptionPlansCache
        : (window && Array.isArray(window.__subscriptionPlansCache) ? window.__subscriptionPlansCache : null);

    if (cache) {
        const loaded = applyLiveSubscriptionRateOverrides(
            cache.map(plan => normalizeSubscriptionPlan(plan, defaultPlans)).filter(Boolean),
            window.__subscriptionRatesCache || {}
        );

        defaultPlans.forEach(defaultPlan => {
            if (!loaded.some(plan => plan.id === defaultPlan.id)) {
                loaded.push(applyLiveSubscriptionRateOverrides([defaultPlan], window.__subscriptionRatesCache || {})[0]);
            }
        });

        return loaded;
    }

    return applyLiveSubscriptionRateOverrides(defaultPlans, window.__subscriptionRatesCache || {});
}

async function persistSubscriptionPlans() {
    if (!isAdminLoggedIn()) {
        console.warn('[Subscription] Cannot persist plans - admin not logged in.');
        return false;
    }

    const db = await getEffectiveFirestoreDb();
    if (!db) {
        console.warn('[Subscription] Firestore not available, cannot persist subscription plans');
        return false;
    }

    try {
        const savedPlans = subscriptionPlans.map(plan => ({
            id: plan.id,
            name: plan.name,
            price: plan.price,
            durationDays: plan.durationDays,
            maxItems: plan.maxItems,
            features: Array.isArray(plan.features) ? plan.features : [],
            isActive: plan.isActive
        }));
        const planById = new Map(savedPlans.map(plan => [plan.id, plan]));
        const rates = loadSubscriptionRates();
        const basicRate = planById.has('basic') ? planById.get('basic').price : rates.basic;
        const premiumRate = planById.has('premium') ? planById.get('premium').price : rates.premium;
        await db.collection('siteSettings').doc('main').set({
            subscriptionPlans: savedPlans,
            basicRate,
            premiumRate
        }, { merge: true });
        window.__subscriptionRatesCache = { ...(window.__subscriptionRatesCache || {}), free: 0, basic: basicRate, premium: premiumRate };
        __subscriptionPlansCache = subscriptionPlans.map(plan => ({ ...plan }));
        window.__subscriptionPlansCache = __subscriptionPlansCache;
        return true;
    } catch (e) {
        console.error('[Subscription] Could not persist subscription plans to Firestore:', e);
        return false;
    }
}

function saveSubscriptionPlans() {
    syncSubscriptionPlanGlobals();
    const promise = persistSubscriptionPlans();

    if (window.stripeConfig && window.stripeConfig.plans) {
        subscriptionPlans.forEach(plan => {
            if (window.stripeConfig.plans[plan.id]) {
                window.stripeConfig.plans[plan.id].price = plan.price;
            }
        });
    }

    syncSubscriptionPlanGlobals();
    updateProviderPlanDropdown();
    renderSubscriptionPlanFilter();
    return promise;
}

window.__wtE_baseSaveSubscriptionPlans = saveSubscriptionPlans;
window.saveSubscriptionPlans = saveSubscriptionPlans;
window.persistSubscriptionPlans = persistSubscriptionPlans;
window.__loadSubscriptionPlansFromFirestore = __loadSubscriptionPlansFromFirestore;

async function __loadSubscriptionPlansFromFirestore() {
    const db = await getEffectiveFirestoreDb();
    if (!db) {
        console.log('[Subscription] Firestore not ready yet, retrying subscription plans load in 500ms');
        setTimeout(() => {
            if (firebaseDbInstance || window.firebaseDbInstance || (window.firestoreService && window.firestoreService.db)) {
                __loadSubscriptionPlansFromFirestore();
            }
        }, 500);
        return;
    }

    try {
        const doc = await db.collection('siteSettings').doc('main').get();
        if (!doc.exists) {
            console.log('[Subscription] No siteSettings/main doc found, keeping default plans');
            return;
        }

        const data = doc.data() || {};
        const defaultRates = getDefaultSubscriptionRates();
        const savedPlans = Array.isArray(data.subscriptionPlans) ? data.subscriptionPlans : null;
        const savedPlanRate = (planId, legacyRate, fallbackRate) => {
            const savedPlan = savedPlans && savedPlans.find(plan => plan && plan.id === planId);
            const planRate = savedPlan ? Number(savedPlan.price) : NaN;
            if (Number.isFinite(planRate)) return planRate;
            return Number.isFinite(Number(legacyRate)) ? Number(legacyRate) : fallbackRate;
        };
        const liveRates = {
            free: 0,
            basic: savedPlanRate('basic', data.basicRate, defaultRates.basic),
            premium: savedPlanRate('premium', data.premiumRate, defaultRates.premium)
        };
        window.__subscriptionRatesCache = { ...(window.__subscriptionRatesCache || {}), ...liveRates };

        if (savedPlans) {
            const loaded = applyLiveSubscriptionRateOverrides(
                savedPlans.map(plan => normalizeSubscriptionPlan(plan)).filter(Boolean),
                liveRates
            );

            const defaultPlans = [
                { id: 'free', name: 'Free', price: 0, durationDays: 30, maxItems: 10, features: ['basic_menu', 'limited_orders'], isActive: true },
                { id: 'basic', name: 'Basic', price: Number(liveRates.basic || getDefaultSubscriptionRates().basic), durationDays: 30, maxItems: 50, features: ['full_menu', 'unlimited_orders', 'analytics'], isActive: true },
                { id: 'premium', name: 'Premium', price: Number(liveRates.premium || getDefaultSubscriptionRates().premium), durationDays: 30, maxItems: 999999, features: ['full_menu', 'unlimited_orders', 'analytics', 'priority_support', 'custom_branding'], isActive: true }
            ];

            defaultPlans.forEach(defaultPlan => {
                if (!loaded.some(plan => plan.id === defaultPlan.id)) {
                    loaded.push(applyLiveSubscriptionRateOverrides([defaultPlan], liveRates)[0]);
                }
            });

            __subscriptionPlansCache = loaded;
            subscriptionPlans = loaded;
            syncSubscriptionPlanGlobals();
            updateProviderPlanDropdown();
            renderSubscriptionPlans();
            renderSubscriptionPlanFilter();
            console.log('[Subscription] Loaded subscription plans from Firestore', loaded);
        } else if (Number.isFinite(Number(data.basicRate)) || Number.isFinite(Number(data.premiumRate))) {
            const basePlans = Array.isArray(subscriptionPlans) && subscriptionPlans.length ? subscriptionPlans : loadSubscriptionPlans();
            subscriptionPlans = applyLiveSubscriptionRateOverrides(basePlans, liveRates);
            __subscriptionPlansCache = subscriptionPlans.map(plan => ({ ...plan }));
            window.__subscriptionPlansCache = __subscriptionPlansCache;
            syncSubscriptionPlanGlobals();
            updateProviderPlanDropdown();
        }
    } catch (e) {
        console.warn('[Subscription] Failed to load subscription plans from Firestore:', e);
    }
}

function getPlanById(planId) {
    const plans = Array.isArray(subscriptionPlans)
        ? subscriptionPlans
        : (Array.isArray(window.subscriptionPlans) ? window.subscriptionPlans : (Array.isArray(window.__subscriptionPlansCache) ? window.__subscriptionPlansCache : []));

    if (!Array.isArray(plans) || plans.length === 0) {
        return null;
    }

    if (!planId) {
        return plans.find(p => p.id === 'free') || plans[0] || null;
    }
    const plan = plans.find(p => p.id === planId);
    if (plan) return plan;
    return plans.find(p => p.id === 'free') || plans[0] || null;
}

function getPlanMaxItems(planId) {
    const plan = getPlanById(planId);
    if (!plan) return 10;
    const maxItems = Number.isFinite(Number(plan.maxItems)) ? Number(plan.maxItems) : 10;
    return maxItems;
}

function renderSubscriptionPlanFilter() {
    const planFilter = document.getElementById('subscriptionPlanFilter');
    if (!planFilter) return;

    const options = ['<option value="all"' + (currentPlanFilter === 'all' ? ' selected' : '') + '>All Plans</option>'];
    subscriptionPlans.forEach(plan => {
        options.push(`<option value="${escapeHtml(plan.id)}"${currentPlanFilter === plan.id ? ' selected' : ''}>${escapeHtml(plan.name)}</option>`);
    });

    planFilter.innerHTML = options.join('');
    if (currentPlanFilter && subscriptionPlans.some(p => p.id === currentPlanFilter)) {
        planFilter.value = currentPlanFilter;
    } else {
        planFilter.value = 'all';
        currentPlanFilter = 'all';
    }
}

// Show Add/Edit Subscription Plan Form
function showAddSubscriptionPlanForm(planId = null) {
    const form = document.getElementById('addSubscriptionPlanForm');
    const title = document.getElementById('planFormTitle');
    const editingIdInput = document.getElementById('editingPlanId');

    if (!form) return;

    // If editing, populate form
    if (planId) {
        const plan = subscriptionPlans.find(p => p.id === planId);
        if (plan) {
            title.textContent = 'Edit Subscription Plan';
            editingIdInput.value = plan.id;
            document.getElementById('planName').value = plan.name;
            document.getElementById('planPrice').value = plan.price;
            document.getElementById('planDuration').value = 30;
            document.getElementById('planMaxItems').value = plan.maxItems;
            document.getElementById('planFeatures').value = plan.features ? plan.features.join(', ') : '';
            document.getElementById('planIsActive').checked = plan.isActive !== false;
        }
    } else {
        // New plan - reset form
        title.textContent = 'Add Subscription Plan';
        editingIdInput.value = '';
        document.getElementById('planName').value = '';
        document.getElementById('planPrice').value = '';
        document.getElementById('planDuration').value = '30';
        document.getElementById('planMaxItems').value = '10';
        document.getElementById('planFeatures').value = '';
        document.getElementById('planIsActive').checked = true;
    }

    form.classList.remove('hidden');
}

// Hide Add/Edit Subscription Plan Form
function hideAddSubscriptionPlanForm() {
    const form = document.getElementById('addSubscriptionPlanForm');
    if (form) {
        form.classList.add('hidden');
    }

    // Reset form
    const formEl = document.getElementById('subscriptionPlanForm');
    if (formEl) formEl.reset();
}

// Render Subscription Plans List
function renderSubscriptionPlans() {
    const container = document.getElementById('subscriptionPlansList');
    if (!container) return;
    const plans = (typeof subscriptionPlans !== 'undefined' && Array.isArray(subscriptionPlans)) ? subscriptionPlans : (window.subscriptionPlans || []);
    if (plans.length === 0) {
        container.innerHTML = '<div class="empty-state"><p>No subscription plans configured yet.</p></div>';
        return;
    }
    container.innerHTML = plans.map(plan => {
        const activeClass = plan.isActive ? 'active-plan' : 'inactive-plan';
        const activeBadge = plan.isActive ? '<span class="plan-badge active">✓ Active</span>' : '<span class="plan-badge inactive">✗ Inactive</span>';

        return `
            <div class="subscription-plan-card ${activeClass}">
                <div class="plan-header">
                    <span class="plan-name">${escapeHtml(plan.name)}</span>
                    <span class="plan-price">$${parseFloat(plan.price).toFixed(2)}/month</span>
                    ${activeBadge}
                </div>
                <div class="plan-details">
                    <span>Duration: ${plan.durationDays} days</span>
                    <span>Max Items: ${plan.maxItems === 999999 ? 'Unlimited' : plan.maxItems}</span>
                </div>
                <div class="plan-features">
                    <strong>Features:</strong> ${plan.features ? plan.features.join(', ') : 'None'}
                </div>
                <div class="plan-actions">
                    <button class="edit-btn" onclick="showAddSubscriptionPlanForm('${plan.id}')">Edit</button>
                    <button class="delete-btn" onclick="deleteSubscriptionPlan('${plan.id}')">Delete</button>
                </div>
            </div>
        `;
    }).join('');
    renderSubscriptionPlanFilter();
}

// Save Subscription Plan
async function saveSubscriptionPlan(e) {
    if (e) e.preventDefault();

    const editingId = document.getElementById('editingPlanId').value;
    const name = document.getElementById('planName').value.trim();
    const price = parseFloat(document.getElementById('planPrice').value) || 0;
    const durationDays = 30; // Enforce monthly renewal for all plans
    const maxItems = parseInt(document.getElementById('planMaxItems').value) || 10;
    const featuresStr = document.getElementById('planFeatures').value.trim();
    const features = featuresStr ? featuresStr.split(',').map(f => f.trim()).filter(f => f) : [];
    const isActive = document.getElementById('planIsActive').checked;
    const previousPlans = subscriptionPlans.map(plan => ({ ...plan }));
    const previousRates = { ...(window.__subscriptionRatesCache || {}) };

    if (!name) {
        showNotification('Plan name is required', 'error');
        return;
    }

    // Generate ID if new plan
    // Use UUID for collision-resistant IDs (Date.now() can collide for same-name plans)
    const id = editingId || (name.toLowerCase().replace(/\s+/g, '_') + '_' + window.generateUUID());

    if (editingId) {
        // Update existing plan
        const planIndex = subscriptionPlans.findIndex(p => p.id === editingId);
        if (planIndex !== -1) {
            subscriptionPlans[planIndex] = { id, name, price, durationDays, maxItems, features, isActive };
        }
    } else {
        // Add new plan
        subscriptionPlans.push({ id, name, price, durationDays, maxItems, features, isActive });
    }

    if (id === 'basic' || id === 'premium') {
        window.__subscriptionRatesCache = { ...previousRates, [id]: price };
    }
    syncSubscriptionPlanGlobals();
    const saved = await saveSubscriptionPlans();
    if (!saved) {
        subscriptionPlans = previousPlans;
        window.__subscriptionRatesCache = previousRates;
        syncSubscriptionPlanGlobals();
        if (window.stripeConfig && window.stripeConfig.plans) {
            previousPlans.forEach(plan => {
                if (window.stripeConfig.plans[plan.id]) {
                    window.stripeConfig.plans[plan.id].price = plan.price;
                }
            });
        }
        renderSubscriptionPlans();
        showNotification('Could not save the subscription plan. Check the Firestore connection and try again.', 'error');
        return;
    }

    renderSubscriptionPlans();
    hideAddSubscriptionPlanForm();

    showNotification(editingId ? 'Plan updated successfully!' : 'Plan created successfully!', 'success');

    // Update provider registration dropdown
    updateProviderPlanDropdown();
}

// Delete Subscription Plan
function deleteSubscriptionPlan(planId) {
    const plan = subscriptionPlans.find(p => p.id === planId);
    if (!plan) return;

    const assignedProviders = providers.filter(p => p.subscriptionPlan === planId).length;
    if (assignedProviders > 0) {
        if (!confirm(`This plan is currently assigned to ${assignedProviders} provider(s). Deleting it will keep existing subscriptions active but remove it from new registrations. Continue?`)) return;
        plan.isActive = false;
    } else {
        if (!confirm('Are you sure you want to delete this subscription plan?')) return;
        subscriptionPlans = subscriptionPlans.filter(p => p.id !== planId);
    }

    syncSubscriptionPlanGlobals();
    saveSubscriptionPlans();
    renderSubscriptionPlans();

    showNotification(assignedProviders > 0 ? 'Plan removed from new registrations and marked inactive.' : 'Plan deleted successfully!', assignedProviders > 0 ? 'success' : 'success');

    // Update provider registration dropdown
    updateProviderPlanDropdown();
}

// Update Provider Registration Plan Dropdown
function updateProviderPlanDropdown() {
    const dropdown = document.getElementById('providerPlan');
    if (!dropdown) return;

    const plans = Array.isArray(subscriptionPlans)
        ? subscriptionPlans
        : (Array.isArray(window.subscriptionPlans) ? window.subscriptionPlans : (Array.isArray(window.__subscriptionPlansCache) ? window.__subscriptionPlansCache : (Array.isArray(window.SUBSCRIPTION_PLANS) ? window.SUBSCRIPTION_PLANS : [])));

    if (Array.isArray(plans) && plans.length) {
        subscriptionPlans = plans.map(plan => ({ ...plan }));
        syncSubscriptionPlanGlobals();
    }

    const activePlans = Array.isArray(subscriptionPlans)
        ? subscriptionPlans.filter(p => p && p.isActive !== false)
        : [];
    const defaultRates = getDefaultSubscriptionRates();
    const fallbackPlans = [
        { id: 'free', name: 'Free', price: 0, durationDays: 30, maxItems: 10, isActive: true },
        { id: 'basic', name: 'Basic', price: Number(defaultRates.basic || 100.50), durationDays: 30, maxItems: 50, isActive: true },
        { id: 'premium', name: 'Premium', price: Number(defaultRates.premium || 200.00), durationDays: 30, maxItems: 999999, isActive: true }
    ];
    const plansToRender = activePlans.length ? activePlans : fallbackPlans;
    const previousValue = dropdown.value || '';

    dropdown.innerHTML = plansToRender.map(plan => {
        const priceDisplay = Number(plan.price) > 0 ? `$${Number(plan.price).toFixed(2)}/month` : '$0/month';
        const durationDisplay = `(${Number(plan.durationDays || 30)} days, up to ${Number(plan.maxItems || 0) === 999999 ? 'unlimited' : Number(plan.maxItems || 0)} menu items)`;
        const displayName = plan.name || plan.id || 'Plan';
        return `<option value="${plan.id}">${displayName} Plan - ${priceDisplay} ${durationDisplay}</option>`;
    }).join('');

    const normalizedPrevious = previousValue && plansToRender.some(plan => plan.id === previousValue) ? previousValue : (plansToRender.find(plan => plan.id === 'free') ? 'free' : plansToRender[0]?.id || '');
    if (normalizedPrevious) {
        dropdown.value = normalizedPrevious;
    } else {
        dropdown.selectedIndex = 0;
    }
}

// Make functions global
window.showAddSubscriptionPlanForm = showAddSubscriptionPlanForm;
window.hideAddSubscriptionPlanForm = hideAddSubscriptionPlanForm;
window.saveSubscriptionPlan = saveSubscriptionPlan;
window.deleteSubscriptionPlan = deleteSubscriptionPlan;
window.updateProviderPlanDropdown = updateProviderPlanDropdown;
window.applyPlanFilter = applyPlanFilter;

// Initialize Subscription Plan Form
function initSubscriptionPlanForm() {
    const form = document.getElementById('subscriptionPlanForm');
    if (form) {
        form.addEventListener('submit', saveSubscriptionPlan);
    }

    // Render plans on page load if admin panel exists
    if (document.getElementById('subscriptionPlansList')) {
        renderSubscriptionPlans();
    }

    // Update provider dropdown
    updateProviderPlanDropdown();
}

// ==================== SUBSCRIPTIONS MANAGEMENT (SUPER ADMIN) ====================
// Current subscription plan filter (for maintaining state)
let currentPlanFilter = 'all';

// Current order status filter (for maintaining state in Super Admin dashboard)
let currentOrderStatusFilter = 'all';

// Apply Order Status Filter function (called from dropdown)
function applyOrderStatusFilter(status) {
    console.log('[AdminOrders] applyOrderStatusFilter called with:', status);
    currentOrderStatusFilter = status;

    // Re-render the orders list
    renderAdminOrders();
}

// Legacy stub superseded by async renderAdminSubscriptions above
function renderAdminSubscriptionsLegacyStub() {
    console.log('[AdminSubscriptions] renderAdminSubscriptions called');
    console.log('[AdminSubscriptions] currentAdmin:', currentAdmin);
    console.log('[AdminSubscriptions] isAdminLoggedIn():', isAdminLoggedIn());

    if (!isAdminLoggedIn()) {
        console.log('[AdminSubscriptions] Not logged in, returning');
        return;
    }

    const subsContainer = document.getElementById('adminProviderSubscriptionsList')
        || document.getElementById('adminSubsList')
        || document.getElementById('admin-subscriptions-panel');
    if (!subsContainer) {
        console.log('[AdminSubscriptions] No subscription container found!');
        return;
    }

    renderSubscriptionPlanFilter();

    console.log('[AdminSubscriptions] Container found, providers array length:', providers.length);

    if (providers.length === 0) {
        subsContainer.innerHTML = '<div class="empty-state"><p>No restaurants/caterers registered yet.</p></div>';
        return;
    }

    // No need to inject a filter if the static control already exists in the layout.
    const existingFilter = document.getElementById('subscriptionPlanFilter');
    if (!existingFilter) {
        subsContainer.insertAdjacentHTML('beforebegin', `
            <div class="subscription-filter-bar" style="margin-bottom: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px;">
                <label style="font-weight: bold; margin-right: 10px;">Filter by Plan:</label>
                <select id="subscriptionPlanFilter" onchange="applyPlanFilter(this.value)" style="padding: 8px; border-radius: 4px; border: 1px solid #ddd;">
                    <option value="all" ${currentPlanFilter === 'all' ? 'selected' : ''}>All Plans</option>
                </select>
            </div>
        `);
        renderSubscriptionPlanFilter();
    }

    // Apply plan filter if set
    let filteredProviders = [...providers];
    if (currentPlanFilter && currentPlanFilter !== 'all') {
        filteredProviders = providers.filter(p => (p.subscriptionPlan || 'free') === currentPlanFilter);
    }

    // Sort: pending first, then by date
    const sortedProviders = [...providers].sort((a, b) => {
        const statusOrder = { pending: 0, approved: 1, suspended: 2, rejected: 3 };
        const statusA = statusOrder[a.subscriptionStatus] ?? 4;
        const statusB = statusOrder[b.subscriptionStatus] ?? 4;
        if (statusA !== statusB) return statusA - statusB;
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });

    subsContainer.innerHTML = sortedProviders.map(provider => {
        const createdDate = provider.createdAt ? formatDateTime(provider.createdAt) : 'N/A';
        const subStatus = provider.subscriptionStatus || 'pending';

        // Status badge
        let statusBadge = '';
        let statusClass = '';
        if (subStatus === 'pending') {
            statusBadge = '⏳ Pending Approval';
            statusClass = 'pending';
        } else if (subStatus === 'approved') {
            const endDate = provider.subscriptionEnd ? new Date(provider.subscriptionEnd) : null;
            const now = new Date();
            if (endDate && endDate < now) {
                statusBadge = '⚠️ Expired';
                statusClass = 'expired';
            } else {
                statusBadge = '✅ Active';
                statusClass = 'approved';
            }
        } else if (subStatus === 'rejected') {
            statusBadge = '❌ Rejected';
            statusClass = 'rejected';
        } else if (subStatus === 'suspended') {
            statusBadge = '⛔ Suspended';
            statusClass = 'suspended';
        }

// ALWAYS show the Plan that the restaurant/caterer signed up with (regardless of status)
        const selectedPlan = provider.subscriptionPlan || 'free';
        const planInfo = getPlanById(selectedPlan);
        const planMaxItems = getPlanMaxItems(selectedPlan);
        const planLabel = planInfo ? planInfo.name : selectedPlan;
        const planMaxItemsLabel = planMaxItems === 999999 ? 'Unlimited' : planMaxItems;

        // Subscription details - always include the plan, then add status-specific info
        let subDetails = '';
        if (subStatus === 'pending') {
            subDetails = `
                <div class="sub-details">
                    <span><strong>Selected Plan: ${planLabel}</strong></span>
                    <span>Max Items: ${planMaxItemsLabel}</span>
                </div>
            `;
        } else if (subStatus === 'approved') {
            const startDate = provider.subscriptionStart ? formatDateTime(provider.subscriptionStart) : 'N/A';
            const endDate = provider.subscriptionEnd ? formatDateTime(provider.subscriptionEnd) : 'N/A';
            subDetails = `
                <div class="sub-details">
                    <span><strong>Plan: ${planLabel}</strong></span>
                    <span>Max Items: ${planMaxItemsLabel}</span>
                    <span>Start: ${startDate}</span>
                    <span>End: ${endDate}</span>
                    <span>Approved by: ${provider.subscriptionApprovedBy || 'N/A'}</span>
                </div>
            `;
        } else if (subStatus === 'rejected') {
            subDetails = `
                <div class="sub-details">
                    <span><strong>Selected Plan: ${planLabel}</strong></span>
                    <span>Max Items: ${planMaxItemsLabel}</span>
                </div>
                <div class="sub-details rejection">Reason: ${provider.subscriptionRejectionReason || 'No reason'}</div>
            `;
        } else if (subStatus === 'suspended') {
            subDetails = `
                <div class="sub-details">
                    <span><strong>Plan: ${planLabel}</strong></span>
                    <span>Max Items: ${planMaxItemsLabel}</span>
                </div>
                <div class="sub-details suspension">Reason: ${provider.subscriptionSuspendedReason || 'No reason'}</div>
            `;
        }

// Action buttons based on status
        let actionButtons = '';
        if (subStatus === 'pending') {
            actionButtons = `
                <button class="approve-btn" onclick="approveProvider('${provider.id || provider.uid}')">Approve</button>
                <button class="reject-btn" onclick="rejectProvider('${provider.id || provider.uid}')">Reject</button>
            `;
        } else if (subStatus === 'approved') {
            actionButtons = `
                <button class="suspend-btn" onclick="suspendProvider('${provider.id || provider.uid}')">Suspend</button>
                <button class="renew-btn" onclick="renewProvider('${provider.id || provider.uid}')">Renew</button>
                <button class="change-plan-btn" onclick="showChangePlanForm('${provider.id || provider.uid}')">Change Plan</button>
            `;
        } else if (subStatus === 'suspended') {
            actionButtons = `
                <button class="approve-btn" onclick="activateProvider('${provider.id || provider.uid}')">Activate</button>
                <button class="reject-btn" onclick="rejectProvider('${provider.id || provider.uid}')">Reject</button>
            `;
        } else if (subStatus === 'rejected') {
            actionButtons = `
                <button class="approve-btn" onclick="approveProvider('${provider.id || provider.uid}')">Approve</button>
            `;
        }

        return `
            <div class="admin-item subscription-item">
                <div class="admin-item-header">
                    <span class="admin-item-name">${escapeHtml(provider.name)}</span>
                    <span class="sub-status-badge ${statusClass}">${statusBadge}</span>
                </div>
                <div class="admin-item-details">
                    <span>📧 ${escapeHtml(provider.email)}</span>
                    <span>📞 ${escapeHtml(provider.phone)}</span>
                    <span>🌍 ${escapeHtml(provider.country)}</span>
                    <span>📅 Registered: ${createdDate}</span>
                </div>
                ${subDetails}
                <div class="admin-item-actions">
                    ${actionButtons}
                </div>
            </div>
        `;
    }).join('');
}

// Apply Plan Filter for Subscriptions
function applyPlanFilter(planType) {
    console.log('[AdminSubscriptions] applyPlanFilter called with:', planType);
    currentPlanFilter = planType;

    // Re-render the subscriptions list
    renderAdminSubscriptions();
}

// Approve Provider via server-side validation
async function approveProvider(providerId) {
    const provider = providers.find(p => p.id === providerId);
    if (!provider) return;

    try {
        const result = await callProtectedFunction('approveProvider', {
            providerId,
            providerUid: provider.uid || provider.id,
            plan: provider.subscriptionPlan || 'free'
        });

        if (result.provider) {
            Object.assign(provider, result.provider);
        }
        provider.subscriptionStatus = 'approved';
        provider.subscriptionApprovedBy = currentAdmin ? currentAdmin.username : 'superadmin';
        provider.subscriptionApprovedAt = new Date().toISOString();
        showNotification(`${provider.name} has been approved!`, 'success');
        renderAdminSubscriptions();
    } catch (error) {
        console.error('[approveProvider] Server action failed:', error);
        showNotification(error.message || 'Provider approval failed.', 'error');
    }
}

// Reject Provider via server-side validation
async function rejectProvider(providerId) {
    const reason = prompt('Enter rejection reason (optional):');
    if (reason === null) return;

    const provider = providers.find(p => p.id === providerId);
    if (!provider) return;

    try {
        const result = await callProtectedFunction('rejectProvider', {
            providerId,
            providerUid: provider.uid || provider.id,
            reason: reason || 'No reason provided'
        });

        if (result.provider) {
            Object.assign(provider, result.provider);
        }
        provider.subscriptionStatus = 'rejected';
        provider.subscriptionRejectionReason = reason || 'No reason provided';
        showNotification(`${provider.name} has been rejected.`, 'success');
        renderAdminSubscriptions();
    } catch (error) {
        console.error('[rejectProvider] Server action failed:', error);
        showNotification(error.message || 'Provider rejection failed.', 'error');
    }
}

// Suspend Provider via server-side validation
async function suspendProvider(providerId) {
    const reason = prompt('Enter suspension reason:');
    if (reason === null) return;

    const provider = providers.find(p => p.id === providerId);
    if (!provider) return;

    try {
        const result = await callProtectedFunction('changeProviderSubscription', {
            providerId,
            providerUid: provider.uid || provider.id,
            action: 'suspend',
            reason: reason || 'No reason provided'
        });
        if (result.provider) Object.assign(provider, result.provider);
        provider.subscriptionStatus = 'suspended';
        provider.subscriptionSuspendedReason = reason || 'No reason provided';
        showNotification(`${provider.name} has been suspended.`, 'success');
        renderAdminSubscriptions();
    } catch (error) {
        console.error('[suspendProvider] Server action failed:', error);
        showNotification(error.message || 'Provider suspension failed.', 'error');
    }
}

// Activate Provider (from suspended) via server-side validation
async function activateProvider(providerId) {
    const provider = providers.find(p => p.id === providerId);
    if (!provider) return;

    try {
        const result = await callProtectedFunction('changeProviderSubscription', {
            providerId,
            providerUid: provider.uid || provider.id,
            action: 'activate'
        });
        if (result.provider) Object.assign(provider, result.provider);
        provider.subscriptionStatus = 'approved';
        provider.subscriptionSuspendedReason = '';
        showNotification(`${provider.name} has been reactivated!`, 'success');
        renderAdminSubscriptions();
    } catch (error) {
        console.error('[activateProvider] Server action failed:', error);
        showNotification(error.message || 'Provider activation failed.', 'error');
    }
}

// Renew Provider Subscription via server-side validation
async function renewProvider(providerId) {
    const provider = providers.find(p => p.id === providerId);
    if (!provider) return;

    try {
        const result = await callProtectedFunction('changeProviderSubscription', {
            providerId,
            providerUid: provider.uid || provider.id,
            action: 'renew'
        });
        if (result.provider) Object.assign(provider, result.provider);
        provider.subscriptionStatus = 'approved';
        provider.subscriptionSuspendedReason = '';
        showNotification(`${provider.name}'s subscription has been renewed!`, 'success');
        renderAdminSubscriptions();
    } catch (error) {
        console.error('[renewProvider] Server action failed:', error);
        showNotification(error.message || 'Provider renewal failed.', 'error');
    }
}

// Make subscription functions global
window.approveProvider = approveProvider;
window.rejectProvider = rejectProvider;
window.suspendProvider = suspendProvider;
window.activateProvider = activateProvider;
window.renewProvider = renewProvider;
window.showChangePlanForm = showChangePlanForm;

// Show Change Plan Form for Super Admin to change a provider's plan
async function showChangePlanForm(providerId) {
    const provider = providers.find(p => p.id === providerId);
    if (!provider) return;

    const currentPlan = provider.subscriptionPlan || 'free';
    const planOptions = subscriptionPlans.map(p => `${p.id} (${p.name})`).join(', ');
    const newPlan = prompt(`Change plan for ${provider.name}\nCurrent plan: ${currentPlan.toUpperCase()}\n\nEnter new plan from the following options:\n${planOptions}`, currentPlan);

    if (!newPlan) return;

    const selectedPlan = newPlan.toLowerCase().trim();
    const validPlans = subscriptionPlans.map(p => p.id);

    // Validate plan
    if (!validPlans.includes(selectedPlan)) {
        showNotification(`Invalid plan. Please enter one of: ${validPlans.join(', ')}`, 'error');
        return;
    }

    // Get plan details
    const planInfo = getPlanById(selectedPlan);
    if (!planInfo) {
        showNotification('Plan not found', 'error');
        return;
    }

    try {
        const result = await callProtectedFunction('changeProviderSubscription', {
            providerId,
            providerUid: provider.uid || provider.id,
            action: 'changePlan',
            plan: selectedPlan
        });
        if (result.provider) Object.assign(provider, result.provider);
        provider.subscriptionPlan = selectedPlan;
        if (provider.subscriptionStatus === 'approved') {
            provider.subscriptionStatus = 'approved';
        }
        showNotification(`${provider.name}'s plan changed to ${selectedPlan.toUpperCase()}!`, 'success');
        renderAdminSubscriptions();
    } catch (error) {
        console.error('[showChangePlanForm] Server action failed:', error);
        showNotification(error.message || 'Plan update failed.', 'error');
    }
}

// Filter Admin Subscriptions by Plan
function filterAdminSubscriptionsByPlan(planFilter) {
    const subsContainer = document.getElementById('adminSubsList');
    if (!subsContainer) return;

    if (providers.length === 0) {
        subsContainer.innerHTML = '<div class="empty-state"><p>No restaurants/caterers registered yet.</p></div>';
        return;
    }

    let filteredProviders = [...providers];

    // Apply plan filter
    if (planFilter && planFilter !== 'all') {
        filteredProviders = providers.filter(p => (p.subscriptionPlan || 'free') === planFilter);
    }

    // Sort: pending first, then by date
    filteredProviders.sort((a, b) => {
        const statusOrder = { pending: 0, approved: 1, suspended: 2, rejected: 3 };
        const statusA = statusOrder[a.subscriptionStatus] ?? 4;
        const statusB = statusOrder[b.subscriptionStatus] ?? 4;
        if (statusA !== statusB) return statusA - statusB;
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });

    subsContainer.innerHTML = filteredProviders.map(provider => {
        const createdDate = provider.createdAt ? formatDateTime(provider.createdAt) : 'N/A';
        const subStatus = provider.subscriptionStatus || 'pending';

        // Status badge
        let statusBadge = '';
        let statusClass = '';
        if (subStatus === 'pending') {
            statusBadge = '⏳ Pending Approval';
            statusClass = 'pending';
        } else if (subStatus === 'approved') {
            const endDate = provider.subscriptionEnd ? new Date(provider.subscriptionEnd) : null;
            const now = new Date();
            if (endDate && endDate < now) {
                statusBadge = '⚠️ Expired';
                statusClass = 'expired';
            } else {
                statusBadge = '✅ Active';
                statusClass = 'approved';
            }
        } else if (subStatus === 'rejected') {
            statusBadge = '❌ Rejected';
            statusClass = 'rejected';
        } else if (subStatus === 'suspended') {
            statusBadge = '⛔ Suspended';
            statusClass = 'suspended';
        }

        const selectedPlan = provider.subscriptionPlan || 'free';

        let subDetails = '';
        if (subStatus === 'pending') {
            subDetails = `
                <div class="sub-details">
                    <span><strong>Selected Plan: ${selectedPlan.toUpperCase()}</strong></span>
                </div>
            `;
        } else if (subStatus === 'approved') {
            const startDate = provider.subscriptionStart ? formatDateTime(provider.subscriptionStart) : 'N/A';
            const endDate = provider.subscriptionEnd ? formatDateTime(provider.subscriptionEnd) : 'N/A';
            subDetails = `
                <div class="sub-details">
                    <span><strong>Plan: ${selectedPlan.toUpperCase()}</strong></span>
                    <span>Start: ${startDate}</span>
                    <span>End: ${endDate}</span>
                    <span>Approved by: ${provider.subscriptionApprovedBy || 'N/A'}</span>
                </div>
            `;
        } else if (subStatus === 'rejected') {
            subDetails = `
                <div class="sub-details">
                    <span><strong>Selected Plan: ${selectedPlan.toUpperCase()}</strong></span>
                </div>
                <div class="sub-details rejection">Reason: ${provider.subscriptionRejectionReason || 'No reason'}</div>
            `;
        } else if (subStatus === 'suspended') {
            subDetails = `
                <div class="sub-details">
                    <span><strong>Plan: ${selectedPlan.toUpperCase()}</strong></span>
                </div>
                <div class="sub-details suspension">Reason: ${provider.subscriptionSuspendedReason || 'No reason'}</div>
            `;
        }

        let actionButtons = '';
        if (subStatus === 'pending') {
            actionButtons = `
                <button class="approve-btn" onclick="approveProvider('${provider.id || provider.uid}')">Approve</button>
                <button class="reject-btn" onclick="rejectProvider('${provider.id || provider.uid}')">Reject</button>
            `;
        } else if (subStatus === 'approved') {
            actionButtons = `
                <button class="suspend-btn" onclick="suspendProvider('${provider.id || provider.uid}')">Suspend</button>
                <button class="renew-btn" onclick="renewProvider('${provider.id || provider.uid}')">Renew</button>
                <button class="change-plan-btn" onclick="showChangePlanForm('${provider.id || provider.uid}')">Change Plan</button>
            `;
        } else if (subStatus === 'suspended') {
            actionButtons = `
                <button class="approve-btn" onclick="activateProvider('${provider.id || provider.uid}')">Activate</button>
                <button class="reject-btn" onclick="rejectProvider('${provider.id || provider.uid}')">Reject</button>
            `;
        } else if (subStatus === 'rejected') {
            actionButtons = `
                <button class="approve-btn" onclick="approveProvider('${provider.id || provider.uid}')">Approve</button>
            `;
        }

        return `
            <div class="admin-item subscription-item">
                <div class="admin-item-header">
                    <span class="admin-item-name">${escapeHtml(provider.name)}</span>
                    <span class="sub-status-badge ${statusClass}">${statusBadge}</span>
                </div>
                <div class="admin-item-details">
                    <span>📧 ${escapeHtml(provider.email)}</span>
                    <span>📞 ${escapeHtml(provider.phone)}</span>
                    <span>🌍 ${escapeHtml(provider.country)}</span>
                    <span>📅 Registered: ${createdDate}</span>
                </div>
                ${subDetails}
                <div class="admin-item-actions">
                    ${actionButtons}
                </div>
            </div>
        `;
    }).join('');
}

// Initialize plan filter for subscriptions
function initPlanFilter() {
    const planFilter = document.getElementById('subscriptionPlanFilter');
    if (!planFilter) return;

    // Restore previous filter selection
    if (currentPlanFilter && currentPlanFilter !== 'all') {
        planFilter.value = currentPlanFilter;
    }

    planFilter.addEventListener('change', function() {
        currentPlanFilter = this.value;
        filterAdminSubscriptionsByPlan(this.value);
    });
}

// Make filter function global
window.filterAdminSubscriptionsByPlan = filterAdminSubscriptionsByPlan;

// Render Admin Subscriptions Panel with Filter (Combined Function)
// This replaces both renderAdminSubscriptions AND filterAdminSubscriptionsByPlan
// to properly handle the filter UI state
function renderAdminSubscriptionsWithFilter() {
    if (!isAdminLoggedIn()) return;
    const subsContainer = document.getElementById('adminSubsList');
    if (!subsContainer) return;

    if (providers.length === 0) {
        subsContainer.innerHTML = '<div class="empty-state"><p>No restaurants/caterers registered yet.</p></div>';
        return;
    }

    // Build filter dropdown HTML with current selection preserved
    const filterOptions = subscriptionPlans.map(plan => `
        <option value="${escapeHtml(plan.id)}" ${currentPlanFilter === plan.id ? 'selected' : ''}>${escapeHtml(plan.name)}</option>
    `).join('');
    const filterHtml = `
        <div class="subscription-filter-bar" style="margin-bottom: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px;">
            <label style="font-weight: bold; margin-right: 10px;">Filter by Plan:</label>
            <select id="subscriptionPlanFilter" onchange="applyPlanFilter(this.value)" style="padding: 8px; border-radius: 4px; border: 1px solid #ddd;">
                <option value="all" ${currentPlanFilter === 'all' ? 'selected' : ''}>All Plans</option>
                ${filterOptions}
            </select>
        </div>
    `;

    // Apply plan filter if set
    let filteredProviders = [...providers];
    if (currentPlanFilter && currentPlanFilter !== 'all') {
        filteredProviders = providers.filter(p => (p.subscriptionPlan || 'free') === currentPlanFilter);
    }

    // Sort: pending first, then by date
    filteredProviders.sort((a, b) => {
        const statusOrder = { pending: 0, approved: 1, suspended: 2, rejected: 3 };
        const statusA = statusOrder[a.subscriptionStatus] ?? 4;
        const statusB = statusOrder[b.subscriptionStatus] ?? 4;
        if (statusA !== statusB) return statusA - statusB;
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });

    // Build provider cards HTML
    const providersHtml = filteredProviders.map(provider => {
        const createdDate = provider.createdAt ? formatDateTime(provider.createdAt) : 'N/A';
        const subStatus = provider.subscriptionStatus || 'pending';

        // Status badge
        let statusBadge = '';
        let statusClass = '';
        if (subStatus === 'pending') {
            statusBadge = '⏳ Pending Approval';
            statusClass = 'pending';
        } else if (subStatus === 'approved') {
            const endDate = provider.subscriptionEnd ? new Date(provider.subscriptionEnd) : null;
            const now = new Date();
            if (endDate && endDate < now) {
                statusBadge = '⚠️ Expired';
                statusClass = 'expired';
            } else {
                statusBadge = '✅ Active';
                statusClass = 'approved';
            }
        } else if (subStatus === 'rejected') {
            statusBadge = '❌ Rejected';
            statusClass = 'rejected';
        } else if (subStatus === 'suspended') {
            statusBadge = '⛔ Suspended';
            statusClass = 'suspended';
        }

        const selectedPlan = provider.subscriptionPlan || 'free';

        let subDetails = '';
        if (subStatus === 'pending') {
            subDetails = `<div class="sub-details"><span><strong>Selected Plan: ${selectedPlan.toUpperCase()}</strong></span></div>`;
        } else if (subStatus === 'approved') {
            const startDate = provider.subscriptionStart ? formatDateTime(provider.subscriptionStart) : 'N/A';
            const endDate = provider.subscriptionEnd ? formatDateTime(provider.subscriptionEnd) : 'N/A';
            subDetails = `<div class="sub-details"><span><strong>Plan: ${selectedPlan.toUpperCase()}</strong></span><span>Start: ${startDate}</span><span>End: ${endDate}</span><span>Approved by: ${provider.subscriptionApprovedBy || 'N/A'}</span></div>`;
        } else if (subStatus === 'rejected') {
            subDetails = `<div class="sub-details"><span><strong>Selected Plan: ${selectedPlan.toUpperCase()}</strong></span></div><div class="sub-details rejection">Reason: ${provider.subscriptionRejectionReason || 'No reason'}</div>`;
        } else if (subStatus === 'suspended') {
            subDetails = `<div class="sub-details"><span><strong>Plan: ${selectedPlan.toUpperCase()}</strong></span></div><div class="sub-details suspension">Reason: ${provider.subscriptionSuspendedReason || 'No reason'}</div>`;
        }

        let actionButtons = '';
        if (subStatus === 'pending') {
            actionButtons = `<button class="approve-btn" onclick="approveProvider('${provider.id || provider.uid}')">Approve</button><button class="reject-btn" onclick="rejectProvider('${provider.id || provider.uid}')">Reject</button>`;
        } else if (subStatus === 'approved') {
            actionButtons = `<button class="suspend-btn" onclick="suspendProvider('${provider.id || provider.uid}')">Suspend</button><button class="renew-btn" onclick="renewProvider('${provider.id || provider.uid}')">Renew</button><button class="change-plan-btn" onclick="showChangePlanForm('${provider.id || provider.uid}')">Change Plan</button>`;
        } else if (subStatus === 'suspended') {
            actionButtons = `<button class="approve-btn" onclick="activateProvider('${provider.id || provider.uid}')">Activate</button><button class="reject-btn" onclick="rejectProvider('${provider.id || provider.uid}')">Reject</button>`;
        } else if (subStatus === 'rejected') {
            actionButtons = `<button class="approve-btn" onclick="approveProvider('${provider.id || provider.uid}')">Approve</button>`;
        }

        return `
            <div class="admin-item subscription-item">
                <div class="admin-item-header">
                    <span class="admin-item-name">${escapeHtml(provider.name)}</span>
                    <span class="sub-status-badge ${statusClass}">${statusBadge}</span>
                </div>
                <div class="admin-item-details">
                    <span>📧 ${escapeHtml(provider.email)}</span>
                    <span>📞 ${escapeHtml(provider.phone)}</span>
                    <span>🌍 ${escapeHtml(provider.country)}</span>
                    <span>📅 Registered: ${createdDate}</span>
                </div>
                ${subDetails}
                <div class="admin-item-actions">${actionButtons}</div>
            </div>
        `;
    }).join('');

    subsContainer.innerHTML = filterHtml + providersHtml;
}

// Apply Plan Filter function (called from dropdown)
function applyPlanFilterLegacyStub(filterValue) {
    currentPlanFilter = filterValue;
    renderAdminSubscriptions();
}

// Make function global
window.applyPlanFilter = applyPlanFilter;

// Legacy stub superseded by async renderAdminOrders above
function renderAdminOrdersLegacyStub() {
    if (!isAdminLoggedIn()) return;
    const ordersList = document.getElementById('adminOrdersList');

    if (!ordersList) return;

    // Get the status filter value from dropdown
    const statusFilter = document.getElementById('adminOrderStatusFilter');
    const filterValue = statusFilter ? statusFilter.value : 'all';

    if (clientMessages.length === 0) {
        ordersList.innerHTML = '<div class="empty-state"><p>No orders yet.</p></div>';
        return;
    }

    // Filter orders based on selected status
    let filteredOrders = [...clientMessages];
    if (filterValue !== 'all') {
        filteredOrders = filteredOrders.filter(order => order.status === filterValue);
    }

    // Sort by newest first
    const sortedOrders = filteredOrders.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });

    ordersList.innerHTML = sortedOrders.map(order => {
        const statusInfo = getStatusInfo(order.status || 'pending');
        const createdDate = order.createdAt ? formatDateTime(order.createdAt) : 'N/A';

        return `
            <div class="admin-item">
                <div class="admin-item-header">
                    <span class="admin-item-name">${escapeHtml(order.foodItem)}</span>
                    <span class="order-status-badge" style="background: ${statusInfo.bg}; color: ${statusInfo.color};">
                        ${statusInfo.text}
                    </span>
                </div>
                <div class="admin-item-details">
                    <span>🏪 ${escapeHtml(order.providerName)}</span>
                    <span>👤 ${escapeHtml(order.clientName)}</span>
                    <span>📧 ${escapeHtml(order.clientEmail)}</span>
                </div>
                <div class="admin-item-stats">
                    <span>🍽️ ${escapeHtml(order.portion)}</span>
                    <span>💰 $${(order.price || 0).toFixed(2)}</span>
                    <span>🚚 ${escapeHtml(order.orderType)}</span>
                    <span>📅 ${createdDate}</span>
                </div>
            </div>
        `;
    }).join('');
}

// Legacy stub superseded by async renderAdminReviews above
function renderAdminReviewsLegacyStub() {
    if (!isAdminLoggedIn()) return;
    const reviewsList = document.getElementById('adminReviewsList');

    if (!reviewsList) return;

    if (reviews.length === 0) {
        reviewsList.innerHTML = '<div class="empty-state"><p>No reviews yet.</p></div>';
        return;
    }

    // Sort by newest first
    const sortedReviews = [...reviews].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });

    reviewsList.innerHTML = sortedReviews.map(review => {
        const createdDate = review.createdAt ? formatDateTime(review.createdAt) : 'N/A';

        return `
            <div class="admin-item">
                <div class="admin-item-header">
                    <span class="admin-item-name">${escapeHtml(review.providerName)}</span>
                    <span class="review-rating">${'★'.repeat(review.rating)}${'☆'.repeat(5 - review.rating)}</span>
                </div>
                <div class="admin-item-details">
                    <span>👤 ${escapeHtml(review.clientName)}</span>
                    <span>📧 ${escapeHtml(review.clientEmail)}</span>
                </div>
                ${review.comment ? `<div class="admin-item-comment">"${escapeHtml(review.comment)}"</div>` : ''}
                <div class="admin-item-stats">
                    <span>📅 ${createdDate}</span>
                </div>
            </div>
        `;
    }).join('');
}

function adminRecordMatchesIdentifier(record, identifier) {
    const key = String(identifier || '').trim().toLowerCase();
    if (!record || !key) return false;
    return [record.id, record.uid, record.email, record.providerId]
        .some(value => String(value || '').trim().toLowerCase() === key);
}

async function refreshAdminAfterAccountDelete() {
    if (typeof window.refreshAllAdminPanels === 'function') {
        await window.refreshAllAdminPanels();
        return;
    }
    if (typeof window.bootstrapAdminData === 'function') {
        await window.bootstrapAdminData({ force: true });
    }
    await Promise.all([renderAdminClients(), renderAdminProviders()]);
}

async function deleteAdminClient(clientIdentifier) {
    const identifier = String(clientIdentifier || '').trim();
    if (!identifier) {
        showNotification('Missing client identifier. Nothing was deleted.', 'error');
        return { deleted: false };
    }
    if (!confirm('Are you sure you want to permanently delete this client, their login account, and all attached data? This action cannot be undone.')) {
        return { deleted: false };
    }
    if (typeof window.adminBackendDeleteClient !== 'function') {
        showNotification('Secure client deletion is unavailable. Nothing was deleted.', 'error');
        return { deleted: false };
    }

    try {
        const result = await window.adminBackendDeleteClient({ uidOrIdOrEmail: identifier });
        if (!result || result.deleted !== true || (result.authDeleted !== true && result.authAlreadyAbsent !== true)) {
            throw new Error('The server did not confirm complete client account deletion.');
        }

        clients = clients.filter(client => !adminRecordMatchesIdentifier(client, identifier));
        syncWindowCollectionState();
        if (currentClient && adminRecordMatchesIdentifier(currentClient, identifier)) {
            currentClient = null;
        }
        await refreshAdminAfterAccountDelete();
        showNotification('Client login account and all attached data were deleted successfully.', 'success');
        return result;
    } catch (error) {
        console.error('[Admin] Complete client deletion failed:', error);
        showNotification(`Client was not completely deleted: ${error.message || 'server error'}`, 'error');
        return { deleted: false, error: error.message || 'server error' };
    }
}

async function deleteAdminProvider(providerIdentifier) {
    const identifier = String(providerIdentifier || '').trim();
    if (!identifier) {
        showNotification('Missing provider identifier. Nothing was deleted.', 'error');
        return { deleted: false };
    }
    if (!confirm('Are you sure you want to permanently delete this restaurant/caterer, their login account, menu items, and all attached data? This action cannot be undone.')) {
        return { deleted: false };
    }
    if (typeof window.adminBackendDeleteProvider !== 'function') {
        showNotification('Secure provider deletion is unavailable. Nothing was deleted.', 'error');
        return { deleted: false };
    }

    try {
        const result = await window.adminBackendDeleteProvider({ uidOrIdOrEmail: identifier });
        if (!result || result.deleted !== true || (result.authDeleted !== true && result.authAlreadyAbsent !== true)) {
            throw new Error('The server did not confirm complete provider account deletion.');
        }

        providers = providers.filter(provider => !adminRecordMatchesIdentifier(provider, identifier));
        menuItems = menuItems.filter(item => String(item.providerId || '').trim().toLowerCase() !== identifier.toLowerCase());
        syncWindowCollectionState();
        if (currentProvider && adminRecordMatchesIdentifier(currentProvider, identifier)) {
            currentProvider = null;
        }
        await refreshAdminAfterAccountDelete();
        showNotification('Provider login account and all attached data were deleted successfully.', 'success');
        return result;
    } catch (error) {
        console.error('[Admin] Complete provider deletion failed:', error);
        showNotification(`Provider was not completely deleted: ${error.message || 'server error'}`, 'error');
        return { deleted: false, error: error.message || 'server error' };
    }
}

// Make admin functions global
window.deleteAdminClient = deleteAdminClient;
window.deleteAdminProvider = deleteAdminProvider;

// ==================== SITE SETTINGS (SUPER ADMIN) ====================
// Legacy stub superseded by renderAdminSettings above
function renderAdminSettingsLegacyStub() {
    if (!isAdminLoggedIn()) return;
    const settingsContainer = document.getElementById('adminSettingsPanel');

    if (!settingsContainer) return;

    const settings = loadSiteSettings();

    // Load footer text into textarea
    const footerTextInput = document.getElementById('footerTextInput');
    const footerPreview = document.getElementById('footerPreview');

    if (footerTextInput) {
        footerTextInput.value = settings.footerText;
    }
    if (footerPreview) {
        footerPreview.textContent = settings.footerText;
    }

    // Also update preview in real-time when user types
    if (footerTextInput && footerPreview) {
        footerTextInput.addEventListener('input', function() {
            footerPreview.textContent = this.value;
        });
    }
}

// Save Footer Text
function saveFooterText() {
    const footerTextInput = document.getElementById('footerTextInput');
    if (!footerTextInput) return;

    const newFooterText = footerTextInput.value.trim();

    if (!newFooterText) {
        showNotification('Footer text cannot be empty', 'error');
        return;
    }

    // Save to browserStorage
    const settings = loadSiteSettings();
    settings.footerText = newFooterText;
    saveSiteSettings(settings);

    // Re-render footer
    renderFooter();

    showNotification('Footer text saved successfully!', 'success');
}

// Reset Footer Text to Default
function resetFooterText() {
    if (confirm('Are you sure you want to reset the footer text to default?')) {
        const settings = loadSiteSettings();
        settings.footerText = DEFAULT_SITE_SETTINGS.footerText;
        saveSiteSettings(settings);

        // Re-render footer
        renderFooter();

        // Also reset the input field in settings panel
        const footerTextInput = document.getElementById('footerTextInput');
        if (footerTextInput) {
            footerTextInput.value = DEFAULT_SITE_SETTINGS.footerText;
        }

        // Update preview
        const footerPreview = document.getElementById('footerPreview');
        if (footerPreview) {
            footerPreview.textContent = DEFAULT_SITE_SETTINGS.footerText;
        }

        showNotification('Footer text reset to default!', 'success');
    }
}

// ==================== RECURRING SUBSCRIPTION RATES (SUPER ADMIN) ====================
// Default monthly recurring rates for provider subscription plans.
// These are used for Stripe billing and displayed to providers during registration.
var DEFAULT_SUBSCRIPTION_RATES = {
    free: 0.00,
    basic: 100.50,
    premium: 200.00
};

// Load subscription rates from Firestore cache or defaults
function loadSubscriptionRates() {
    const plans = (typeof subscriptionPlans !== 'undefined' && Array.isArray(subscriptionPlans)) ? subscriptionPlans : (window.subscriptionPlans || []);
    const defaultRates = plans.reduce((rates, plan) => {
        rates[plan.id] = Number.isFinite(Number(plan.price)) ? Number(plan.price) : 0;
        return rates;
    }, {});

    if (window.__subscriptionRatesCache && typeof window.__subscriptionRatesCache === 'object') {
        return { ...defaultRates, ...window.__subscriptionRatesCache };
    }

    return defaultRates;
}

async function persistSubscriptionRates(rates) {
    const settings = loadSiteSettings();
    const db = await getEffectiveFirestoreDb();
    if (typeof window.__saveSettingsToFirestore === 'function') {
        return window.__saveSettingsToFirestore(settings, rates);
    }
    if (!db) {
        console.warn('[Settings] Firestore not available, cannot persist subscription rates');
        return false;
    }

    try {
        await db.collection('siteSettings').doc('main').set({
            footerText: settings.footerText,
            basicRate: rates.basic,
            premiumRate: rates.premium,
            updatedAt: new Date().toISOString()
        }, { merge: true });
        return true;
    } catch (e) {
        console.error('[Settings] Could not persist subscription rates to Firestore:', e);
        return false;
    }
}

async function saveSubscriptionRates() {
    if (!isAdminLoggedIn()) {
        showNotification('You must be logged in as Super Admin.', 'error');
        return;
    }

    const basicInput = document.getElementById('basicPlanRate');
    const premiumInput = document.getElementById('premiumPlanRate');

    if (!basicInput || !premiumInput) {
        showNotification('Subscription rate inputs not found.', 'error');
        return;
    }

    const basicRate = parseFloat(basicInput.value);
    const premiumRate = parseFloat(premiumInput.value);

    if (isNaN(basicRate) || basicRate < 0) {
        showNotification('Please enter a valid Basic Plan rate (0 or more).', 'error');
        basicInput.focus();
        return;
    }
    if (isNaN(premiumRate) || premiumRate < 0) {
        showNotification('Please enter a valid Premium Plan rate (0 or more).', 'error');
        premiumInput.focus();
        return;
    }

    const rates = {
        free: 0.00,
        basic: basicRate,
        premium: premiumRate
    };

    const saved = await persistSubscriptionRates(rates);
    if (!saved) {
        showNotification('Failed to save subscription rates. Check Firestore connection.', 'error');
        return;
    }

    window.__subscriptionRatesCache = { ...rates };

    if (window.stripeConfig && window.stripeConfig.plans) {
        window.stripeConfig.plans.basic.price = basicRate;
        window.stripeConfig.plans.premium.price = premiumRate;
        window.stripeConfig.plans.free.price = 0;
    }

    if (Array.isArray(subscriptionPlans)) {
        subscriptionPlans.forEach(plan => {
            if (plan.id === 'basic') plan.price = basicRate;
            else if (plan.id === 'premium') plan.price = premiumRate;
            else if (plan.id === 'free') plan.price = 0;
        });
    }
    saveSubscriptionPlans();

    updateProviderPlanDropdown();

    const statusEl = document.getElementById('subscriptionRatesStatus');
    if (statusEl) {
        statusEl.textContent = `✅ Rates saved: Basic $${basicRate.toFixed(2)}/mo, Premium $${premiumRate.toFixed(2)}/mo`;
        setTimeout(() => { statusEl.textContent = ''; }, 5000);
    }

    showNotification('Subscription rates saved successfully!', 'success');
    console.log('[Settings] Subscription rates saved:', rates);
}

// Reset subscription rates to defaults
async function resetSubscriptionRates() {
    if (!isAdminLoggedIn()) {
        showNotification('You must be logged in as Super Admin.', 'error');
        return;
    }

    const defaultRates = getDefaultSubscriptionRates();
    if (!confirm(`Reset subscription rates to defaults? Basic: $${defaultRates.basic.toFixed(2)}, Premium: $${defaultRates.premium.toFixed(2)}`)) {
        return;
    }

    const rates = {
        free: 0.00,
        basic: DEFAULT_SUBSCRIPTION_RATES.basic,
        premium: DEFAULT_SUBSCRIPTION_RATES.premium
    };

    const saved = await persistSubscriptionRates(rates);
    if (!saved) {
        showNotification('Failed to reset subscription rates. Check Firestore connection.', 'error');
        return;
    }

    window.__subscriptionRatesCache = { ...rates };

    const basicInput = document.getElementById('basicPlanRate');
    const premiumInput = document.getElementById('premiumPlanRate');
    if (basicInput) basicInput.value = rates.basic.toFixed(2);
    if (premiumInput) premiumInput.value = rates.premium.toFixed(2);

    if (window.stripeConfig && window.stripeConfig.plans) {
        window.stripeConfig.plans.basic.price = rates.basic;
        window.stripeConfig.plans.premium.price = rates.premium;
        window.stripeConfig.plans.free.price = 0;
    }

    if (Array.isArray(subscriptionPlans)) {
        subscriptionPlans.forEach(plan => {
            if (plan.id === 'basic') plan.price = rates.basic;
            else if (plan.id === 'premium') plan.price = rates.premium;
            else if (plan.id === 'free') plan.price = 0;
        });
    }
    saveSubscriptionPlans();

    updateProviderPlanDropdown();

    const statusEl = document.getElementById('subscriptionRatesStatus');
    if (statusEl) {
        const defaultRates = getDefaultSubscriptionRates();
        statusEl.textContent = `✅ Rates reset to defaults: Basic $${defaultRates.basic.toFixed(2)}/mo, Premium $${defaultRates.premium.toFixed(2)}/mo`;
        setTimeout(() => { statusEl.textContent = ''; }, 5000);
    }

    showNotification('Subscription rates reset to defaults!', 'success');
}

// Get the current subscription rate for a plan
function getSubscriptionRate(planId) {
    const plan = Array.isArray(subscriptionPlans) ? subscriptionPlans.find(p => p.id === planId) : null;
    if (plan && Number.isFinite(Number(plan.price))) {
        return Number(plan.price);
    }
    const rates = loadSubscriptionRates();
    const defaultRates = getDefaultSubscriptionRates();
    return rates[planId] !== undefined ? rates[planId] : (defaultRates[planId] || 0);
}

// Legacy stub superseded by updateProviderPlanDropdown above
function updateProviderPlanDropdownLegacyStub() {
    const planSelect = document.getElementById('providerPlan');
    if (!planSelect) return;

    const basicRate = getSubscriptionRate('basic');
    const premiumRate = getSubscriptionRate('premium');

    planSelect.innerHTML = `
        <option value="free">Free Plan - $0/month (admin approval required)</option>
        <option value="basic">Basic Plan - $${basicRate.toFixed(2)}/month (recurring subscription)</option>
        <option value="premium">Premium Plan - $${premiumRate.toFixed(2)}/month (recurring subscription)</option>
    `;
}

// Make subscription rates functions global
window.saveSubscriptionRates = saveSubscriptionRates;
window.resetSubscriptionRates = resetSubscriptionRates;
window.loadSubscriptionRates = loadSubscriptionRates;
window.getSubscriptionRate = getSubscriptionRate;
window.updateProviderPlanDropdown = updateProviderPlanDropdown;

function initSubscriptionRatesInputs() {
    const rates = loadSubscriptionRates() || {};
    const defaultRates = getDefaultSubscriptionRates();
    const basicInput = document.getElementById('basicPlanRate');
    const premiumInput = document.getElementById('premiumPlanRate');
    const freeInput = document.getElementById('freePlanRate');
    if (basicInput) basicInput.value = (rates && rates.basic !== undefined ? Number(rates.basic) : defaultRates.basic).toFixed(2);
    if (premiumInput) premiumInput.value = (rates && rates.premium !== undefined ? Number(rates.premium) : defaultRates.premium).toFixed(2);
    if (freeInput) freeInput.value = (rates && rates.free !== undefined ? Number(rates.free) : defaultRates.free).toFixed(2);
}

// Initialize Settings Buttons (Save and Reset)
function initSettingsButtons() {
    // These are handled via onclick attributes in renderAdminSettings
    // But we can add keyboard shortcuts here if needed
    document.addEventListener('keydown', (e) => {
        // Ctrl+S to save settings when in settings panel
        if (e.ctrlKey && e.key === 's') {
            const settingsContainer = document.getElementById('adminSettingsPanel');
            if (settingsContainer && !settingsContainer.classList.contains('hidden')) {
                e.preventDefault();
                saveFooterText();
            }
        }
    });
}

// Make settings functions global
window.saveFooterText = saveFooterText;
window.resetFooterText = resetFooterText;
